// src/data/eventsData.js - ENHANCED: Added city fields to all events + cities configuration

// NEW: Cities configuration for multi-city support
export const citiesConfig = {
  tokyo: {
    name: "Tokyo",
    flag: "🗾",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    currencySymbol: "¥",
    region: "Asia Pacific"
  },
  osaka: {
    name: "Osaka",
    flag: "🏯", // Castle emoji representing Osaka Castle
    country: "Japan",
    timezone: "Asia/Tokyo", // Same timezone as Tokyo
    currency: "JPY",
    currencySymbol: "¥",
    region: "Asia Pacific"
  },
  fukuoka: {
    name: "Fukuoka",
    flag: "🏮",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    currencySymbol: "¥",
    region: "Asia Pacific"
  },
  honolulu: {
    name: "Honolulu", 
    flag: "🏝️",
    country: "USA",
    timezone: "Pacific/Honolulu",
    currency: "USD",
    currencySymbol: "$",
    region: "North America"
  },
  chicago: {
    name: "Chicago", 
    flag: "🏙️",
    country: "USA",
    timezone: "Central/Chicago",
    currency: "USD",
    currencySymbol: "$",
    region: "North America"
  },
  "st-petersburg": {
    name: "St. Petersburg",
    flag: "🇺🇸",
    country: "United States",
    timezone: "America/New_York",
    currency: "USD",
    currencySymbol: "$",
    region: "North America"
  },
  
  lisbon: {
    name: "Lisbon",
    flag: "🇵🇹",
    country: "Portugal",
    timezone: "Europe/Lisbon",
    currency: "EUR",
    currencySymbol: "€",
    region: "Europe"
  },

    taipei: {
    name: "Taipei",
    flag: "🇹🇼",
    country: "Taiwan",
    timezone: "Asia/Taiwan",
    currency: "TWD",
    currencySymbol: "NT$",
    region: "Asia"
  }


  // Future cities can be easily added here
};

// Helper function to get city configuration
export const getCityConfig = (citySlug) => {
  return citiesConfig[citySlug] || citiesConfig.tokyo; // Default to Tokyo
};

// Helper function to get all available cities with event counts
export const getAvailableCities = (events) => {
  const cityStats = {};
  
  events.forEach(event => {
    const city = event.city || 'tokyo';
    if (!cityStats[city]) {
      cityStats[city] = {
        ...getCityConfig(city),
        slug: city,
        eventCount: 0,
        upcomingCount: 0
      };
    }
    cityStats[city].eventCount++;
    if (event.status === 'open') {
      cityStats[city].upcomingCount++;
    }
  });
  
  return Object.values(cityStats);
};

export const enhancedEventsData = [
  {
    id: 'fika-at-33rpm-october-2026',
    eventType: 'fika',
    iconType: 'image',
    iconImage: '/images/events/Turntable.jpg',
    iconValue: '🎙️',
    title: "Fika at 33⅓: A Vinyl Listening & Coffee Social + Optional Dinner",
    description: "Fika meets the listening bar. Slow down, tune in, and connect over vinyl records spinning through tube amplifiers. Conversation is welcome — but so is the silence between notes. Stay on after for an optional dinner at a nearby spot.",
    waitlist: "0",
    next: "Sat, October 17, 2026",
    time: "5:00 PM - 7:00 PM",
    eventDateTime: "2026-10-17T08:00:00Z", // 5:00 PM JST

    location: "TBD",
    color: "from-stone-700 to-amber-800",
    capacity: 7,
    difficulty: "Casual Social",
    tags: ["Vinyl", "Coffee", "Listening Bar", "Music", "Fika", "Analog", "Slow Living", "Community"],
    featured: true,
    slug: 'fika-at-33rpm-october-2026',

    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    cost: "Pay for your own drinks/food",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },

    philosophy: {
      title: "Fika at 33⅓: A Vinyl Listening & Coffee Social",
      sections: [
        {
          type: 'paragraph',
          content: "Most gatherings ask you to fill the silence. This one asks you to hear it. Fika at 33⅓ is our listening-first social — an afternoon spent in one of Tokyo's finest record cafés, where hand-drip coffee arrives in Nordic antique cups, vinyl rotates at the correct speed, and the conversation finds its own pace around the music."
        },
        {
          type: 'paragraph',
          content: "The Swedish concept of fika has always been about more than coffee. It's about giving yourself — and the people around you — permission to stop. To actually be somewhere. A vinyl listening bar does the same thing from a different direction: the music slows you down before you even realize it's happening. Put the two together, and something shifts. People listen differently. Conversations go places they wouldn't in a louder room."
        },
        {
          type: 'header',
          content: "What Makes This Different"
        },
        {
          type: 'paragraph',
          content: "This isn't a fika with background music. The records are the point. A staff DJ spins all afternoon — jazz, rock, classical, electro, the occasional left-field surprise — through vacuum tube amplifiers and Tannoy speakers that fill the room without overwhelming it. You'll hear things in the music you've never noticed before. That's not an accident. It's what a room like this is designed to do."
        },
        {
          type: 'paragraph',
          content: "Between tracks, conversation opens up. Within tracks, it quiets to something more intimate — a comment about the song, a shared look across the table, the kind of small moment that somehow sticks. Fika has always been built on exactly these: the unhurried, unremarkable exchanges that turn strangers into regulars."
        },
        {
          type: 'header',
          content: "The Setting"
        },
        {
          type: 'paragraph',
          content: "Our venue is a record café and wine bar — a room of concrete walls, amber light, vinyl-lined shelves, and a counter where you can watch the record spin and the coffee pour in the same glance. It seats a handful of people at a time. The intimacy is the whole idea."
        },
        {
          type: 'header',
          content: "What to Expect"
        },
        {
          type: 'bullets',
          items: [
            "🎵 Live vinyl spinning all evening — jazz, rock, classical, electro, and surprises — through tube amplifiers and serious speakers",
            "☕ Espresso drinks, coffee cocktails, natural wine, beer, and soft drinks at a beautifully curated record café",
            "🥐 Light café food to accompany your evening — cheese on toast, seasonal sweets, and more",
            "👥 Intimate group of 8 — small enough for real conversation, quiet enough to actually hear each other",
            "🎙️ Conversation is welcomed and encouraged — the music sets the pace, not the other way around",
            "💳 Individual drinks and food ordered and paid at the venue",
            "🍽️ Optional dinner at a nearby spot after — details shared on the day"
          ]
        },
        {
          type: 'header',
          content: "This One is For You If..."
        },
        {
          type: 'bullets',
          items: [
            "You've always wanted to visit a Tokyo listening bar but weren't sure how to walk in alone",
            "You love music and wish more social gatherings treated it as something to actually pay attention to",
            "You're drawn to analog things in a digital world — vinyl, drip coffee, real conversation",
            "You want to meet people without the noise and pressure of a typical social event",
            "You believe that the best conversations happen sideways — while you're both listening to something else"
          ]
        },
        {
          type: 'paragraph',
          content: "Fika at 33⅓ runs at the right speed. Come and hear for yourself."
        },
        {
          type: 'header',
          content: "Good to Know"
        },
        {
          type: 'bullets',
          items: [
            "Venue is smoke-free — comfortable for everyone",
            "Seating is intimate and limited — RSVP is essential",
            "Coffee, coffee cocktails, wine, beer, soft drinks, and food ordered and paid individually — budget approx. ¥800–1,500",
            "Capacity: 8 people — this one stays small by design",
            "Optional dinner at a nearby spot after the event — no pressure, details on the day"
          ]
        }
      ]
    },

    whatToExpect: [
      "Vinyl spinning all evening through tube amplifiers and serious speakers — jazz, rock, classical, and more",
      "Espresso drinks, coffee cocktails, natural wine, beer, and soft drinks at a beautifully curated record café",
      "Intimate group of 8 for conversation that moves at the pace of the music",
      "A social format where silence is welcome and conversation earns its moment",
      "Optional dinner at a nearby spot for those who want to keep the evening going"
    ],

    venue: {
      name: "TBD",
      description: "A carefully chosen record café and listening bar — concrete walls, Tannoy speakers, vacuum tube amplifiers, and vinyl-lined shelves. The kind of room Tokyo does better than anywhere.",
      fullAddress: "TBD",
      priceRange: "Individual coffee and food (approx. ¥800–1,500)",
      amenities: [
        "Vinyl listening bar",
        "Hand-drip specialty coffee",
        "Tube amplifiers and Tannoy speakers",
        "Nordic antique cup collection",
        "Cashless payments accepted",
        "Smoke-free environment",
        "Small art gallery space"
      ]
    },

    memberBenefits: [
      {
        icon: "🎵",
        title: "Vinyl, Properly Played",
        description: "Staff-curated records through vacuum tube amplifiers and Tannoy speakers — the sound that makes you understand why analog never died"
      },
      {
        icon: "🍷",
        title: "Drink What You Like",
        description: "Espresso drinks, coffee cocktails, natural wine, beer, and soft drinks — something for everyone, ordered individually at the bar"
      },
      {
        icon: "👥",
        title: "8 People. One Room.",
        description: "An intentionally small group where the intimacy is the feature, not the limitation"
      },
      {
        icon: "🔊",
        title: "A New Kind of Social",
        description: "Fika's slow philosophy meets Tokyo's listening bar culture — a format you won't find anywhere else in the city"
      }
    ],

    relatedEvents: ['authentic-swedish-fika-june-sunday-2026', 'weekly-fika', 'drift-happy-hour-june-2026'],
    status: "open",

    eventFrequency: "monthly",
    dayOfWeek: "Saturday",
    culturalFocus: "Vinyl listening culture meets Swedish fika tradition",
    specialFeature: "Live vinyl DJ through tube amplifiers in an intimate record café, with optional dinner after",

    socialProof: {
      totalAttended: 0,
      averageRating: null,
      repeatAttendees: null
    },

    hostInfo: {
      name: "Mark",
      bio: "Chasing the overlap between good coffee, good music, and the kind of conversations that only happen when you slow everything down.",
      avatar: "mark-hosts.jpg"
    },

    listeningNotes: "This is an active listening environment. The music is meant to be heard, not ignored. Conversation is warmly encouraged — but you may find yourself pausing mid-sentence to catch a chord. That's exactly the point.",

    whatToBring: [
      "Cash or card for your drinks and food order",
      "An open ear — the genre selection moves across jazz, rock, classical, electro, and beyond",
      "Curiosity about whoever ends up sitting next to you"
    ],

    idealFor: [
      "Vinyl enthusiasts and curious newcomers alike",
      "Anyone who finds loud, crowded events exhausting",
      "Coffee people who want music taken as seriously as the coffee",
      "Fika regulars looking for something new",
      "People who believe the best conversations happen while you're both listening to something else"
    ]
  },
  
  {
    id: 'koenji-morning-walk-october-2026',
    eventType: 'walks',
    iconType: 'image',
    iconImage: '/images/events/KoenjiWalk.jpg',
    iconValue: '🏮',
    title: "Keep It Real: A Koenji Morning Walk & Coffee Crawl + Optional food&drinks in the neighborhood at the end ",
    description: "Tokyo's most rebellious neighborhood doesn't open early — but when it does, it opens well. A 2–3.5 hour morning walk through Koenji's labyrinthine shotengai, starting at Japan's only weather shrine, crawling three specialty coffee spots, and winding through vintage stores, record shops, and backstreets that have been quietly resisting gentrification for decades. Optional food and drinks in the neighborhood after.",
    waitlist: "0",
    next: "Sat, October 10, 2026",
    time: "11:00 AM (2–3.5 hours)",
    eventDateTime: "2026-10-10T02:00:00Z", // 11:00 AM JST

    location: "Koenji, Tokyo — TBD (exact meeting point sent 24 hours before)",
    color: "from-zinc-700 to-stone-600",
    capacity: 7,
    difficulty: "Easy Urban Walk",
    tags: ["Koenji", "Morning Walk", "Coffee Crawl", "Shotengai", "Vintage", "Record Stores", "Shrine", "Tokyo Neighborhoods", "Community", "Hidden Gems", "Analog Tokyo"],
    featured: true,
    slug: 'koenji-morning-walk-october-2026',

    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    cost: "Pay for your own coffee and food",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },

    philosophy: {
      title: "Keep It Real: A Koenji Morning Walk & Coffee Crawl",
      sections: [
        {
          type: 'paragraph',
          content: "In a city that moves fast and rebuilds faster, Koenji has spent decades doing the opposite. While the neighborhoods around it have been smoothed out, sanitized and optimized, this pocket of western Tokyo — a few stations from Shinjuku on the Chuo Line — has kept its rough edges on purpose. Former punk scene epicenter. Home to one of Tokyo's most vocal communities against redevelopment. A neighborhood that once organized a protest against a Starbucks opening, and lost, and kept going anyway."
        },
        {
          type: 'paragraph',
          content: "Keep It Real is a 2–3.5 hour morning walk through Koenji's best version of itself. We move through its famously tangled shotengai — shopping streets that sprawl and branch and double back on themselves, lined with vintage clothing, record stores, tiny bakeries, and bars that won't open for hours but look like they contain entire lives. We make three coffee stops along the way. We start at a shrine unlike any other in Japan."
        },
        {
          type: 'header',
          content: "Where We Start"
        },
        {
          type: 'paragraph',
          content: "Our first stop is Kisho Jinja — Japan's only Shinto shrine dedicated to the weather deities. It's a genuine outlier in a neighborhood full of them: quietly famous among locals, almost entirely unknown to visitors. A good place to begin a walk through a neighborhood that has always done things its own way."
        },
        {
          type: 'header',
          content: "The Coffee Crawl"
        },
        {
          type: 'paragraph',
          content: "Three coffee stops are woven into the route. Each one reflects something different about Koenji — the neighborhood has a genuine specialty coffee scene that developed long before it became fashionable elsewhere in Tokyo. Stops are chosen for quality and character, not for being well-known. Names revealed on the day."
        },
        {
          type: 'header',
          content: "The Streets"
        },
        {
          type: 'paragraph',
          content: "Koenji's shotengai are best experienced slowly and without a specific destination. We'll move through the arcade streets north and south of the station, past vintage stores that have been curating their racks for decades, record shops with handwritten recommendation cards in the windows, and the occasional irresistible detour. The neighborhood rewards curiosity more than planning."
        },
        {
          type: 'header',
          content: "What to Expect"
        },
        {
          type: 'bullets',
          items: [
            "⛩️ Kisho Jinja — Japan's only weather shrine, a genuinely unusual first stop that sets the tone for the whole walk",
            "☕ Three specialty coffee stops woven into the route — names revealed on the day, all chosen for quality and neighborhood character",
            "🏮 Koenji's labyrinthine shotengai — vintage clothing, record stores, bakeries, and backstreets that don't appear on most maps",
            "⏱️ Duration: 2–3.5 hours at a relaxed pace with stops — no rushing, no forced marching",
            "👥 Six people maximum — small enough to actually move through the narrow streets as a group",
            "🍜 Optional food and drinks in the neighborhood at the end — Koenji has excellent options and we'll share recommendations on the day"
          ]
        },
        {
          type: 'header',
          content: "This One is For You If..."
        },
        {
          type: 'bullets',
          items: [
            "You've been to Shimokitazawa and want to know what's west of it",
            "You find Tokyo's more polished neighborhoods slightly exhausting",
            "You have opinions about record stores and are willing to share them",
            "You want to meet people while actually going somewhere, not standing in a bar",
            "The words 'labyrinthine shopping arcade' made you stop reading and start RSVPing"
          ]
        },
        {
          type: 'paragraph',
          content: "Koenji keeps it real. We're just following its lead."
        },
        {
          type: 'header',
          content: "Optional: Food & Drinks After"
        },
        {
          type: 'paragraph',
          content: "For those who want to keep the morning going, we'll share neighborhood recommendations for lunch or a post-walk drink. Koenji has no shortage of good options — from tempura lunch spots to bars that feel like they've been there forever. No pressure, no agenda. Just the natural extension of a good morning."
        },
        {
          type: 'header',
          content: "Good to Know"
        },
        {
          type: 'bullets',
          items: [
            "Arrive by 10:45 AM at the meeting point — walk departs promptly at 11:00 AM",
            "Duration: 2–3.5 hours with coffee stops along the route",
            "Budget approx. ¥1,000–2,000 for three coffee stops",
            "Route is flat and walkable — comfortable shoes recommended",
            "Exact meeting point disclosed to registered attendees 24 hours before the event",
            "Capacity: 6 people — kept small so we can actually move through the narrow streets together"
          ]
        }
      ]
    },

    whatToExpect: [
      "Kisho Jinja — Japan's only Shinto shrine dedicated to the weather deities",
      "Three specialty coffee stops woven into the route",
      "Koenji's labyrinthine shotengai — vintage, records, bakeries, and backstreets",
      "2–3.5 hours at a relaxed pace with a group of six",
      "Optional food and drinks in the neighborhood to close out the morning"
    ],

    venue: {
      name: "TBD",
      description: "Koenji, Tokyo — one of the city's last genuinely rebellious neighborhoods, a few stations west of Shinjuku on the Chuo Line. Exact meeting point shared 24 hours before.",
      fullAddress: "Koenji, Suginami City, Tokyo — exact meeting point disclosed to confirmed attendees 24 hours before the event",
      priceRange: "Individual coffee at three stops (approx. ¥1,000–2,000 total)",
      amenities: [
        "Morning neighborhood walk",
        "Three specialty coffee stops",
        "Kisho Jinja weather shrine",
        "Shotengai exploration",
        "Optional post-walk food and drinks",
        "English-language social",
        "Small intimate group"
      ]
    },

    memberBenefits: [
      {
        icon: "⛩️",
        title: "Japan's Only Weather Shrine",
        description: "Kisho Jinja is genuinely one of a kind — a Shinto shrine dedicated to the weather deities, tucked into a neighborhood that does everything its own way"
      },
      {
        icon: "☕",
        title: "Three Coffee Stops",
        description: "A proper crawl — three specialty coffee spots woven into the route, each reflecting a different side of Koenji's character"
      },
      {
        icon: "🏮",
        title: "The Shotengai",
        description: "Koenji's shopping arcades are among Tokyo's best for getting genuinely lost — vintage, records, bakeries, and the occasional discovery that makes the whole walk"
      },
      {
        icon: "👥",
        title: "Six People. One Neighborhood.",
        description: "Small enough to actually move through the narrow streets together, intimate enough that the conversation keeps going long after the coffee does"
      }
    ],

    relatedEvents: ['kabutocho-evening-walk-august-2026', 'weekly-fika', 'drift-happy-hour-august-2026'],
    status: "open",

    eventFrequency: "occasional",
    dayOfWeek: "Sunday",
    walkDuration: "2–3.5 hours",
    language: "English",
    culturalFocus: "Tokyo neighborhood character, specialty coffee culture, shotengai shopping streets",
    specialFeature: "Starts at Japan's only weather shrine; three specialty coffee crawl through Koenji's labyrinthine shotengai",

    socialProof: {
      totalAttended: 0,
      averageRating: null,
      repeatAttendees: null
    },

    hostInfo: {
      name: "Mark",
      bio: "Perpetually looking for the Tokyo neighborhoods that haven't been smoothed out yet — and finding the best coffee in them while he's there.",
      avatar: "mark-hosts.jpg"
    },

    locationPolicy: "Exact meeting point shared with confirmed attendees 24 hours before the event.",

    whatToBring: [
      "Cash and card — some of the best spots in Koenji are cash only",
      "Comfortable walking shoes — 2–3.5 hours on foot through narrow streets",
      "Camera or phone — Koenji's visual texture rewards it",
      "An appetite for good coffee and whatever detour the shotengai suggests"
    ],

    idealFor: [
      "Anyone who's been to Shimokitazawa and wants to go further west",
      "Record store people, vintage clothing people, or people who pretend not to be either",
      "Coffee drinkers who want three stops and a good reason for each one",
      "Anyone who finds Tokyo's more polished neighborhoods slightly exhausting",
      "People who believe the best way to meet someone is to actually go somewhere with them"
    ],

    weatherPolicy: "Event proceeds rain or shine — the shotengai are covered arcades and the coffee stops provide shelter. Check the forecast and bring an umbrella for October mornings.",

    accessibilityNotes: "Route is flat throughout Koenji. Please reach out in advance if you have mobility concerns.",

    seasonalNote: "October is ideal for Koenji — summer heat is gone, the neighborhood's event calendar is active, and the covered shotengai are particularly good for an unhurried morning crawl."
  },

  {
    id: 'kabutocho-evening-walk-september-2026',
    eventType: 'walks',
    iconType: 'image',
    iconImage: '/images/events/KabutoChoWalk.jpg',
    iconValue: '🏛️',
    title: "After the Bell: A Kabutocho Evening Walk + Optional Wine & Cocktails/Mocktails",
    description: "Tokyo's answer to Wall Street — but after hours, it's something else entirely. A 30–45 minute evening walk through Kabutocho's Meiji-era financial streets, renovated bank buildings, and hidden gems, with stops for ice cream or coffee along the way. Optional wine or cocktails after to close out the night.",
    waitlist: "0",
    next: "Sun, September 20, 2026",
    time: "5:00 PM - 7:30 PM",
    eventDateTime: "2026-09-20T08:00:00Z", // 5:00 PM JST

    location: "Kabutocho, Tokyo — TBD",
    color: "from-stone-700 to-slate-800",
    capacity: 5,
    difficulty: "Easy Urban Walk",
    tags: ["Kabutocho", "Evening Walk", "Financial District", "Tokyo Stock Exchange", "Hidden Gems", "Coffee", "Ice Cream", "Wine Bar", "Architecture", "Community", "Slow Living", "Weekend"],
    featured: true,
    slug: 'kabutocho-evening-walk-september-2026',

    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    cost: "Pay for your own drinks/food",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },

    philosophy: {
      title: "After the Bell: A Kabutocho Evening Walk",
      sections: [
        {
          type: 'paragraph',
          content: "By day, Kabutocho is all business. Stock tickers scroll across a giant revolving cube in the lobby of Kabuto One, armies of financiers move between Meiji-era stone facades, and the whole neighborhood hums with the particular energy of money being made at speed. But the bell rings at 3:30 PM. And after that, something shifts."
        },
        {
          type: 'paragraph',
          content: "After the Bell is a 30–45 minute evening walk through one of Tokyo's most quietly fascinating neighborhoods — Japan's answer to Wall Street, now in the middle of its own unlikely renaissance. Since 2017, Kabutocho has been quietly reinventing itself without forgetting what it is. Boutique hotels have moved into 1920s bank vaults. Craft coffee shops and artisan bakeries have taken up residence between brokerage houses. A sake brewery opened inside an elegant timber-frame building. The Tokyo Stock Exchange still stands at the neighborhood's center — a surprisingly good photo spot, and a reminder that the neighborhood was, and still is, the beating financial heart of Japan."
        },
        {
          type: 'header',
          content: "What Makes Kabutocho Different"
        },
        {
          type: 'paragraph',
          content: "This isn't Shibuya or Shimokitazawa. Kabutocho doesn't perform for visitors. The revitalization here has been deliberate and low-key — old buildings renovated rather than demolished, new businesses chosen for quality rather than footfall. The result is a neighborhood that feels like a discovery: dense with good architecture, genuinely interesting places to stop, and the particular quiet that descends on a financial district after hours. In summer, that evening light hitting the stone facades of the old exchange buildings is something else."
        },
        {
          type: 'paragraph',
          content: "The neighborhood sits in the Nihonbashi area of downtown Tokyo, close to the Tsukiji and Hamarikyu waterfront, with Kayabacho Station right at its doorstep. It's central, easy to reach, and almost entirely off the tourist trail."
        },
        {
          type: 'header',
          content: "How the Walk Works"
        },
        {
          type: 'paragraph',
          content: "We move as a loose group through Kabutocho's compact backstreets — past the Tokyo Stock Exchange, through the renovated bank buildings turned coffee shops and hotels, down the lanes that look like they haven't changed since the Showa era but contain excellent things if you know where to look. The walk itself takes 30–45 minutes at a relaxed pace, with stops for ice cream or coffee along the way. No rushing, no forced marching. Just a neighborhood unfolding at the right speed."
        },
        {
          type: 'bullets',
          items: [
            "🏛️ The Tokyo Stock Exchange building — Japan's financial heartbeat and a surprisingly compelling piece of architecture",
            "🏦 Renovated Meiji and Taisho-era bank buildings, including the boutique hotel K5 (a former 1920s bank) and the Bank Kabutocho complex",
            "🍦 Stops for ice cream or coffee along the route — Kabutocho now has proper artisan options worth pausing for",
            "🍶 Heiwa Doburoku Kabutocho Brewery — sake brewed on-site in a beautiful timber-frame building, if you want to stop",
            "🍷 Optional wine or cocktail bar after the walk for those who want to keep the evening going",
            "👥 Maximum 6 people — small enough that nobody gets lost in the shuffle"
          ]
        },
        {
          type: 'header',
          content: "This One is For You If..."
        },
        {
          type: 'bullets',
          items: [
            "You're curious about Tokyo neighborhoods that locals actually use, not ones that make the travel lists",
            "You find old financial districts quietly fascinating — the architecture, the history, the contrast with what they're becoming",
            "You want to meet people while doing something rather than standing in a circle making small talk",
            "You'd rather spend a Sunday evening exploring somewhere new than sitting in the same bar you always go to",
            "The idea of a sake brewery inside a timber building in Japan's Wall Street district sounds exactly like your kind of evening"
          ]
        },
        {
          type: 'paragraph',
          content: "After the Bell runs on neighborhood time — unhurried, curious, and open to whatever the evening turns out to be."
        },
        {
          type: 'header',
          content: "Optional: Wine or Cocktails After"
        },
        {
          type: 'paragraph',
          content: "For those who aren't ready to call it a night, we'll head to a nearby wine or cocktail bar after the walk. No pressure, no agenda — just the natural extension of a good evening with people you've just spent an hour getting to know. Details shared on the day."
        },
        {
          type: 'header',
          content: "Good to Know"
        },
        {
          type: 'bullets',
          items: [
            "Arrive by 4:50 PM at the meeting point — walk departs at 5:00 PM",
            "Duration: 30–45 minutes walking, plus stops, plus optional drinks after",
            "Budget approx. ¥500–1,500 for ice cream or coffee stops along the route",
            "Cash and card recommended — bring both to be safe",
            "Route is flat and easy — comfortable shoes are fine",
            "Exact meeting point disclosed to registered attendees 24 hours before the event",
            "Capacity: 6 people — staying small by design"
          ]
        }
      ]
    },

    whatToExpect: [
      "A 30–45 minute evening walk through Kabutocho's Meiji-era financial streets and renovated bank buildings",
      "Stops for ice cream or coffee along the route",
      "The Tokyo Stock Exchange building and Kabutocho's hidden gems — sake brewery, artisan bakeries, boutique hotel K5",
      "A small group of 6 for real conversation on the move",
      "Optional wine or cocktail bar after for those who want to keep the evening going"
    ],

    venue: {
      name: "TBD",
      description: "Kabutocho, Tokyo's financial district — Meiji-era stone facades, renovated 1920s bank buildings, a neighborhood in the middle of a quiet renaissance. Exact meeting point shared 24 hours before.",
      fullAddress: "Kabutocho, Nihonbashi, Tokyo — exact meeting point disclosed to confirmed attendees 24 hours before the event",
      priceRange: "Individual ice cream, coffee, or drinks — budget approx. ¥500–1,500 for walk stops",
      amenities: [
        "Evening walk through Kabutocho",
        "Tokyo Stock Exchange",
        "Renovated Meiji-era architecture",
        "Ice cream and coffee stops",
        "Optional post-walk drinks",
        "English-language social",
        "Small intimate group"
      ]
    },

    memberBenefits: [
      {
        icon: "🏛️",
        title: "Japan's Wall Street, After Hours",
        description: "Kabutocho at 5 PM is a different place from Kabutocho at 9 AM — quieter, more interesting, and full of things that weren't there ten years ago"
      },
      {
        icon: "🍦",
        title: "Ice Cream & Coffee on the Move",
        description: "Stops along the route at Kabutocho's artisan spots — the neighborhood has quietly become one of downtown Tokyo's best for both"
      },
      {
        icon: "🍷",
        title: "Optional Drinks After",
        description: "Wine or cocktails at a nearby bar for those who aren't ready to call it a night — no pressure, just a good option"
      },
      {
        icon: "👥",
        title: "6 People. One Neighborhood.",
        description: "Small enough that nobody gets lost, intimate enough that the conversation actually goes somewhere"
      }
    ],

    relatedEvents: ['fika-coffee-crawl-setagaya-may-2026', 'weekly-fika', 'drift-happy-hour-june-2026'],
    status: "open",

    eventFrequency: "occasional",
    dayOfWeek: "Sunday",
    walkDuration: "30–45 minutes",
    language: "English",
    culturalFocus: "Tokyo's financial district history meets its contemporary renaissance",
    specialFeature: "Evening walk through Japan's Wall Street with ice cream/coffee stops and optional post-walk drinks",

    socialProof: {
      totalAttended: 0,
      averageRating: null,
      repeatAttendees: null
    },

    hostInfo: {
      name: "Mark",
      bio: "Community builder who finds the interesting things in Tokyo's least-obvious neighborhoods — always one turn away from something worth stopping for.",
      avatar: "mark-hosts.jpg"
    },

    locationPolicy: "Exact meeting point shared with confirmed attendees 24 hours before the event.",

    whatToBring: [
      "Cash and card — bring both to be safe",
      "Comfortable walking shoes — flat route throughout",
      "Camera or phone — the Meiji-era architecture and evening light reward it",
      "An appetite for good ice cream, coffee, and whatever the evening turns into"
    ],

    idealFor: [
      "Anyone curious about Tokyo neighborhoods that locals use but tourists miss",
      "Architecture and history enthusiasts who like their walks to have a good story behind them",
      "People who find financial districts fascinating rather than boring — especially ones in the middle of reinventing themselves",
      "Anyone who'd rather spend a Sunday evening somewhere new than somewhere obvious",
      "Fika regulars looking for a completely different format"
    ],

    weatherPolicy: "Event proceeds rain or shine — the walk is short and the stops provide shelter. Check the forecast and bring an umbrella for August evenings.",

    accessibilityNotes: "Route is flat throughout. Please reach out in advance if you have mobility concerns."
  },

  {
    id: 'curated-spanish-tapas-august-2026',
    eventType: 'foodie',
    iconType: 'image',
    iconImage: '/images/events/SpanishTapas.jpg',
    iconValue: '🍷',
    title: "Hidden Basque: Spanish Tapas Experience (Tokyo)",
    description: "Intimate Omakase Spanish tapas dinner at authentic wine bar (¥5,000 cash). Chef-curated Basque specialties, grilled dishes, and premium Spanish wines. Drinks additional.",
    waitlist: "0",
    next: "Sat, August 29, 2025",
    time: "5:00 PM - 7:30 PM",
    eventDateTime: "2026-08-29T08:00:00Z", // 5:00 PM JST

    location: "TBD - Authentic Spanish Wine Bar",
    color: "from-red-600 to-amber-600",
    capacity: 8,
    difficulty: "Culinary Experience",
    tags: ["Spanish Tapas", "Wine Pairing", "Intimate Dining", "Basque Cuisine", "Grilled", "Premium Experience"],
    featured: true,
    slug: 'curated-spanish-tapas-august-2026',

    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    cost: "¥5,000 (Cash only) per person — Omakase, drinks additional",

    philosophy: {
      title: "Curated Spanish Tapas Evening: Authentic Basque Dining Experience",
      sections: [
        {
          type: 'header',
          content: "Please review the information below and visit the Details tab before reserving your spot."
        },
        {
          type: 'paragraph',
          content: "Experience an intimate evening of authentic Spanish cuisine at a hidden gem in Waseda where the warmth of Spanish hospitality meets Tokyo's attention to detail. This isn't just dinner—it's a cultural journey through Spain's rich culinary traditions, guided by a chef who brings authentic Basque flavors to life through grilling and time-honored techniques."
        },
        {
          type: 'paragraph',
          content: "Tucked away in the quiet residential neighborhood of Waseda, our carefully selected Spanish wine bar offers an atmosphere that transports you straight to a cozy Spanish taverna. The wood-warm interior and intimate setting create the perfect backdrop for an evening of slow food, good wine, and meaningful conversation with fellow food enthusiasts."
        },
        {
          type: 'header',
          content: "What Makes This Evening Special"
        },
        {
          type: 'paragraph',
          content: "Authentic Basque Experience: Our chef specializes in traditional Spanish regional cuisine, featuring signature grilled dishes and house-made pintxos that showcase the true flavors of northern Spain."
        },
        {
          type: 'paragraph',
          content: "Intimate Setting: Limited to just 8 guests for this exclusive evening, ensuring personalized attention and the kind of unhurried dining experience that defines Spanish food culture."
        },
        {
          type: 'paragraph',
          content: "Curated Wine Selection: Including the rare Basque wine 'Txakoli' and carefully selected Spanish varietals that perfectly complement each course."
        },
        {
          type: 'header',
          content: "The Omakase Journey"
        },
        {
          type: 'bullets',
          items: [
            "Chef's Selection Pintxos: Seasonal opening bites showcasing the finest Basque ingredients and traditional preparations",
            "Premium Seafood Course: Daily catch from Basque waters prepared using time-honored coastal cooking techniques",
            "Signature Grilled Specialties: Chef's masterpiece featuring premium meats and vegetables over authentic Spanish charcoal",
            "Artisanal Basque Cheese & Charcuterie: Carefully selected regional products paired with traditional accompaniments",
            "Traditional Spanish Dessert: Seasonal sweet finish highlighting authentic Basque pastry and confectionery techniques"
          ]
        },
        {
          type: 'header',
          content: "What's Included in Your ¥5,000 Omakase"
        },
        {
          type: 'bullets',
          items: [
            "Complete Omakase chef's menu featuring authentic Spanish Basque specialties (¥5,000 cash)",
            "Intimate dining experience limited to 8 food enthusiasts maximum",
            "Cultural context and stories behind traditional Spanish cooking techniques",
            "Access to exclusive Basque wine selection and premium Spanish varietals (drinks paid additionally)"
          ]
        },
        {
          type: 'header',
          content: "Perfect For Food Lovers Who"
        },
        {
          type: 'bullets',
          items: [
            "Appreciate authentic regional cuisine over fusion interpretations",
            "Enjoy intimate dining experiences with fellow food enthusiasts",
            "Want to discover traditional Spanish flavors and cooking techniques",
            "Love the unhurried pace of European dining culture",
            "Are curious about Spanish wine culture, especially rare Basque varietals"
          ]
        },
        {
          type: 'paragraph',
          content: "Join us for this exclusive culinary journey where Spanish tradition meets Japanese precision. Limited to 8 guests who understand that the best meals are meant to be savored slowly and shared with interesting company."
        },
        {
          type: 'header',
          content: "Important Cancellation Policy"
        },
        {
          type: 'paragraph',
          content: "Due to the intimate nature of this experience and advance planning required with our chef, cancellations must be made at least 48 hours before the event. Cancellations made with less than 48 hours notice will be responsible for the full ¥5,000 payment. This policy ensures fairness to other food enthusiasts on our waitlist and allows our chef adequate time to adjust preparations."
        },
        {
          type: 'header',
          content: "Location & Atmosphere"
        },
        {
          type: 'paragraph',
          content: "Our selected venue is located in the peaceful Waseda neighborhood. The restaurant's wood-warm interior and intimate setting create an authentic Spanish taverna atmosphere perfect for unhurried dining and conversation. Exact location will be shared with confirmed attendees 48 hours before the event."
        }
      ]
    },

    whatToExpect: [
      "5-course Omakase chef-curated Spanish tapas menu featuring authentic Basque specialties",
      "Intimate dining experience with maximum 8 fellow food enthusiasts",
      "Signature grilled dishes showcasing traditional Spanish techniques",
      "Access to rare Basque wine 'Txakoli' and premium Spanish wine selection (drinks additional)",
      "Unhurried 2.5-hour dining experience in authentic Spanish taverna atmosphere"
    ],

    venue: {
      name: "TBD - Authentic Spanish Wine Bar",
      description: "Authentic Spanish wine bar specializing in Basque regional cuisine and grilled specialties. Wood-warm interior creates intimate Spanish taverna atmosphere in quiet Waseda neighborhood.",
      fullAddress: "TBD - Waseda area, Shinjuku City, Tokyo (exact location revealed to confirmed attendees 48 hours prior)",
      priceRange: "Omakase ¥5,000 (Cash) — drinks additional",
      amenities: ["Authentic Spanish cuisine", "Grilling", "Basque wine selection", "Intimate 8-person capacity", "Traditional Spanish atmosphere"]
    },

    memberBenefits: [
      {
        icon: "🎯",
        title: "Expert Curation",
        description: "Local insider guide and authentic Spanish culinary experience"
      },
      {
        icon: "👥",
        title: "Intimate Experience",
        description: "Limited to 8 people for personal attention and access to tiny venues"
      },
      {
        icon: "🏮",
        title: "Hidden Gems Access",
        description: "Visit family-run shops and locals-only spots tourists never discover"
      },
      {
        icon: "📚",
        title: "Cultural Deep Dive",
        description: "Learn the stories, techniques, and traditions behind each authentic flavor"
      }
    ],

    relatedEvents: ['pizza-night-daikanyama', 'fika', 'weekly-fika', 'happy-hour'],
    status: "open",

    eventFrequency: "monthly",
    dayOfWeek: "Saturday",
    focusArea: "authentic food culture",
    experienceType: "guided culinary adventure",

    socialProof: {
      totalAttended: 0,
      averageRating: 0,
      repeatAttendees: "0%"
    },

    hostInfo: {
      name: "Mark & Tokyo Food Experts",
      bio: "Passionate about connecting people through authentic Tokyo food culture and hidden culinary gems",
      avatar: "mark-foodie-hosts.jpg"
    },

    culinaryFocus: {
      style: "Authentic Tokyo establishments off the tourist trail",
      groupSize: "Maximum 8 for intimate access",
      duration: "2.5 hours of unhurried dining",
      expertise: "Local insiders with deep establishment relationships"
    },

    includedExperience: {
      guide: "Expert local food guide",
      tastings: "Complete Omakase chef's menu",
      cultural_context: "Stories and traditions behind each dish",
      futureGuide: "Detailed recommendations for solo adventures"
    },

    foodiePhilosophy: "Discover Tokyo's authentic food soul through small group experiences at family-run establishments where recipes and techniques haven't changed in generations",

    idealFor: [
      "Food enthusiasts seeking authentic over touristy experiences",
      "Curious eaters who appreciate cultural context with their meals",
      "Small group experience lovers who value quality over quantity",
      "Anyone wanting insider access to Tokyo's hidden culinary gems",
      "People who believe the best food comes with the best stories"
    ],

    preparationTips: [
      "Come with an adventurous palate and open mind",
      "Bring ¥5,000 in cash for the Omakase — no ATM nearby",
      "Bring curiosity about food traditions and techniques",
      "Inform us of dietary restrictions when you RSVP"
    ],

    uniqueAspects: [
      "Access to establishments that don't accommodate large tour groups",
      "Relationships built over years with authentic food artisans",
      "Stories and cultural context unavailable on typical food tours",
      "Small group ensuring personal attention from expert guide",
      "Focus on authenticity over Instagram-worthy spots"
    ]
  },

   
   {
    id: 'shoot-the-breeze-nov-2026', 
    eventType: 'happy-hour',
    iconType: 'image',
    iconImage: '/images/events/GinzaHappyHour.jpg',
    iconValue: '🥂',
    title: "Shoot the Breeze Happy Hour Saturday: Tokyo Connections ",
    description: "Casual Saturday happy hour where authentic connections happen over drinks at a hidden gem location. No agenda, no awkward networking—just good people and great conversations.",
    waitlist: "0",
    next: "Sat, November 7, 2026",
    time: "6:00 PM - 8:30 PM",
    eventDateTime: "2026-11-07T09:00:00Z",  // ✅ 6:00 PM JST = 9:00 AM UTC

    location: "TBD",
    color: "from-amber-500 to-orange-500",
    capacity: 15,
    difficulty: "Casual Social",
    tags: ["Happy Hour",  "Connection", "Casual", "Saturday", "Shoot the Breeze"],
    featured: true,
    slug: 'shoot-the-breeze-nov-2026',
  
    // Payment configuration
    cost: "FREE (pay for your own drinks)",
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 0,
    currency: "JPY",
    cost: "Pay for you drinks/food",

    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: false,
      allowWaitlist: false
    },
  
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
  
    philosophy: {
      title: "Shoot the Breeze Happy Hour Saturday: Tokyo Connections",
      sections: [
        {
          type: 'paragraph',
          content: "Join us for ConnectCo.'s \"Shoot the Breeze\" Happy Hour Saturday! We're gathering  for 2 hours of relaxed conversation, new connections, and Tokyo stories."
        },
        {
          type: 'paragraph',
          content: "No agenda, no awkward networking games—just good people, great drinks, and the kind of conversations that make Tokyo feel like home. Whether you're new to the city, a seasoned expat, digital nomad, or a local looking to expand your international circle, you'll find friendly faces ready to chat about everything or nothing at all."
        },
        {
          type: 'header',
          content: "🥂 What is Shoot the Breeze?"
        },
        {
          type: 'paragraph',
          content: "Shooting the breeze means having relaxed, casual conversations without any particular agenda—just enjoying the moment and the company. It's about those organic chats where friendships naturally form over shared stories and laughter. Our Shoot the Breeze Happy Hours embrace this spirit, creating a space where Tokyo residents can unwind, connect authentically, and build genuine friendships in a sophisticated yet unpretentious setting."
        },
       {
          type: 'header',
          content: "Cost & Drinks"
        },
        {
          type: 'paragraph',
          content: "Pay for your own drinks (one drink minimum, ¥1,000-3,000 range, sodas, mocktails & cocktails available)"
        },
        {
          type: 'header',
          content: "Why Join Us?"
        },
        {
          type: 'paragraph',
          content: "This isn't your typical forced networking event. It's for people who value authentic connections, enjoy a good laugh, and appreciate a perfectly mixed drink in a beautiful setting. The intimate group size means you'll actually remember the people you meet and the conversations you have."
        },
        {
          type: 'paragraph',
          content: "Bring your stories, your questions about Tokyo life, or just your willingness to go with the flow. Sometimes the most meaningful connections happen when we're simply shooting the breeze."
        },
        {
          type: 'header',
          content: "What to Expect"
        },
        {
          type: 'bullets',
          items: [
            "6:00-6:15 PM: Arrive, grab a drink, meet your first new friend",
            "6:15-8:00 PM: Organic mingling and conversation on the terrace",
            "8:00-8:30 PM: Wind down with your favorite new connections"
          ]
        },
        {
          type: 'paragraph',
          content: "You'll leave with genuine connections, interesting stories, and probably plans for future meetups."
        },
        {
          type: 'header',
          content: "The Vibe"
        },
        {
          type: 'bullets',
          items: [
            "Sophisticated yet utterly unpretentious—just like the best friendships",
            "Beautiful Tokyo setting (weather permitting)",
            "Intimate group of 15 max—you'll actually remember everyone",
            "Mix of newcomers and regulars, expats and locals",
            "English-friendly environment with international crowd"
          ]
        },
        
      ]
    },
    
    whatToExpected: [
      "Relaxed atmosphere in the heart of Tokyo",
      "Organic conversations with 20-25 like-minded people",
      "Mix of expats, locals, and international Tokyo residents",
      "Authentic connections over drinks—no forced networking"
    ],
    
    venue: { 
      name: "TBD ",
      description: "Beautiful  location in Tokyo perfect for casual happy hour mingling. Exact venue details will be shared with confirmed attendees closer to the event date.",
      fullAddress: "Tokyo (Exact address TBD - will be shared with attendees)",
      priceRange: "FREE entry - drinks ¥1,000-3,000 range (pay for your own)",
      amenities: ["Indoor", "Central Tokyo location", "Full bar service", "Indoor/outdoor seating"]
    },
    
    memberBenefits: [
      {
        icon: "🥂",
        title: "Pay-As-You-Go",
        description: "FREE event entry - just order your own drinks (¥1,000-3,000 range)"
      },
      {
        icon: "🏙️", 
        title: "Premium Tokyo Location",
        description: "Beautiful  setting in Tokyo's most sophisticated neighborhood"
      },
    
      {
        icon: "🤝",
        title: "Intimate Group Size",
        description: "Just 15 people max—small enough for genuine connections"
      }
    ],
    
    relatedEvents: ['fika', 'weekly-fika', 'discussions', 'happy-hour'],
    status: "open",
    
    // Additional metadata
    eventFrequency: "occasional",
    dayOfWeek: "Saturday", 
    neighborhood: "Tokyo",
    pricePoint: "free-entry",
    
    socialProof: {
      totalAttended: 0,
      averageRating: 0,
      repeatAttendees: "0%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Creating relaxed and authentic connection experiences across Tokyo's best neighborhoods",
      avatar: "mark-jason-host.jpg"
    },
  
    drinkSpecials: {
      packagePrice: "FREE entry",
      paymentMethod: "Pay for your own drinks at the venue",
      drinkRange: "¥1,000-3,000",
      options: ["Cocktails", "Mocktails", "Sodas", "Wine", "Beer"],
      minimum: "One drink minimum per person"
    },
    
  
    
    attendancePolicy: "Members who RSVP 'yes' but fail to attend the event without canceling their RSVP beforehand will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system.",
    
    networkingStyle: "Organic, casual mingling—no structured activities or forced networking. Just good conversation flowing naturally over drinks on a beautiful terrace.",
    
    idealFor: [
      "New Tokyo residents wanting to build their social circle", 
      "Seasoned expats looking for genuine connections", 
      "Digital nomads seeking local community", 
      "Anyone tired of awkward networking events", 
      "People who believe the best friendships start with casual conversation"
    ],
  
    atmosphereKeywords: ["casual", "relaxed", "sophisticated", "unpretentious", "organic", "authentic", "intimate", "rooftop", "terrace", "Ginza"]
  },


  
   {
    id: 'shoot-the-breeze-sept-2026', 
    eventType: 'happy-hour',
    iconType: 'image',
    iconImage: '/images/events/GinzaHappyHour.jpg',
    iconValue: '🥂',
    title: "Shoot the Breeze Happy Hour Saturday: Tokyo Connections ",
    description: "Casual Saturday happy hour where authentic connections happen over drinks on rooftop location. No agenda, no awkward networking—just good people and great conversations.",
    waitlist: "0",
    next: "Sat, September 26, 2026",
    time: "6:00 PM - 8:30 PM",
    eventDateTime: "2026-09-26T09:00:00Z",  // ✅ 6:00 PM JST = 9:00 AM UTC

    location: "TBD",
    color: "from-amber-500 to-orange-500",
    capacity: 15,
    difficulty: "Casual Social",
    tags: ["Happy Hour",  "Connection", "Casual", "Saturday", "Shoot the Breeze"],
    featured: true,
    slug: 'shoot-the-breeze-sept-2026',
  
    // Payment configuration
    cost: "FREE (pay for your own drinks)",
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 0,
    currency: "JPY",
    cost: "Pay for you drinks/food",

    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: false,
      allowWaitlist: false
    },
  
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
  
    philosophy: {
      title: "Shoot the Breeze Happy Hour Saturday: Tokyo Connections",
      sections: [
        {
          type: 'paragraph',
          content: "Join us for ConnectCo.'s \"Shoot the Breeze\" Happy Hour Saturday! We're gathering  for 2 hours of relaxed conversation, new connections, and Tokyo stories."
        },
        {
          type: 'paragraph',
          content: "No agenda, no awkward networking games—just good people, great drinks, and the kind of conversations that make Tokyo feel like home. Whether you're new to the city, a seasoned expat, digital nomad, or a local looking to expand your international circle, you'll find friendly faces ready to chat about everything or nothing at all."
        },
        {
          type: 'header',
          content: "🥂 What is Shoot the Breeze?"
        },
        {
          type: 'paragraph',
          content: "Shooting the breeze means having relaxed, casual conversations without any particular agenda—just enjoying the moment and the company. It's about those organic chats where friendships naturally form over shared stories and laughter. Our Shoot the Breeze Happy Hours embrace this spirit, creating a space where Tokyo residents can unwind, connect authentically, and build genuine friendships in a sophisticated yet unpretentious setting."
        },
       {
          type: 'header',
          content: "Cost & Drinks"
        },
        {
          type: 'paragraph',
          content: "Pay for your own drinks (one drink minimum, ¥1,000-3,000 range, sodas, mocktails & cocktails available)"
        },
        {
          type: 'header',
          content: "Why Join Us?"
        },
        {
          type: 'paragraph',
          content: "This isn't your typical forced networking event. It's for people who value authentic connections, enjoy a good laugh, and appreciate a perfectly mixed drink in a beautiful setting. The intimate group size means you'll actually remember the people you meet and the conversations you have."
        },
        {
          type: 'paragraph',
          content: "Bring your stories, your questions about Tokyo life, or just your willingness to go with the flow. Sometimes the most meaningful connections happen when we're simply shooting the breeze."
        },
        {
          type: 'header',
          content: "What to Expect"
        },
        {
          type: 'bullets',
          items: [
            "6:00-6:15 PM: Arrive, grab a drink, meet your first new friend",
            "6:15-8:00 PM: Organic mingling and conversation on the terrace",
            "8:00-8:30 PM: Wind down with your favorite new connections"
          ]
        },
        {
          type: 'paragraph',
          content: "You'll leave with genuine connections, interesting stories, and probably plans for future meetups."
        },
        {
          type: 'header',
          content: "The Vibe"
        },
        {
          type: 'bullets',
          items: [
            "Sophisticated yet utterly unpretentious—just like the best friendships",
            "Beautiful Tokyo setting (weather permitting)",
            "Intimate group of 15 max—you'll actually remember everyone",
            "Mix of newcomers and regulars, expats and locals",
            "English-friendly environment with international crowd"
          ]
        },
        
      ]
    },
    
    whatToExpected: [
      "Relaxed atmosphere in the heart of Tokyo",
      "Organic conversations with 20-25 like-minded people",
      "Mix of expats, locals, and international Tokyo residents",
      "Authentic connections over drinks—no forced networking"
    ],
    
    venue: { 
      name: "TBD ",
      description: "Beautiful  location in Tokyo perfect for casual happy hour mingling. Exact venue details will be shared with confirmed attendees closer to the event date.",
      fullAddress: "Tokyo (Exact address TBD - will be shared with attendees)",
      priceRange: "FREE entry - drinks ¥1,000-3,000 range (pay for your own)",
      amenities: ["Indoor", "Central Tokyo location", "Full bar service", "Indoor/outdoor seating"]
    },
    
    memberBenefits: [
      {
        icon: "🥂",
        title: "Pay-As-You-Go",
        description: "FREE event entry - just order your own drinks (¥1,000-3,000 range)"
      },
      {
        icon: "🏙️", 
        title: "Premium Tokyo Location",
        description: "Beautiful  setting in Tokyo's most sophisticated neighborhood"
      },
    
      {
        icon: "🤝",
        title: "Intimate Group Size",
        description: "Just 15 people max—small enough for genuine connections"
      }
    ],
    
    relatedEvents: ['fika', 'weekly-fika', 'discussions', 'happy-hour'],
    status: "open",
    
    // Additional metadata
    eventFrequency: "occasional",
    dayOfWeek: "Saturday", 
    neighborhood: "Tokyo",
    pricePoint: "free-entry",
    
    socialProof: {
      totalAttended: 0,
      averageRating: 0,
      repeatAttendees: "0%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Creating relaxed and authentic connection experiences across Tokyo's best neighborhoods",
      avatar: "mark-jason-host.jpg"
    },
  
    drinkSpecials: {
      packagePrice: "FREE entry",
      paymentMethod: "Pay for your own drinks at the venue",
      drinkRange: "¥1,000-3,000",
      options: ["Cocktails", "Mocktails", "Sodas", "Wine", "Beer"],
      minimum: "One drink minimum per person"
    },
    
  
    
    attendancePolicy: "Members who RSVP 'yes' but fail to attend the event without canceling their RSVP beforehand will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system.",
    
    networkingStyle: "Organic, casual mingling—no structured activities or forced networking. Just good conversation flowing naturally over drinks on a beautiful terrace.",
    
    idealFor: [
      "New Tokyo residents wanting to build their social circle", 
      "Seasoned expats looking for genuine connections", 
      "Digital nomads seeking local community", 
      "Anyone tired of awkward networking events", 
      "People who believe the best friendships start with casual conversation"
    ],
  
    atmosphereKeywords: ["casual", "relaxed", "sophisticated", "unpretentious", "organic", "authentic", "intimate", "rooftop", "terrace", "Ginza"]
  },

  {
    id: 'slice-of-slow-rooftop-fika-pie-june-2026',
    eventType: 'fika',
    iconType: 'image',
    iconImage: '/images/events/RooftopFikaPie.jpg',
    iconValue: '🥧',
    title: "Slice of Slow: A Rooftop Fika & Pie Social",
    description: "Handmade pies, open sky, and ten people with nowhere else to be. An afternoon on a secret Tokyo rooftop where the food is made from scratch, the view is worth keeping, and the conversation finds its own pace. Stay on after for optional churros and drinks at a nearby spot.",
    waitlist: "0",
    next: "Sat, June 20, 2026",
    time: "4:00 PM - 6:00 PM",
    eventDateTime: "2026-06-20T07:00:00Z", // 4:00 PM JST
    location: "TBD",
    color: "from-amber-700 to-orange-600",
    capacity: 10,
    difficulty: "Casual Social",
    tags: ["Rooftop", "Pies", "Churros", "Coffee", "Fika", "Slow Living", "Handmade", "Hidden Gem", "Community", "Outdoor"],
    featured: true,
    slug: 'slice-of-slow-rooftop-fika-pie-june-2026',

    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    cost: "Pay for your own food & drinks (approx. ¥1,500–2,500)",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },

    philosophy: {
      title: "Slice of Slow: A Rooftop Fika & Pie Social",
      sections: [
        {
          type: 'paragraph',
          content: "Some places in Tokyo take real effort to find. A narrow staircase, no sign out front, a rooftop that opens up when you didn't expect it to. This is one of those. Slice of Slow is an afternoon on a secret rooftop with handmade pies, good coffee, a view of the city from somewhere most people walk past without ever looking up."
        },
        {
          type: 'paragraph',
          content: "The concept is simple: slow food, open air, ten people, two hours. Fika has always been about giving yourself permission to stop — to actually be in a place instead of passing through it. A rooftop does the same thing differently. Something about being above street level shifts the pace. Conversations go longer. Nobody rushes."
        },
        {
          type: 'header',
          content: "The Food"
        },
        {
          type: 'paragraph',
          content: "The pies are handmade from scratch — rotating savory varieties using quality-sourced ingredients, the kind of food that takes most of the week to prepare and disappears in a very satisfying afternoon. The menu changes, but the craft doesn't. Pair with a flat white, a craft beer, a cider, or something cold."
        },
        {
          type: 'header',
          content: "What to Expect"
        },
        {
          type: 'bullets',
          items: [
            "🥧 Handmade rotating pie selection — savory varieties made from scratch with quality-sourced ingredients",
            "☕ Flat white, brewed coffee, craft beer, ciders, flavored spritzers, and soft drinks",
            "🍫 Optional continuation after the rooftop: churros and drinks at a nearby spot for those who want to keep the afternoon going",
            "🌆 Rooftop terrace with open sky and city views — the kind of spot Tokyo keeps quietly to itself",
            "👥 Ten people maximum ",
            "💳 Individual food and drinks ordered and paid at the venue"
          ]
        },
        {
          type: 'header',
          content: "This One is For You If..."
        },
        {
          type: 'bullets',
          items: [
            "You believe the best meals are made by people who care what goes into them",
            "You'd rather spend an afternoon somewhere unexpected than somewhere obvious",
            "You like the idea of a social event with a view and without a dress code",
            "You want to meet people in a setting that feels like a discovery, not a networking function",
            "Rooftops, handmade food, and good coffee in the same afternoon sounds like exactly enough"
          ]
        },
        {
          type: 'paragraph',
          content: "Slice of Slow runs at its own speed. The pies are worth the climb. Come hungry."
        },
        {
          type: 'header',
          content: "Optional: Churros & Drinks Afterwards"
        },
        {
          type: 'paragraph',
          content: "If the afternoon has you wanting more, we'll walk over to a nearby spot for churros and drinks. No pressure, no agenda — just the natural extension of a good afternoon with people you've just met and already want to keep talking to. Details shared on the day."
        },
        {
          type: 'header',
          content: "Good to Know"
        },
        {
          type: 'bullets',
          items: [
            "Capacity: 6 people — this one stays small by design",
            "Rooftop terrace — dress for an outdoor afternoon (light layers recommended)",
            "Food and drinks ordered and paid individually — budget approx. ¥1,500–2,500 per person",
            "🍫 Optional post-event churros and drinks at a nearby location — details shared on the day",
            "Exact venue address sent to confirmed attendees 24 hours before the event",
            "Pie menu rotates weekly — expect something made properly, whatever it happens to be"
          ]
        }
      ]
    },

    whatToExpect: [
      "Handmade rotating pie selection using quality-sourced ingredients",
      "Flat white, brewed coffee, craft beer, ciders, and spritzers",
      "Optional post-event churros and drinks at a nearby spot for those who want to keep the afternoon going",
      "Open sky rooftop terrace with city views",
      "Ten people maximum ",
      "A social format built around good food and unhurried conversation"
    ],

    venue: {
      name: "TBD",
      description: "A hidden rooftop terrace in Tokyo — open sky, city views, and handmade food from a kitchen that takes its ingredients seriously. The kind of place you don't find unless someone tells you about it.",
      fullAddress: "Tokyo — exact address revealed to confirmed attendees 24 hours before the event",
      priceRange: "Individual food and drinks (approx. ¥1,500–2,500 per person)",
      amenities: [
        "Rooftop terrace",
        "Open sky and city views",
        "Handmade rotating pie menu",
        "Flat white and brewed coffee",
        "Craft beer and ciders",
        "Flavored spritzers and soft drinks",
        "Smoke-free environment"
      ]
    },

    memberBenefits: [
      {
        icon: "🥧",
        title: "Handmade from Scratch",
        description: "Rotating savory pies made with quality-sourced ingredients — the kind of food that takes most of the week to prepare and most of the afternoon to properly appreciate"
      },
      {
        icon: "🌆",
        title: "A Rooftop Worth Finding",
        description: "Open sky, city views, and the particular pleasure of being somewhere most people walk past without ever looking up"
      },
     
      {
        icon: "🍫",
        title: "Optional: Churros After",
        description: "Stay on after the rooftop for churros and drinks at a nearby spot — no pressure, just the natural next move for a good afternoon"
      }
    ],

    relatedEvents: ['fika-at-33rpm-may-2026', 'weekly-fika', 'drift-happy-hour-june-2026'],
    status: "open",

    eventFrequency: "occasional",
    dayOfWeek: "Saturday",
    culturalFocus: "Handmade Aussie-style food meets Swedish slow living philosophy",
    specialFeature: "Secret rooftop terrace with handmade rotating pie menu, plus optional churros and drinks at a nearby spot afterwards",

    socialProof: {
      totalAttended: 0,
      averageRating: null,
      repeatAttendees: null
    },

    hostInfo: {
      name: "Mark",
      bio: "Always looking for the Tokyo spots worth climbing the stairs for — good food, open sky, and the kind of afternoon that doesn't need a plan.",
      avatar: "mark-hosts.jpg"
    },

    locationPolicy: "Exact address shared with confirmed attendees 24 hours before the event.",

    whatToBring: [
      "Cash or card for your food and drinks",
      "An appetite — the pies are handmade and the portions are honest",
      "Light layers for an outdoor rooftop afternoon",
      "Curiosity about whoever ends up sharing the view with you"
    ],

    idealFor: [
      "People who appreciate food made by someone who actually cares",
      "Anyone who'd rather spend an afternoon on a rooftop than in a bar",
      "Fika regulars looking for something with a different flavor",
      "New members who want an easy, low-pressure first event",
      "Anyone who finds six-person gatherings significantly less exhausting than twenty-person ones"
    ]
  },

  {
    id: 'fika-at-33rpm-august-2026',
    eventType: 'fika',
    iconType: 'image',
    iconImage: '/images/events/Turntable.jpg',
    iconValue: '🎙️',
    title: "Fika at 33⅓: A Vinyl Listening & Coffee Social + Optional Dinner",
    description: "Fika meets the listening bar. Slow down, tune in, and connect over vinyl records spinning through tube amplifiers. Conversation is welcome — but so is the silence between notes. Stay on after for an optional dinner at a nearby spot.",
    waitlist: "0",
    next: "Sun, August 23, 2025",
    time: "5:00 PM - 7:00 PM",
    eventDateTime: "2026-08-23T08:00:00Z", // 5:00 PM JST

    location: "TBD (exact venue sent 24 hours before the event)",
    color: "from-stone-700 to-amber-800",
    capacity: 8,
    difficulty: "Casual Social",
    tags: ["Vinyl", "Coffee", "Listening Bar", "Music", "Fika", "Analog", "Slow Living", "Community"],
    featured: true,
    slug: 'fika-at-33rpm-august-2026',

    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    cost: "Pay for your own drinks/food",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },

    philosophy: {
      title: "Fika at 33⅓: A Vinyl Listening & Coffee Social",
      sections: [
        {
          type: 'paragraph',
          content: "Most gatherings ask you to fill the silence. This one asks you to hear it. Fika at 33⅓ is our listening-first social — an afternoon spent in one of Tokyo's finest record cafés, where hand-drip coffee arrives in Nordic antique cups, vinyl rotates at the correct speed, and the conversation finds its own pace around the music."
        },
        {
          type: 'paragraph',
          content: "The Swedish concept of fika has always been about more than coffee. It's about giving yourself — and the people around you — permission to stop. To actually be somewhere. A vinyl listening bar does the same thing from a different direction: the music slows you down before you even realize it's happening. Put the two together, and something shifts. People listen differently. Conversations go places they wouldn't in a louder room."
        },
        {
          type: 'header',
          content: "What Makes This Different"
        },
        {
          type: 'paragraph',
          content: "This isn't a fika with background music. The records are the point. A staff DJ spins all afternoon — jazz, rock, classical, electro, the occasional left-field surprise — through vacuum tube amplifiers and Tannoy speakers that fill the room without overwhelming it. You'll hear things in the music you've never noticed before. That's not an accident. It's what a room like this is designed to do."
        },
        {
          type: 'paragraph',
          content: "Between tracks, conversation opens up. Within tracks, it quiets to something more intimate — a comment about the song, a shared look across the table, the kind of small moment that somehow sticks. Fika has always been built on exactly these: the unhurried, unremarkable exchanges that turn strangers into regulars."
        },
        {
          type: 'header',
          content: "The Setting"
        },
        {
          type: 'paragraph',
          content: "Our venue is a record café and wine bar in a carefully chosen Tokyo neighborhood — a room of concrete walls, amber light, vinyl-lined shelves, and a counter where you can watch the record spin and the coffee pour in the same glance. It seats a handful of people at a time. The intimacy is the whole idea."
        },
        {
          type: 'header',
          content: "What to Expect"
        },
        {
          type: 'bullets',
          items: [
            "🎵 Live vinyl spinning all evening — jazz, rock, classical, electro, and surprises — through tube amplifiers and serious speakers",
            "☕ Espresso drinks, coffee cocktails, natural wine, beer, and soft drinks at a beautifully curated record café",
            "🥐 Light café food to accompany your evening — cheese on toast, seasonal sweets, and more",
            "👥 Intimate group of 8 — small enough for real conversation, quiet enough to actually hear each other",
            "🎙️ Conversation is welcomed and encouraged — the music sets the pace, not the other way around",
            "💳 Individual drinks and food ordered and paid at the venue",
            "🍽️ Optional dinner at a nearby spot after — details shared on the day"
          ]
        },
        {
          type: 'header',
          content: "This One is For You If..."
        },
        {
          type: 'bullets',
          items: [
            "You've always wanted to visit a Tokyo listening bar but weren't sure how to walk in alone",
            "You love music and wish more social gatherings treated it as something to actually pay attention to",
            "You're drawn to analog things in a digital world — vinyl, drip coffee, real conversation",
            "You want to meet people without the noise and pressure of a typical social event",
            "You believe that the best conversations happen sideways — while you're both listening to something else"
          ]
        },
        {
          type: 'paragraph',
          content: "Fika at 33⅓ runs at the right speed. Come and hear for yourself."
        },
        {
          type: 'header',
          content: "Good to Know"
        },
        {
          type: 'bullets',
          items: [
            "Venue is smoke-free — comfortable for everyone",
            "Seating is intimate and limited — RSVP is essential",
            "Coffee, coffee cocktails, wine, beer, soft drinks, and food ordered and paid individually — budget approx. ¥800–1,500",
            "Exact venue address sent to confirmed attendees 24 hours before",
            "Capacity: 8 people — this one stays small by design",
            "Optional dinner at a nearby spot after the event — no pressure, details on the day"
          ]
        }
      ]
    },

    whatToExpect: [
      "Vinyl spinning all evening through tube amplifiers and serious speakers — jazz, rock, classical, and more",
      "Espresso drinks, coffee cocktails, natural wine, beer, and soft drinks at a beautifully curated record café",
      "Intimate group of 8 for conversation that moves at the pace of the music",
      "A social format where silence is welcome and conversation earns its moment",
      "Optional dinner at a nearby spot for those who want to keep the evening going"
    ],

    venue: {
      name: "TBD",
      description: "A carefully chosen record café and listening bar — concrete walls, Tannoy speakers, vacuum tube amplifiers, and vinyl-lined shelves. The kind of room Tokyo does better than anywhere.",
      fullAddress: "Tokyo — exact address revealed to confirmed attendees 24 hours before the event",
      priceRange: "Individual coffee and food (approx. ¥800–1,500)",
      amenities: [
        "Vinyl listening bar",
        "Hand-drip specialty coffee",
        "Tube amplifiers and Tannoy speakers",
        "Nordic antique cup collection",
        "Cashless payments accepted",
        "Smoke-free environment",
        "Small art gallery space"
      ]
    },

    memberBenefits: [
      {
        icon: "🎵",
        title: "Vinyl, Properly Played",
        description: "Staff-curated records through vacuum tube amplifiers and Tannoy speakers — the sound that makes you understand why analog never died"
      },
      {
        icon: "🍷",
        title: "Drink What You Like",
        description: "Espresso drinks, coffee cocktails, natural wine, beer, and soft drinks — something for everyone, ordered individually at the bar"
      },
      {
        icon: "👥",
        title: "8 People. One Room.",
        description: "An intentionally small group where the intimacy is the feature, not the limitation"
      },
      {
        icon: "🔊",
        title: "A New Kind of Social",
        description: "Fika's slow philosophy meets Tokyo's listening bar culture — a format you won't find anywhere else in the city"
      }
    ],

    relatedEvents: ['authentic-swedish-fika-june-sunday-2026', 'weekly-fika', 'drift-happy-hour-june-2026'],
    status: "open",

    eventFrequency: "monthly",
    dayOfWeek: "Sunday",
    culturalFocus: "Vinyl listening culture meets Swedish fika tradition",
    specialFeature: "Live vinyl DJ through tube amplifiers in an intimate record café, with optional dinner after",

    socialProof: {
      totalAttended: 0,
      averageRating: null,
      repeatAttendees: null
    },

    hostInfo: {
      name: "Mark",
      bio: "Chasing the overlap between good coffee, good music, and the kind of conversations that only happen when you slow everything down.",
      avatar: "mark-hosts.jpg"
    },

    locationPolicy: "Exact address shared with confirmed attendees 24 hours before the event.",

    listeningNotes: "This is an active listening environment. The music is meant to be heard, not ignored. Conversation is warmly encouraged — but you may find yourself pausing mid-sentence to catch a chord. That's exactly the point.",

    whatToBring: [
      "Cash or card for your drinks and food order",
      "An open ear — the genre selection moves across jazz, rock, classical, electro, and beyond",
      "Curiosity about whoever ends up sitting next to you"
    ],

    idealFor: [
      "Vinyl enthusiasts and curious newcomers alike",
      "Anyone who finds loud, crowded events exhausting",
      "Coffee people who want music taken as seriously as the coffee",
      "Fika regulars looking for something new",
      "People who believe the best conversations happen while you're both listening to something else"
    ]
  },



  {
  id: 'fika-at-33rpm-nakameguro-june-2026',
  eventType: 'fika',
  //iconType: 'emoji',
  iconType: 'image',
  iconImage: '/images/events/Turntable.jpg',
  iconValue: '🎙️',
  title: "Fika at 33⅓: A Vinyl Listening & Coffee Social + Optional Dinner",
  description: "Fika meets the listening bar. Slow down, tune in, and connect over vinyl records spinning through tube amplifiers. Conversation is welcome — but so is the silence between notes. Stay on after for an optional dinner at a nearby spot.",
  waitlist: "0",
  next: "Sat, June 27, 2025",
  time: "4:00 PM - 6:00 PM",
  eventDateTime: "2026-06-27T07:00:00Z", // 4:00 PM JST

  location: "TBD (exact venue sent 24 hours before the event)",
  color: "from-stone-700 to-amber-800",
  capacity: 6,
  difficulty: "Casual Social",
  tags: ["Vinyl", "Coffee", "Listening Bar", "Music", "Fika", "Analog", "Slow Living", "Nakameguro", "Community"],
  featured: true,
  slug: 'fika-at-33rpm-june-2026',

  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  cost: "Pay for your own drinks/food",

  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },

  philosophy: {
    title: "Fika at 33⅓: A Vinyl Listening & Coffee Social",
    sections: [
      {
        type: 'paragraph',
        content: "Most gatherings ask you to fill the silence. This one asks you to hear it. Fika at 33⅓ is our new listening-first social — an afternoon spent in one of Tokyo's finest record cafés, where hand-drip coffee arrives in Nordic antique cups, vinyl rotates at the correct speed, and the conversation finds its own pace around the music."
      },
      {
        type: 'paragraph',
        content: "The Swedish concept of fika has always been about more than coffee. It's about giving yourself — and the people around you — permission to stop. To actually be somewhere. A vinyl listening bar does the same thing from a different direction: the music slows you down before you even realize it's happening. Put the two together, and something shifts. People listen differently. Conversations go places they wouldn't in a louder room."
      },
      {
        type: 'header',
        content: "What Makes This Different"
      },
      {
        type: 'paragraph',
        content: "This isn't a fika with background music. The records are the point. A staff DJ spins all afternoon — jazz, rock, classical, electro, the occasional left-field surprise — through vacuum tube amplifiers and Tannoy speakers that fill the room without overwhelming it. You'll hear things in the music you've never noticed before. That's not an accident. It's what a room like this is designed to do."
      },
      {
        type: 'paragraph',
        content: "Between tracks, conversation opens up. Within tracks, it quiets to something more intimate — a comment about the song, a shared look across the table, the kind of small moment that somehow sticks. Fika has always been built on exactly these: the unhurried, unremarkable exchanges that turn strangers into regulars."
      },
      {
        type: 'header',
        content: "The Setting"
      },
      {
        type: 'paragraph',
        content: "Our venue for this launch is a record café and wine bar tucked into the snazzy neighborhood — a room of concrete walls, amber light, vinyl-lined shelves, and a counter where you can watch the record spin and the coffee pour in the same glance. It seats a handful of people at a time. The intimacy is the whole idea."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "🎵 Live vinyl spinning all afternoon — jazz, rock, classical, electro, and surprises — through tube amplifiers and serious speakers",
          "☕ Espresso drinks, coffee cocktails, natural wine, beer, and soft drinks at a beautifully curated record café",
          "🥐 Light café food to accompany your afternoon — cheese on toast, seasonal sweets, and more",
          "👥 Intimate group of 10 — small enough for real conversation, quiet enough to actually hear each other",
          "🎙️ Conversation is welcomed and encouraged — the music sets the pace, not the other way around",
          "💳 Individual drinks and food ordered and paid at the venue"
        ]
      },
      {
        type: 'header',
        content: "This One is For You If..."
      },
      {
        type: 'bullets',
        items: [
          "You've always wanted to visit a Tokyo listening bar but weren't sure how to walk in alone",
          "You love music and wish more social gatherings treated it as something to actually pay attention to",
          "You're drawn to analog things in a digital world — vinyl, drip coffee, real conversation",
          "You want to meet people without the noise and pressure of a typical social event",
          "You believe that the best conversations happen sideways — while you're both listening to something else"
        ]
      },
      {
        type: 'paragraph',
        content: "Fika at 33⅓ runs at the right speed. Come and hear for yourself."
      },
      {
        type: 'header',
        content: "Good to Know"
      },
      {
        type: 'bullets',
        items: [
          "Venue is smoke-free — comfortable for everyone",
          "Seating is intimate and limited — RSVP is essential",
          "Coffee, coffee cocktails, wine, beer, soft drinks, and food ordered and paid individually — budget approx. ¥800–1,500",
          "Exact venue address sent to confirmed attendees 24 hours before",
          "Capacity: 10 people — this one stays small by design"
        ]
      },
      
    ]
  },

  whatToExpect: [
    "Vinyl spinning all afternoon through tube amplifiers and serious speakers — jazz, rock, classical, and more",
   "Espresso drinks, coffee cocktails, natural wine, beer, and soft drinks at a beautifully curated record café",
    "Intimate group of 10 for conversation that moves at the pace of the music",
    "A social format where silence is welcome and conversation earns its moment",
  ],

  venue: {
    name: "TBD ",
    description: "A carefully chosen record café and listening bar — concrete walls, Tannoy speakers, vacuum tube amplifiers, and vinyl-lined shelves. The kind of room Tokyo does better than anywhere.",
    fullAddress: " Tokyo — exact address revealed to confirmed attendees 24 hours before the event",
    priceRange: "Individual coffee and food (approx. ¥800–1,500)",
    amenities: [
      "Vinyl listening bar",
      "Hand-drip specialty coffee",
      "Tube amplifiers and Tannoy speakers",
      "Nordic antique cup collection",
      "Cashless payments accepted",
      "Smoke-free environment",
      "Small art gallery space"
    ]
  },

  memberBenefits: [
    {
      icon: "🎵",
      title: "Vinyl, Properly Played",
      description: "Staff-curated records through vacuum tube amplifiers and Tannoy speakers — the sound that makes you understand why analog never died"
    },
    {
      icon: "🍷",
      title: "Drink What You Like",
      description: "Espresso drinks, coffee cocktails, natural wine, beer, and soft drinks — something for everyone, ordered individually at the bar"
    },
    {
      icon: "👥",
      title: "10 People. One Room.",
      description: "An intentionally small group where the intimacy is the feature, not the limitation"
    },
    {
      icon: "🔊",
      title: "A New Kind of Social",
      description: "Fika's slow philosophy meets Tokyo's listening bar culture — a format you won't find anywhere else in the city"
    }
  ],

  relatedEvents: ['authentic-swedish-fika-may-sunday-2026', 'weekly-fika', 'yanaka-cemetery-walk'],
  status: "open",

  eventFrequency: "monthly",
  dayOfWeek: "Saturday",
  culturalFocus: "Vinyl listening culture meets Swedish fika tradition",
  specialFeature: "Live vinyl DJ through tube amplifiers in an intimate  record café",

  socialProof: {
    totalAttended: 0,
    averageRating: null,
    repeatAttendees: null
  },

  hostInfo: {
    name: "Mark",
    bio: "Chasing the overlap between good coffee, good music, and the kind of conversations that only happen when you slow everything down.",
    avatar: "mark-hosts.jpg"
  },

  //attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without advance notice, will be considered a no-show. After three no-show instances, membership will be automatically revoked.",

  locationPolicy: "Exact address shared with confirmed attendees 24 hours before the event.",

  listeningNotes: "This is an active listening environment. The music is meant to be heard, not ignored. Conversation is warmly encouraged — but you may find yourself pausing mid-sentence to catch a chord. That's exactly the point.",

  whatToBring: [
    "Cash or card for your drinks and food order",
    "An open ear — the genre selection moves across jazz, rock, classical, electro, and beyond",
    "Curiosity about whoever ends up sitting next to you"
  ],

  idealFor: [
    "Vinyl enthusiasts and curious newcomers alike",
    "Anyone who finds loud, crowded events exhausting",
    "Coffee people who want music taken as seriously as the coffee",
    "Fika regulars looking for something new",
    "People who believe the best conversations happen while you're both listening to something else"
  ]
},

  {
  id: 'fika-at-33rpm-nakameguro-may-2026',
  eventType: 'fika',
  //iconType: 'emoji',
  iconType: 'image',
  iconImage: '/images/events/Turntable.jpg',
  iconValue: '🎙️',
  title: "Fika at 33⅓: A Vinyl Listening & Coffee Social",
  description: "Fika meets the listening bar. Slow down, tune in, and connect over vinyl records spinning through tube amplifiers. Conversation is welcome — but so is the silence between notes.",
  waitlist: "0",
  next: "Sat, May 16, 2026",
  time: "4:00 PM - 6:00 PM",
  eventDateTime: "2026-05-16T07:00:00Z", // 4:00 PM JST

  location: "TBD (exact venue sent 24 hours before the event)",
  color: "from-stone-700 to-amber-800",
  capacity: 10,
  difficulty: "Casual Social",
  tags: ["Vinyl", "Coffee", "Listening Bar", "Music", "Fika", "Analog", "Slow Living", "Nakameguro", "Community"],
  featured: true,
  slug: 'fika-at-33rpm-may-2026',

  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  cost: "Pay for your own drinks/food",

  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },

  philosophy: {
    title: "Fika at 33⅓: A Vinyl Listening & Coffee Social",
    sections: [
      {
        type: 'paragraph',
        content: "Most gatherings ask you to fill the silence. This one asks you to hear it. Fika at 33⅓ is our new listening-first social — an afternoon spent in one of Tokyo's finest record cafés, where hand-drip coffee arrives in Nordic antique cups, vinyl rotates at the correct speed, and the conversation finds its own pace around the music."
      },
      {
        type: 'paragraph',
        content: "The Swedish concept of fika has always been about more than coffee. It's about giving yourself — and the people around you — permission to stop. To actually be somewhere. A vinyl listening bar does the same thing from a different direction: the music slows you down before you even realize it's happening. Put the two together, and something shifts. People listen differently. Conversations go places they wouldn't in a louder room."
      },
      {
        type: 'header',
        content: "What Makes This Different"
      },
      {
        type: 'paragraph',
        content: "This isn't a fika with background music. The records are the point. A staff DJ spins all afternoon — jazz, rock, classical, electro, the occasional left-field surprise — through vacuum tube amplifiers and Tannoy speakers that fill the room without overwhelming it. You'll hear things in the music you've never noticed before. That's not an accident. It's what a room like this is designed to do."
      },
      {
        type: 'paragraph',
        content: "Between tracks, conversation opens up. Within tracks, it quiets to something more intimate — a comment about the song, a shared look across the table, the kind of small moment that somehow sticks. Fika has always been built on exactly these: the unhurried, unremarkable exchanges that turn strangers into regulars."
      },
      {
        type: 'header',
        content: "The Setting"
      },
      {
        type: 'paragraph',
        content: "Our venue for this launch is a record café and wine bar tucked into the snazzy neighborhood — a room of concrete walls, amber light, vinyl-lined shelves, and a counter where you can watch the record spin and the coffee pour in the same glance. It seats a handful of people at a time. The intimacy is the whole idea."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "🎵 Live vinyl spinning all afternoon — jazz, rock, classical, electro, and surprises — through tube amplifiers and serious speakers",
          "☕ Espresso drinks, coffee cocktails, natural wine, beer, and soft drinks at a beautifully curated record café",
          "🥐 Light café food to accompany your afternoon — cheese on toast, seasonal sweets, and more",
          "👥 Intimate group of 10 — small enough for real conversation, quiet enough to actually hear each other",
          "🎙️ Conversation is welcomed and encouraged — the music sets the pace, not the other way around",
          "💳 Individual drinks and food ordered and paid at the venue"
        ]
      },
      {
        type: 'header',
        content: "This One is For You If..."
      },
      {
        type: 'bullets',
        items: [
          "You've always wanted to visit a Tokyo listening bar but weren't sure how to walk in alone",
          "You love music and wish more social gatherings treated it as something to actually pay attention to",
          "You're drawn to analog things in a digital world — vinyl, drip coffee, real conversation",
          "You want to meet people without the noise and pressure of a typical social event",
          "You believe that the best conversations happen sideways — while you're both listening to something else"
        ]
      },
      {
        type: 'paragraph',
        content: "Fika at 33⅓ runs at the right speed. Come and hear for yourself."
      },
      {
        type: 'header',
        content: "Good to Know"
      },
      {
        type: 'bullets',
        items: [
          "Venue is smoke-free — comfortable for everyone",
          "Seating is intimate and limited — RSVP is essential",
          "Coffee, coffee cocktails, wine, beer, soft drinks, and food ordered and paid individually — budget approx. ¥800–1,500",
          "Exact venue address sent to confirmed attendees 24 hours before",
          "Capacity: 10 people — this one stays small by design"
        ]
      },
      
    ]
  },

  whatToExpect: [
    "Vinyl spinning all afternoon through tube amplifiers and serious speakers — jazz, rock, classical, and more",
   "Espresso drinks, coffee cocktails, natural wine, beer, and soft drinks at a beautifully curated record café",
    "Intimate group of 10 for conversation that moves at the pace of the music",
    "A social format where silence is welcome and conversation earns its moment",
  ],

  venue: {
    name: "TBD ",
    description: "A carefully chosen record café and listening bar — concrete walls, Tannoy speakers, vacuum tube amplifiers, and vinyl-lined shelves. The kind of room Tokyo does better than anywhere.",
    fullAddress: " Tokyo — exact address revealed to confirmed attendees 24 hours before the event",
    priceRange: "Individual coffee and food (approx. ¥800–1,500)",
    amenities: [
      "Vinyl listening bar",
      "Hand-drip specialty coffee",
      "Tube amplifiers and Tannoy speakers",
      "Nordic antique cup collection",
      "Cashless payments accepted",
      "Smoke-free environment",
      "Small art gallery space"
    ]
  },

  memberBenefits: [
    {
      icon: "🎵",
      title: "Vinyl, Properly Played",
      description: "Staff-curated records through vacuum tube amplifiers and Tannoy speakers — the sound that makes you understand why analog never died"
    },
    {
      icon: "🍷",
      title: "Drink What You Like",
      description: "Espresso drinks, coffee cocktails, natural wine, beer, and soft drinks — something for everyone, ordered individually at the bar"
    },
    {
      icon: "👥",
      title: "10 People. One Room.",
      description: "An intentionally small group where the intimacy is the feature, not the limitation"
    },
    {
      icon: "🔊",
      title: "A New Kind of Social",
      description: "Fika's slow philosophy meets Tokyo's listening bar culture — a format you won't find anywhere else in the city"
    }
  ],

  relatedEvents: ['authentic-swedish-fika-may-sunday-2026', 'weekly-fika', 'yanaka-cemetery-walk'],
  status: "open",

  eventFrequency: "monthly",
  dayOfWeek: "Saturday",
  culturalFocus: "Vinyl listening culture meets Swedish fika tradition",
  specialFeature: "Live vinyl DJ through tube amplifiers in an intimate  record café",

  socialProof: {
    totalAttended: 0,
    averageRating: null,
    repeatAttendees: null
  },

  hostInfo: {
    name: "Mark",
    bio: "Chasing the overlap between good coffee, good music, and the kind of conversations that only happen when you slow everything down.",
    avatar: "mark-hosts.jpg"
  },

  //attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without advance notice, will be considered a no-show. After three no-show instances, membership will be automatically revoked.",

  locationPolicy: "Exact address shared with confirmed attendees 24 hours before the event.",

  listeningNotes: "This is an active listening environment. The music is meant to be heard, not ignored. Conversation is warmly encouraged — but you may find yourself pausing mid-sentence to catch a chord. That's exactly the point.",

  whatToBring: [
    "Cash or card for your drinks and food order",
    "An open ear — the genre selection moves across jazz, rock, classical, electro, and beyond",
    "Curiosity about whoever ends up sitting next to you"
  ],

  idealFor: [
    "Vinyl enthusiasts and curious newcomers alike",
    "Anyone who finds loud, crowded events exhausting",
    "Coffee people who want music taken as seriously as the coffee",
    "Fika regulars looking for something new",
    "People who believe the best conversations happen while you're both listening to something else"
  ]
},

  {
  id: 'tokyo-coffee-festival-2026',
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/TokyoCoffeeFestival2026.jpg',
  iconValue: '☕',
  title: "Grounds for Connection: ConnectCo at Tokyo's Biggest Coffee Festival",
  description: "Tokyo's largest specialty coffee festival, once a year. 40+ roasters, brews from across Japan and Asia, a farmers market buzzing alongside it. Join us for a Saturday morning steeped in good coffee and even better company.",
  waitlist: "0",
  next: "Sat, May 2, 2025",
  time: "10:00 AM - 12:30 PM",
  eventDateTime: "2026-05-02T01:00:00Z",
  location: "TBD — Exact location sent 24 hours before",
  color: "from-amber-700 to-yellow-500",
  capacity: 10,
  difficulty: "Casual Social",
  tags: ["Coffee", "Food", "Social", "Specialty Coffee", "Festival", "Aoyama", "Weekend", "Make Friends"],
  featured: true,
  slug: 'tokyo-coffee-festival-may-2026',

  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  cost: "Pay for your own drinks/food",

    // Payment configuration properties
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },



  philosophy: {
    title: "Grounds for Connection: ConnectCo at Tokyo's Biggest Coffee Festival",
    sections: [
      {
        type: 'paragraph',
        content: "Once a year, Tokyo gathers its finest roasters in one outdoor courtyard for what has become the city's most anticipated coffee event. Forty-plus specialty coffee purveyors from across Japan and Asia — all in one place, one morning, with no agenda other than finding something extraordinary in your cup."
      },
      {
        type: 'paragraph',
        content: "We're going as a group. Not to network - just to wander together, taste things side by side, argue about our favorites, and do what ConnectCo does best: turn a shared experience into a genuine connection. If you care about coffee even a little, this is a morning you won't want to spend alone."
      },
      {
        type: 'header',
        content: "What the Festival Is"
      },
      {
        type: 'paragraph',
        content: "This is Tokyo's largest specialty coffee festival — a biannual outdoor event that draws 40+ roasters and independent cafés from Japan and across Asia including Singapore, Taiwan, Thailand, and Hong Kong. It runs over three days (May 2–4), and we're going on opening day Saturday when energy is highest and the full lineup is fresh."
      },
      {
        type: 'bullets',
        items: [
          "☕ 40+ roasters and specialty coffee booths — pour-overs, espressos, cold brews, and things you won't find anywhere else",
          "🌏 International lineup — Japan's best alongside cafés from Singapore, Taiwan, Thailand, and Hong Kong",
          "🛒 Farmers Market running alongside the festival — organic produce, handmade crafts, natural sweets, Japanese honey",
          "🎟️ Advance ticket recommended: ¥2,700 gets you a special festival mug + 4 drink coins + 30-minute early entry",
          "🚪 Walk-in also available at ¥3,000 (same-day at the entrance)",
          "📍 Exact meeting point sent to confirmed attendees 24 hours before"
        ]
      },
      {
        type: 'header',
        content: "How We're Doing This"
      },
      {
        type: 'paragraph',
        content: "We'll meet as a group and explore together. No rigid schedule — just a loose wandering through the booths, tasting as we go. The format naturally lends itself to conversation: sharing sips, debating roast profiles, stumbling on something unexpected together. Think of it as a Fika, but caffeinated and outdoors."
      },
      {
        type: 'paragraph',
        content: "After a couple of hours at the festival, we'll break off to a nearby spot for a proper sit-down conversation — just the group, winding down the caffeine buzz with something slower."
      },
      {
        type: 'header',
        content: "Cost & Tickets"
      },
      {
        type: 'bullets',
        items: [
          "Festival entry is free — you only pay for what you taste",
          "Advance ticket set: ¥2,700 (recommended — includes mug, 4 drink coins, early entry)",
          "Door ticket set: ¥3,000 (same experience, no early entry)",
          "Buy your own ticket in advance via the festival's official site",
          "Budget ¥1,000–¥2,000 extra if you want additional pours or market finds"
        ]
      },
      {
        type: 'header',
        content: "Why Opening Day"
      },
      {
        type: 'paragraph',
        content: "The festival runs three days, but Day 1 is the one. Different roasters feature across different days, and Saturday draws the full lineup at peak freshness. If you're going to do it, do it right."
      },
      {
        type: 'paragraph',
        content: "Grounds for connection. See you on Saturday. ☕"
      }
    ]
  },

  whatToExpect: [
    "40+ specialty roasters from Japan, Singapore, Taiwan, Thailand, and Hong Kong in one outdoor space",
    "A festival mug + drink coins to sample your way through the lineup",
    "A farmers market running alongside — organic, handmade, and worth a wander",
    "A loose, relaxed group format — wander, taste, talk, repeat",
    "Post-festival wind-down at a nearby café with the group"
  ],

  venue: {
    name: "TBD meeting point",
    description: "Tokyo's largest specialty coffee festival, held in an iconic open-air courtyard in Aoyama. Full meeting point details sent to confirmed attendees 24 hours before the event.",
    fullAddress: "TBD — revealed 24 hours prior",
    priceRange: "Pay for your own ticket",
    amenities: [
      "40+ specialty coffee booths",
      "International roasters from Japan and Asia",
      "Concurrent farmers market",
      "Outdoor open-air setting",
      "Festival mug included with ticket"
    ]
  },

  memberBenefits: [
    {
      icon: "☕",
      title: "Tokyo's Best in One Place",
      description: "40+ roasters from Japan and Asia — more specialty coffee per square meter than anywhere else in the city"
    },
    {
      icon: "🌏",
      title: "Asia-Wide Lineup",
      description: "Independent cafés from Singapore, Taiwan, Thailand, and Hong Kong alongside Japan's finest"
    },
    {
      icon: "🛒",
      title: "Farmers Market Bonus",
      description: "Organic produce, handmade crafts, and natural Japanese sweets running alongside the festival"
    },
    {
      icon: "👥",
      title: "Group of 12, Wandering Together",
      description: "Small enough to stay connected, big enough to split off and reconvene with recommendations"
    }
  ],

  relatedEvents: ['fika', 'weekly-fika', 'rooftop-pizza-setagaya', 'happy-hour'],
  status: "open",

  eventFrequency: "special",
  dayOfWeek: "Saturday",
  foodFocus: "Specialty coffee",
  neighborhood: "Aoyama",

  socialProof: {
    totalAttended: 0,
    averageRating: null,
    repeatAttendees: "N/A"
  },

  hostInfo: {
    name: "Mark",
    bio: "Specialty coffee obsessive and ConnectCo founder — on a permanent hunt for the perfect cup in Tokyo.",
    avatar: "mark-host.jpg"
  },

  paymentStructure: {
    entryFee: "Free (festival entry)",
    ticketSet: "¥2,700 advance (recommended) or ¥3,000 at door",
    additionalPours: "Individual purchase at each booth",
    paymentMethod: "Check festival site — likely cashless"
  },

  //attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",

  locationPolicy: "Exact meeting point shared with confirmed attendees 24 hours before the event date.",

  ticketNote: "Purchase your advance ticket (¥2,700) directly via the Tokyo Coffee Festival official site — link shared upon RSVP confirmation. Highly recommended over door tickets for early entry and the festival mug.",

  festivalNote: "Festival runs May 2–4. Different roasters feature across different days — we're going Day 1 (Saturday) for the full lineup at peak freshness.",

  whatToBring: [
    "Your advance festival ticket (purchased separately)",
    "IC card or cashless payment",
    "An empty stomach and strong opinions about coffee",
    "Curiosity — 40 booths is a lot of ground to cover"
  ],

  postEventOptions: [
    "Post-festival wind-down at a nearby café — details shared on the day",
    "Optional farmers market browse before or after the group session"
  ]
},

  {
  id: 'rooftop-pizza-setagaya',
  eventType: 'foodie',
  iconType: 'image',
  iconImage: '/images/events/RooftopPizzaSetagaya.jpg',
  iconValue: '🍕',
  title: "Pies & Skies: ConnectCo Rooftop Pizza Night in Setagaya",
  description: "Eight seats. Open skies. High-heat Neapolitan pies in one of Tokyo's coolest neighborhoods. No slices here—just whole pies, good company, and a rooftop with serious vibes.",
  waitlist: "2",
  next: "Sunday, April 19, 2026",
  time: "5:30 PM - 7:30 PM",
  eventDateTime: "2026-04-19T01:00:00Z",
  location: "Setagaya — Exact location sent 24 hours before",
  color: "from-orange-500 to-yellow-500",
  capacity: 8,
  difficulty: "Casual Social",
  tags: ["Food", "Social", "Setagaya", "Neapolitan Pizza", "Rooftop", "Weekend", "Make Friends"],
  featured: true,
  slug: 'rooftop-pizza-setagaya-april-2025',

  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  cost: "Pay for your own drinks/food",
  price: 0,

    // Payment configuration properties
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,
   
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },

  philosophy: {
    title: "Pies & Skies: ConnectCo Rooftop Pizza Night in Setagaya",
    sections: [
      {
        type: 'paragraph',
        content: "We're taking pizza night to new heights—literally. Join us on a rooftop terrace in one of Tokyo's most vibrant neighborhoods for an evening of high-heat  Neapolitan pies, open skies, and the kind of easy conversation that only happens when the setting is just right."
      },
      {
        type: 'paragraph',
        content: "This isn't your typical pizza-by-the-slice setup. Here, they do things the proper Italian way: whole pies, fired at blistering temperatures, with a crust that's charred, chewy, and completely worth the wait. The menu is tight and intentional—a small selection of pies ranging from the classics to a neighborhood-exclusive surprise you won't find anywhere else in Tokyo."
      },
      {
        type: 'header',
        content: "The Details"
      },
      {
        type: 'bullets',
        items: [
          "🍕 Whole pies only — no slices. Pies range from ¥1,999 to ¥2,299.",
          "🤝 Sharing is encouraged — split a pie with someone and try a second. Great way to taste more of the menu.",
          "🥤 Drinks available on-site — order what you like, pay as you go.",
          "💳 Cashless only — bring your card or IC card, no cash accepted.",
          "📍 Exact location in Setagaya sent to confirmed attendees 24 hours before the event.",
          "👥 Strictly 8 people — this one stays small and intentional."
        ]
      },
      {
        type: 'header',
        content: "Why This One is Special"
      },
      {
        type: 'paragraph',
        content: "Eight people. A rooftop. Pizzas fired at temperatures hot enough to have their own name. Setagaya has a creative, neighborhood energy that's different from anywhere else in Tokyo—unhurried, a little indie, genuinely cool. This is the perfect backdrop for a ConnectCo evening: no agenda, no forced mingling, just good food and people who actually want to be there."
      },
      {
        type: 'paragraph',
        content: "Whether you split a pie and make a friend or work your way through the menu solo, you'll leave with a full stomach and at least one good conversation under your belt."
      },
      {
        type: 'header',
        content: "Good to Know"
      },
      {
        type: 'bullets',
        items: [
          "Everyone pays for their own food and drinks — no pooling required",
          "Pies range from ¥1,999–¥3,150— budget accordingly",
          "Consider sharing a pie to try more varieties",
          "Cashless payments only — no cash accepted at this venue",
          "Location revealed 24 hours before the event date",
          "8 spots total — RSVP is essential"
        ]
      },
      {
        type: 'paragraph',
        content: "Pies, skies, and good company in Setagaya. See you on the rooftop. 🍕"
      }
    ]
  },

  whatToExpect: [
    "high-heat  Neapolitan pies at a rooftop terrace in Setagaya",
    "Whole pies only — perfect for sharing or going solo",
    "Intimate group of 8 for real conversations and easy connections",
    "Pay-your-own-way format — no group logistics, maximum freedom",
    "A neighborhood with serious personality and zero tourist energy"
  ],

  venue: {
    name: "TBD, Setagaya",
    description: "A rooftop terrace in one of Tokyo's most beloved neighborhoods. Full address sent to confirmed attendees 24 hours before the event.",
    fullAddress: "Setagaya, Tokyo — revealed 24 hours prior",
    priceRange: "¥1,999–¥3,150 per pie + individual drinks",
    amenities: ["Rooftop / open-air terrace", "high-heat  Neapolitan pizza", "Cashless payments only", "Walk-in as a group", "Drinks available on-site"]
  },

  memberBenefits: [
    {
      icon: "🍕",
      title: "Proper Neapolitan Pies",
      description: "Whole pies fired at extreme heat — the real deal, no shortcuts"
    },
    {
      icon: "🌇",
      title: "Rooftop Setting",
      description: "Open skies and Setagaya neighborhood energy — a vibe that just works"
    },
    {
      icon: "👥",
      title: "Intimate Group of 8",
      description: "Small enough for real conversations, big enough to mix and mingle"
    },
    {
      icon: "🤝",
      title: "Share & Discover",
      description: "Split pies with new friends and work your way through the menu together"
    }
  ],

  relatedEvents: ['fika', 'weekly-fika', 'happy-hour', 'pizza-night-daikanyama'],
  status: "open",

  eventFrequency: "special",
  dayOfWeek: "Saturday",
  foodFocus: "Neapolitan high-heat pizza",
  neighborhood: "Setagaya",

  socialProof: {
    totalAttended: 0,
    averageRating: null,
    repeatAttendees: "N/A"
  },

  hostInfo: {
    name: "Mark",
    bio: "On a mission to find the best pizza in Tokyo — one rooftop at a time.",
    avatar: "mark-host.jpg"
  },

  paymentStructure: {
    entryFee: "None",
    food: "Individual — pies ¥1,999–¥2,299",
    drinks: "Individual purchase at venue",
    paymentMethod: "Cashless only (no cash accepted)"
  },

  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",

  locationPolicy: "Exact address shared with confirmed attendees 24 hours before the event date.",

  pizzaNotes: "Whole pies only — no slices available. Sharing a pie with another attendee is a great way to sample more of the menu.",

  whatToBring: ["Card or IC card (cashless only — no cash accepted)", "Appetite for proper Neapolitan pizza", "A willingness to share a pie"],

  postEventOptions: ["Continue the evening at one of Setagaya's many bars and cafés nearby"]
},

{
  id: 'fika-coffee-crawl-setagaya-may-2026',
  eventType: 'walks',
  //iconType: 'emoji',
  iconType: 'image',
  iconImage: '/images/events/LuckyCupsWalk.jpg',
  iconValue: '☕',
  title: "Lucky Cups: A Setagaya Specialty Fika Coffee & Temple Crawl",
  description: "Three specialty cafés, one ancient temple, and a morning spent moving slowly through one of Tokyo's most quietly beautiful wards. This is fika on foot — unhurried, caffeinated, and full of the kind of conversations that only happen when you're walking somewhere together.",
  waitlist: "0",
  next: "Sun, May 24, 2026",
 // time: "11:00 AM - 1:00 PM",
 // eventDateTime: "2026-05-24T02:00:00Z", // 11:00 AM JST
  time: "10:00 AM - 12:00 PM",
  eventDateTime: "2026-05-24T01:00:00Z", // 10:00 AM JST

  location: "Setagaya - TBD",
  color: "from-amber-700 to-stone-800",
  capacity: 6,
  difficulty: "Easy Urban Walk",
  tags: ["Coffee", "Specialty Coffee", "Coffee Crawl", "Temple", "Shrine", "Fika", "Setagaya", "Walking", "Morning", "Slow Living", "Community", "Weekend"],
  featured: true,
  slug: 'fika-coffee-crawl-setagaya-may-2026',

  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  cost: "Pay for your own drinks/food",

  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },

  philosophy: {
    title: "Lucky Cups: A Setagaya Specialty Coffee & Temple Crawl",
    sections: [
      {
        type: 'paragraph',
        content: "Setagaya doesn't announce itself. No neon, no famous crossing, no line around the block. It's one of Tokyo's largest and least-Instagrammed wards — a slow-moving patchwork of residential backstreets, neighborhood shotengai, and quietly excellent coffee shops that have no interest in being discovered by anyone in a hurry. Which makes it exactly right for a morning like this."
      },
      {
        type: 'paragraph',
        content: "Lucky Cups is a fika crawl — a two-hour Sunday morning walk that moves between three of Setagaya's best specialty coffee spots, with a stop at one of the area's most atmospheric temples along the way. The format is simple: walk a little, sit a little, drink well, talk to the person next to you. Repeat. The Swedish concept of fika has always been about the pause more than the coffee. A crawl just gives you more pauses."
      },
      {
        type: 'header',
        content: "Why Setagaya"
      },
      {
        type: 'paragraph',
        content: "Setagaya is where a certain kind of Tokyoite goes to live quietly and drink seriously. The specialty coffee scene here isn't loud about itself — no queues out the door, no influencer signage — but the craft is as good as anything in Shimokitazawa or Daikanyama, and the atmosphere is closer to a neighborhood you actually belong in than a destination you're visiting. The ward is also home to one of Tokyo's most beloved temples: Gotokuji, the legendary birthplace of the maneki-neko — the beckoning cat said to bring good fortune to those who visit. Whether or not you believe in luck, it's one of the most quietly beautiful temple grounds in the city. A good omen for a Sunday morning either way."
      },
      {
        type: 'header',
        content: "How the Crawl Works"
      },
      {
        type: 'paragraph',
        content: "We move as a loose group — no flag, no rope, no rush. Between stops, the walk itself is the conversation starter: narrow residential lanes, the occasional shotengai, glimpses of garden walls and old rooftops. At each café, we order, we sit, we talk. The stops are spaced for a natural pace — you won't feel rushed at any of them, and you won't feel overly stuffed with caffeine by the end. If anything, you'll feel exactly right."
      },
      {
        type: 'bullets',
        items: [
          "☕ Three carefully chosen specialty coffee stops — different styles, different vibes, same quality bar",
          "⛩️ A visit to a Temple — some of the most peaceful grounds in Tokyo",
          "🚶 Easy walking through Setagaya's residential backstreets — flat, quiet, and best seen at Sunday morning pace",
          "👥 Small group of 6 — intimate enough for real conversation across the whole morning",
          "💬 The fika format applied to movement: slow, social, and built around the spaces between things"
        ]
      },
      {
        type: 'header',
        content: "This One is For You If..."
      },
      {
        type: 'bullets',
        items: [
          "You take your coffee seriously but don't take yourself too seriously",
          "You've explored Shimokitazawa and want to know what's in the quieter neighborhoods just beyond it",
          "You want to meet people while actually doing something — not standing in a circle at a networking event",
          "You believe Sunday mornings are best spent slowly, on foot, with something warm in your hand",
          
        ]
      },
      {
        type: 'paragraph',
        content: "Lucky Cups runs on Sunday time. No alarm-clock energy, no rushing. Just a morning in one of Tokyo's finest neighborhoods, cup by cup."
      },
      {
        type: 'header',
        content: "Good to Know"
      },
      {
        type: 'bullets',
        items: [
          "Arrive by 9:50 AM at the designated meeting point — walk departs at 10:00 AM",
          "Duration: approximately 2 hours total, including all stops",
          "Budget approx. ¥1,200–2,000 for drinks across the morning (3–4 stops, individual orders)",
          "Cash recommended — some independent cafés in Setagaya are card-only or cash-only; bring both to be safe",
          "Route is flat and easy — comfortable shoes are enough",
          "Exact meeting point disclosed to registered attendees 24 hours before the event",
          "Capacity: 6 people — staying small by design"
        ]
      }
    ]
  },

  whatToExpect: [
    "Three specialty coffee stops across a two-hour morning walk in Setagaya",
    "Easy walking through quiet residential backstreets and local shotengai",
    "A small group of 10 for the kind of conversation that actually goes somewhere",
    "The fika format in motion: unhurried, social, and built for a Sunday"
  ],

  venue: {
    name: "TBD",
    description: "Three specialty cafés and one ancient temple across Setagaya's quiet backstreets — a ward that takes its coffee as seriously as its pace of life.",
    fullAddress: "Setagaya-ku, Tokyo — exact meeting point disclosed to confirmed attendees 24 hours before the event",
    priceRange: "Individual drinks at each stop — budget approx. ¥1,200–2,000 across the morning",
    amenities: [
      "Specialty coffee at multiple stops",
      "Temple visit",
      "Easy flat walking route",
      "English-language social",
      "Small intimate group",
      "Residential backstreet exploration"
    ]
  },

  memberBenefits: [
    {
      icon: "☕",
      title: "Three Cafés, One Morning",
      description: "Three distinct specialty coffee experiences — different brewing styles, different spaces, same commitment to doing it properly"
    },
   
    {
      icon: "🚶",
      title: "Setagaya on Foot",
      description: "One of Tokyo's largest wards, explored at the only speed that makes sense for it — slow, attentive, and without anywhere particular to be"
    },
    {
      icon: "👥",
      title: "Fika in Motion",
      description: "The same slow, social fika energy — just spread across a morning walk instead of a single table"
    }
  ],

  relatedEvents: ['authentic-swedish-fika-may-sunday-2026', 'weekly-fika', 'yanaka-cemetery-walk', 'fika-at-33rpm-may-2026'],
  status: "open",

  eventFrequency: "monthly",
  dayOfWeek: "Sunday",
  walkDuration: "2 hours",
  language: "English",
  culturalFocus: "Tokyo specialty coffee culture meets neighborhood exploration",
  specialFeature: "Multi-stop coffee crawl format with temple visit in Setagaya",

  socialProof: {
    totalAttended: 0,
    averageRating: null,
    repeatAttendees: null
  },

  hostInfo: {
    name: "Mark",
    bio: "Community builder who has spent years finding the best coffee in Tokyo's least-obvious neighborhoods — always one wrong turn from somewhere worth stopping.",
    avatar: "mark-hosts.jpg"
  },

  locationPolicy: "Exact meeting point shared with confirmed attendees 24 hours before the event.",

  whatToBring: [
    "Cash and card — independent cafés in Setagaya vary, so bring both",
    "Comfortable walking shoes — flat route, easy underfoot",
    "Camera or phone — Gotokuji and Setagaya's backstreets reward slow looking",
    "An appetite for good coffee and Sunday morning conversation"
  ],

  idealFor: [
    "Specialty coffee enthusiasts who want to explore beyond the obvious neighborhoods",
    "Anyone who finds loud, crowded events exhausting but still wants to meet people",
    "Curious walkers who prefer discovering a city slowly, on foot",
    "Fika regulars looking for a new format",
    "People who've been meaning to visit Gotokuji Temple and just needed a reason to finally go"
  ],

  weatherPolicy: "Event proceeds rain or shine — café stops provide natural shelter, and Setagaya's covered shotengai keep the route walkable in light rain",

  accessibilityNotes: "Route is flat and easy throughout. Temple grounds at Gotokuji involve some gentle gravel paths. Please reach out in advance if you have mobility concerns."
},

  {
    id: 'yanaka-cemetery-walk',
    eventType: 'walks',
    iconType: 'image',
    iconImage: '/images/events/yanaka-tokyo-story.jpg',
    iconValue: '⛩️',
    title: "The Neighborhood That Time Forgot: Wandering Old Tokyo's Temples, Cat Lanes & Kissaten Culture",
    description: "Step into the Tokyo that survived everything. This 2.5-hour walk threads through Yanaka's ancient cemetery paths, winding backstreets, and legendary shotengai — a neighborhood that outlasted earthquakes, firebombing, and the modern city's relentless hunger for glass towers. We'll end the walk with a stop at a historic kissaten before finishing at a specialty coffee social.",
    waitlist: "0",
    next: "April 18, 2026",
    time: "10:00 AM - 12:30 PM",
    eventDateTime: "2026-04-18T01:00:00Z",
    location: "Yanaka Area (Meeting point disclosed 24 hours before the event)",
    color: "from-amber-600 to-stone-700",
    capacity: 10,
    difficulty: "Easy Urban Walk",
    tags: ["Morning", "History", "Spring", "English Conversation", "Photography", "Coffee", "Weekend", "Temples", "Shitamachi"],
    featured: true,
    slug: 'yanaka-cemetery-walk-apr-2026',

    // City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    cost: "Pay for your own drinks/food",

    // Payment configuration properties
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },

    philosophy: {
      title: "The Neighborhood That Time Forgot: Wandering Old Tokyo's Temples, Cat Lanes & Kissaten Culture",
      sections: [
        {
          type: 'paragraph',
          content: "Most of Tokyo was rebuilt from scratch — twice. Yanaka wasn't. Tucked into the quiet hills north of Ueno, this is one of the last places in the city where wooden machiya townhouses still lean into narrow lanes, where a 300-year-old shrine sits between a tofu shop and a hand-painted sign, and where the pace of daily life hasn't quite caught up with the 21st century. The Great Kanto Earthquake of 1923. The firebombing of 1945. The Bubble Era's wrecking ball. Yanaka survived all of it — and what remains is the closest thing Tokyo has to its own soul."
        },
        {
          type: 'paragraph',
          content: "Yanaka belongs to the 'Yanesen' triangle — Yanaka, Nezu, and Sendagi — a trio of shitamachi neighborhoods that together form a kind of living museum of the city that used to be. On a Saturday morning, before the day warms up and the crowds arrive, these streets have a quality of light and silence that feels genuinely rare in a city of 14 million people."
        },
        {
          type: 'header',
          content: "In 2 hours, you'll experience:"
        },
        {
          type: 'bullets',
          items: [
            "One of Tokyo's oldest and most atmospheric Shinto shrines — ancient, intimate, and largely overlooked by tourists rushing to bigger names nearby",
            "A vast, serene cemetery where the last shogun of Japan is buried alongside artists, prime ministers, and poets — and where cherry trees line the main path in full spring bloom",
            "A centuries-old Buddhist temple with an open-air bronze Buddha cast in 1690, sitting quietly at the edge of the cemetery grounds",
            "Yanaka's famous shotengai — a living, breathing shopping street where third-generation fishmongers and local snack vendors keep old Tokyo commerce alive",
            "The legendary Sunset Steps — a view of old rooftops and a distant tower that tells you exactly why this neighborhood refuses to change",
            "A mid-walk stop at one of Tokyo's most beloved kissaten — a coffee house that has been pouring drip coffee in the same wooden building since before World War II"
          ]
        },
        {
          type: 'paragraph',
          content: "This isn't a tour. There's no microphone, no flag to follow, no rope between you and the neighborhood. It's a slow walk with a small group of curious people, where the best moments happen when you stop — to peer into a shrine alcove, to photograph a cat sleeping on a stone lantern, to wonder how a neighborhood this quiet has stayed this quiet, this close to the center of one of the world's largest cities."
        },
        {
          type: 'header',
          content: "A Tale of Two Coffee Cultures"
        },
        {
          type: 'paragraph',
          content: "Yanaka is the perfect neighborhood to feel the full arc of Japanese coffee history in a single walk. Midway through, we'll pause at a legendary kissaten — one of those Showa-era coffee houses where time slows down, the chairs are worn leather, the music is jazz played low, and the tamago sando arrives on a ceramic plate. This is coffee culture as a way of being, not just a caffeine delivery system. Then, at the end of the walk, we'll land at a carefully chosen specialty café where the third wave has quietly taken root among the old wooden buildings — single-origin beans, precision brewing, and the kind of unhurried space that makes conversation easy. Together, they tell the full story of how seriously this city has always taken its coffee."
        },
        {
          type: 'header',
          content: "Perfect for:"
        },
        {
          type: 'bullets',
          items: [
            "Anyone who feels more at home in old neighborhoods than shiny ones",
            "History and architecture lovers drawn to Edo-period shitamachi atmosphere",
            "Photography enthusiasts hunting Tokyo's most atmospheric backstreet light",
            "Coffee explorers curious about the difference between a kissaten and a third-wave café — in the best possible field setting",
            "English speakers looking for genuine conversation while actually doing something interesting"
          ]
        },
        {
          type: 'paragraph',
          content: "April is one of the finest months to walk Yanaka. The cemetery's cherry trees may still be trailing the last of their blossoms, the spring light is generous, and the temperature is just right for a morning on foot. Join our small group of 10 for a walk through a Tokyo most visitors never find — and a cup of coffee that makes you want to stay."
        },
        {
          type: 'header',
          content: "Important Details"
        },
        {
          type: 'bullets',
          items: [
            "Arrival time: 9:45 AM at the designated meeting point",
            "Walk starts promptly at 10:00 AM (please don't be late — we move as a group)",
            "Duration: 2-hour walk including kissaten stop and specialty coffee social",
            "Cost: Pay for your own food and drinks along the way",
            "Group size: Limited to 10 people for an intimate experience",
            "Meeting point: Disclosed to registered attendees 24 hours before the event",
          ]
        },
        {
          type: 'header',
          content: "About the Yanaka Route"
        },
        {
          type: 'paragraph',
          content: "Our route moves through the full Yanaka-Nezu-Sendagi area — a neighborhood so compact you can cross it in twenty minutes, or spend an entire morning disappearing into its details. We'll begin at one of Tokyo's oldest Shinto shrines and move north through residential backstreets before reaching the cemetery, the temples, the shotengai, and the steps. The route is flat and easy underfoot, though a few sections involve uneven stone paths — comfortable walking shoes are recommended. Distances are short. The pace is slow. That's the whole idea."
        }
      ]
    },

    whatToExpected: [
      "2-hour walk through Yanaka's temples, backstreets, cemetery, and shotengai",
      "A mid-walk stop at a legendary pre-war kissaten for coffee and tamago sando",
      "Specialty coffee social at a third-wave café to close the morning",
      "English conversation with a small, curious group of 10",
      "One of Tokyo's finest spring walking neighborhoods — potentially with the last cherry blossoms of the season"
    ],

    venue: {
      name: "TBD",
      description: "Tokyo's most intact shitamachi neighborhood — a triangle of historic lanes, ancient shrines, a vast cemetery, and a beloved shopping street that have survived earthquakes, war, and the modern city's appetite for redevelopment.",
      fullAddress: "Yanaka, Taito-ku / Nezu, Bunkyo-ku, Tokyo (Exact meeting point disclosed 24 hours before the event to registered attendees)",
      meetingPoint: "Specific location in Yanaka area (disclosed 24 hours before the event)",
      priceRange: "Pay for your own food/drinks — kissaten coffee ¥500–700, specialty café ¥600–900",
      amenities: ["Easy urban walking", "English conversation", "Temple & shrine access", "Kissaten coffee stop", "Specialty coffee social", "Photography opportunities", "Spring cherry blossom potential"]
    },

    memberBenefits: [
      {
        icon: "⛩️",
        title: "Hidden Old Tokyo",
        description: "Explore a neighborhood most visitors walk past on their way to Ueno — and discover why locals consider it the city's most authentic quarter"
      },
      {
        icon: "🪦",
        title: "The Cemetery Walk",
        description: "Wander one of Tokyo's most unexpectedly beautiful public spaces — cherry trees, ancient headstones, and the graves of the last shogun"
      },
      {
        icon: "☕",
        title: "Kissaten + Third Wave",
        description: "Experience both ends of Tokyo's coffee culture: a pre-war kissaten frozen in the Showa era, and a precision specialty café where old buildings meet new craft"
      },
      {
        icon: "📸",
        title: "Spring Backstreet Light",
        description: "April morning light through wooden machiya lanes, stone lanterns, and temple gates — some of Tokyo's best photography without a crowd in sight"
      }
    ],

    relatedEvents: ['walks', 'weekly-fika', 'fika'],
    status: "open",

    // Additional metadata
    eventFrequency: "monthly",
    dayOfWeek: "Saturday",
    walkDuration: "2 hours",
    language: "English",

    socialProof: {
      totalAttended: 0,
      averageRating: null,
      repeatAttendees: null
    },

    hostInfo: {
      name: "Mark",
      bio: "Community builder and urban explorer who has spent years getting deliberately lost in Tokyo's least-Googled neighborhoods, always ending up at a good cup of coffee",
      avatar: "mark-alley-walks.jpg"
    },

    // Special logistics
    meetingInstructions: "Arrive by 9:45 AM at the designated Yanaka meeting point (exact location disclosed 24 hours before the event to registered attendees). Walk departs promptly at 10:00 AM.",
    weatherPolicy: "Event proceeds rain or shine — Yanaka's covered alleys, temples, and cafés provide plenty of shelter, and the neighborhood is genuinely beautiful in light rain",
    whatToBring: [
      "Comfortable walking shoes (some uneven stone paths)",
      "Camera or phone — this neighborhood rewards slow looking",
      "Cash for the kissaten and specialty café (some shops are cash only)",
      "An appetite for tamago sando and street snacks"
    ],
    accessibilityNotes: "The route includes some uneven stone paths, a small number of gentle slopes, and one short set of steps. The overall terrain is mild. Please reach out in advance if you have mobility concerns so we can plan accordingly."
  },



  {
  id: 'ai-financial-freedom-notebooklm-workshop-april-2026',  
  eventType: 'workshop',
  iconType: 'image', 
  iconImage: '/images/events/FinancialFreedom.jpg',
  iconValue: '💰🤖',
  title: "AI-Powered Financial Freedom: Master NotebookLM for Smart Money Management",
  subtitle: "Turn Google's AI into your personal CFP assistant",
  description: "Learn to harness NotebookLM's power for budget analysis, savings planning, and financial decision-making. Leave with a fully configured system and actionable insights for your financial future.",
  waitlist: "0",
  next: "April 26, 2025",
  time: "2:00 PM - 4:00 PM",
  eventDateTime: "2026-04-26T05:00:00Z",

  location: "TBD - Modern co-working space with WiFi (Central Tokyo - details sent one day before event)",
  color: "from-emerald-500 to-green-600",
  capacity: 13,
  difficulty: "Beginner-Friendly Tech Workshop",
  tags: ["Financial Literacy", "AI Tools", "NotebookLM", "Personal Finance", "Budgeting", "Investment Planning", "Tech Skills", "Productivity"],
  featured: true,
  slug: 'ai-financial-freedom-notebooklm-workshop-april-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  // Payment configuration properties
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 450000,     // ¥4,500 regular price
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true,
    earlyBirdPrice: 350000,      // ¥3,500 early bird
    earlyBirdDeadline: "2026-03-08T00:00:00Z"
  },

  workshopStructure: {
    title: "Complete Workshop Agenda",
    sections: [
      {
        time: "2:00-2:15 PM",
        title: "Financial Reality Check",
        content: "Anonymous financial literacy survey, common money mistakes, and why 80% of budgets fail"
      },
      {
        time: "2:15-2:45 PM", 
        title: "NotebookLM Setup & Basics",
        content: "Live setup on your laptop, interface walkthrough, uploading your first financial documents"
      },
      {
        time: "2:45-3:15 PM",
        title: "Power Prompts Library",
        content: "Budget analysis, expense categorization, savings planning, and investment research prompts"
      },
      {
        time: "4:05 -3:30 PM",
        title: "Coffee Break & Networking",
        content: "Connect with fellow participants while systems process"
      },
      {
        time: "3:30-4:15 PM",
        title: "Hands-On Financial Analysis",
        content: "Upload your real expenses, generate personalized insights, identify optimization opportunities"
      },
      {
        time: "4:15-4:45 PM",
        title: "Create Your 90-Day Action Plan",
        content: "Build automated tracking systems, set up accountability measures, plan next steps"
      },
      {
        time: "4:45-5:00 PM",
        title: "Community Sharing & Q&A",
        content: "Share quick wins, troubleshoot issues, join ongoing accountability group"
      }
    ]
  },
  
  philosophy: {
    title: "Why AI + Financial Planning = Your Path to Freedom",
    sections: [
      {
        type: 'paragraph',
        content: "Living in Tokyo as an expat or international professional comes with unique financial challenges—high cost of living, complex tax situations, currency fluctuations, and limited access to traditional financial advisors who understand your situation. Most generic budgeting apps and advice don't account for the realities of Tokyo life, cross-border finances, or the specific goals of globally-minded professionals."
      },
      {
        type: 'paragraph',
        content: "That's where AI changes everything. Google's NotebookLM can analyze your specific financial documents, understand your unique situation, and provide personalized insights that would cost thousands from a financial advisor. In this workshop, you'll learn to transform this free AI tool into your personal CFP assistant, capable of analyzing spending patterns, optimizing budgets, researching investments, and keeping you accountable to your financial goals."
      },
      {
        type: 'header',
        content: "What Makes This Workshop Different"
      },
      {
        type: 'paragraph',
        content: "Hands-On Learning: No boring lectures—you'll set up and configure your actual financial AI system during the workshop, leaving with a working tool you can use immediately."
      },
      {
        type: 'paragraph',
        content: "Real Financial Analysis: Bring your actual bank statements and expenses (optional but recommended). We'll analyze your real data and generate actionable insights, not theoretical examples."
      },
      {
        type: 'paragraph',
        content: "Tokyo-Specific Strategies: All examples, case studies, and advice are tailored for professionals living in Tokyo, including tax considerations, investment options, and cost-of-living realities."
      },
      {
        type: 'paragraph',
        content: "Proven Prompt Library: Receive a comprehensive library of tested financial prompts that work specifically with NotebookLM, saving you hours of trial and error."
      },
      {
        type: 'header',
        content: "The Power of AI Financial Planning"
      },
      {
        type: 'paragraph',
        content: "Imagine having a financial advisor available 24/7 who knows your complete financial history, can instantly analyze any spending decision, and helps you optimize every yen. NotebookLM can process your bank statements, categorize expenses with Tokyo-specific understanding, identify spending patterns you might miss, and suggest personalized strategies based on your actual financial behavior."
      },
      {
        type: 'paragraph',
        content: "Whether you're struggling to save in Tokyo's expensive environment, confused about investment options as a foreign resident, or just want to optimize your financial strategy, AI can provide the personalized guidance you need without the high cost of traditional financial advisory services."
      },
      {
        type: 'header',
        content: "What You'll Learn & Create"
      },
      {
        type: 'bullets',
        items: [
          "NotebookLM Setup Mastery: Complete configuration for personal finance management with your actual documents",
          "Power Prompts Library: 20+ proven prompts for budget analysis, savings planning, investment research, and financial optimization. Each prompt is designed to extract maximum insight from your own financial documents — giving you the kind of structured financial thinking that usually only happens in a consultation room, at zero cost, on your schedule.",
          "Expense Analysis System: Automated categorization and analysis of your spending patterns with Tokyo cost-of-living context",
          "Savings Strategy Generator: AI-powered plans for emergency funds, retirement savings, and specific financial goals",
          "Investment Research Assistant: Prompts for researching investment options suitable for Tokyo-based professionals",
          "Accountability & Tracking: Automated systems to keep you on track with your financial goals"
        ]
      },
      {
        type: 'header',
        content: "Requirements & What to Bring"
      },
      {
        type: 'bullets',
        items: [
          "Laptop with reliable internet connection (tablets won't work for this workshop)",
          "Google account (free - we'll help you set up if needed)",
          "Recent bank statements or expense tracking (optional but highly recommended for personalized analysis)",
          "Willingness to share general insights with the group (specific numbers remain private)",
          "Notebook for taking notes on your personalized action plan"
        ]
      },
      {
        type: 'header',
        content: "Workshop Investment & Value"
      },
     
      {
        type: 'bullets',
        items: [
          "2-hour intensive hands-on workshop with Mark, ConnectCo founder",
          "Complete NotebookLM financial system configured on your laptop",
          "20+ prompts developed and tested specifically for NotebookLM's capabilities — covering zero-based budgeting, investment thesis stress-testing, and debt payoff strategy",
          "Personalized 90-day financial action plan based on your actual data",
          "Workshop materials and templates for ongoing use"
        ]
      },
      {
        type: 'paragraph',
        content: "Compare this to a single financial advisor consultation in Tokyo (¥20,000–50,000+) or premium financial planning software (¥5,000–15,000/year) — and unlike both, you're building a system you own and can improve indefinitely."
      },
      {
        type: 'header',
        content: "Important Workshop Policy"
      },
      {
        type: 'paragraph',
        content: "This is a technical workshop requiring active participation and laptop use throughout. Due to the intensive nature and limited capacity (13 people maximum), cancellations after April 12 will be charged 50% of payment. No-shows will be charged the entire payment. This policy ensures serious participants and allows for proper workshop preparation."
      }
    ]
  },
  
  whatToExpected: [
    "Set up and configure NotebookLM for comprehensive financial management",
    "Learn 20+ powerful prompts for budget analysis, savings planning, and investment research",
    "Analyze your actual financial data with AI assistance (bring bank statements if comfortable)",
    "Create a personalized 90-day financial action plan with specific, measurable goals",
    "Build automated tracking and accountability systems using AI",
    "Join a community of financially-savvy Tokyo professionals for ongoing support",
    "Receive follow-up resources and advanced tips for 30 days after the workshop"
  ],
  
  venue: { 
    name: "TBD - Modern Co-working Space",
    description: "A tech-friendly co-working space in central Tokyo with high-speed WiFi, power outlets at every seat, and large screens for demonstrations. The space is designed for productivity and learning, with comfortable seating and all necessary technical infrastructure.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event",
    priceRange: "Workshop fee: ¥TBD(Pay cash at the event)",
    amenities: ["High-speed WiFi", "Power outlets", "Large demonstration screens", "Comfortable seating", "Air conditioning", "Coffee/tea available", "Whiteboards", "Tech support"]
  },
  
  memberBenefits: [
    {
      icon: "🤖",
      title: "AI Financial Assistant Setup",
      description: "Leave with NotebookLM fully configured as your personal financial planning tool"
    },
    {
      icon: "💡", 
      title: "20+ Power Prompts Library + Income/Expenses Spreadsheet Template",
      description: "Proven prompts for budget analysis, savings planning, investment research, and financial optimization. Prompts developed and tested specifically for NotebookLM's capabilities — covering zero-based budgeting, investment thesis stress-testing, and debt payoff strategy"
    },
    {
      icon: "📊",
      title: "Real Data Analysis", 
      description: "Analyze your actual financial situation with AI assistance and generate personalized insights"
    },
    {
      icon: "🎯",
      title: "90-Day Action Plan",
      description: "Create a specific, measurable financial improvement plan tailored to your Tokyo lifestyle"
    },
     {
      icon: "☕",
      title: "Premium Artisan Cold Brew",
      description: "Enjoy Tokyo's finest cold brew crafted by Brewed Awakening throughout the workshop (¥800 retail value)"
    }
  ],
  
  relatedEvents: ['monthly-fika', 'productivity-workshop', 'professional-development'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "one-time-workshop",
  dayOfWeek: "Saturday", 
  culturalFocus: "Financial literacy and AI productivity for Tokyo professionals",
  specialFeature: "Hands-on AI configuration with real financial data analysis",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Founder of ConnectCo with extensive experience in financial planning and AI productivity tools. Passionate about helping Tokyo professionals achieve financial freedom through smart technology use.",
    avatar: "mark-hosts.jpg",
    credentials: "15+ years financial experience, AI tools expert, ConnectCo community founder"
  },

  // Workshop-specific features
  technicalRequirements: {
    required: [
      "Laptop computer (tablets not sufficient)",
      "Reliable internet connection", 
      "Google account (free)",
      "Modern web browser (Chrome, Firefox, Safari)"
    ],
    recommended: [
      "Recent bank statements (for personalized analysis)",
      "Expense tracking data (any format)",
      "Financial goals or questions you want addressed",
      "Notebook for action plan notes"
    ]
  },
  
  learningOutcomes: {
    beginner: [
      "Understand how AI can revolutionize personal financial management",
      "Set up NotebookLM account and basic configuration",
      "Learn fundamental prompts for budget analysis and expense tracking"
    ],
    intermediate: [
      "Master advanced prompts for investment research and savings optimization", 
      "Create automated financial tracking and accountability systems",
      "Develop personalized strategies for Tokyo-specific financial challenges"
    ],
    advanced: [
      "Build custom AI workflows for complex financial scenarios",
      "Integrate NotebookLM with other financial tools and systems",
      "Create accountability and tracking systems for long-term financial success"
    ]
  },
  
  workshopMaterials: {
    included: [
      "Complete prompt library (digital download)",
      "Workshop slides and reference materials",
      "Template documents for ongoing use",
      "30-day follow-up email series",
      "Access to private community group"
    ],
    bring_yourself: [
      "Laptop",
      "Financial documents for analysis (optional)",
      "Notebook for taking notes",
      "Specific financial goals or challenges to address"
    ]
  },
  
  followUpSupport: {
    immediate: "Private community group access for ongoing questions and accountability",
    short_term: "30-day email series with advanced tips and check-ins",
    long_term: "Quarterly group check-ins and system updates (optional)",
    individual: "1-on-1 follow-up session available (additional fee)"
  },
  
  attendancePolicy: "Due to the technical nature and limited capacity, cancellations after April 12 will be charged 50% of payment. No-shows will be charged the entire payment. This ensures serious participants and proper workshop preparation.",
  
  idealFor: [
    "Tokyo-based professionals struggling with budgeting and savings",
    "Expats dealing with complex cross-border financial situations", 
    "Anyone interested in using AI for personal productivity and financial planning",
    "ConnectCo members wanting practical skills beyond networking",
    "People ready to take serious action on their financial future"
  ],
  
  notIdealFor: [
    "People looking for get-rich-quick schemes or investment tips",
    "Those not comfortable using technology or computers",
    "Anyone not ready to actively participate in hands-on learning",
    "People expecting personalized financial advice (this is education, not advisory)"
  ],
  
  successMetrics: [
    "100% of participants leave with working NotebookLM financial system",
    "Average of 3+ actionable insights discovered per participant", 
    "90% of participants report improved financial clarity within 30 days",
    "80% of participants still using the system 3 months later"
  ],
  
  testimonialPrompts: [
    "What was the most valuable insight you gained about your finances?",
    "How has using AI changed your approach to budgeting and planning?",
    "What surprised you most about the workshop?",
    "Would you recommend this to other Tokyo professionals? Why?"
  ],
  
  competitiveAdvantages: [
    "Only workshop in Tokyo combining AI tools with personal finance",
    "Hands-on setup vs. theoretical learning",
    "Tokyo-specific financial strategies and examples",
    "Small group size ensures individual attention",
    "Ongoing community support and accountability",
    "Founder-led workshop with real business and financial experience"
  ],
  
  pricingJustification: {
    value_comparison: "Financial advisor consultation: ¥20,000-50,000+",
    software_comparison: "Premium financial software: ¥5,000-15,000/year", 
    education_comparison: "Financial planning course: ¥10,000-30,000",
    time_savings: "Hours of setup and prompt development: Priceless",
    community_value: "Ongoing support and accountability: ¥5,000+ value"
  },
  
  marketingMessages: [
    "Stop letting money stress control your Tokyo life",
    "Turn free AI tools into your personal financial advisor",
    "Join Tokyo's smartest professionals in mastering money + AI",
    "3 hours that could save you years of financial struggle",
    "Finally get the financial clarity you've been seeking"
  ]
},





{
    id: 'kagurazaka-alley-walk',
    eventType: 'walks',
    iconType: 'image', 
    iconImage: '/images/events/Kagurazaka.jpg',
    iconValue: '🏮',
    title: "Lost in Kagurazaka: Cobblestone Alleys, Geisha Whispers & Specialty Coffee",
    description: "Get wonderfully lost in Tokyo's most enchanting maze of hidden alleys. This 1-hour walking journey winds through Kagurazaka's cobblestone yokocho — where geisha still slip between 400-year-old lanes — before landing at a specialty coffee social in one of the neighborhood's beloved third-wave cafés.",
    waitlist: "0",
    next: "March 21, 2026",
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-03-21T02:00:00Z",  
    location: "Kagurazaka Area (Meeting point disclosed 24 hours before the event)",
    color: "from-rose-600 to-violet-600",
    capacity: 12,
    difficulty: "Easy Urban Walk",
    tags: ["Morning", "Alleys", "Spring", "English Conversation", "Photography", "Coffee", "Weekend", "History"],
    featured: true,
    slug: 'kagurazaka-alley-walk-mar-2026',
    
    // City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo", 
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    cost: "¥1,500 - ¥3,000 Pay for your own drinks/food",

    // Payment configuration properties
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000, 
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
    
    philosophy: {
      title: "Lost in Kagurazaka: Cobblestone Alleys, Geisha Whispers & Specialty Coffee",
      sections: [
        {
          type: 'paragraph',
          content: "Step off the main street and into another century. Kagurazaka — meaning 'the slope of the gods' music' — is Tokyo's most captivating tangle of cobblestone lanes, where black-lacquered wooden walls hide centuries-old ryotei, the faint echo of shamisen practice drifts from behind closed doors, and you might just cross paths with one of the neighborhood's 25 remaining geisha hurrying between appointments. Often called Tokyo's 'Little Paris' for its French quarter charm, Kagurazaka is the rare Tokyo neighborhood where Edo-period mystery and modern café culture exist side by side."
        },
        {
          type: 'header', 
          content: "In just 1 hour, you'll discover:"
        },
        {
          type: 'bullets',
          items: [
            "Kakurenbo Yokocho — the legendary 'Hide-and-Seek Alley' where the lanes twist so tightly you can vanish in three steps",
            "Hyogo Yokocho — a cobblestone passage named after a samurai-era weapons merchant, now lined with Michelin-worthy restaurants",
            "Geisha Shinmichi — the atmospheric stone stairway where geisha once gathered, complete with heart-shaped stones hidden in the cobbles",
            "Kenban Yokocho — home to the geisha office and shamisen practice hall, where you might hear traditional music from behind the walls",
            "Zenkokuji Temple and Akagi Shrine — spiritual anchors of a neighborhood steeped in 400 years of history"
          ]
        },
        {
          type: 'paragraph',
          content: "This isn't a history lecture — it's a slow, curious wander through lanes barely wide enough for two people, where every turn reveals a hidden café, a tucked-away shrine, or a quiet courtyard you'd never find on your own. Conversation flows naturally when you're exploring together."
        },
        {
          type: 'header',
          content: "Ending with Specialty Coffee"
        },
        {
          type: 'paragraph',
          content: "After our walk, we'll settle into one of Kagurazaka's beloved third-wave coffee spots for a social coffee session. This neighborhood is home to some of Tokyo's finest specialty cafés, where single-origin pour-overs and expertly pulled espresso meet the kind of warm, unhurried atmosphere that makes conversation easy. Think of it as the perfect reward after an hour of urban exploration — great coffee, new connections, and stories still fresh from the alleys."
        },
        {
          type: 'header',
          content: "Perfect for:"
        },
        {
          type: 'bullets', 
          items: [
            "Curious explorers who love getting lost in places most tourists never find",
            "History and culture enthusiasts drawn to living, breathing Edo-period atmosphere",
            "Photography lovers looking for Tokyo's most atmospheric backstreet compositions",
            "English speakers wanting meaningful conversation while walking",
            "Coffee enthusiasts eager to discover Kagurazaka's specialty café scene"
          ]
        },
        {
          type: 'paragraph',
          content: "Join our intimate group of 10 explorers for a morning of serendipity in Tokyo's most enchanting hidden neighborhood — where getting lost is the whole point."
        },
        {
          type: 'header',
          content: "Important Details"
        },
        {
          type: 'bullets',
          items: [
            "Arrival time: 10:45 AM at designated meeting point",
            "Walk starts promptly: 11:00 AM (no late arrivals can be accommodated)",
            "Duration: 1-hour guided alley walk + specialty coffee social",
            "Language: English conversation throughout the journey", 
            "Cost: Free (coffee at your own expense)",
            "Group size: Limited to 12 people for intimate experience",
            "Meeting point: Disclosed 24 hours before the event to registered attendees",
            "Coffee Social: Included as part of the experience at a third-wave café",
          ]
        },
        {
          type: 'header',
          content: "About the Kagurazaka Route"
        },
        {
          type: 'paragraph',
          content: "Our route threads through Kagurazaka's most atmospheric yokocho — the narrow alleys that split off from the main slope like secret passages. These cobblestone lanes, some barely a meter wide, have remained largely unchanged since the neighborhood served as one of Tokyo's most celebrated geisha districts. We'll navigate from the iconic Zenkokuji Temple through the labyrinth of Kakurenbo Yokocho, past the elegant black-walled ryotei of Hyogo Yokocho, and along the stone steps of Geisha Shinmichi. On Saturdays, the main street is closed to traffic, adding an extra layer of calm to an already peaceful neighborhood."
        },
        {
          type: 'header',
          content: "Ending with Coffee"
        },
        {
          type: 'paragraph',
          content: "Our walk wraps up at a carefully chosen third-wave specialty café nestled in Kagurazaka's backstreets. This is where the morning shifts from exploration to connection — settle in with a single-origin pour-over or a beautifully crafted latte, and let the conversation continue. Kagurazaka's café culture is among the best in Tokyo, blending Japanese precision with a distinctly European sense of ease. Please note that seating arrangements depend on café availability and group size."
        }
      ]
    },
    
    whatToExpected: [
      "1-hour guided walk through Kagurazaka's hidden cobblestone yokocho",
      "English conversation with international culture enthusiasts",
      "Discover secret alleys, geisha heritage sites, and hidden shrines",
      "Specialty coffee social at a third-wave café after the walk",
      "Intimate group experience limited to 10 participants"
    ],
    
    venue: {
      name: "Kagurazaka Backstreets & Yokocho",
      description: "Tokyo's most enchanting maze of cobblestone alleys, where 400 years of geisha heritage, French-quarter charm, and hidden temples converge in lanes barely wide enough for two.",
      fullAddress: "Kagurazaka, Shinjuku-ku, Tokyo (Exact meeting point disclosed 24 hours before the event to registered attendees)",
      meetingPoint: "Specific location in Kagurazaka area (disclosed 24 hours before the event)", 
      priceRange: "Pay for your own food/drinks - optional coffee ¥500-900",
      amenities: ["Cobblestone alley walking", "English conversation", "Photography opportunities", "Specialty coffee social", "Historical site visits", "Shrine & temple access"]
    },
    
    memberBenefits: [
      {
        icon: "🏮",
        title: "Hidden Alley Discovery",
        description: "Navigate Tokyo's most atmospheric cobblestone yokocho that most visitors never find"
      },
      {
        icon: "🗣️",
        title: "English Walking Chat", 
        description: "Practice conversational English while exploring centuries-old backstreets"
      },
      {
        icon: "📸",
        title: "Backstreet Photography",
        description: "Capture moody cobblestone lanes, lacquered walls, and hidden shrine moments"
      },
      {
        icon: "☕",
        title: "Third-Wave Coffee Social",
        description: "Wind down with specialty coffee and new connections at a beloved local café"
      }
    ],
    
    relatedEvents: ['walks', 'weekly-fika', 'fika'],
    status: "open",
    
    // Additional metadata
    eventFrequency: "monthly",
    dayOfWeek: "Saturday",
    walkDuration: "1 hour", 
    language: "English",
    
    socialProof: {
      totalAttended: 0, // New event
      averageRating: null,
      repeatAttendees: null
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Community builder and urban explorer passionate about Tokyo's hidden neighborhoods and meaningful connections over coffee",
      avatar: "mark-alley-walks.jpg"
    },
  
    // Special logistics
    meetingInstructions: "Arrive by 10:45 AM at the designated Kagurazaka meeting point (exact location disclosed 24 hours before the event to registered attendees). Walk departs promptly at 11:00 AM.",
    weatherPolicy: "Event proceeds rain or shine — Kagurazaka's covered alleys and nearby cafés provide shelter",
    whatToBring: ["Comfortable walking shoes for cobblestone paths", "Camera for backstreet photography", "Curiosity and an appetite for getting lost"],
    accessibilityNotes: "Route includes some uneven cobblestone surfaces and narrow alleys. A small number of stone steps are part of the walk. Please reach out in advance if you have mobility concerns so we can plan accordingly."
  },

  {
  id: 'book-club-april-2026',
  eventType: 'growth-circle',
  iconType: 'image',
  iconImage: '/images/events/BookClub.jpg',
  iconValue: '📚',
  title: "The Growth Circle Book Club: What Should We Read First?",
  description: "ConnectCo's first-ever book club kicks off at a cozy Tokyo third-wave café — and for this inaugural session, we're not discussing a book yet. We're choosing one together. Come with a recommendation, a genre you've been craving, or just genuine curiosity about what ten strangers in Tokyo are reading. The best book clubs aren't built around books. They're built around the people who love them.",
  waitlist: "0",
  next: "May 16, 2025",
  time: "1:00 PM - 2:30 PM",
  eventDateTime: "2026-05-16T04:00:00Z",
  location: "Third Wave café, Tokyo (Exact location disclosed 24 hours before the event)",
  color: "from-emerald-700 to-teal-500",
  capacity: 10,
  difficulty: "Easy",
  tags: ["Afternoon", "Books", "Spring", "English Conversation", "Culture", "Weekend", "Discussion", "Monthly"],
  featured: true,
  slug: 'book-club-april-2026',

  // City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  cost: "Pay for your own food/drinks",

  // Payment configuration properties
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 0,
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },

  philosophy: {
    title: "The Growth Circle Book Club: What Should We Read First?",
    sections: [
      {
        type: 'paragraph',
        content: "There's a particular kind of loneliness that comes from living in a city of fourteen million people where almost no one knows what you're reading. You finish a book that rearranged something inside you — a sentence that stopped you cold, an ending that stayed for days — and then you just... move on. No one to process it with. No one who was there."
      },
      {
        type: 'paragraph',
        content: "The Growth Circle Book Club is ConnectCo's answer to that. Once a month, a small group of us — maximum ten people — will gather at a carefully chosen third-wave café, order something good, and talk about a book. Not at each other. With each other. There's a difference, and it matters. Good book clubs aren't really about books. They're about the people who show up for them — the ideas they bring in, the experiences they can't help but read through, the questions they've been carrying without knowing it."
      },
      {
        type: 'header',
        content: "This First Session Is Different"
      },
      {
        type: 'paragraph',
        content: "For this inaugural gathering, we're not discussing a book yet. We're choosing one together. Come with a recommendation — something you've loved, something you've been meaning to read, something that feels right for a group of international Tokyo residents in spring 2026. Come with a genre you've been craving, a theme that feels alive right now, or just an open mind and genuine curiosity about what the people in the room are reaching for. The book we choose will tell us something about who we are as a group. That's worth ninety minutes of anyone's Saturday."
      },
      {
        type: 'header',
        content: "Why a Book Club, Why Now?"
      },
      {
        type: 'paragraph',
        content: "Research on social connection consistently finds that shared intellectual experiences — discussing the same story, inhabiting the same fictional world — accelerate intimacy in ways that casual conversation rarely does. A book gives you a safe container for ideas that might otherwise feel too raw to raise with strangers: grief, ambition, loneliness, joy, the parts of yourself you're still figuring out. The book is the permission slip. The conversation is the real thing."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "A ninety-minute gathering at a Tokyo third-wave café — relaxed, unhurried, no agenda beyond a good conversation",
          "Maximum ten people, which means everyone actually talks — and is heard",
          "This session: we pool our recommendations and choose the first book together as a group",
          "Future sessions: one book per month, discussed over coffee in the same intimate format",
          "No pressure to be well-read, literary, or articulate — bring yourself, that's enough"
        ]
      },
      {
        type: 'header',
        content: "How Book Selection Works"
      },
      {
        type: 'paragraph',
        content: "Everyone is encouraged — but not required — to bring one book recommendation. It can be a novel, a memoir, non-fiction, a short story collection, or something that defies easy categorisation. Ideally something that made you feel something, or something you've been wanting to explore with other people. We'll discuss the candidates as a group and vote on what we read for May. Simple, democratic, and usually more revealing than expected."
      },
      {
        type: 'header',
        content: "Perfect For:"
      },
      {
        type: 'bullets',
        items: [
          "Readers who've been craving a community to process books with — not just consume them alone",
          "People who love conversation that goes somewhere unexpected",
          "Anyone who has a book recommendation they've been desperate to press into someone's hands",
          "International Tokyo residents who want a recurring ritual that's genuinely worth showing up for, month after month",
          "First-timers to ConnectCo — book clubs are one of the gentlest, most natural ways to meet people for real"
        ]
      },
      {
        type: 'header',
        content: "The Details"
      },
      {
        type: 'bullets',
        items: [
          "Date: Saturday, April 4, 2026",
          "Time: 1:00 PM — 2:30 PM (arrive a few minutes early to settle in)",
          "Location: A third-wave café in Tokyo — exact address disclosed 24 hours before the event",
          "Cost: Coffee and drinks at your own expense",
          "Group size: Strictly limited to 10 people",
          "Frequency: Monthly — same format, a different book each time",
          "Language: English"
        ]
      },
     
    ]
  },

  whatToExpected: [
    "A ninety-minute conversation at a Tokyo third-wave café with up to ten people",
    "This session: pooling recommendations and voting on the first book together",
    "Future sessions: one book per month discussed in the same intimate format",
    "No lecture, no performance — just honest conversation about something we all read",
    "The kind of Saturday afternoon that leaves you feeling quietly better about the city you live in"
  ],

  venue: {
    name: "Third Wave Café, Tokyo",
    description: "A carefully chosen specialty coffee spot — the kind of place that takes both its coffee and its atmosphere seriously. Somewhere quiet enough to think, comfortable enough to stay.",
    fullAddress: "Tokyo, Japan",
    meetingPoint: "Exact café disclosed 24 hours before the event to registered attendees",
    priceRange: "Coffee and drinks at your own expense",
    amenities: ["Specialty coffee", "English conversation", "Intimate group setting", "Monthly recurring event", "Book discussion"]
  },



  memberBenefits: [
    {
      icon: "📚",
      title: "A Monthly Ritual Worth Keeping",
      description: "One Saturday afternoon a month set aside for something that actually feeds you — a book, a room of curious people, a conversation that goes somewhere real"
    },
    {
      icon: "☕",
      title: "Great Coffee, Better Company",
      description: "We meet at some of Tokyo's best third-wave spots — places worth visiting even before the conversation starts"
    },
    {
      icon: "🗣️",
      title: "Small Enough to Matter",
      description: "Ten people maximum means the discussion never becomes a lecture series — everyone's voice shapes where it goes"
    },
    {
      icon: "🌏",
      title: "An International Lens",
      description: "Reading the same book as ten Tokyo residents from different countries and backgrounds produces a conversation you genuinely cannot have anywhere else"
    }
  ],

  relatedEvents: ['weekly-fika', 'fika', 'rental-family-film-night', 'curated-fika'],
  status: "open",

  // Additional metadata
  eventFrequency: "monthly",
  dayOfWeek: "Saturday",
  walkDuration: null,
  language: "English",

  socialProof: {
    totalAttended: 0,
    averageRating: null,
    repeatAttendees: null
  },

  hostInfo: {
    name: "Mark",
    bio: "Community builder and lifelong reader who believes the best way to meet someone is to find out what they've been reading lately",
    avatar: "mark-book-club.jpg"
  },

  // Special logistics
  meetingInstructions: "Arrive by 1:55 PM at the designated café (exact location disclosed 24 hours before the event to registered attendees). If you're bringing a book recommendation, bring the title, author, and thirty seconds on why — that's all you need.",
  weatherPolicy: "Indoor event — rain, shine, or sakura season. The kettle is always on.",
  whatToBring: [
    "One book recommendation if you have one (title + one sentence on why — that's it)",
    "An open mind about what the group might choose",
    "Cash or IC card for your own drinks"
  ],
  accessibilityNotes: "Please reach out in advance if you have any specific accessibility requirements so we can confirm the café's suitability."
},

  {
    id: 'rental-family-film-night',
    eventType: 'film-nights',
    iconType: 'image',
    iconImage: '/images/events/RentalFamily.jpg',
    iconValue: '🎬',
    title: "Cinema Fika: Rental Family — A Tokyo Story About Borrowed Belonging",
    description: "Join us for a movie night watching Rental Family, the critically acclaimed Tokyo-set dramedy starring Brendan Fraser — then stay for a post-film conversation about connection, belonging, and what it means to find your people in a foreign city. Set right here in Tokyo, this one hits close to home.",
    waitlist: "0",
    next: "March 15, 2026",
    time: "4:10 PM - 6:00 PM",
    eventDateTime: "2026-03-15T06:30:00Z",
    location: "Hibiya or Roppongi (Exact location disclosed 24 hours before the event)",
    color: "from-amber-600 to-rose-700",
    capacity: 6,
    difficulty: "Easy",
    tags: ["Afternoon", "Film", "Spring", "English Conversation", "Culture", "Weekend", "Discussion"],
    featured: true,
    slug: 'rental-family-film-night-mar-2026',

    // City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    cost: "Cinema ticket ¥2,000 (pay me on arrival) · Food & drinks at your own expense as well.",

    // Payment configuration properties
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 0,
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },

    philosophy: {
      title: "Cinema Fika: Rental Family — A Tokyo Story About Borrowed Belonging",
      sections: [
        {
          type: 'paragraph',
          content: "Imagine landing in Tokyo with a suitcase, a fading career, and no idea who you really are anymore. That's where we meet Phillip Vanderploeg — a down-on-his-luck American actor who's been drifting through Tokyo for seven years, coasting on the residuals of a toothpaste commercial. Then a strange new gig finds him: working for a rental family agency, where ordinary people hire actors to play the roles missing from their lives. A surrogate father for a little girl who's never met her real dad. A doting son for an aging actor slipping into dementia. A familiar face at a gathering where loneliness would otherwise sit in the empty seat."
        },
        {
          type: 'paragraph',
          content: "Directed by Hikari and starring Oscar-winner Brendan Fraser in the role he was quietly built for, Rental Family is a film about faking connection until you stumble into something real. It earned 87% on Rotten Tomatoes and an audience score of 96%, with critics calling it 'sweet-natured,' 'deeply human,' and 'the kind of film we don't see often enough.' Fraser described it as 'a love letter to Japan.' Filmed entirely on location in Tokyo, it's one of the most honest portraits of expat life — loneliness, longing, and the unexpected places belonging finds you — ever put to screen."
        },
        {
          type: 'header',
          content: "Why Watch It Together?"
        },
        {
          type: 'paragraph',
          content: "Here's the thing about Rental Family: it's set in our city. The streets Phillip wanders are streets we know. The quiet ache of being a foreigner in Japan — understood on the surface, invisible underneath — is a feeling many of us carry. Watching this film as a small group of international Tokyo residents isn't just entertainment. It's a mirror. And the conversation that happens afterward, over drinks in Roppongi Hills, tends to be the kind you don't get anywhere else."
        },
        {
          type: 'header',
          content: "What to Expect"
        },
        {
          type: 'bullets',
          items: [
            "An afternoon at the cinema watching Rental Family — a 103-minute US-Japan co-production from director Hikari and Searchlight Pictures",
            "An intimate group of just 6 people — small enough for a real post-film conversation",
            "Discussion over drinks after the film: what resonated, what surprised you, what it made you think about your own Tokyo story",
            "Themes that land differently when you actually live here: belonging, performance, the loneliness hidden in plain sight, and the connections that sneak up on you"
          ]
        },
        {
          type: 'header',
          content: "About the Film"
        },
        {
          type: 'bullets',
          items: [
            "Directed by Hikari (37 Seconds, TIFF 2019), one of Japan's most exciting filmmakers working internationally",
            "Stars Brendan Fraser in what critics called 'an ideal showcase for his sensitive star power'",
            "87% Rotten Tomatoes critics score | 96% audience score | CinemaScore: A",
            "Named to the National Board of Review's Top 10 Films of the Year",
            "Premiered at the Toronto International Film Festival and shown at the 38th Tokyo International Film Festival",
            "Runtime: 103 minutes"
          ]
        },
        {
          type: 'header',
          content: "Perfect For:"
        },
        {
          type: 'bullets',
          items: [
            "Expats and international residents who've felt the particular Tokyo brand of loneliness — and found their way through it",
            "Film lovers who want to watch and actually talk about what they just saw",
            "Anyone curious about the real rental family industry, which has existed in Japan since the 1980s with over 300 agencies",
            "People who think the best conversations happen in the hours after a good film"
          ]
        },
        {
          type: 'header',
          content: "Tickets & Cost"
        },
        {
          type: 'paragraph',
          content: "I'll purchase all cinema tickets in advance to secure our seats as a group. Simply reimburse me in cash when you arrive. Food, drinks, and anything else during the afternoon are at your own expense."
        },
        {
          type: 'header',
          content: "⚠️ Cancellation Policy — Please Read Before Registering"
        },
        {
          type: 'paragraph',
          content: "Because tickets are purchased in advance and out of pocket, this event carries a firm cancellation policy. If you need to cancel, please do so at least 48 hours before the event so your spot can be offered to someone on the waitlist. Cancellations made within 48 hours of the event — for any reason — will still be responsible for the cost of the cinema ticket. This isn't about being rigid; it's about being fair. A no-show or last-minute cancellation means a paid seat goes empty with no way to recover the cost. Please only register if you're genuinely committed to attending."
        },
        {
          type: 'header',
          content: "Important Details"
        },
        {
          type: 'bullets',
          items: [
            "Arrival time: 3:45 PM",
            "Film begins promptly at 4:10 PM",
            "Post-film conversation and drinks to follow — plan for an easy, unhurried afternoon",
            "Cinema ticket: Purchased in advance by the host ¥2,000 — reimburse in cash on arrival",
            "Food & drinks: At your own expense",
            "Group size: Strictly limited to 6 people",
            "Cancellation deadline: 48 hours before the event — late cancellations remain responsible for the ticket cost",
            "Location: exact cinema disclosed 24 hours before the event to registered attendees"
          ]
        }
      ]
    },

    whatToExpected: [
      "An afternoon at the cinema watching Rental Family (2025) — 103 minutes, set in Tokyo",
      "Cinema ticket covered by the host — reimburse in cash on arrival",
      "Intimate group of just 6 people for a genuine post-film conversation",
      "Discussion over drinks/food after the film",
      "A shared experience that tends to surface real stories about life in Tokyo",
      "No agenda, no networking — just a great film and honest conversation"
    ],

    venue: {
      name: "Hibiya/Roppongi",
      description: "One of Tokyo's most vibrant cultural districts, home to world-class cinema, galleries, rooftop views, and the kind of cafés and bars that make post-film conversations easy and unhurried.",
      fullAddress: "Hibiya/Roppongi",
      meetingPoint: "Specific cinema in Hibiya or Roppongi Hills area (disclosed 24 hours before the event)",
      priceRange: "I'll purchase the movie tickets in advance (reimburse on arrival) · Food & drinks at your own expense as well",
      amenities: ["Cinema", "English conversation", "Post-film drinks", "Cultural discussion", "Intimate group experience"]
    },

    cancellationPolicy: {
      hoursRequired: 48,
      policyText: "Cancellations must be made at least 48 hours before the event. Late cancellations — regardless of reason — remain responsible for the cost of the cinema ticket, as tickets are purchased in advance and cannot be refunded or transferred.",
      ticketResponsibility: true
    },

    memberBenefits: [
      {
        icon: "🎬",
        title: "Ticket Covered",
        description: "Your cinema ticket is purchased in advance by the host — just reimburse in cash when you arrive"
      },
      {
        icon: "🗣️",
        title: "Real Post-Film Conversation",
        description: "Ten people is small enough that everyone actually gets to speak — and say something true"
      },
      {
        icon: "🏙️",
        title: "Tokyo on Screen",
        description: "Watch the city you live in through a filmmaker's eyes — and see it slightly differently afterward"
      },
      {
        icon: "🤝",
        title: "Found Connection",
        description: "The themes in Rental Family — belonging, performance, unexpected bonds — tend to open up something personal in the people who watch it together"
      }
    ],

    relatedEvents: ['weekly-fika', 'fika', 'kagurazaka-alley-walk', 'curated-fika'],
    status: "open",

    // Additional metadata
    eventFrequency: "occasional",
    dayOfWeek: "Sunday",
    walkDuration: null,
    language: "English",

    socialProof: {
      totalAttended: 0,
      averageRating: null,
      repeatAttendees: null
    },

    hostInfo: {
      name: "Mark",
      bio: "Community builder and film enthusiast who believes the best conversations happen in the two hours after a movie ends",
      avatar: "mark-film-nights.jpg"
    },

    // Special logistics
    meetingInstructions: "Arrive by 3:10 PM at the designated cinema(exact meeting disclosed 24 hours before the event to registered attendees). Film begins promptly at 2:30 PM. Please bring cash to reimburse your cinema ticket on arrival.",
    weatherPolicy: "Indoor event — weather is no issue. Rain or shine, the film rolls.",
    whatToBring: ["Cash to reimburse your cinema ticket on arrival", "An open mind and a story or two of your own", "Appetite for honest conversation"],
    accessibilityNotes: "Please reach out in advance if you have any specific accessibility requirements so we can confirm the cinema's suitability."
  },


 

  {
    id: 'nakameguro-river-walk',
    eventType: 'walks',
    iconType: 'image', 
    iconImage: '/images/events/MeguroRiver.jpg',
    iconValue: '🍂',
    title: "Fall Along the Meguro: Tokyo's Most Scenic River Walk & Coffee Social",
    description: "Monthly autumn morning walk along Tokyo's most picturesque river, following the famous cherry blossom trees in their golden fall glory from Nakameguro to trendy Ebisu.",
    waitlist: "0",
    next: "February 15, 2026",
    time: "10:00 AM - 1:00 PM",
    eventDateTime: "2026-02-15T01:00:00Z",  
    location: "Nakameguro Station (Meeting point TBA to registered attendees)",
    color: "from-amber-600 to-orange-600",
    capacity: 15,
    difficulty: "Easy River Walk",
    tags: ["Morning", "River", "Winter", "English Conversation", "Photography", "Coffee", "Weekend"],
    featured: true,
    slug: 'meguro-river-winter-walk-feb-2026',
    
    // City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo", 
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    cost: "Pay for your own food/drinks",

    // Payment configuration properties
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
    
    philosophy: {
      title: "Fall Along the Meguro: Tokyo's Most Scenic River Walk & Coffee Social (Optional)",
      sections: [
        {
          type: 'paragraph',
          content: "Experience Tokyo's most beloved river walk as autumn transforms the famous Meguro River cherry trees into a golden corridor of fall beauty. This intimate morning journey takes you along the gentle waters from historic Meguro to the vibrant coffee culture of Nakameguro, where the same trees that create spring's pink canopy now offer a tapestry of amber, gold, and crimson reflections on the water."
        },
        {
          type: 'header', 
          content: "In just 30 minutes, you'll discover:"
        },
        {
          type: 'bullets',
          items: [
            "Tokyo's most photogenic autumn river scenery as morning light dances on water",
            "The seasonal transformation of iconic cherry blossom trees into fall masterpieces",
            "Hidden riverside spots where locals start their weekend mornings",
            "Stories and casual English conversation with fellow autumn enthusiasts",
            "The perfect transition from peaceful Meguro to bustling Nakameguro's cafe scene"
          ]
        },
        {
          type: 'paragraph',
          content: "End your riverside journey in Nakameguro's renowned coffee district, where our optional cafe social continues with specialty brews surrounded by the area's creative energy."
        },
        {
          type: 'header',
          content: "Perfect for:"
        },
        {
          type: 'bullets', 
          items: [
            "Early birds seeking Tokyo's most beautiful autumn morning views",
            "Photography lovers capturing fall foliage reflections on water",
            "English speakers wanting mindful conversation while walking",
            "Anyone discovering the seasonal magic of Tokyo's urban waterways",
            "Coffee enthusiasts exploring Nakameguro's famous cafe culture"
          ]
        },
        {
          type: 'paragraph',
          content: "Join our intimate group of 20 morning wanderers for this exclusive autumn experience – where Tokyo's urban river becomes your gateway to seasonal serenity."
        },
        {
          type: 'header',
          content: "Important Details"
        },
        {
          type: 'bullets',
          items: [
            "Arrival time: 9:30 AM at designated meeting point",
            "Walk starts promptly: 9:45 AM (no late arrivals can be accommodated)",
            "Duration: 30 minutes riverside walk + optional Ebisu cafe social",
            "Language: English conversation throughout the journey", 
            "Cost: Completely free (optional coffee at your own expense)",
            "Group size: Limited to 20 people for intimate experience",
            "Meeting point: Exact location sent to registered attendees one week before event",
            "Weather: Event rescheduled if rain is forecasted (weather permitting)",
            "Coffee Social: Optional",
          ]
        },
        {
          type: 'header',
          content: "About the Meguro River Route"
        },
        {
          type: 'paragraph',
          content: "Our carefully chosen path follows the river's most scenic stretches, where hundreds of cherry trees create an autumn tunnel of gold and crimson. The gentle riverside walkways offer perfect spots for photography and conversation, while the morning light creates magical reflections on the calm waters. This route showcases why Meguro River is considered one of Tokyo's most beautiful urban waterways."
        },
        {
          type: 'header',
          content: "Ending in Ebisu"
        },
        {
          type: 'paragraph',
          content: "Our walk concludes in the heart of Ebisu, Tokyo's beloved coffee and culture district. Those joining the optional cafe social will gather at a charming riverside cafe in Nakameguro, where we'll continue our morning conversations over specialty coffee while the autumn river views provide the perfect backdrop. Please note that seating cannot be guaranteed due to our group size and busy weekend crowds."
        }
      ]
    },
    
    whatToExpected: [
      "30-minute scenic walk along autumn-transformed Meguro River",
      "English conversation with international morning enthusiasts",
      "Capture stunning fall foliage reflections on calm river waters", 
      "Optional coffee social in trendy Nakameguro district",
      "Intimate group experience limited to 20 participants"
    ],
    
    venue: {
      name: "Meguro River Riverside Path",
      description: "Tokyo's most picturesque urban river walk, featuring hundreds of cherry trees in spectacular autumn colors creating golden reflections on calm waters.",
      fullAddress: "Nakameguro Station, starting in Nakameguro and ending in Ebisu, Tokyo (Exact meeting point sent to registered attendees one week prior)",
      meetingPoint: "Specific location along Meguro River (details provided to attendees)", 
      priceRange: "Free event, optional coffee ¥400-800",
      amenities: ["Riverside walking paths", "English conversation", "Photography opportunities", "Multiple cafe options in Nakameguro", "Autumn foliage viewing"]
    },
    
    memberBenefits: [
      {
        icon: "🍂",
        title: "Autumn River Magic",
        description: "Experience Tokyo's most beautiful fall foliage reflections on tranquil waters"
      },
      {
        icon: "🗣️",
        title: "English Riverside Chat", 
        description: "Practice conversational English while strolling along scenic waterways"
      },
      {
        icon: "📸",
        title: "Golden Hour Photography",
        description: "Capture morning light transforming autumn leaves into riverside art"
      },
      {
        icon: "☕",
        title: "Shibuya Cafe Culture",
        description: "End with optional coffee social in Tokyo's most beloved cafe district"
      }
    ],
    
    relatedEvents: ['forest-walks', 'weekly-fika', 'fika'],
    status: "open",
    
    // Additional metadata
    eventFrequency: "monthly",
    dayOfWeek: "Sunday",
    walkDuration: "30 minutes", 
    language: "English",
    
    socialProof: {
      totalAttended: 0, // New event
      averageRating: null,
      repeatAttendees: null
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Morning enthusiast passionate about Tokyo's seasonal urban beauty and riverside walks",
      avatar: "mark-river-walks.jpg"
    },
  
    // Special logistics
    meetingInstructions: "Arrive between 8:30-8:45 AM at the designated Meguro River meeting point (exact location sent to registered attendees one week prior). Walk departs promptly at 8:45 AM",
    weatherPolicy: "Event continues rain or shine - covered walkways and cafe options available",
    whatToBring: ["Comfortable walking shoes", "Camera for autumn photography", "Positive morning energy"],
    accessibilityNotes: "Flat riverside paths suitable for all fitness levels, wheelchair accessible routes available"
  },

  {
  id: 'filipino-dinner-shinjuku-april-2026',
  eventType: 'foodie',
  iconType: 'image',
  iconImage: '/images/events/FilipinoDinner.jpg',
  iconValue: '🇵🇭',
  title: "Filipino Dinner Experience — From Adobo to Halo-Halo (Tokyo)",
  seoTitle: "Authentic Filipino Dinner in Tokyo: Adobo, Sisig & Halo-Halo — April 2026",
  description: "Where Manila's soulful kitchen meets Tokyo's backstreets. An intimate evening through the bold, tangy, and deeply comforting flavors of the Philippines—smoky sisig, slow-braised adobo, sizzling grills, and desserts that taste like nostalgia. Eight seats. One table. Flavors that take you somewhere.",
  waitlist: "0",
  next: "April 25, 2025",
  time: "6:00 PM - 8:00 PM",
  eventDateTime: "2026-04-25T09:00:00Z",

  location: "Tokyo",
  color: "from-yellow-400 to-blue-600",
  capacity: 8,
  difficulty: "Casual Dining",
  tags: ["Filipino Cuisine", "Adobo", "Sisig", "Southeast Asian", "Dinner", "Tokyo", "Halo-Halo", "Shinjuku"],
  featured: true,
  slug: 'filipino-dinner-shinjuku-april-2026',

  // City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",
  cost: "¥1,400–2,850 per dish (each person pays for their own order)",

  // Payment configuration
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000, // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },

  philosophy: {
    title: "Filipino Dinner: The Most Underrated Cuisine in Asia — Finally at Your Table",
    sections: [
      {
        type: 'header',
        content: "For a seamless registration experience, please review the information below and visit the Details tab before reserving your spot."
      },
      {
        type: 'paragraph',
        content: "Filipino food doesn't ask permission. It's bold, generous, unapologetically soulful—and it has been hiding in plain sight as one of the world's great underrated cuisines for far too long. This evening, we're changing that. Join us for an intimate dinner through the flavors that define the Filipino table: slow-braised adobo, sizzling sisig, smoky BBQ, and desserts that are genuinely unforgettable. Eight seats. One communal table. Food that demands conversation."
      },
      {
        type: 'paragraph',
        content: "The Philippines sits at one of the world's great culinary crossroads. Spanish colonization for over 300 years left behind vinegar-braised stews and rich caldereta. Chinese traders brought soy sauce, noodles, and techniques that fused seamlessly with indigenous ingredients. American influence added comfort food sensibilities. And underneath it all, a pre-colonial Austronesian food culture built on fermentation, sourness, and an obsession with fire. The result is a cuisine that is simultaneously familiar and completely its own."
      },
      {
        type: 'header',
        content: "Why Filipino Cuisine Deserves Your Full Attention"
      },
      {
        type: 'paragraph',
        content: "Food critics have been saying it for years: Filipino food is on the verge of its global moment. The cuisine's signature balancing act—between sour, salty, sweet, and umami—produces dishes of surprising complexity. Adobo, often called the national dish, is deceptively simple: vinegar, soy sauce, garlic, and time. But the depth it achieves is extraordinary. Sinigang, a tamarind-soured broth, is simultaneously refreshing and deeply comforting. And sisig—a sizzling platter of chopped pork, chili, and calamansi—is one of the finest bar foods ever conceived."
      },
      {
        type: 'header',
        content: "Important Timing"
      },
      {
        type: 'paragraph',
        content: "⏰ IMPORTANT: Please arrive by 5:45 PM (15 minutes before the 6:00 PM start). Filipino dining is communal and dishes arrive together—punctuality ensures we all experience the table as intended and no one misses the opening act. With just eight seats, every person matters to the rhythm of the evening."
      },
      {
        type: 'header',
        content: "The Culinary Journey"
      },
      {
        type: 'paragraph',
        content: "Expect the dishes that define Filipino cooking at its most confident and comforting. Chicken or pork adobo that has been coaxed to perfection—golden, sticky, with that irresistible tang from vinegar and the depth of soy and bay leaf. Sisig arriving on a cast-iron sizzle plate, crackling at the edges, brightened with calamansi and chilies. Slow-braised dishes that tell you exactly why Filipino food has been called 'the original comfort food.' And for dessert, halo-halo—a glorious assembly of shaved ice, purple ube ice cream, sweetened beans, coconut, and jackfruit that is equal parts architecture and joy."
      },
      {
        type: 'paragraph',
        content: "The menu reflects the full breadth of Filipino culinary identity: the lowland comfort of Manila's family restaurants, the Visayan love of grilled seafood, the Ilocano tradition of fermented and sour flavors. Every dish is designed for sharing—hands across the table, stories swapped over steaming plates, the particular warmth that Filipino food culture calls 'kamayan spirit.' This is food that gathers people, not just feeds them."
      },
      {
        type: 'header',
        content: "The Venue"
      },
      {
        type: 'paragraph',
        content: "Our chosen restaurant is one of Tokyo's most exciting Filipino spots—a bistro that delivers authentic and modern Filipino cooking with genuine care and skill. The space is stylish, warm, and intimate, with the kind of energy that makes two hours feel like twenty minutes. Easily accessible from central Tokyo, the neighborhood is a lively, cosmopolitan corner of the city that perfectly suits an evening built around discovery and good company. The exact name and address will be revealed to confirmed attendees 24 hours before the event."
      },
      {
        type: 'header',
        content: "Payment & Pricing"
      },
      {
        type: 'bullets',
        items: [
          "Dinner dishes typically range from ¥1,500 to ¥2,500 per dish",
          "Each person orders and pays individually at the table",
          "Drinks ordered and paid separately (Filipino beers and tropical cocktails available)",
          "Budget approximately ¥3,000–5,000 per person for a full, satisfying meal",
          "Credit cards accepted (cashless preferred)"
        ]
      },
      {
        type: 'header',
        content: "Perfect For Those Who"
      },
      {
        type: 'bullets',
        items: [
          "Are ready to discover why Filipino food is one of Asia's greatest underrated cuisines",
          "Love bold, sour, savory flavors—vinegar, citrus, fermented notes, and umami",
          "Appreciate food that is soulful, communal, and deeply tied to cultural identity",
          "Have been curious about Filipino cuisine but never had the right introduction",
          "Want a dinner that feels like a genuine discovery, not just a meal",
          "Believe the best tables are shared ones",
          "Have a serious dessert agenda and want to meet halo-halo properly"
        ]
      },
      {
        type: 'header',
        content: "Location & Getting There"
      },
      {
        type: 'paragraph',
        content: "The restaurant is located in the Shinjuku area—one of Tokyo's most vibrant and easily accessible neighborhoods, well connected to most major train lines. The full venue details, including the exact address, will be shared with all confirmed attendees 24 hours before the event."
      },
      {
        type: 'header',
        content: "What to Bring"
      },
      {
        type: 'paragraph',
        content: "Come hungry—Filipino portions are generous and the instinct to order one more dish is always correct. Come curious—there will be dishes you've never tried, and every one of them has a story. Come ready to share—Filipino food culture is fundamentally communal, and the best way to experience it is with a full table and an open mind. Casual attire is perfect for the relaxed, welcoming atmosphere."
      },
      {
        type: 'header',
        content: "A Note on the Flavors"
      },
      {
        type: 'paragraph',
        content: "Filipino cuisine leans sour and savory rather than spicy-hot. The acidity from vinegar and calamansi is central to the cuisine's identity and is far more nuanced than it sounds on paper. If you have dietary restrictions or strong aversions to pork or seafood, let us know when you RSVP—Filipino menus are generally accommodating and the kitchen is happy to guide your choices."
      }
    ]
  },

  whatToExpect: [
    "Intimate Filipino dinner with a small group of 8 people",
    "Authentic dishes: adobo, sisig, sinigang, grilled BBQ, and halo-halo",
    "Bold flavors anchored in vinegar, soy, citrus, and fire",
    "Stylish, warm bistro setting with genuine Filipino hospitality",
    "Shared plates and communal dining Filipino-style",
    "2-hour evening experience from 6:00 PM to 8:00 PM",
    "Cultural insight into the Philippines' remarkable culinary heritage",
    "Optional Filipino cocktails or San Miguel to complement the meal"
  ],

  venue: {
    name: "Filipino Bistro, Shinjuku, Tokyo",
    description: "A stylish and welcoming Filipino bistro in the heart of Shinjuku, serving authentic and modern Filipino dishes with care, skill, and a genuine love for the cuisine. From sizzling sisig to slow-braised adobo and iconic halo-halo, this is Filipino cooking at its most confident and inviting.",
    fullAddress: "Shinjuku, Tokyo (exact venue name and address revealed to confirmed attendees 24 hours prior)",
    priceRange: "¥3,000–5,000 per person (drinks separate)",
    amenities: ["Authentic Filipino cuisine", "Stylish bistro setting", "Filipino beers and cocktails", "Shared plate format", "Easy station access", "Credit cards accepted", "Warm communal atmosphere"]
  },

  memberBenefits: [
    {
      icon: "🍖",
      title: "Legendary Adobo",
      description: "Vinegar-braised, soy-kissed, and slow-cooked to a depth that redefines comfort food"
    },
    {
      icon: "🔥",
      title: "Sizzling Sisig",
      description: "Chopped pork, calamansi, and chili arriving on a cast-iron plate still crackling"
    },
    {
      icon: "🌏",
      title: "Cultural Discovery",
      description: "Three centuries of Spanish, Chinese, and Austronesian influence on one table"
    },
    {
      icon: "🍧",
      title: "Halo-Halo Glory",
      description: "Shaved ice, ube ice cream, and tropical sweetness—dessert as celebration"
    }
  ],

  relatedEvents: ['peruvian-lunch-azabudai-february-2026', 'thai-skyline-dinner-february-2026', 'french-vietnamese-lunch-january-2026', 'curated-spanish-tapas-december-2025'],
  status: "open",

  eventFrequency: "monthly",
  dayOfWeek: "Wednesday",
  focusArea: "Authentic Filipino cuisine with communal dining culture",
  experienceType: "casual dinner with shared plates and intimate group discovery",

  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },

  hostInfo: {
    name: "Mark & ConnectCo",
    bio: "Curating intimate culinary experiences where global cuisines, authentic flavors, and genuine connection create memorable shared moments",
    avatar: "mark-foodie-hosts.jpg"
  },

  culinaryFocus: {
    style: "Authentic Filipino home-style and bistro cooking",
    groupSize: "Intimate gathering of 8 for meaningful conversation and shared discovery",
    duration: "2 hours of leisurely communal dining",
    expertise: "The sour-savory depth and communal warmth of Filipino food culture"
  },

  includedExperience: {
    atmosphere: "Warm, stylish bistro with genuine Filipino hospitality",
    connection: "Shared plates and communal dining as natural conversation starters",
    quality: "Authentic ingredients, time-honored techniques, modern bistro presentation",
    pace: "Relaxed evening allowing you to savor flavors, stories, and company"
  },

  foodiePhilosophy: "Filipino food doesn't just feed you—it gathers you. There's a reason every dish is made for sharing: the food is the invitation, the table is the destination, and the people are the point.",

  idealFor: [
    "First-timers to Filipino cuisine ready for a proper, guided introduction",
    "Southeast Asian food lovers who know the cuisine deserves more attention",
    "Adventurous eaters drawn to sour, savory, and deeply umami-forward flavors",
    "Anyone who finds comfort food from other cultures deeply fascinating",
    "People curious about the Spanish-Chinese-Austronesian culinary fusion story",
    "Travelers at heart who believe the best passport is a menu",
    "Anyone who wants dinner to feel like a discovery",
    "Community members looking for an evening of genuine connection over exceptional food"
  ],

  preparationTips: [
    "Arrive by 5:45 PM (15 minutes early) for a 6:00 PM start—the table experience depends on everyone beginning together",
    "Share any dietary restrictions or pork/seafood concerns when you RSVP",
    "Come hungry—Filipino portions are generous and ordering widely is always the right call",
    "Consider trying a Pisco Sour equivalent: a calamansi cocktail or San Miguel for the full experience",
    "Casual attire is perfect—the space is welcoming and relaxed",
    "Save room for halo-halo. Non-negotiable.",
    "Credit cards accepted (cashless preferred)"
  ],

  uniqueAspects: [
    "One of Tokyo's most authentic Filipino bistro experiences in the Shinjuku area",
    "A cuisine that tells 500 years of colonial and cultural history through every dish",
    "The legendary adobo—one of Asia's most nuanced and satisfying braises",
    "Sizzling sisig, the dish that made Filipino food famous beyond its shores",
    "An intimate table of 8 for genuine connection and shared culinary discovery",
    "Halo-halo as dessert: a signature Filipino experience unto itself",
    "Food culture rooted in generosity, sharing, and the joy of gathering",
    "The rare chance to understand why chefs globally are calling Filipino cuisine 'the next big thing'"
  ],

  culinaryHighlights: [
    "Chicken or pork adobo—vinegar, soy, garlic, bay leaf, and patience",
    "Sisig—sizzling chopped pork or seafood with calamansi and chili",
    "Sinigang—tamarind-soured broth with vegetables and your choice of protein",
    "Filipino BBQ skewers—marinated, charred, and deeply satisfying",
    "Kare-kare—peanut-sauced oxtail stew with bagoong (fermented shrimp paste)",
    "Lechon kawali—crispy deep-fried pork belly",
    "Halo-halo—shaved ice with ube, sweetened beans, coconut, jackfruit, and leche flan"
  ]
},


  {
    id: 'shibuya-billiards-social',
    eventType: 'social-events',
    iconType: 'image', 
    iconImage: '/images/events/BilliardsNight.jpg',
    iconValue: '🎱',
    title: "Cue the Conversation: Saturday Afternoon Billiards & Brews Social",
    description: "Kick off your weekend with friendly billiards, cold drinks, and genuine conversation in the heart of Shibuya. Whether you're a pool shark or can barely hold a cue, join us for a laid-back Saturday afternoon where the real goal is connection, not competition. Table time is ¥900 per person/hour, drinks at your own expense.",
    waitlist: "0",
    next: "January 17, 2026",
    time: "3:00 PM - 5:00 PM",
    eventDateTime: "2026-01-17T06:00:00Z",  
    location: "Shibuya, Tokyo (TBD)",
    color: "from-emerald-600 to-teal-600",
    capacity: 8,
    difficulty: "All Skill Levels",
    tags: ["Evening", "Friday", "Shibuya", "English Conversation", "Games", "Social", "Drinks", "Weekend"],
    featured: true,
    slug: 'shibuya-billiards-social-jan-2026',
    
    // City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo", 
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    cost: "¥900 per person/hour (Table Fee)",

    // Payment configuration properties
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2400,     // ¥2,400 (¥800/hour × 3 hours)
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
    
    philosophy: {
      title: "Cue the Conversation: Saturday Afternoon Billiards & Brews Social",
      sections: [
        {
          type: 'paragraph',
          content: "Trade your desk chair for a pool cue and kick off your weekend the right way. This isn't about perfect bank shots or running the table – it's about genuine conversation, friendly competition, and connecting with people who value friendship over networking. Whether you've never held a cue stick or you're secretly hustling us all, you'll find your place in this laid-back Saturday Afternoon hangout."
        },
        {
          type: 'header', 
          content: "What makes this different:"
        },
        {
          type: 'bullets',
          items: [
            "No pressure, no pretense – just friendly games and real conversation",
            "Mixed skill levels mean everyone plays, everyone connects",
            "Rotating partners ensure you meet the whole group naturally",
            "English-speaking international crowd from across Tokyo",
            "Premium Shibuya billiards hall with craft drinks available"
          ]
        },
        {
          type: 'paragraph',
          content: "We'll form casual teams, rotate partners throughout the evening, and let the games facilitate what we're really here for: meeting interesting people and building genuine friendships over the satisfying crack of pool balls and cold drinks."
        },
        {
          type: 'header',
          content: "Perfect for:"
        },
        {
          type: 'bullets', 
          items: [
            "Anyone looking to start their weekend with friendly faces",
            "English speakers wanting casual conversation in a relaxed setting",
            "Billiards enthusiasts and complete beginners alike",
            "People tired of forced networking who want authentic connections",
            "Saturday afternoon adventurers exploring Shibuya's social scene"
          ]
        },
        {
          type: 'paragraph',
          content: "Join our intimate group of 8 for an evening where the only thing we're chasing is the 8-ball and good company. Competition optional, connection guaranteed."
        },
        {
          type: 'header',
          content: "Important Details"
        },
        {
          type: 'bullets',
          items: [
            "Arrival time: 2:45 PM at designated venue",
            "Games start: 3:00 PM sharp",
            "Duration: 2 hours of casual play and socializing",
            "Language: English conversation throughout", 
            "Table fee: ¥900 per person/hour (Venue Charge)",
            "Drinks: At your own expense (¥500-1,000 per drink)",
            "Group size: Limited to 8 people for quality connections",
            "Venue location: Exact address sent to registered attendees 24 hours before event",
            "Skill level: Absolutely all levels welcome",
            "Dress code: Casual (no special attire needed)"
          ]
        },
        {
          type: 'header',
          content: "How the Evening Flows"
        },
        {
          type: 'paragraph',
          content: "After quick introductions, we'll form casual teams and rotate throughout the evening to ensure everyone mingles naturally. No one sits out – we keep games moving so conversation flows alongside the friendly competition. Whether you're teaching someone how to hold a cue or learning the perfect break shot, the games become the backdrop for genuine connection. Expect laughter, terrible puns about 'breaking the ice,' and that satisfying feeling of meeting people who get it."
        },
        {
          type: 'header',
          content: "About the Venue"
        },
        {
          type: 'paragraph',
          content: "We're going to one of Shibuya's premier billiards halls – a modern space with professional tables, great lighting for play and conversation, and a relaxed atmosphere perfect for both serious shots and complete misses. The venue offers craft drinks and snacks, comfortable seating areas between games, and that perfect Saturday afternoon energy that makes you glad you came. Table time is charged at ¥800 per person/hour, with drinks available for separate purchase."
        },
        {
          type: 'header',
          content: "The Anti-Networking Approach"
        },
        {
          type: 'paragraph',
          content: "This is ConnectCo, which means no business cards, no LinkedIn pitches, no 'what do you do?' interrogations. Just people, pool cues, and the kind of authentic conversations that happen when you're focused on sinking the 5-ball instead of impressing anyone. Show up as yourself, leave with friends."
        }
      ]
    },
    
    whatToExpect: [
      "3 hours of friendly billiards with rotating partners",
      "English conversation with international crowd",
      "Mix of skill levels from beginners to experienced players", 
      "Premium Shibuya venue with craft drinks available",
      "Intimate group limited to 8 participants for quality connections"
    ],
    
    venue: {
      name: "Premium Billiards Hall in Shibuya",
      description: "Modern billiards venue in the heart of Shibuya featuring professional tables, comfortable atmosphere, and craft drink selection. Perfect for both serious play and social connection.",
      fullAddress: "Shibuya, Tokyo (Exact location sent to registered attendees 24 hours prior to event)",
      meetingPoint: "Venue entrance (specific details provided 24 hours before)", 
      priceRange: "¥900/person/hour table fee, drinks ¥500-1,000",
      amenities: ["Professional billiard tables", "Craft drinks available", "Comfortable seating areas", "English-speaking friendly", "Central Shibuya location", "Casual atmosphere"]
    },
    
    memberBenefits: [
      {
        icon: "🎱",
        title: "Friendly Competition",
        description: "Casual billiards games where winning matters less than the conversation"
      },
      {
        icon: "🗣️",
        title: "Natural Connections", 
        description: "Rotating partners mean you meet everyone through play, not forced mingling"
      },
      {
        icon: "🍻",
        title: "Friday Night Vibes",
        description: "Kick off your weekend in Shibuya's vibrant social scene"
      },
      {
        icon: "🌏",
        title: "International Crowd",
        description: "English-speaking community from across Tokyo and beyond"
      }
    ],
    
    relatedEvents: ['weekly-fika', 'fika', 'curated-fika'],
    status: "open",
    
    // Additional metadata
    eventFrequency: "one-time",
    dayOfWeek: "Friday",
    eventDuration: "3 hours", 
    language: "English",
    
    socialProof: {
      totalAttended: 0, // New event
      averageRating: null,
      repeatAttendees: null
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Community builder who believes the best connections happen over games, not business cards",
      avatar: "mark-social-events.jpg"
    },
  
    // Special logistics
    meetingInstructions: "Arrive at 3:45 PM at the designated billiards venue in Shibuya (exact address sent 24 hours prior). We'll gather at the entrance before heading to our reserved tables at 4:00 PM sharp.",
    weatherPolicy: "Indoor event – rain or shine, the games go on!",
    whatToBring: ["Your competitive (but friendly) spirit", "Casual attire", "Cash for drinks if desired"],
    accessibilityNotes: "Venue is wheelchair accessible with elevator access. All skill levels genuinely welcome – we'll teach beginners the basics!"
  },

  {
  id: 'peruvian-lunch-azabudai-february-2026',
  eventType: 'foodie',
  iconType: 'image',
  iconImage: '/images/events/PeruvianLunch.jpg',
  iconValue: '🇵🇪',
  title: "Peruvian Lunch Experience - Optional Coffee (Tokyo)",
  description: "Where Lima meets Tokyo. An intimate midday journey through authentic Peruvian flavors—bold ceviches, grilled anticuchos, and the vibrant fusion that happens when Pacific coastlines collide. Fresh, bright, and unapologetically flavorful. Optional social coffee afterward for those wanting to extend the connection.",
  waitlist: "0",
  next: "February 21, 2026",
  time: "1:30 PM - 3:00 PM (+ optional coffee)",
  eventDateTime: "2026-02-21T04:00:00Z",   

  location: "Tokyo",
  color: "from-red-500 to-amber-600",
  capacity: 6,
  difficulty: "Casual Fine Dining",
  tags: ["Peruvian Cuisine", "Ceviche", "Latin American", "Lunch", "Tokyo", "Nikkei Fusion"],
  featured: true,
  slug: 'peruvian-lunch-azabudai-february-2026',
  
  // City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan", 
  timezone: "Asia/Tokyo",
  currency: "JPY",
  cost: "¥2,000-4,000 per person (drinks separate)",

  // Payment configuration properties
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },

  philosophy: {
    title: "Peruvian Lunch: Where Pacific Traditions Meet Tokyo Precision",
    sections: [
      {
        type: 'header',
        content: "For a seamless registration experience, please review the information below and visit the Details tab before reserving your spot."
      },
      {
        type: 'paragraph',
        content: "Join us for an intimate midday exploration of Peru's coastal cuisine where citrus-bright ceviches, smoky anticuchos, and bold flavors tell the story of a culture shaped by Pacific waters and mountain peaks. This is Peruvian food that respects tradition while embracing the precision and presentation that Tokyo does best."
      },
      {
        type: 'paragraph',
        content: "Peru's culinary scene has captured global attention for good reason. It's a cuisine born from extraordinary biodiversity over 3,000 varieties of potatoes, countless types of corn, and seafood from one of the world's richest marine ecosystems. Add Japanese immigration history (creating the famed Nikkei fusion), Spanish colonial influences, and indigenous Andean traditions, and you get food that's complex, layered, and utterly unique."
      },
      {
        type: 'header',
        content: "What Makes This Experience Special"
      },
      {
        type: 'paragraph',
        content: "This isn't fusion for the sake of novelty, it's the genuine meeting point of two Pacific cultures with deep respect for fresh ingredients and meticulous technique. Located in Tokyo's modern area, our selected venue brings Lima's coastal energy to Japan's capital. Expect pristine ceviche where citrus and chili awaken every sense, anticuchos with that perfect char, and dishes that showcase why Peru has become one of the world's most celebrated culinary destinations."
      },
     
      {
        type: 'header',
        content: "Important Timing"
      },
      {
        type: 'paragraph',
        content: "⏰ IMPORTANT: Please arrive by 1:15 PM(15 minutes before 1:30PM start). This ensures everyone is seated and ready to begin our Peruvian journey together at 1:00 PM sharp. Punctuality matters when dining with a small group—it shows respect for everyone's time and allows us to experience the meal's pacing as intended."
      },
      {
        type: 'header',
        content: "The Culinary Journey"
      },
      {
        type: 'paragraph',
        content: "Expect the classics done exceptionally well. Ceviche that showcases the purity of raw fish kissed by lime, red onion, and ají amarillo—the balance of acid, heat, and sweetness that defines Peruvian cuisine. Anticuchos (grilled skewers) with that distinctive charred exterior and tender interior, traditionally made with beef heart but adapted to showcase various proteins. Causa—layered potato terrine that tells Peru's agricultural story in every colorful stratum."
      },
      {
        type: 'paragraph',
        content: "The menu draws from Peru's coastal cevicherías and mountain picanterías, adapted with Tokyo's exacting standards for freshness and presentation. You'll encounter flavors that are simultaneously familiar and revelatory. The umami depth of Nikkei preparations, the bright acidity that makes Peruvian food so craveable, the subtle heat that builds rather than overwhelms. Every dish is designed to be shared, encouraging conversation and connection over bold flavors."
      },
      {
        type: 'header',
        content: "Optional Social Coffee (3:00 PM onwards)"
      },
      {
        type: 'paragraph',
        content: "☕ The best conversations often continue after the plates are cleared. For those who want to extend the afternoon, we'll transition to a nearby café for optional social coffee. No pressure, no additional cover charge—just an invitation to keep the connection going in a more relaxed setting. Think of it as the dessert course for conversation."
      },
      {
        type: 'paragraph',
        content: "This is completely optional and spontaneous. Some will have afternoon commitments; others will want to linger. Both are perfectly fine. The coffee spot will be near the restaurant."
      },
      {
        type: 'header',
        content: "The Peruvian Story"
      },
      {
        type: 'paragraph',
        content: "To understand Peruvian cuisine is to understand a country where geography creates culinary destiny. The Humboldt Current brings cold, nutrient-rich waters that create one of the world's most abundant fishing grounds. The Andes mountains provide microclimates that support incredible agricultural diversity. Lima, the coastal capital, became a melting pot where indigenous ingredients met Spanish, African, Chinese, Japanese, and Italian influences."
      },
      {
        type: 'paragraph',
        content: "The result? A cuisine that UNESCO recognized as Intangible Cultural Heritage, that food critics call South America's most exciting culinary scene, and that Peruvians approach with fierce national pride. This lunch gives you a taste of why Lima's restaurants regularly top global best-of lists—it's food that honors tradition while constantly evolving."
      },
      {
        type: 'header',
        content: "Payment & Pricing"
      },
      {
        type: 'bullets',
        items: [
          "Lunch dishes typically range from ¥2,000 to ¥4,000 per person",
          "Each person pays for what they order individually",
          "Drinks  ordered and paid separately",
          "Optional social coffee afterward—pay for your own coffee/tea at the café",
          "Credit cards accepted (cashless preferred)"
        ]
      },
      {
        type: 'header',
        content: "Perfect For Those Who"
      },
      {
        type: 'bullets',
        items: [
          "Love seafood and aren't afraid of bold, citrus-forward flavors",
          "Appreciate cuisines that tell cultural stories through ingredients",
          "Want to understand why Peru has become a global culinary powerhouse",
          "Seek lunch experiences that feel like discoveries, not just meals",
          "Are curious about Nikkei cuisine and Japanese-Peruvian fusion",
          "Believe the best way to travel is through authentic food",
          "Appreciate when good meals naturally transition into good conversations over coffee"
        ]
      },
      {
        type: 'header',
        content: "Location & Getting There"
      },
      {
        type: 'paragraph',
        content: "Our selected venue is located in the modern area of Tokyo. The neighborhood represents new Tokyo: contemporary architecture, international influences, and a cosmopolitan energy that makes it perfect for exploring global cuisines. For those joining the optional social coffee, the area offers excellent cafés within walking distance."
      },
      {
        type: 'header',
        content: "What to Bring"
      },
      {
        type: 'paragraph',
        content: "Bring an appetite for adventure and bold flavors. Come ready to experience cuisine that's both ancient and contemporary, familiar and surprising. This is a small group experience where the food provides talking points and shared discovery creates connection. If you're open to staying for optional coffee afterward, keep your afternoon schedule flexible. Casual but neat attire recommended—the space is relaxed yet refined."
      },
      {
        type: 'header',
        content: "A Note on Spice"
      },
      {
        type: 'paragraph',
        content: "Peruvian cuisine uses chili peppers (ají) extensively, but it's about flavor complexity, not just heat. The spice builds and enhances rather than overwhelms. If you have concerns about spice levels, simply let us know when you RSVP—the beauty of Peruvian food is its adaptability while maintaining authentic flavor profiles."
      }
    ]
  },
  
  whatToExpect: [
    "Intimate Peruvian lunch with a small group of 6 people",
    "Authentic coastal cuisine: ceviche, anticuchos, causa, and more",
    "Bold, fresh flavors featuring citrus, ají peppers, and pristine seafood",
    "Modern setting with contemporary Latin American-Japanese aesthetic",
    "Shared plates encouraging conversation and culinary exploration",
    "2-hour midday experience from 1:00 PM to 3:00 PM",
    "Optional social coffee afterward for those wanting to continue the connection",
    "Cultural insight into Peru's culinary heritage and Nikkei fusion"
  ],
  
  venue: { 
    name: "Peruvian Restaurant, Tokyo",
    description: "A contemporary Peruvian restaurant in Tokyo's modern district, specializing in authentic coastal cuisine with Japanese precision. Fresh ceviche, grilled anticuchos, and the vibrant flavors that have made Peru a global culinary destination. Optional social coffee at nearby café afterward.",
    fullAddress: "Tokyo (exact venue name and address revealed to confirmed attendees 24 hours prior)",
    priceRange: "¥3,000-5,000 per person (drinks separate)",
    amenities: ["Authentic Peruvian cuisine", "Contemporary setting", "Nikkei fusion options", "Fresh seafood specialties", "Easy station access", "Credit cards accepted", "Nearby cafés for optional social coffee"]
  },
  
  memberBenefits: [
    {
      icon: "🐟",
      title: "Pristine Ceviche",
      description: "Fresh Pacific seafood kissed by lime, ají, and Peruvian tradition"
    },
    {
      icon: "🔥", 
      title: "Grilled Perfection",
      description: "Anticuchos with that signature char and tender interior"
    },
    {
      icon: "🌎",
      title: "Cultural Journey", 
      description: "Lima's coastal energy meets Tokyo's precision and care"
    },
    {
      icon: "☕",
      title: "Extended Connection",
      description: "Optional social coffee for those who want the conversation to continue"
    }
  ],
  
  relatedEvents: ['thai-skyline-dinner-february-2026', 'french-vietnamese-lunch-january-2026', 'community-brunch-january-2026', 'curated-spanish-tapas-december-2025'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "monthly",
  dayOfWeek: "Friday", 
  focusArea: "Authentic Peruvian coastal cuisine with Nikkei influences",
  experienceType: "casual fine dining lunch with optional social coffee",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark & ConnectCo",
    bio: "Curating intimate culinary experiences where global cuisines, authentic flavors, and genuine connection create memorable shared moments",
    avatar: "mark-foodie-hosts.jpg"
  },

  // Special features for Peruvian lunch
  culinaryFocus: {
    style: "Authentic Peruvian coastal cuisine with Nikkei influences",
    groupSize: "Intimate gathering of 6 for quality conversation and shared discovery",
    duration: "2 hours of leisurely lunch + optional social coffee",
    expertise: "Bold Pacific flavors meeting Tokyo precision and presentation"
  },
  
  includedExperience: {
    atmosphere: "Contemporary setting with Latin American warmth",
    connection: "Shared plates and culinary exploration as conversation catalysts", 
    quality: "Pristine ingredients, authentic preparation, meticulous presentation",
    pace: "Relaxed lunch allowing you to savor flavors, stories, and company—with optional coffee extension"
  },
  
  foodiePhilosophy: "The best meals transport you—where authentic flavors tell cultural stories, and sharing them with the right people turns lunch into a journey. And sometimes, the best part is when no one wants it to end.",
  
  idealFor: [
    "Seafood lovers ready for citrus-bright, boldly seasoned dishes",
    "Anyone curious about why Peru has become a culinary superpower",
    "People who appreciate cuisines shaped by geography and cultural fusion",
    "Those seeking lunch that feels like an adventure, not just a meal",
    "Nikkei cuisine enthusiasts wanting authentic Peruvian-Japanese connections",
    "Travelers at heart who explore the world through food",
    "Anyone who loves when good meals naturally become good conversations",
    "Community members looking for midday culinary discovery with great people"
  ],
  
  preparationTips: [
    "Arrive by 1:15 PM (15 minutes early) for a 1:30 PM start—punctuality ensures we begin together",
    "Share any dietary restrictions or seafood allergies when you RSVP—critical for ceviche-focused menus",
    "Keep your afternoon schedule flexible if you're interested in optional social coffee",
    "Casual but neat attire works perfectly for this relaxed yet refined space",
    "Come with an adventurous palate—Peruvian cuisine rewards curiosity",
    "Consider trying a Pisco Sour, Peru's national cocktail (optional, of course)",
    "Note: Credit cards accepted (cashless preferred)"
  ],
  
  uniqueAspects: [
    "Authentic Peruvian coastal cuisine in Tokyo's modern district",
    "Classic ceviche showcasing the purity of lime, fish, and ají peppers",
    "Nikkei fusion reflecting Peru's Japanese heritage",
    "Small intimate group of 6 for meaningful culinary exploration",
    "Dishes that tell Peru's story—Pacific waters, Andean mountains, cultural fusion",
    "Contemporary setting honoring both Latin American and Japanese aesthetics",
    "Optional social coffee extension for organic connection building",
    "Lunch that doubles as cultural education and genuine connection",
    "The rare chance to understand why Lima has become a global food capital"
  ],
  
  culinaryHighlights: [
    "Traditional ceviche with corvina or similar white fish",
    "Anticuchos—grilled skewers with Peruvian spices",
    "Causa—layered potato terrine with creative fillings",
    "Lomo saltado or other stir-fried specialties (Chifa influence)",
    "Ají amarillo and rocoto peppers adding signature Peruvian heat",
    "Purple corn chicha morada or other traditional beverages",
    "Nikkei-style tiradito (Peruvian-Japanese sashimi fusion)"
  ]
},

 
{
  id: 'thai-skyline-dinner-february-2026',
  eventType: 'foodie',
  iconType: 'image',
  iconImage: '/images/events/ThaiSkylineDinner.jpg',
  iconValue: '🌃',
  title: "Modern Thai Skyline Dinner (Tokyo)",
  description: "Authentic Thai flavors meet breathtaking Tokyo views. An intimate dinner experience perched high above the city, where modern Thai cuisine comes alive with the four essential flavors—spicy, sweet, sour, salty—while Tokyo's lights stretch endlessly below.",
  waitlist: "0",
  next: "February 21, 2025",
  time: "6:00 PM - 8:00 PM",
  eventDateTime: "2026-02-21T09:00:00Z",   

  location: "TBD - Restaurant, Tokyo",
  color: "from-orange-500 to-purple-600",
  capacity: 8,
  difficulty: "Elevated Casual Dining",
  tags: ["Thai Cuisine", "Skyline Views", "Modern Thai", "Dinner", "Tokyo"],
  featured: true,
  slug: 'thai-skyline-dinner-february-2026',
  
  // City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan", 
  timezone: "Asia/Tokyo",
  currency: "JPY",
  cost: "¥3,000-6,000 per person (drinks separate)",

  // Payment configuration properties
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },

  philosophy: {
    title: "Modern Thai Skyline Dinner: Where Authentic Flavors Meet Breathtaking Heights",
    sections: [
      {
        type: 'header',
        content: "For a seamless registration experience, please review the information below and visit the Details tab before reserving your spot."
      },
      {
        type: 'paragraph',
        content: "Join us for an intimate dinner that elevates Thai cuisine—literally and figuratively. Thirty-nine floors above Tokyo, where floor-to-ceiling windows frame the city's endless lights, we'll gather for modern Thai cuisine that honors the four essential flavors: spicy, sweet, sour, and salty. This is authentic Thai food with Australian sophistication, served in a setting that makes you forget you're still in the city."
      },
      {
        type: 'paragraph',
        content: "Our selected venue delivers the rare combination Tokyo dining dreams are made of—exceptional food and views that take your breath away before you've even tasted the first bite. As night falls over Tokyo, watch the skyline transform into a sea of lights while you experience Thai cuisine that respects tradition while embracing modern presentation and technique."
      },
      {
        type: 'header',
        content: "What Makes This Experience Special"
      },
      {
        type: 'paragraph',
        content: "This isn't your neighborhood Thai restaurant. Born in Sydney and perfected in Tokyo, the cuisine balances authentic Thai flavors with modern sensibilities—aromatic herbs, bold spices, vibrant colors, and the kind of presentation that makes you pause before diving in. Every dish showcases the essential Thai philosophy: the harmony of spicy, sweet, sour, and salty flavors working together."
      },
      {
        type: 'paragraph',
        content: "But the food is only half the story. The panoramic views turn dinner into theater—Tokyo Tower glowing in the distance, the city grid spreading out like constellations, and if you're lucky, the subtle glow of Rainbow Bridge on the horizon. It's the kind of view that makes conversation pause, that reminds you why Tokyo feels like nowhere else on earth."
      },
      {
        type: 'header',
        content: "Important Timing"
      },
      {
        type: 'paragraph',
        content: "⏰ IMPORTANT: Please arrive by 5:45 PM (15 minutes before 6:00 PM). This ensures everyone is seated with optimal views so we can begin our evening together at 6:00 PM sharp. Late arrivals miss the initial wow moment when you first see the skyline spread out before you."
      },
      {
        type: 'header',
        content: "The Culinary Journey"
      },
      {
        type: 'paragraph',
        content: "Expect modern Thai at its finest—vibrant spring rolls that showcase pristine ingredients, green curry that reveals layer after layer of complexity, the restaurant's signature 'eggnet' salad where a delicate egg lace covers pork, prawns, and fresh herbs. You'll encounter dishes like the 'Crying Tiger' steak (named because the spices are so bold they'd make even a tiger weep), tom yum soup that awakens every sense, and pad thai that honors tradition while elevating technique."
      },
      {
        type: 'paragraph',
        content: "Every dish arrives beautifully plated, aromatic with fresh herbs and spices, and designed to be shared. The flavors are bold and authentic—this is Thai food that doesn't compromise, served in a space that celebrates both the cuisine and the spectacular setting. The dark wood interior and warm lighting create intimacy, while those windows constantly remind you of the extraordinary view."
      },
      {
        type: 'header',
        content: "The View Experience"
      },
      {
        type: 'paragraph',
        content: "Let's be clear: the panoramic Tokyo skyline view is not background ambiance—it's an integral part of the experience. From the above floors up, you'll see Tokyo in a way most visitors never do. The sprawling cityscape, iconic landmarks punctuating the horizon, and the way the city's lights create patterns that stretch to the edges of sight. As dinner progresses and night deepens, the view transforms, revealing new layers of Tokyo's beauty."
      },
      {
        type: 'paragraph',
        content: "Window seats at this elevation are coveted for good reason. We've secured a table where everyone can appreciate the view, turning dinner into an experience that engages all your senses—the visual drama of the skyline, the aromatic complexity of Thai spices, the bold flavors, and the warmth of good company sharing it all."
      },
      {
        type: 'header',
        content: "Payment & Pricing"
      },
      {
        type: 'bullets',
        items: [
          "Dinner dishes typically range from ¥3,000 to ¥6,000 per person",
          "Each person pays for what they order",
          "Drinks and cocktails ordered and paid separately",
          "Cashless payment only (credit cards accepted, no cash)"
        ]
      },
      {
        type: 'header',
        content: "Perfect For Those Who"
      },
      {
        type: 'bullets',
        items: [
          "Appreciate authentic Thai cuisine elevated through modern technique",
          "Believe a spectacular view enhances an already great meal",
          "Love bold, aromatic flavors that make you reach for your water (and smile)",
          "Seek dining experiences that feel like occasions, not just meals",
          "Want to see Tokyo from a perspective most never experience",
          "Are looking for that rare combination: excellent food and breathtaking setting",
          "Believe the best dinners are shared with interesting people"
        ]
      },
    
      {
        type: 'header',
        content: "Location & Getting There"
      },
      {
        type: 'paragraph',
        content: "Our selected venue is located in central Tokyo with convenient train access. Perched on the top floor, the restaurant offers floor-to-ceiling windows with panoramic views of Tokyo's skyline—a perspective that makes you feel both part of the city and wonderfully removed from it. The venue name and exact address will be shared with confirmed attendees 24 hours before the event."
      },
      {
        type: 'header',
        content: "What to Bring"
      },
      {
        type: 'paragraph',
        content: "Bring an appetite for bold flavors and breathtaking views. Come ready to experience Thai cuisine that doesn't hold back, in a setting that elevates the entire evening. This is a small group experience where the food provides talking points and the views create shared moments of wonder. Smart casual dress recommended—the space is sophisticated but welcoming."
      }
    ]
  },
  
  whatToExpect: [
    "Intimate Thai dinner with panoramic Tokyo skyline views",
    "Modern Thai cuisine balancing authentic flavors with contemporary presentation",
    "Signature dishes: green curry, 'Crying Tiger' steak, eggnet salad, spring rolls",
    "Small group dining (max 8 people) for meaningful conversation",
    "Floor-to-ceiling windows showcasing Tokyo's nighttime beauty",
    "Bold, aromatic Thai flavors—spicy, sweet, sour, salty in harmony",
    "2-hour evening experience from 6:00 PM to 8:00 PM"
  ],
  
  venue: { 
    name: "TBD - Restaurant, Tokyo",
    description: "A modern Thai restaurant featuring floor-to-ceiling windows with breathtaking panoramic views of Tokyo's skyline. Authentic Thai cuisine with Australian sophistication in a chic, sophisticated setting.",
    fullAddress: "TBD - Tokyo,  (venue name and exact location revealed to confirmed attendees 24 hours prior)",
    priceRange: "¥3,000-6,000 per person (drinks separate)",
    amenities: ["Modern Thai cuisine", "panoramic views", "Floor-to-ceiling windows", "Sophisticated atmosphere", "Easy train access", "Cashless only (credit cards)"]
  },
  
  memberBenefits: [
    {
      icon: "🌃",
      title: "Skyline Spectacular",
      description: "Panoramic views that make Tokyo look like a sea of stars"
    },
    {
      icon: "🌶️", 
      title: "Authentic Thai Fire",
      description: "Bold flavors honoring the essential spicy, sweet, sour, salty balance"
    },
    {
      icon: "✨",
      title: "Modern Presentation", 
      description: "Australian sophistication elevating traditional Thai cuisine"
    },
    {
      icon: "👥",
      title: "Intimate Connection",
      description: "Small group ensures both the food and the views are shared experiences"
    }
  ],
  
  relatedEvents: ['french-vietnamese-lunch-january-2026', 'community-brunch-january-2026', 'curated-spanish-tapas-december-2025', 'fika'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "monthly",
  dayOfWeek: "Friday", 
  focusArea: "Modern Thai cuisine with skyline views",
  experienceType: "elevated casual fine dining",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark & ConnectCo",
    bio: "Curating intimate culinary experiences where exceptional food, breathtaking settings, and genuine connection come together",
    avatar: "mark-foodie-hosts.jpg"
  },

  // Special features for Thai skyline dinner  
  culinaryFocus: {
    style: "Modern Thai with Australian sophistication",
    groupSize: "Intimate gathering of 8 for quality conversation",
    duration: "2 hours of elevated dining with panoramic views",
    expertise: "Authentic Thai flavors from Sydney-born restaurant"
  },
  
  includedExperience: {
    atmosphere: "Sophisticated high-rise setting with breathtaking city views",
    connection: "Shared table experience with views and flavors as conversation starters", 
    quality: "Bold, authentic Thai cuisine with modern presentation",
    pace: "Leisurely dinner allowing you to savor food, views, and company"
  },
  
  foodiePhilosophy: "The best dining experiences engage all your senses—where bold flavors meet breathtaking views, and both are elevated by sharing them with the right people",
  
  idealFor: [
    "Thai food lovers seeking authentic flavors with modern refinement",
    "Anyone who believes a spectacular view enhances great food",
    "People who appreciate bold, aromatic cuisine that awakens the palate",
    "Those seeking dining experiences that feel genuinely special",
    "Anyone wanting to see Tokyo from a perspective most never experience",
    "Community members looking for dinner that transcends the ordinary",
    "Food enthusiasts ready for flavors that don't hold back"
  ],
  
  preparationTips: [
    "Arrive by 6:45 PM (15 minutes early) for a 6:00 PM start to catch the initial skyline wow moment",
    "Share any dietary restrictions when you RSVP—essential for Thai cuisine planning",
    "Smart casual dress recommended—sophisticated space but welcoming atmosphere",
    "Bring an appetite for bold flavors—this is authentic Thai that doesn't compromise",
    "Come ready for both culinary adventure and genuine conversation with fellow food enthusiasts",
    "Note: Restaurant is cashless (credit cards only)"
  ],
  
  uniqueAspects: [
    "Breathtaking panoramic views of Tokyo's skyline",
    "Modern Thai cuisine from acclaimed Sydney-born restaurant",
    "Floor-to-ceiling windows making the city view part of the dining experience",
    "Authentic Thai flavors—spicy, sweet, sour, salty—with modern presentation",
    "Small intimate group ensuring both food and views are shared experiences",
    "Signature dishes like 'Crying Tiger' steak and delicate eggnet salad",
    "The rare combination: exceptional food and setting that both exceed expectations",
    "Dining where Tokyo's beauty from above matches the bold flavors on your plate"
  ]
},

  {
    id: 'shoot-the-breeze-february-2026', 
    eventType: 'happy-hour',
    iconType: 'image',
    iconImage: '/images/events/GinzaHappyHour.jpg',
    iconValue: '🥂',
    title: "Shoot the Breeze Happy Hour Saturday: Tokyo Connections ",
    description: "Casual Saturday happy hour where authentic connections happen over drinks on hidden gem location. No agenda, no awkward networking—just good people and great conversations.",
    waitlist: "0",
    next: "February 7, 2026",
    time: "6:00 PM - 8:30 PM",
    eventDateTime: "2026-02-7T09:00:00Z",  // ✅ 6:00 PM JST = 9:00 AM UTC

    location: "TBD",
    color: "from-amber-500 to-orange-500",
    capacity: 25,
    difficulty: "Casual Social",
    tags: ["Happy Hour",  "Connection", "Casual", "Saturday", "Shoot the Breeze"],
    featured: true,
    slug: 'shoot-the-breeze-february-2026',
  
    // Payment configuration
    cost: "FREE (pay for your own drinks)",
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 0,
    currency: "JPY",
    cost: "¥2,500",

    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: false,
      allowWaitlist: false
    },
  
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
  
    philosophy: {
      title: "Shoot the Breeze Happy Hour Saturday: Tokyo Connections",
      sections: [
        {
          type: 'paragraph',
          content: "Join us for ConnectCo.'s \"Shoot the Breeze\" Happy Hour Saturday! We're gathering  for 2 hours of relaxed conversation, new connections, and Tokyo stories."
        },
        {
          type: 'paragraph',
          content: "No agenda, no awkward networking games—just good people, great drinks, and the kind of conversations that make Tokyo feel like home. Whether you're new to the city, a seasoned expat, digital nomad, or a local looking to expand your international circle, you'll find friendly faces ready to chat about everything or nothing at all."
        },
        {
          type: 'header',
          content: "🥂 What is Shoot the Breeze?"
        },
        {
          type: 'paragraph',
          content: "Shooting the breeze means having relaxed, casual conversations without any particular agenda—just enjoying the moment and the company. It's about those organic chats where friendships naturally form over shared stories and laughter. Our Shoot the Breeze Happy Hours embrace this spirit, creating a space where Tokyo residents can unwind, connect authentically, and build genuine friendships in a sophisticated yet unpretentious setting."
        },
       {
          type: 'header',
          content: "Cost & Drinks"
        },
        {
          type: 'paragraph',
          content: "Pay for your own drinks (one drink minimum, ¥1,000-3,000 range, sodas, mocktails & cocktails available)"
        },
        {
          type: 'header',
          content: "Why Join Us?"
        },
        {
          type: 'paragraph',
          content: "This isn't your typical forced networking event. It's for people who value authentic connections, enjoy a good laugh, and appreciate a perfectly mixed drink in a beautiful setting. The intimate group size means you'll actually remember the people you meet and the conversations you have."
        },
        {
          type: 'paragraph',
          content: "Bring your stories, your questions about Tokyo life, or just your willingness to go with the flow. Sometimes the most meaningful connections happen when we're simply shooting the breeze."
        },
        {
          type: 'header',
          content: "What to Expect"
        },
        {
          type: 'bullets',
          items: [
            "6:00-6:15 PM: Arrive, grab a drink, meet your first new friend",
            "6:15-8:00 PM: Organic mingling and conversation on the terrace",
            "8:00-8:30 PM: Wind down with your favorite new connections"
          ]
        },
        {
          type: 'paragraph',
          content: "You'll leave with genuine connections, interesting stories, and probably plans for future meetups."
        },
        {
          type: 'header',
          content: "The Vibe"
        },
        {
          type: 'bullets',
          items: [
            "Sophisticated yet utterly unpretentious—just like the best friendships",
            "Beautiful Tokyo setting (weather permitting)",
            "Intimate group of 25 max—you'll actually remember everyone",
            "Mix of newcomers and regulars, expats and locals",
            "English-friendly environment with international crowd"
          ]
        },
        {
          type: 'header',
          content: "Important Attendance Policy"
        },
        {
          type: 'paragraph',
          content: "Members who RSVP 'yes' but fail to attend the event without canceling their RSVP beforehand will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
        }
      ]
    },
    
    whatToExpected: [
      "Relaxed atmosphere in the heart of Tokyo",
      "Organic conversations with 20-25 like-minded people",
      "Mix of expats, locals, and international Tokyo residents",
      "Authentic connections over drinks—no forced networking"
    ],
    
    venue: { 
      name: "TBD ",
      description: "Beautiful  location in Tokyo perfect for casual happy hour mingling. Exact venue details will be shared with confirmed attendees closer to the event date.",
      fullAddress: "Tokyo (Exact address TBD - will be shared with attendees)",
      priceRange: "FREE entry - drinks ¥1,000-3,000 range (pay for your own)",
      amenities: ["Indoor", "Central Tokyo location", "Full bar service", "Indoor/outdoor seating"]
    },
    
    memberBenefits: [
      {
        icon: "🥂",
        title: "Pay-As-You-Go",
        description: "FREE event entry - just order your own drinks (¥1,000-3,000 range)"
      },
      {
        icon: "🏙️", 
        title: "Premium Tokyo Location",
        description: "Beautiful  setting in Tokyo's most sophisticated neighborhood"
      },
    
      {
        icon: "🤝",
        title: "Intimate Group Size",
        description: "Just 25 people max—small enough for genuine connections"
      }
    ],
    
    relatedEvents: ['fika', 'weekly-fika', 'discussions', 'happy-hour'],
    status: "open",
    
    // Additional metadata
    eventFrequency: "occasional",
    dayOfWeek: "Saturday", 
    neighborhood: "Tokyo",
    pricePoint: "free-entry",
    
    socialProof: {
      totalAttended: 0,
      averageRating: 0,
      repeatAttendees: "0%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Creating relaxed and authentic connection experiences across Tokyo's best neighborhoods",
      avatar: "mark-jason-host.jpg"
    },
  
    drinkSpecials: {
      packagePrice: "FREE entry",
      paymentMethod: "Pay for your own drinks at the venue",
      drinkRange: "¥1,000-3,000",
      options: ["Cocktails", "Mocktails", "Sodas", "Wine", "Beer"],
      minimum: "One drink minimum per person"
    },
    
  
    
    attendancePolicy: "Members who RSVP 'yes' but fail to attend the event without canceling their RSVP beforehand will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system.",
    
    networkingStyle: "Organic, casual mingling—no structured activities or forced networking. Just good conversation flowing naturally over drinks on a beautiful terrace.",
    
    idealFor: [
      "New Tokyo residents wanting to build their social circle", 
      "Seasoned expats looking for genuine connections", 
      "Digital nomads seeking local community", 
      "Anyone tired of awkward networking events", 
      "People who believe the best friendships start with casual conversation"
    ],
  
    atmosphereKeywords: ["casual", "relaxed", "sophisticated", "unpretentious", "organic", "authentic", "intimate", "rooftop", "terrace", "Ginza"]
  },

  {
  id: 'authentic-swedish-fika-march-sunday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo's Beloved Sunday Tradition (March)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "March 8, 2026",
  time: "11:00 AM - 1:00 PM",
  eventDateTime: "2026-03-08T02:00:00Z",

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 25,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee", "Riverside", "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-march-sunday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

    // Payment configuration properties
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us for our monthly Fika of 2026  with our beloved Sunday tradition! This month, we're gathering at a beautiful third wave coffee shop to celebrate fresh beginnings, meaningful connections, and the art of slowing down together. This wonderful venue represents the perfect setting for our community to start the year right—coming together over exceptional coffee and delicious pastries to create spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "This charming third wave coffee shop sits like a hidden gem within a waterside community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
      {
        type: 'paragraph',
        content: "Riverside Tranquility: Enjoy our fika in a setting that embodies the love for nature and peaceful moments. With sweeping views of flowing water and verdant parkland, this location offers the perfect backdrop for the contemplative, restorative experience that defines true fika."
      },
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. In this riverside sanctuary, surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
          "Riverside Reflection: Take advantage of the peaceful waterside setting to practice mindfulness and finding perfect balance and contentment in the moment"
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene riverside, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting", "Riverside views", "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
    {
      icon: "🌊",
      title: "Riverside Sanctuary",
      description: "Serene waterside setting with panoramic views creating perfect backdrop for contemplative fika"
    }
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},


{
  id: 'authentic-swedish-fika-february-sunday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo's Beloved Sunday Tradition (Feb)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "February 8, 2026",
  time: "10:00 AM - 1:00 PM",
  eventDateTime: "2026-02-08T01:00:00Z",

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 25,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee", "Riverside", "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-february-sunday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

    // Payment configuration properties
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us for our monthly Fika of 2026  with our beloved Sunday tradition! This month, we're gathering at a beautiful third wave coffee shop to celebrate fresh beginnings, meaningful connections, and the art of slowing down together. This wonderful venue represents the perfect setting for our community to start the year right—coming together over exceptional coffee and delicious pastries to create spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "This charming third wave coffee shop sits like a hidden gem within a waterside community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
      {
        type: 'paragraph',
        content: "Riverside Tranquility: Enjoy our fika in a setting that embodies the love for nature and peaceful moments. With sweeping views of flowing water and verdant parkland, this location offers the perfect backdrop for the contemplative, restorative experience that defines true fika."
      },
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. In this riverside sanctuary, surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
          "Riverside Reflection: Take advantage of the peaceful waterside setting to practice mindfulness and finding perfect balance and contentment in the moment"
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene riverside, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting", "Riverside views", "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
    {
      icon: "🌊",
      title: "Riverside Sanctuary",
      description: "Serene waterside setting with panoramic views creating perfect backdrop for contemplative fika"
    }
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},



{
  id: 'authentic-swedish-fika-january-sunday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo's Beloved Sunday Tradition (Jan)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "January 25, 2026",
  time: "10:00 AM - 1:00 PM",
  eventDateTime: "2026-01-25T01:00:00Z",  // ✅ 10:00 AM JST = 1:00 AM UTC

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 25,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee", "Riverside", "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-january-sunday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",
  cost: "¥2,500",

    // Payment configuration properties
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us for our very first monthly Fika of 2026 as we kick off the new year with our beloved Sunday tradition! This month, we're gathering at a beautiful third wave coffee shop to celebrate fresh beginnings, meaningful connections, and the art of slowing down together. This wonderful venue represents the perfect setting for our community to start the year right—coming together over exceptional coffee and delicious pastries to create spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "This charming third wave coffee shop sits like a hidden gem within a waterside community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
      {
        type: 'paragraph',
        content: "Riverside Tranquility: Enjoy our fika in a setting that embodies the love for nature and peaceful moments. With sweeping views of flowing water and verdant parkland, this location offers the perfect backdrop for the contemplative, restorative experience that defines true fika."
      },
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. In this riverside sanctuary, surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
          "Riverside Reflection: Take advantage of the peaceful waterside setting to practice mindfulness and finding perfect balance and contentment in the moment"
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD",
    description: "A beautiful third wave coffee venue nestled along the cityscape, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting", "Riverside views", "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
    {
      icon: "🌊",
      title: "Riverside Sanctuary",
      description: "Serene waterside setting with panoramic views creating perfect backdrop for contemplative fika"
    }
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},


{
  id: 'underground-jazz-march-2026',
  eventType: 'happy-hour',
  iconType: 'image',
  iconImage: '/images/events/JazzNight.jpg',
  iconValue: '🎷',
  title: "No Smokin', Drinkin', Never Thinkin': Underground Jazz Session (Tokyo)",
  description: "Step into Tokyo's legendary basement jazz scene for an authentic jam session experience. Limited to 6 people for an intimate evening where professional and amateur musicians gather to improvise standards in one of Tokyo's most cherished underground jazz spots since 1988.",
  waitlist: "0",
  next: "March 7, 2025",
  time: "6:00 PM - 8:30 PM",
  eventDateTime: "2026-01-31T09:00:00Z",

  location: "TBD - Underground Jazz Club",
  color: "from-amber-500 to-purple-600",
  capacity: 6,
  difficulty: "Jazz Appreciation",
  tags: ["Live Jazz", "Jam Session", "Underground Music", "Authentic Tokyo", "Intimate Setting", "Jazz Culture"],
  featured: true,
  slug: 'underground-jazz-march-2026',
  
  // City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan", 
  timezone: "Asia/Tokyo",
  currency: "JPY",
   cost: "¥2,000",

  // Payment configuration properties
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },

  philosophy: {
    title: "Underground Jazz: Where Tokyo's Jazz Soul Lives",
    sections: [
      {
        type: 'header',
        content: "For a seamless registration experience, please review the information below and visit the Details tab before reserving your spot."
      },
      {
        type: 'paragraph',
        content: "Descend into the basement heart of Tokyo's jazz scene, where Drinkin', Never Thinkin'' isn't just a motto—it's a way of life. This isn't a polished concert hall or a trendy listening bar. This is where real jazz happens: cramped, soulful, and utterly authentic. Since 1988, this legendary underground club has been the woodshedding ground for generations of Japanese jazz musicians, a place where students and seasoned professionals alike gather to jam through standards until the early morning hours."
      },
      {
        type: 'paragraph',
        content: "The walls are lined with thousands of vinyl records. The space is so intimate you can see every expression on the musicians' faces. The bartender-owner alternates between pouring drinks and jumping on stage to rip saxophone solos. This is the quintessential Tokyo jazz experience—raw, real, and impossibly atmospheric. Musicians from around the world ask the same question when they arrive in Tokyo: 'Where can I go for a jazz jam tonight?' The answer, for over three decades, has been the same underground basement in Takadanobaba."
      },
      {
        type: 'header',
        content: "What Makes This Evening Special"
      },
      {
        type: 'paragraph',
        content: "Legendary Jazz Workshop: Experience the venue where greats like Herbie Hancock and Larry Coryell have dropped in for impromptu sessions—and where tomorrow's jazz stars are honing their craft tonight."
      },
      {
        type: 'paragraph',
        content: "Ultra-Intimate Access: Limited to just 6 people (versus the usual packed basement), giving you breathing room and unobstructed views in a space that's normally shoulder-to-shoulder with jazz devotees."
      },
      {
        type: 'paragraph',
        content: "Live Jam Sessions: Not a rehearsed performance—authentic spontaneous jazz where musicians communicate freely, riffing off each other's energy and running through standards with the kind of raw creativity that only happens in the moment."
      },
      {
        type: 'paragraph',
        content: "Transparent Pricing: ¥2,000 cover charge includes your first drink. Additional drinks are just ¥500 each—craft cocktails and classics that complement the live music without breaking the bank."
      },
      {
        type: 'header',
        content: "Your Jazz Journey"
      },
      {
        type: 'bullets',
        items: [
          "Basement Atmosphere: Narrow, cramped by design, with vintage decor and warm lighting that creates the perfect jazz club ambiance—exactly what an underground jazz spot should feel like",
          "Rotating Musicians: Watch as different jazz players arrive throughout the evening to sit in—the band lineup shifts organically, creating unique moments you'll never experience twice",
          "Bartender-Musician Magic: Witness the owner's legendary moves: taking a saxophone solo, then running behind the bar to serve drinks while the band vamps, then jumping back into the music",
          "Vinyl Selection: Request songs from the massive collection of records lining the walls—if you're not feeling the jam, the owner will spin your selection on the vintage turntable",
          "Jazz Standards: Experience musicians running through the canon of jazz—from bebop to hard bop, standards that have been played in basement clubs since the 1950s",
          "International Jazz Community: Encounter the full spectrum—Japanese students learning their craft, Tokyo professionals unwinding after gigs, touring musicians from around the world sitting in for the pure love of improvisation"
        ]
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "¥2,000 cover charge includes your first drink (already included in your registration)",
          "Additional drinks at ¥500 each—craft cocktails and classic jazz bar favorites",
          "Intimate 6-person group ensuring you actually have space in this famously tight venue",
          "Authentic jazz club experience",
          "No food service, but unlimited jazz until late evening",
          "Casual 'drop in and play' jam sessions rather than polished performances",
          "Real musicians of all abilities—from university students to touring professionals",
          "Underground basement location just 2 minutes from Takadanobaba Station"
        ]
      },
      {
        type: 'header',
        content: "Perfect For Jazz Lovers Who"
      },
      {
        type: 'bullets',
        items: [
          "Want to experience authentic Tokyo jazz culture, not a tourist-friendly version",
          "Appreciate the raw energy of live improvisation over polished performances",
          "Love intimate spaces where you're part of the music, not just watching from a distance",
          "Are drawn to underground culture and the kind of basement clubs that feel like hidden treasures",
          "Don't mind tight quarters, smoke, and the beautiful chaos of a real jam session",
          "Want to witness the 'jazz workshop' vibe where musicians communicate freely and learn from each other",
          "Seek experiences locals cherish—this is where Tokyo's jazz musicians come to play",
          "Understand that the best jazz moments happen spontaneously, not on a setlist"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this exclusive 6-person gathering in one of Tokyo's most legendary underground jazz clubs. This isn't a tourist attraction—it's the real deal, the kind of jazz experience that has remained unchanged since 1988 because it doesn't need to change. Just good music, strong drinks, and the soul of Tokyo's jazz scene compressed into one basement."
      },
      {
        type: 'header',
        content: "Important Cancellation Policy"
      },
      {
        type: 'paragraph',
        content: "Due to the extremely limited capacity (6 people max) and the intimate nature of this experience, please cancel at least 72 hours before the event if you cannot attend. This ensures fairness to jazz enthusiasts on our waitlist."
      },
      {
        type: 'header',
        content: "Important Details"
      },
      {
        type: 'paragraph',
        content: "Cover charge is ¥2,000, which includes your first drink. Additional drinks are ¥500 each. No food service available—this is a pure jazz experience."
      },
      {
        type: 'header',
        content: "Location & Atmosphere"
      },
      {
        type: 'paragraph',
        content: "Our selected venue is a legendary basement jazz club in Takadanobaba, just 2 minutes from the station on busy Waseda-dori. Narrow and cramped even by Tokyo standards, the space is lined with thousands of vinyl records and features vintage decor that hasn't changed in decades. Musicians gather here 3-4 nights per week for spontaneous jam sessions that often run until 5 AM on weekends. The bartender-owner, Inoue-san, plays alto saxophone between serving drinks. Exact location will be shared with confirmed attendees 48 hours before the event."
      }
    ]
  },
  
  whatToExpect: [
    "Live jazz jam sessions with rotating musicians throughout the evening",
    "¥2,000 cover charge includes first drink (additional drinks ¥500 each)",
    "Ultra-intimate 6-person maximum group in a famously tight space",
    "No food service—pure jazz and drinks experience",
    "Casual drop-in jam format with musicians of all levels",
    "Vintage vinyl collection available on request between sets",
    "Located 2 minutes from Takadanobaba Station"
  ],
  
  venue: { 
    name: "TBD - Underground Jazz Club",
    description: "Legendary basement jazz club established 1988, featuring live jam sessions 3-4 nights per week. Intimate space lined with vinyl records where professional and amateur musicians gather to improvise. Known for  Drinkin', Never Thinkin'' motto and authentic jazz workshop atmosphere. Bartender-owner Inoue-san alternates between pouring drinks and playing alto saxophone.",
    fullAddress: "TBD - Shinjuku City, Tokyo (exact location revealed to confirmed attendees 24 hours prior)",
    priceRange: "¥2,000 cover (includes first drink), ¥500 per additional drink",
    amenities: ["Live jam sessions", "Massive vinyl collection", "Craft cocktails", "Intimate basement setting",  "Open late", "2 min from station"]
  },
  
  memberBenefits: [
    {
      icon: "🎷",
      title: "Authentic Jazz Culture",
      description: "Experience the real Tokyo jazz scene where musicians have gathered since 1988"
    },
    {
      icon: "👥", 
      title: "Ultra-Intimate Access",
      description: "Limited to 6 people instead of the usual packed basement—breathe and actually see the musicians"
    },
    {
      icon: "🎵",
      title: "Live Improvisation", 
      description: "Witness spontaneous jam sessions where professional and amateur musicians create magic in real-time"
    },
    {
      icon: "🏆",
      title: "Legendary Venue",
      description: "Join the tradition of musicians who ask 'Where can I jam in Tokyo?'—this is where they go"
    }
  ],
  
  relatedEvents: ['natural-wine-journey-december-2025', 'weekly-fika', 'happy-hour'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special",
  dayOfWeek: "Friday", 
  focusArea: "Underground Tokyo jazz culture and live improvisation",
  experienceType: "intimate jazz jam session",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark & Tokyo Jazz Scene",
    bio: "Passionate about connecting people through Tokyo's authentic underground jazz culture",
    avatar: "mark-jazz-hosts.jpg"
  },

  // Special features for jazz experience  
  culturalFocus: {
    style: "Underground Tokyo jazz workshop tradition since 1988",
    groupSize: "Maximum 6 for ultra-intimate experience",
    duration: "3 hours of live improvised jazz",
    expertise: "Legendary jam session venue known throughout Tokyo's jazz community"
  },
  
  includedExperience: {
    cover_charge: "¥2,000 includes first drink",
    live_music: "Spontaneous jazz jam sessions with rotating musicians", 
    cultural_context: "Experience where Tokyo's jazz musicians gather to hone their craft",
    atmosphere: "Authentic basement jazz club unchanged since 1988"
  },
  
  jazzPhilosophy: "Experience authentic Tokyo jazz culture in the legendary basement where generations of musicians have gathered to jam—raw, real, and utterly atmospheric",
  
  idealFor: [
    "Jazz enthusiasts seeking authentic underground music experiences",
    "People who appreciate live improvisation over polished performances",
    "Those drawn to intimate, cramped spaces with soul and character",
    "Anyone wanting to experience where Tokyo's jazz musicians actually hang out",
    "Travelers seeking the real Tokyo beyond tourist attractions",
    "Music lovers comfortable with smoke and the beautiful chaos of jam sessions",
    "Those who understand the best jazz happens spontaneously at 2 AM in a basement",
    "People who want extremely limited group access to a usually packed venue"
  ],
  
  preparationTips: [
    "Come ready for an intimate, authentic experience",
    "Arrive on time as space is extremely limited with only 6 spots",
    "No jazz knowledge required—just appreciation for live improvised music",
    "Bring cash for additional drinks (¥500 each after first drink)",
    "Don't expect a setlist—enjoy the spontaneous nature of true jazz jams",
    "Embrace the cramped quarters—it's what makes the experience authentic"
  ],
  
  uniqueAspects: [
    "Legendary venue operating since 1988—where Tokyo jazz musicians woodshed",
    "Ultra-limited 6-person capacity (venue normally packed shoulder-to-shoulder)",
    "Owner Inoue-san alternates between bartending and playing alto saxophone",
    "Witnessed performances by jazz greats including Herbie Hancock and Larry Coryell",
    "Motto: 'Smokin', Drinkin', Never Thinkin''—and they mean it",
    "Massive vinyl collection lining the walls—request songs between sets",
    "Live jam sessions where musicians communicate freely and improvise",
    "Authentic 'jazz workshop' atmosphere unchanged for over 30 years",
    "2 minutes from Takadanobaba Station in underground basement",
    "Where touring musicians worldwide come when they ask 'Where can I jam in Tokyo?'",
    "Transparent pricing: ¥2,000 cover + ¥500 drinks (no hidden costs)",
    "Real musicians of all levels—from students to touring professionals"
  ]
},


{
  id: 'kawagoe-day-excursion-march-2026',
  eventType: 'excursion',
  iconType: 'image',
  iconImage: '/images/events/KawagoeDayTrip.jpg',
  iconValue: '🏯',
  title: "A Day in Little Edo",
  description: "Step back into Edo-period Japan without leaving the greater Tokyo area. Kawagoe - just an hour from Shinjuku, is where clay-walled warehouses, ancient temples, and a street once famous for penny candy transport you to another era. A relaxed group day out where we wander, eat, discover, and enjoy at our own pace.",
  waitlist: "0",
  next: "March 14, 2024",
  time: "8:45 AM - 4:00 PM (flexible departure)",
  eventDateTime: "2026-03-14T00:00:00Z",

  location: "Meet at Shinjuku Station",
  color: "from-amber-600 to-red-700",
  capacity: 10,
  difficulty: "Leisurely Walking",
  tags: ["Kawagoe", "Little Edo", "Day Trip", "History", "Temples", "Street Food", "Saitama"],
  featured: true,
  slug: 'kawagoe-day-excursion-march-2026',

  // City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo → Kawagoe",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",
  cost: "¥1,500-3,000 estimated (Pay for your own)",

  // Payment configuration
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 0,
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: false,
    allowWaitlist: true
  },

  philosophy: {
    title: "A Day in Little Edo: Wandering Kawagoe Together",
    sections: [
      {
        type: 'header',
        content: "For a seamless registration experience, please review the information below and visit the Details tab before reserving your spot."
      },
      {
        type: 'paragraph',
        content: "Less than an hour from Shinjuku, there's a town where the Edo period never quite left. Kawagoe - known affectionately as Koedo, or 'Little Edo', is one of those rare places in modern Japan where you can actually feel the weight of history in the streets. Clay-walled warehouses that survived fires centuries ago. A bell tower that has been marking time since the 1800s. Temples that once housed pieces of Tokyo's own castle. And a candy alley that fed all of Japan after the Great Kanto Earthquake."
      },
      {
        type: 'paragraph',
        content: "This isn't a rushed sightseeing tour. It's a day out - the kind where we take the train together, wander at our own pace, stumble into things that catch our eye, and let the afternoon unfold naturally. Some of us will explore temples. Some will get lost in sweet potato shops. Some will rent a kimono and photograph everything. All of it is the point."
      },
      {
        type: 'header',
        content: "What Makes Kawagoe Special"
      },
      {
        type: 'paragraph',
        content: "Kawagoe became a castle town in the 15th century and grew into one of the most important merchant cities supplying Edo - the old Tokyo. That wealth is still visible today. The kurazukuri warehouses lining the main street were built from thick clay walls specifically to survive the fires that ravaged the region. They were expensive to construct, and only the wealthiest merchants could afford them. Walking along Kurazukuri Street now, you're essentially walking through a street of historical status symbols each one a testament to how prosperous this small town once was."
      },
      {
        type: 'paragraph',
        content: "But Kawagoe isn't a museum. It's a living town with a relaxed rhythm. Vendors call out to passersby. School children race through candy alley comparing their finds. The bell tower still rings four times a day: at 6 AM, noon, 3 PM, and 6 PM — just as it has for generations. There's a warmth here that larger tourist destinations often lose."
      },
      {
        type: 'header',
        content: "Important: Getting There Together"
      },
      {
        type: 'paragraph',
        content: "⏰ We meet at Shinjuku Station at 8:45 AM—please arrive by then so we can board together at 9:00 AM. The group will take the Seibu Shinjuku Line from Seibu-Shinjuku Station (a short walk from JR Shinjuku). Each person purchases their own ticket individually. The Koedo Limited Express reaches Hon-Kawagoe Station in about 45 minutes (¥1,013 round trip with the Kawagoe Access Ticket — worth looking into). Alternatively, the regular express takes about 57 minutes for ¥520 one way. We'll share the exact boarding details in the group chat closer to the day."
      },
      {
        type: 'paragraph',
        content: "A quick note on the Seibu Kawagoe Access Ticket: this round-trip pass from Seibu-Shinjuku to Hon-Kawagoe includes the limited express fare and can be purchased at the Seibu ticket window. It's the most convenient option for the day. You can also explore JR routes if you prefer—just make sure you're at Shinjuku by 8:45."
      },
      {
        type: 'header',
        content: "What There Is to See and Do"
      },
      {
        type: 'paragraph',
        content: "Kawagoe's highlights are all within easy walking distance of Hon-Kawagoe Station, and the town is flat—no hills to navigate. Here's what's waiting, though there's no fixed itinerary. The beauty of this day is that we explore together without a schedule."
      },
      {
        type: 'paragraph',
        content: "Kitain Temple is one of the most historically significant stops. Founded around 830 AD, it houses actual buildings relocated from Edo Castle after a fire in 1638 - structures that no longer exist anywhere else in the world. The temple grounds also contain 538 stone Rakan statues, each with a unique expression, carved over 50 years during the Edo period. It's the kind of place that quietly stops you in your tracks."
      },
      {
        type: 'paragraph',
        content: "The Kurazukuri warehouse district is Kawagoe's visual heart. Walking this street feels like stepping into a photograph from another century—thick clay walls, elegant merchant architecture, and shops that have evolved from storing goods to selling everything from local craft beer (Coedo Brewery is a local favourite) to traditional textiles. The Toki no Kane bell tower stands at the northern end, a 16-meter wooden structure that has been the town's timekeeper for centuries."
      },
      {
        type: 'paragraph',
        content: "Kashiya Yokocho — Penny Candy Alley is a narrow street that became famous after the 1923 earthquake, when Kawagoe's candy shops stepped in to supply Tokyo. Over 70 shops once operated here. Around 20 remain today, and the atmosphere is wonderfully nostalgic. This is also where you'll find Kawagoe's sweet potato universe: purple sweet potato ice cream, sweet potato fries, sweet potato chips, and yes, even sweet potato coffee."
      },
      {
        type: 'paragraph',
        content: "Hikawa Shrine is over 1,500 years old and carries a gentle, peaceful atmosphere. It's particularly beloved as a shrine for love and good fortune — worth a quiet visit regardless. Kawagoe Castle itself is the closest functioning castle to Tokyo accessible to visitors, and its grounds offer a calm counterpoint to the busier streets."
      },
      {
        type: 'header',
        content: "Food & Sweet Potatoes"
      },
      {
        type: 'paragraph',
        content: "Kawagoe is famous for its sweet potatoes — a local specialty that shows up in every possible form. But beyond that, the town has solid lunch options scattered throughout. Expect to find soba noodle shops, unagi (eel) restaurants, and casual spots for a midday break. Prices are reasonable and portions are generous. The sweet potato treats alone are worth the trip - particularly the purple sweet potato ice cream from Koedo Osatsuan, which locals and visitors alike consider a must."
      },
    
      {
        type: 'header',
        content: "Heading Back"
      },
      {
        type: 'paragraph',
        content: "The group will gather to head back around 4:00 PM, plenty of time to catch the late afternoon before things wind down in town (Kawagoe gets quieter after 5 PM). That said, there's absolutely no obligation to stay until then. If you've had your fill by 2 PM, head back whenever feels right. Trains run frequently and the return trip is straightforward. This is your day—enjoy it at whatever pace suits you."
      },
      {
        type: 'header',
        content: "Payment & Costs"
      },
      {
        type: 'bullets',
        items: [
          "Train ticket: ¥520-¥1,013 one way depending on route and service (round trip recommended)",
          "Each person purchases and pays for their own ticket at the station",
          "Lunch and snacks: ¥1,000-¥2,000 depending on where you eat",
          "Optional kimono rental: ¥1,000-¥3,000",
          "Sweet potato treats and souvenirs: budget as you like",
          "Total estimated spend: ¥1,500-¥3,000 for a comfortable day (excluding optional extras)"
        ]
      },
      {
        type: 'header',
        content: "Perfect For Those Who"
      },
      {
        type: 'bullets',
        items: [
          "Want to experience old Japan without a long journey from Tokyo",
          "Enjoy wandering streets at their own pace without a rigid schedule",
          "Are curious about how Tokyo looked before it became Tokyo",
          "Love discovering local food and street snacks",
          "Appreciate temples and history but don't want it to feel like a lecture",
          "Want a relaxed group outing that feels more like a day with friends than a tour",
          "Haven't left central Tokyo in a while and need a gentle change of scenery"
        ]
      },
      {
        type: 'header',
        content: "What to Bring"
      },
      {
        type: 'paragraph',
        content: "Comfortable walking shoes—the streets are flat but you'll cover a few kilometers throughout the day. A small bag for any snacks or souvenirs you pick up along the way. Cash is useful here; while many shops accept cards, smaller vendors and candy alley stalls often prefer cash. And honestly? Just bring an open mind. Kawagoe rewards the people who aren't in a hurry."
      }
    ]
  },

  whatToExpect: [
    "A relaxed group day trip to Kawagoe, one hour from Shinjuku",
    "Wandering Edo-period streets and clay-walled warehouse districts",
    "Visiting Kitain Temple—home to original Edo Castle structures and 538 stone statues",
    "Exploring Penny Candy Alley and Kawagoe's famous sweet potato scene",
    "The Toki no Kane bell tower and Hikawa Shrine",
    "Flexible pace—no fixed itinerary, explore at your own rhythm",
    "Freedom to head back whenever you'd like; group returns around 4:00 PM",
    "Optional kimono rental to fully immerse in the atmosphere"
  ],

  venue: {
    name: "Kawagoe (Little Edo), Saitama Prefecture",
    description: "A well-preserved Edo-period castle town just under an hour from Shinjuku. Known for its kurazukuri warehouse district, ancient temples, candy alley, and sweet potato culture. One of the most accessible and charming day trips from Tokyo.",
    fullAddress: "Meet at Seibu-Shinjuku Station (short walk from JR Shinjuku). Destination: Hon-Kawagoe Station, Kawagoe, Saitama Prefecture",
    priceRange: "¥1,500-¥3,000 estimated total (pay for your own transport + food)",
    amenities: ["Flat, walkable town", "Frequent train service back to Tokyo", "Variety of lunch options", "Kimono rental shops available", "Cash recommended for smaller vendors", "Sightseeing loop buses available if needed"]
  },

  memberBenefits: [
    {
      icon: "🏯",
      title: "Living History",
      description: "Original Edo Castle structures, 538-year-old statues, and a bell tower still marking time since the 1800s"
    },
    {
      icon: "🍠",
      title: "Sweet Potato Heaven",
      description: "Kawagoe's famous purple sweet potato treats—ice cream, fries, chips, and more"
    },
    {
      icon: "🛤️",
      title: "Easy & Relaxed",
      description: "45 minutes from Shinjuku, flat streets, and no pressure to rush—just enjoy the day"
    },
    {
      icon: "👥",
      title: "Group Adventure",
      description: "Explore together but roam freely—come and go as it suits you"
    }
  ],

  relatedEvents: ['thai-skyline-dinner-february-2026', 'peruvian-lunch-azabudai-february-2026', 'french-vietnamese-lunch-january-2026'],
  status: "open",

  // Additional metadata
  eventFrequency: "occasional",
  dayOfWeek: "Saturday",
  focusArea: "Edo-period history, street food, temples, and relaxed exploration",
  experienceType: "group day excursion",

  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },

  hostInfo: {
    name: "Mark & ConnectCo",
    bio: "Curating experiences where good company and genuine curiosity lead the way—whether it's around a dinner table or down a centuries-old street",
    avatar: "mark-foodie-hosts.jpg"
  },

  // Excursion-specific features
  excursionFocus: {
    style: "Relaxed group day trip with no fixed itinerary",
    groupSize: "Up to 10 people—small enough to feel like friends, easy enough to move through town",
    duration: "Full day, 9:00 AM departure to 4:00 PM return (flexible)",
    highlight: "Edo-period atmosphere, temples, sweet potatoes, and the freedom to wander"
  },

  includedExperience: {
    atmosphere: "A living Edo-period town with warmth, history, and zero tourist-trap energy",
    connection: "Walking side by side through history—the best conversations happen on the move",
    quality: "One of Japan's most well-preserved historical towns, right on Tokyo's doorstep",
    pace: "Your pace. Seriously. Stay for lunch, skip it, rent a kimono, find a temple bench—it's all good."
  },

  excursionPhilosophy: "The best days out aren't the ones where everything is planned—they're the ones where you end up somewhere unexpected with people you enjoy, and the afternoon just disappears",

  idealFor: [
    "Anyone curious about what Tokyo looked like before it became Tokyo",
    "People who enjoy walking, wandering, and seeing where the day takes them",
    "Temple and history enthusiasts who don't want it to feel like homework",
    "Sweet potato lovers (and soon-to-be sweet potato lovers)",
    "Anyone looking for a genuinely relaxing day away from central Tokyo",
    "People who appreciate small-town Japan without the long bullet train journey",
    "Community members who want to spend time together outside the usual Tokyo bubble"
  ],


  preparationTips: [
    "Arrive at Shinjuku Station by 8:45 AM—we board together at 9:00 AM",
    "The exact meeting point within Shinjuku will be sent 24 hours before the event",
    "Look into the Seibu Kawagoe Access Ticket for a convenient round-trip fare",
    "Bring cash—smaller shops and candy alley vendors often prefer it",
    "Wear comfortable walking shoes; the town is flat but you'll cover a few km",
    "Bring a small bag for snacks and any souvenirs that call to you",
    "No need to plan your day in advance—we'll share suggestions in the group chat",
    "You're free to head back anytime; group returns around 4:00 PM"
  ],

  uniqueAspects: [
    "One of the best-preserved Edo-period towns within an hour of central Tokyo",
    "Kitain Temple houses actual structures moved from Edo Castle—found nowhere else in the world",
    "538 unique stone Rakan statues carved over 50 years during Japan's closed era",
    "Penny Candy Alley—a street that fed Tokyo after the 1923 earthquake",
    "The Toki no Kane bell tower, still ringing four times daily since the 1800s",
    "Kawagoe's sweet potato culture—a local specialty in every possible form",
    "Hikawa Shrine, over 1,500 years old, tucked into the town's quieter streets",
    "A completely flexible day—no schedule, no pressure, just good company and great streets"
  ],

  // Travel logistics
  travelLogistics: {
    departurePoint: "Seibu-Shinjuku Station (short walk north from JR Shinjuku Station)",
    departureTime: "9:00 AM (arrive 8:45 AM)",
    arrivalPoint: "Hon-Kawagoe Station, Kawagoe",
    estimatedTravelTime: "45-57 minutes depending on service",
    recommendedTicket: "Seibu Kawagoe Access Ticket (round-trip, includes limited express fare)",
    alternativeRoutes: [
      "Seibu Shinjuku Line Express: ~57 min, ¥520 one way",
      "Seibu Koedo Limited Express: ~45 min, ¥1,013 round trip (with Access Ticket)",
      "JR Saikyo/Kawagoe Line via Omiya: ~55 min, ¥770 one way"
    ],
    returnTime: "Around 4:00 PM (flexible—leave anytime)",
    returnNotes: "Trains run frequently. No obligation to stay until group return time.",
    ticketPurchase: "Each person buys their own ticket at the station before boarding"
  }
},

{
  id: 'underground-jazz-january-2026',
  eventType: 'happy-hour',
  iconType: 'image',
  iconImage: '/images/events/JazzNight.jpg',
  iconValue: '🎷',
  title: "No Smokin', Drinkin', Never Thinkin': Underground Jazz Session (Tokyo)",
  description: "Step into Tokyo's legendary basement jazz scene for an authentic jam session experience. Limited to 4 people for an intimate evening where professional and amateur musicians gather to improvise standards in one of Tokyo's most cherished underground jazz spots since 1988.",
  waitlist: "0",
  next: "January 31, 2026",
  time: "6:00 PM - 8:30 PM",
  eventDateTime: "2026-01-31T09:00:00Z",

  location: "TBD - Underground Jazz Club",
  color: "from-amber-500 to-purple-600",
  capacity: 6,
  difficulty: "Jazz Appreciation",
  tags: ["Live Jazz", "Jam Session", "Underground Music", "Authentic Tokyo", "Intimate Setting", "Jazz Culture"],
  featured: true,
  slug: 'underground-jazz-january-2026',
  
  // City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan", 
  timezone: "Asia/Tokyo",
  currency: "JPY",
   cost: "¥2,000",

  // Payment configuration properties
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },

  philosophy: {
    title: "Underground Jazz: Where Tokyo's Jazz Soul Lives",
    sections: [
      {
        type: 'header',
        content: "For a seamless registration experience, please review the information below and visit the Details tab before reserving your spot."
      },
      {
        type: 'paragraph',
        content: "Descend into the basement heart of Tokyo's jazz scene, where Drinkin', Never Thinkin'' isn't just a motto—it's a way of life. This isn't a polished concert hall or a trendy listening bar. This is where real jazz happens: cramped, soulful, and utterly authentic. Since 1988, this legendary underground club has been the woodshedding ground for generations of Japanese jazz musicians, a place where students and seasoned professionals alike gather to jam through standards until the early morning hours."
      },
      {
        type: 'paragraph',
        content: "The walls are lined with thousands of vinyl records. The space is so intimate you can see every expression on the musicians' faces. The bartender-owner alternates between pouring drinks and jumping on stage to rip saxophone solos. This is the quintessential Tokyo jazz experience—raw, real, and impossibly atmospheric. Musicians from around the world ask the same question when they arrive in Tokyo: 'Where can I go for a jazz jam tonight?' The answer, for over three decades, has been the same underground basement in Takadanobaba."
      },
      {
        type: 'header',
        content: "What Makes This Evening Special"
      },
      {
        type: 'paragraph',
        content: "Legendary Jazz Workshop: Experience the venue where greats like Herbie Hancock and Larry Coryell have dropped in for impromptu sessions—and where tomorrow's jazz stars are honing their craft tonight."
      },
      {
        type: 'paragraph',
        content: "Ultra-Intimate Access: Limited to just 6 people (versus the usual packed basement), giving you breathing room and unobstructed views in a space that's normally shoulder-to-shoulder with jazz devotees."
      },
      {
        type: 'paragraph',
        content: "Live Jam Sessions: Not a rehearsed performance—authentic spontaneous jazz where musicians communicate freely, riffing off each other's energy and running through standards with the kind of raw creativity that only happens in the moment."
      },
      {
        type: 'paragraph',
        content: "Transparent Pricing: ¥2,000 cover charge includes your first drink. Additional drinks are just ¥500 each—craft cocktails and classics that complement the live music without breaking the bank."
      },
      {
        type: 'header',
        content: "Your Jazz Journey"
      },
      {
        type: 'bullets',
        items: [
          "Basement Atmosphere: Narrow, cramped by design, with vintage decor and warm lighting that creates the perfect jazz club ambiance—exactly what an underground jazz spot should feel like",
          "Rotating Musicians: Watch as different jazz players arrive throughout the evening to sit in—the band lineup shifts organically, creating unique moments you'll never experience twice",
          "Bartender-Musician Magic: Witness the owner's legendary moves: taking a saxophone solo, then running behind the bar to serve drinks while the band vamps, then jumping back into the music",
          "Vinyl Selection: Request songs from the massive collection of records lining the walls—if you're not feeling the jam, the owner will spin your selection on the vintage turntable",
          "Jazz Standards: Experience musicians running through the canon of jazz—from bebop to hard bop, standards that have been played in basement clubs since the 1950s",
          "International Jazz Community: Encounter the full spectrum—Japanese students learning their craft, Tokyo professionals unwinding after gigs, touring musicians from around the world sitting in for the pure love of improvisation"
        ]
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "¥2,000 cover charge includes your first drink (already included in your registration)",
          "Additional drinks at ¥500 each—craft cocktails and classic jazz bar favorites",
          "Intimate 6-person group ensuring you actually have space in this famously tight venue",
          "Authentic jazz club experience",
          "No food service, but unlimited jazz until late evening",
          "Casual 'drop in and play' jam sessions rather than polished performances",
          "Real musicians of all abilities—from university students to touring professionals",
          "Underground basement location just 2 minutes from Takadanobaba Station"
        ]
      },
      {
        type: 'header',
        content: "Perfect For Jazz Lovers Who"
      },
      {
        type: 'bullets',
        items: [
          "Want to experience authentic Tokyo jazz culture, not a tourist-friendly version",
          "Appreciate the raw energy of live improvisation over polished performances",
          "Love intimate spaces where you're part of the music, not just watching from a distance",
          "Are drawn to underground culture and the kind of basement clubs that feel like hidden treasures",
          "Don't mind tight quarters, smoke, and the beautiful chaos of a real jam session",
          "Want to witness the 'jazz workshop' vibe where musicians communicate freely and learn from each other",
          "Seek experiences locals cherish—this is where Tokyo's jazz musicians come to play",
          "Understand that the best jazz moments happen spontaneously, not on a setlist"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this exclusive 6-person gathering in one of Tokyo's most legendary underground jazz clubs. This isn't a tourist attraction—it's the real deal, the kind of jazz experience that has remained unchanged since 1988 because it doesn't need to change. Just good music, strong drinks, and the soul of Tokyo's jazz scene compressed into one basement."
      },
      {
        type: 'header',
        content: "Important Cancellation Policy"
      },
      {
        type: 'paragraph',
        content: "Due to the extremely limited capacity (6 people max) and the intimate nature of this experience, please cancel at least 72 hours before the event if you cannot attend. This ensures fairness to jazz enthusiasts on our waitlist."
      },
      {
        type: 'header',
        content: "Important Details"
      },
      {
        type: 'paragraph',
        content: "Cover charge is ¥2,000, which includes your first drink. Additional drinks are ¥500 each. No food service available—this is a pure jazz experience."
      },
      {
        type: 'header',
        content: "Location & Atmosphere"
      },
      {
        type: 'paragraph',
        content: "Our selected venue is a legendary basement jazz club in Takadanobaba, just 2 minutes from the station on busy Waseda-dori. Narrow and cramped even by Tokyo standards, the space is lined with thousands of vinyl records and features vintage decor that hasn't changed in decades. Musicians gather here 3-4 nights per week for spontaneous jam sessions that often run until 5 AM on weekends. The bartender-owner, Inoue-san, plays alto saxophone between serving drinks. Exact location will be shared with confirmed attendees 48 hours before the event."
      }
    ]
  },
  
  whatToExpect: [
    "Live jazz jam sessions with rotating musicians throughout the evening",
    "¥2,000 cover charge includes first drink (additional drinks ¥500 each)",
    "Ultra-intimate 6-person maximum group in a famously tight space",
    "No food service—pure jazz and drinks experience",
    "Casual drop-in jam format with musicians of all levels",
    "Vintage vinyl collection available on request between sets",
    "Located 2 minutes from Takadanobaba Station"
  ],
  
  venue: { 
    name: "TBD - Underground Jazz Club",
    description: "Legendary basement jazz club established 1988, featuring live jam sessions 3-4 nights per week. Intimate space lined with vinyl records where professional and amateur musicians gather to improvise. Known for 'Smokin', Drinkin', Never Thinkin'' motto and authentic jazz workshop atmosphere. Bartender-owner Inoue-san alternates between pouring drinks and playing alto saxophone.",
    fullAddress: "TBD - Shinjuku City, Tokyo (exact location revealed to confirmed attendees 48 hours prior)",
    priceRange: "¥2,000 cover (includes first drink), ¥500 per additional drink",
    amenities: ["Live jam sessions", "Massive vinyl collection", "Craft cocktails", "Intimate basement setting",  "Open late", "2 min from station"]
  },
  
  memberBenefits: [
    {
      icon: "🎷",
      title: "Authentic Jazz Culture",
      description: "Experience the real Tokyo jazz scene where musicians have gathered since 1988"
    },
    {
      icon: "👥", 
      title: "Ultra-Intimate Access",
      description: "Limited to 6 people instead of the usual packed basement—breathe and actually see the musicians"
    },
    {
      icon: "🎵",
      title: "Live Improvisation", 
      description: "Witness spontaneous jam sessions where professional and amateur musicians create magic in real-time"
    },
    {
      icon: "🏆",
      title: "Legendary Venue",
      description: "Join the tradition of musicians who ask 'Where can I jam in Tokyo?'—this is where they go"
    }
  ],
  
  relatedEvents: ['natural-wine-journey-december-2025', 'weekly-fika', 'happy-hour'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special",
  dayOfWeek: "Friday", 
  focusArea: "Underground Tokyo jazz culture and live improvisation",
  experienceType: "intimate jazz jam session",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark & Tokyo Jazz Scene",
    bio: "Passionate about connecting people through Tokyo's authentic underground jazz culture",
    avatar: "mark-jazz-hosts.jpg"
  },

  // Special features for jazz experience  
  culturalFocus: {
    style: "Underground Tokyo jazz workshop tradition since 1988",
    groupSize: "Maximum 6 for ultra-intimate experience",
    duration: "3 hours of live improvised jazz",
    expertise: "Legendary jam session venue known throughout Tokyo's jazz community"
  },
  
  includedExperience: {
    cover_charge: "¥2,000 includes first drink",
    live_music: "Spontaneous jazz jam sessions with rotating musicians", 
    cultural_context: "Experience where Tokyo's jazz musicians gather to hone their craft",
    atmosphere: "Authentic basement jazz club unchanged since 1988"
  },
  
  jazzPhilosophy: "Experience authentic Tokyo jazz culture in the legendary basement where generations of musicians have gathered to jam—raw, real, and utterly atmospheric",
  
  idealFor: [
    "Jazz enthusiasts seeking authentic underground music experiences",
    "People who appreciate live improvisation over polished performances",
    "Those drawn to intimate, cramped spaces with soul and character",
    "Anyone wanting to experience where Tokyo's jazz musicians actually hang out",
    "Travelers seeking the real Tokyo beyond tourist attractions",
    "Music lovers comfortable with smoke and the beautiful chaos of jam sessions",
    "Those who understand the best jazz happens spontaneously at 2 AM in a basement",
    "People who want extremely limited group access to a usually packed venue"
  ],
  
  preparationTips: [
    "Come ready for an intimate, authentic experience",
    "Arrive on time as space is extremely limited with only 6 spots",
    "No jazz knowledge required—just appreciation for live improvised music",
    "Bring cash for additional drinks (¥500 each after first drink)",
    "Don't expect a setlist—enjoy the spontaneous nature of true jazz jams",
    "Embrace the cramped quarters—it's what makes the experience authentic"
  ],
  
  uniqueAspects: [
    "Legendary venue operating since 1988—where Tokyo jazz musicians woodshed",
    "Ultra-limited 6-person capacity (venue normally packed shoulder-to-shoulder)",
    "Owner Inoue-san alternates between bartending and playing alto saxophone",
    "Witnessed performances by jazz greats including Herbie Hancock and Larry Coryell",
    "Motto: 'Smokin', Drinkin', Never Thinkin''—and they mean it",
    "Massive vinyl collection lining the walls—request songs between sets",
    "Live jam sessions where musicians communicate freely and improvise",
    "Authentic 'jazz workshop' atmosphere unchanged for over 30 years",
    "2 minutes from Takadanobaba Station in underground basement",
    "Where touring musicians worldwide come when they ask 'Where can I jam in Tokyo?'",
    "Transparent pricing: ¥2,000 cover + ¥500 drinks (no hidden costs)",
    "Real musicians of all levels—from students to touring professionals"
  ]
},


{
  id: 'authentic-swedish-fika-february-saturday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo’s Beloved Saturday Tradition (Feb)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "February 28, 2026",
  time: "11:00 AM - 1:00 PM",
  eventDateTime: "2026-02-28T04:00:00Z",
 // eventDateTime: "2026-02-28T01:00:00Z",  // ✅ 10:00 AM JST = 1:00 AM UTC

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 25,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee",  "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-february-saturday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

    // Payment configuration properties
    // set to true to enable payments
  paymentEnabled: false,
  requiresSubscription: false,
  perEventPriceCents: 2100,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Nestled along the serene banks of a gently flowing river, our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a waterside community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. Surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene park, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting",  "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
   
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},

{
  id: 'authentic-swedish-fika-march-saturday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo’s Beloved Saturday Tradition (March)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "March 28, 2026",
  time: "11:00 AM - 1:00 PM",
  eventDateTime: "2026-03-28T04:00:00Z",
 // eventDateTime: "2026-02-28T01:00:00Z",  // ✅ 10:00 AM JST = 1:00 AM UTC

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 25,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee",  "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-march-saturday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

    // Payment configuration properties
    // set to true to enable payments
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Nestled along the serene banks of a gently flowing river, our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a waterside community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. Surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene park, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting",  "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
   
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},

{
  id: 'authentic-swedish-fika-april-sunday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo’s Beloved Sunday Tradition (April)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "April 12, 2026",
  time: "11:00 AM - 1:00 PM",
  eventDateTime: "2026-04-12T04:00:00Z",
 // eventDateTime: "2026-02-28T01:00:00Z",  // ✅ 10:00 AM JST = 1:00 AM UTC

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 25,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee",  "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-april-sunday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

    // Payment configuration properties
    // set to true to enable payments
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Nestled along the serene banks of a gently flowing river, our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a waterside community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. Surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene park, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting",  "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
   
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},

{
    id: 'drift-happy-hour-july-2026',
    eventType: 'happy-hour',
    iconType: 'image',
     iconType: 'image',
    iconImage: '/images/events/GinzaHappyHour.jpg',
    iconValue: '🍷',
    title: "The B-Side: August Happy Hour",
    description: "A slow, intentional Saturday evening in one of Tokyo's most cherished hidden bars. Trip-hop and ambient music set the tone as we gather for excellent natural wine, craft beer, and artisan non-alcoholic options. No agenda—just the right kind of drift.",
    waitlist: "0",
    next: "Sat, Aug 8, 2026",
    time: "6:00 PM - 8:30 PM",
    eventDateTime: "2026-08-08T05:00:00Z",
    location: "TBD (revealed 24 hours before)",
    color: "from-indigo-500 to-violet-400",
    capacity: 15,
    difficulty: "Casual Social",
    tags: ["Happy Hour", "Natural Wine", "Craft Beer", "Trip-Hop", "Ambient Music", "Saturday", "Hidden Gem"],
    featured: true,
    slug: 'drift-happy-hour-july-2026',

    // Payment configuration
    cost: "Pay for your own drinks",
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 0,
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },

    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",

    philosophy: {
      title: "The Drift: August Happy Hour Saturday",
      sections: [
        {
          type: 'paragraph',
          content: "Some evenings you plan. Others you drift into—and those are usually the better ones. Join us for ConnectCo's August Happy Hour: a low-key, high-quality Saturday evening built around great drinks, ambient music turned up just right, and the kind of unhurried conversation a good bar was made for."
        },
        {
          type: 'paragraph',
          content: "The venue—revealed 24 hours before the event—is a carefully chosen bar tucked into a quiet Tokyo side street. Expect a minimalist, design-forward space with meticulously curated sound equipment, an Italian wine selection, draft beer, and excellent non-alcoholic options. This is the kind of place locals keep to themselves. We're sharing it with 15 of you."
        },
        {
          type: 'header',
          content: "🎵 The Soundtrack Matters Here"
        },
        {
          type: 'paragraph',
          content: "This isn't background noise—it's a bar that takes its sound seriously. Trip-hop and ambient music drift through a thoughtfully designed audio setup, creating exactly the right atmosphere for conversations that go somewhere real. Not too loud to talk, not too quiet to feel the room."
        },
        {
          type: 'header',
          content: "🍷 What's Pouring"
        },
        {
          type: 'bullets',
          items: [
            "Natural wine — approachable, well-chosen, surprising",
            "Craft draft beer — rotating and curated",
            "Non-alcoholic options including homemade artisan drinks",
            "Niche distilled spirits for those who like to explore—shochu, awamori, and beyond",
            "Pay as you go, at whatever pace suits you"
          ]
        },
        {
          type: 'header',
          content: "Cost & Drinks"
        },
        {
          type: 'paragraph',
          content: "Just pay for your own drinks. Expect a typical glass of natural wine or craft beer in the ¥800–¥1,500 range. Non-alcoholic options available too."
        },
        {
          type: 'header',
          content: "Why This Bar?"
        },
        {
          type: 'paragraph',
          content: "This venue doesn't do loud or flashy. It does intentional—great sound design, carefully chosen bottles, a space that makes you want to linger. Our June spot fits that energy completely. If you've been looking for a bar that feels like a discovery rather than an appointment, this is it."
        },
       
        {
          type: 'paragraph',
          content: "No forced activities, no awkward icebreakers. The music and the atmosphere do the work. You just show up."
        },
        {
          type: 'header',
          content: "The Vibe"
        },
        {
          type: 'bullets',
          items: [
            "Intimate — maximum 15 people, which means you'll remember everyone",
            "Design-forward, minimalist space with serious sound equipment",
            "Trip-hop and ambient music setting a relaxed, unhurried tone",
            "Mix of Connect & Co regulars and new faces",
            "English-friendly with an international crowd"
          ]
        },
        
        {
          type: 'header',
          content: "Important Attendance Policy"
        },
        {
          type: 'paragraph',
          content: "Members who RSVP 'yes' but fail to attend without canceling their RSVP beforehand will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. With only 15 spots, every confirmed seat matters—please cancel as early as possible if your plans change."
        }
      ]
    },

    whatToExpect: [
      "Intimate, design-forward bar with meticulously curated sound equipment",
      "Trip-hop and ambient music at the perfect volume for real conversation",
      "Natural wine, craft beer, artisan non-alcoholic drinks — pay as you go",
      "Maximum 15 guests — small enough to actually connect",
      "A hidden Tokyo bar — bohemian, walkable, full of character",
      "No structured activities — just open, organic conversation",
      "Venue location revealed 24 hours before the event"
    ],

    venue: {
      name: "TBD - Hidden Bar, Tokyo (revealed 24 hours before)",
      description: "A minimalist, design-forward bar with meticulously designed sound equipment, natural wine, craft draft beer, artisan non-alcoholic drinks, and niche distilled spirits. Homemade artisan fare available. A spot that locals cherish.",
      fullAddress: "Tokyo (exact address TBD - shared with attendees 24 hours prior)",
      priceRange: "FREE entry - drinks approximately ¥800–¥1,500 per glass (pay for your own)",
      amenities: ["Trip-hop and ambient music", "Natural wine selection", "Craft draft beer", "Artisan non-alcoholic drinks", "Niche distilled spirits", "Minimalist interior", "Designed sound system", "Intimate 15-person capacity"]
    },

    memberBenefits: [
      {
        icon: "🎵",
        title: "The Soundtrack",
        description: "A bar that takes its audio setup seriously — trip-hop and ambient music at exactly the right volume"
      },
      {
        icon: "🍷",
        title: "Great Drinks Selection",
        description: "Natural wine, craft draft beer, artisan non-alcoholics, and niche spirits — pay as you go"
      },
      {
        icon: "🏘️",
        title: "Hidden Gem Setting",
        description: "A locals-only bar that rarely makes lists — intimate, design-forward, and full of character"
      },
      {
        icon: "🤝",
        title: "15 People Maximum",
        description: "Small enough that you'll leave knowing everyone's name"
      }
    ],

    relatedEvents: ['fika', 'weekly-fika', 'discussions', 'happy-hour', 'natural-wine-journey-december-2025'],
    status: "open",

    eventFrequency: "occasional",
    dayOfWeek: "Saturday",
    neighborhood: "TBD",
    pricePoint: "free-entry",

    socialProof: {
      totalAttended: 0,
      averageRating: 0,
      repeatAttendees: "0%"
    },

    hostInfo: {
      name: "Mark",
      bio: "Finding Tokyo's best hidden spaces and bringing the right people together in them",
      avatar: "mark-jason-host.jpg"
    },

    drinkSpecials: {
      packagePrice: "FREE entry",
      paymentMethod: "Pay for your own drinks at the venue",
      drinkRange: "¥800–¥1,500 approx. per drink",
      options: ["Natural wine", "Craft draft beer", "Homemade artisan non-alcoholic drinks", "Shochu", "Awamori", "Niche distilled spirits"],
      minimum: "One drink minimum per person"
    },

    venueFeatures: {
      musicStyle: "Trip-hop and ambient — curated sound system",
      interior: "Minimalist, design-forward",
      neighborhood: "TBD",
      venueReveal: "24 hours before the event"
    },

    attendancePolicy: "Members who RSVP 'yes' but fail to attend the event without canceling their RSVP beforehand will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. With a maximum of 15 guests, every spot counts—please cancel early if your plans change.",

    networkingStyle: "Organic, drift-as-you-go — no structure, no ice-breakers. The music, the space, and the drinks create the atmosphere. The rest happens naturally.",

    idealFor: [
      "People who like their Saturday evenings slow and intentional",
      "Natural wine and craft beer explorers",
      "Anyone who feels more like themselves in a hidden local bar than a Roppongi rooftop",
      "Fans of trip-hop, ambient music, and spaces that take sound seriously",
      "New Tokyo residents looking for a genuine crowd",
      "Connect & Co regulars ready to discover something new"
    ],

    atmosphereKeywords: ["drift", "ambient", "trip-hop", "intimate", "minimalist", "bohemian", "unhurried", "design-forward", "natural wine", "craft beer", "hidden gem"]
  },



{
    id: 'drift-happy-hour-june-2026',
    eventType: 'happy-hour',
    iconType: 'image',
     iconType: 'image',
    iconImage: '/images/events/GinzaHappyHour.jpg',
    iconValue: '🍷',
    title: "The B-Side: June Happy Hour",
    description: "A slow, intentional Saturday evening in one of Tokyo's most cherished hidden bars. Trip-hop and ambient music set the tone as we gather for excellent natural wine, craft beer, and artisan non-alcoholic options. No agenda—just the right kind of drift.",
    waitlist: "3",
    next: "Sat, June 6, 2026",
    time: "5:00 PM - 8:00 PM",
    eventDateTime: "2026-06-06T04:00:00Z",
    location: "TBD (revealed 24 hours before)",
    color: "from-indigo-500 to-violet-400",
    capacity: 15,
    difficulty: "Casual Social",
    tags: ["Happy Hour", "Natural Wine", "Craft Beer", "Trip-Hop", "Ambient Music", "Saturday", "Hidden Gem"],
    featured: true,
    slug: 'drift-happy-hour-june-2026',

    // Payment configuration
    cost: "Pay for your own drinks",
    paymentEnabled: false,
    requiresSubscription: true,
    perEventPriceCents: 0,
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },

    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",

    philosophy: {
      title: "The Drift: June Happy Hour Saturday",
      sections: [
        {
          type: 'paragraph',
          content: "Some evenings you plan. Others you drift into—and those are usually the better ones. Join us for ConnectCo's June Happy Hour: a low-key, high-quality Saturday evening built around great drinks, ambient music turned up just right, and the kind of unhurried conversation a good bar was made for."
        },
        {
          type: 'paragraph',
          content: "The venue—revealed 24 hours before the event—is a carefully chosen bar tucked into a quiet Tokyo side street. Expect a minimalist, design-forward space with meticulously curated sound equipment, an Italian wine selection, draft beer, and excellent non-alcoholic options. This is the kind of place locals keep to themselves. We're sharing it with 15 of you."
        },
        {
          type: 'header',
          content: "🎵 The Soundtrack Matters Here"
        },
        {
          type: 'paragraph',
          content: "This isn't background noise—it's a bar that takes its sound seriously. Trip-hop and ambient music drift through a thoughtfully designed audio setup, creating exactly the right atmosphere for conversations that go somewhere real. Not too loud to talk, not too quiet to feel the room."
        },
        {
          type: 'header',
          content: "🍷 What's Pouring"
        },
        {
          type: 'bullets',
          items: [
            "Natural wine — approachable, well-chosen, surprising",
            "Craft draft beer — rotating and curated",
            "Non-alcoholic options including homemade artisan drinks",
            "Niche distilled spirits for those who like to explore—shochu, awamori, and beyond",
            "Pay as you go, at whatever pace suits you"
          ]
        },
        {
          type: 'header',
          content: "Cost & Drinks"
        },
        {
          type: 'paragraph',
          content: "Just pay for your own drinks. Expect a typical glass of natural wine or craft beer in the ¥800–¥1,500 range. Non-alcoholic options available too."
        },
        {
          type: 'header',
          content: "Why This Bar?"
        },
        {
          type: 'paragraph',
          content: "This venue doesn't do loud or flashy. It does intentional—great sound design, carefully chosen bottles, a space that makes you want to linger. Our June spot fits that energy completely. If you've been looking for a bar that feels like a discovery rather than an appointment, this is it."
        },
        {
          type: 'header',
          content: "What to Expect"
        },
        {
          type: 'bullets',
          items: [
            "5:00–5:15 PM: Arrive, grab your first drink, settle in",
            "5:15–7:00 PM: Open mingling—conversations, music, good drinks",
            "7:00–7:30 PM: Wind down with the people you actually want to keep talking to"
          ]
        },
        {
          type: 'paragraph',
          content: "No forced activities, no awkward icebreakers. The music and the atmosphere do the work. You just show up."
        },
        {
          type: 'header',
          content: "The Vibe"
        },
        {
          type: 'bullets',
          items: [
            "Intimate — maximum 15 people, which means you'll remember everyone",
            "Design-forward, minimalist space with serious sound equipment",
            "Trip-hop and ambient music setting a relaxed, unhurried tone",
            "Mix of Connect & Co regulars and new faces",
            "English-friendly with an international crowd"
          ]
        },
        
        {
          type: 'header',
          content: "Important Attendance Policy"
        },
        {
          type: 'paragraph',
          content: "Members who RSVP 'yes' but fail to attend without canceling their RSVP beforehand will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. With only 15 spots, every confirmed seat matters—please cancel as early as possible if your plans change."
        }
      ]
    },

    whatToExpect: [
      "Intimate, design-forward bar with meticulously curated sound equipment",
      "Trip-hop and ambient music at the perfect volume for real conversation",
      "Natural wine, craft beer, artisan non-alcoholic drinks — pay as you go",
      "Maximum 15 guests — small enough to actually connect",
      "A hidden Tokyo bar — bohemian, walkable, full of character",
      "No structured activities — just open, organic conversation",
      "Venue location revealed 24 hours before the event"
    ],

    venue: {
      name: "TBD - Hidden Bar, Tokyo (revealed 24 hours before)",
      description: "A minimalist, design-forward bar with meticulously designed sound equipment, natural wine, craft draft beer, artisan non-alcoholic drinks, and niche distilled spirits. Homemade artisan fare available. A spot that locals cherish.",
      fullAddress: "Tokyo (exact address TBD - shared with attendees 24 hours prior)",
      priceRange: "FREE entry - drinks approximately ¥800–¥1,500 per glass (pay for your own)",
      amenities: ["Trip-hop and ambient music", "Natural wine selection", "Craft draft beer", "Artisan non-alcoholic drinks", "Niche distilled spirits", "Minimalist interior", "Designed sound system", "Intimate 15-person capacity"]
    },

    memberBenefits: [
      {
        icon: "🎵",
        title: "The Soundtrack",
        description: "A bar that takes its audio setup seriously — trip-hop and ambient music at exactly the right volume"
      },
      {
        icon: "🍷",
        title: "Great Drinks Selection",
        description: "Natural wine, craft draft beer, artisan non-alcoholics, and niche spirits — pay as you go"
      },
      {
        icon: "🏘️",
        title: "Hidden Gem Setting",
        description: "A locals-only bar that rarely makes lists — intimate, design-forward, and full of character"
      },
      {
        icon: "🤝",
        title: "15 People Maximum",
        description: "Small enough that you'll leave knowing everyone's name"
      }
    ],

    relatedEvents: ['fika', 'weekly-fika', 'discussions', 'happy-hour', 'natural-wine-journey-december-2025'],
    status: "open",

    eventFrequency: "occasional",
    dayOfWeek: "Saturday",
    neighborhood: "TBD",
    pricePoint: "free-entry",

    socialProof: {
      totalAttended: 0,
      averageRating: 0,
      repeatAttendees: "0%"
    },

    hostInfo: {
      name: "Mark",
      bio: "Finding Tokyo's best hidden spaces and bringing the right people together in them",
      avatar: "mark-jason-host.jpg"
    },

    drinkSpecials: {
      packagePrice: "FREE entry",
      paymentMethod: "Pay for your own drinks at the venue",
      drinkRange: "¥800–¥1,500 approx. per drink",
      options: ["Natural wine", "Craft draft beer", "Homemade artisan non-alcoholic drinks", "Shochu", "Awamori", "Niche distilled spirits"],
      minimum: "One drink minimum per person"
    },

    venueFeatures: {
      musicStyle: "Trip-hop and ambient — curated sound system",
      interior: "Minimalist, design-forward",
      neighborhood: "TBD",
      venueReveal: "24 hours before the event"
    },

    attendancePolicy: "Members who RSVP 'yes' but fail to attend the event without canceling their RSVP beforehand will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. With a maximum of 15 guests, every spot counts—please cancel early if your plans change.",

    networkingStyle: "Organic, drift-as-you-go — no structure, no ice-breakers. The music, the space, and the drinks create the atmosphere. The rest happens naturally.",

    idealFor: [
      "People who like their Saturday evenings slow and intentional",
      "Natural wine and craft beer explorers",
      "Anyone who feels more like themselves in a hidden local bar than a Roppongi rooftop",
      "Fans of trip-hop, ambient music, and spaces that take sound seriously",
      "New Tokyo residents looking for a genuine crowd",
      "Connect & Co regulars ready to discover something new"
    ],

    atmosphereKeywords: ["drift", "ambient", "trip-hop", "intimate", "minimalist", "bohemian", "unhurried", "design-forward", "natural wine", "craft beer", "hidden gem"]
  },


  {
  id: 'authentic-swedish-fika-july-sunday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo’s Beloved Sunday Tradition (July)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "Sun, July 26, 2026",
  time: "11:00 AM - 1:00 PM",
  eventDateTime: "2026-07-26T04:00:00Z",
 // eventDateTime: "2026-02-28T01:00:00Z",  // ✅ 10:00 AM JST = 1:00 AM UTC

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 25,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee",  "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-july-sunday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

    // Payment configuration properties
    // set to true to enable payments
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. Surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene park, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting",  "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
   
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},

  {
  id: 'authentic-swedish-fika-august-sunday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo’s Beloved Sunday Tradition (Aug)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "Sun, Aug 16, 2026",
  time: "11:00 AM - 1:00 PM",
  eventDateTime: "2026-08-16T04:00:00Z",
 // eventDateTime: "2026-02-28T01:00:00Z",  // ✅ 10:00 AM JST = 1:00 AM UTC

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 15,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee",  "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-august-sunday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

    // Payment configuration properties
    // set to true to enable payments
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. Surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene park, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting",  "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
   
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},

  {
  id: 'authentic-swedish-fika-october-sunday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo’s Beloved Sunday Tradition (Oct)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "Sun, October 25, 2026",
  time: "11:00 AM - 1:00 PM",
  eventDateTime: "2026-10-25T04:00:00Z",
 // eventDateTime: "2026-02-28T01:00:00Z",  // ✅ 10:00 AM JST = 1:00 AM UTC

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 15,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee",  "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-october-sunday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

    // Payment configuration properties
    // set to true to enable payments
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. Surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene park, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting",  "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
   
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},

  {
  id: 'authentic-swedish-fika-november-sunday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo’s Beloved Sunday Tradition (Oct)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "Sun, Novembre 29, 2026",
  time: "11:00 AM - 1:00 PM",
  eventDateTime: "2026-11-29T04:00:00Z",
 // eventDateTime: "2026-02-28T01:00:00Z",  // ✅ 10:00 AM JST = 1:00 AM UTC

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 15,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee",  "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-november-sunday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

    // Payment configuration properties
    // set to true to enable payments
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. Surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene park, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting",  "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
   
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},

  {
  id: 'authentic-swedish-fika-september-sunday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo’s Beloved Sunday Tradition (Sept)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "Sun, September 6, 2026",
  time: "11:00 AM - 1:00 PM",
  eventDateTime: "2026-09-06T04:00:00Z",
 // eventDateTime: "2026-02-28T01:00:00Z",  // ✅ 10:00 AM JST = 1:00 AM UTC

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 15,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee",  "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-september-sunday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

    // Payment configuration properties
    // set to true to enable payments
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. Surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene park, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting",  "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
   
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},

{
  id: 'authentic-swedish-fika-june-sunday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo’s Beloved Sunday Tradition (June)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "Sun, June 14, 2026",
  time: "11:00 AM - 1:00 PM",
  eventDateTime: "2026-06-14T04:00:00Z",
 // eventDateTime: "2026-02-28T01:00:00Z",  // ✅ 10:00 AM JST = 1:00 AM UTC

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 25,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee",  "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-june-sunday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

    // Payment configuration properties
    // set to true to enable payments
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. Surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene park, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting",  "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
   
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},

{
  id: 'authentic-swedish-fika-may-sunday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo’s Beloved Sunday Tradition (May)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "Sunday, May 10, 2026",
  time: "11:00 AM - 1:00 PM",
  eventDateTime: "2026-05-10T04:00:00Z",
 // eventDateTime: "2026-02-28T01:00:00Z",  // ✅ 10:00 AM JST = 1:00 AM UTC

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 25,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee",  "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-may-sunday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

    // Payment configuration properties
    // set to true to enable payments
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Nestled along the serene banks of a gently flowing river, our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a waterside community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. Surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene park, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting",  "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
   
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},

{
  id: 'authentic-swedish-fika-may-saturday-2026',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo’s Beloved Saturday Tradition (May)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "Sat, May 30, 2026",
  time: "11:00 AM - 1:00 PM",
  eventDateTime: "2026-05-30T04:00:00Z",
 // eventDateTime: "2026-02-28T01:00:00Z",  // ✅ 10:00 AM JST = 1:00 AM UTC

  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 25,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee",  "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-may-saturday-2026',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

    // Payment configuration properties
    // set to true to enable payments
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },


  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Nestled along the serene banks of a gently flowing river, our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a waterside community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. Surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene park, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting",  "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
   
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},


{
  id: 'art-after-dark-january-2026',
  eventType: 'cultural',
  iconType: 'image',
  iconImage: '/images/events/ArtAfterDark.jpg',
  iconValue: '🎨',
  title: "Art After Dark: Multi-Artist Exhibit featuring ConnectCo Community Member at Shibuya Club",
  description: "Where art breaks free from white walls. Join us for an intimate evening celebrating a ConnectCo community member exhibiting alongside talented artists at a Shibuya nightclub's B1 floor. This isn't your typical opening reception. It's art in its natural habitat: unexpected, alive, and meant to spark real conversation—and we're showing up to support one of our own.",
  waitlist: "0",
  next: "January 10, 2026",
  time: "6:00 PM - 9:00 PM",
  eventDateTime: "2026-01-10T09:00:00Z",  // ✅ 6:00 PM JST = 9:00 AM UTC

  location: "TBD - Shibuya Club (B1 Floor), Tokyo",
  color: "from-purple-600 to-pink-600",
  capacity: 4,
  difficulty: "Casual Social",
  tags: ["Art Exhibit", "Community Artist", "Multi-Artist", "Cultural", "Shibuya", "Evening Event", "Tokyo", "B1 Floor"],
  featured: true,
  slug: 'art-after-dark-january-2026',
  
  // City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan", 
  timezone: "Asia/Tokyo",
  currency: "JPY",
  cost: "Free entry (6-7 PM) + 1 drink purchase",

  // Payment configuration properties
  paymentEnabled: true,  // Changed to false since entry is free
  requiresSubscription: true,
  perEventPriceCents: 0,  // Changed to 0 since no entry fee
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: false,
    allowWaitlist: true
  },

  philosophy: {
    title: "Art After Dark: When Nightlife Becomes Gallery Space",
    sections: [
     
      {
        type: 'paragraph',
        content: "This is what happens when a ConnectCo community member takes a leap—joining forces with other talented artists to transform a Shibuya nightclub's B1 floor into an intimate gallery space for one extraordinary evening. No sterile white walls, no pretentious art speak, no awkward wine-and-cheese mingling. Just honest creativity in an unexpected underground setting, and we're here to celebrate that one of the voices belongs to our community."
      },
      {
        type: 'paragraph',
        content: "Our community member is opening their artistic world alongside fellow artists, and we're showing up to support that courage. A Shibuya club's basement level—normally alive with weekend nightlife energy—becomes something entirely different when artists take over. Club lighting illuminates artwork instead of dance floors. The raw, industrial aesthetic that usually frames entertainment now frames vulnerability and expression from diverse creative voices, including someone from our own ConnectCo family."
      },
      {
        type: 'header',
        content: "Why This Matters"
      },
      {
        type: 'paragraph',
        content: "Multi-artist exhibits in club basements aren't common in Tokyo, and there's a reason this feels special. It's the juxtaposition—creativity thriving where you least expect it, beauty emerging in spaces designed for different purposes. It's also deeply personal: one of the exhibiting artists is from our community, inviting us into their creative process alongside other talented voices, showing how diverse perspectives can coexist in one underground space."
      },
      {
        type: 'paragraph',
        content: "The intimate format (just 10 people) means this isn't about crowds and consumption—it's about connection and genuine appreciation. You'll have space to actually engage with each artist's work, ask questions, understand the stories behind different pieces. And knowing that one of those artists is someone from ConnectCo makes this more than just an art exhibit—it's community showing up to support one of our own."
      },
     
      {
        type: 'paragraph',
        content: "⏰ IMPORTANT: Please arrive by 5:45 PM (15 minutes before 6:00 PM). This ensures we can all enter together and the artists can properly introduce the exhibit at 6:00 PM sharp. Late arrivals disrupt the flow of this intimate, carefully curated experience."
      },
      {
        type: 'header',
        content: "Entry & Venue Requirements"
      },
      {
        type: 'paragraph',
        content: "Entry is completely free if you arrive between 6:00 PM and 7:00 PM. The venue requires one drink purchase per person to support their space—a small gesture that transforms a nightclub into our gallery for the evening. Think of it as buying the artist (and yourself) a drink while experiencing their work in an unconventional underground setting."
      },
      {
        type: 'header',
        content: "The Experience"
      },
      {
        type: 'paragraph',
        content: "Expect to spend three hours in an underground space that defies expectations. The B1 floor's layout means the exhibit unfolds as you explore—different works, different artists, different perspectives converging in one atmospheric basement setting. The venue's natural drama (think atmospheric lighting, architectural interest, unexpected corners) becomes part of the art itself."
      },
      {
        type: 'paragraph',
        content: "The artists will be present throughout the evening, available for genuine conversation about their work, processes, and visions. This isn't a formal presentation—it's an invitation into their creative worlds, with special significance knowing we're directly supporting a ConnectCo community member pursuing their artistic path. You'll move through the space at your own pace, engage with pieces that speak to you, discover how different artistic voices can harmonize in one shared space, and connect with fellow attendees who appreciate that art is better when it's shared with thoughtful people."
      },
      {
        type: 'paragraph',
        content: "The venue offers a full bar beyond the required one-drink minimum if you'd like to continue sipping throughout the evening. There's something perfect about enjoying a cocktail while contemplating artwork in an underground space designed for celebration—it reminds us that art doesn't have to be serious to be meaningful."
      },
      {
        type: 'header',
        content: "The Venue"
      },
      {
        type: 'paragraph',
        content: "Our selected venue is one of Shibuya's most distinctive entertainment spaces—specifically, the atmospheric B1 floor of a multi-level club that normally hosts everything from DJ events to fashion parties. For this evening, the basement level becomes a shared canvas for multiple artists, including our community member. The industrial-chic aesthetic, dramatic underground lighting possibilities, and intimate layout make it the perfect backdrop for art that refuses to be confined to traditional gallery spaces."
      },
      {
        type: 'header',
        content: "Drinks & Refreshments"
      },
      {
        type: 'bullets',
        items: [
          "Entry is free from 6:00 PM to 7:00 PM",
          "One drink purchase required (venue minimum to use the space)",
          "Full bar available throughout the evening for additional purchases",
          "Standard venue pricing for all beverages",
          "Cash and credit cards accepted",
          "Choose any drink from beer to cocktails to soft drinks—your call"
        ]
      },
      {
        type: 'header',
        content: "Perfect For Those Who"
      },
      {
        type: 'bullets',
        items: [
          "Want to support a ConnectCo community member's creative journey",
          "Appreciate art that breaks out of traditional gallery confines",
          "Love unexpected experiences in unconventional underground spaces",
          "Seek intimate cultural events over massive openings",
          "Believe the best conversations happen around creative expression",
          "Are curious about where nightlife and art intersect",
          "Value authenticity over pretense in artistic spaces",
          "Enjoy discovering diverse artistic perspectives in one curated space"
        ]
      },
    
     
      {
        type: 'header',
        content: "Location & Getting There"
      },
      {
        type: 'paragraph',
        content: "The exhibit takes place on the B1 floor of a premier entertainment venue in the heart of Shibuya. The underground space offers an industrial-chic aesthetic that perfectly complements unconventional art experiences. Easy access from Shibuya Station makes this an effortless evening out. The exact venue name and address will be shared with confirmed attendees 24 hours before the event."
      },
      {
        type: 'header',
        content: "What to Bring"
      },
      {
        type: 'paragraph',
        content: "Bring an open mind and curiosity about both the art and the artists behind it. Also bring cash or card for your one required drink purchase (and any additional drinks you'd like throughout the evening). This is a small group experience where diverse creative works spark conversation and the conversation enriches appreciation. Come ready to explore, ask questions, and especially to celebrate the ConnectCo community member who's brave enough to share their creative vision alongside other talented artists."
      },
      {
        type: 'header',
        content: "Supporting Our Community Member Artist"
      },
      {
        type: 'paragraph',
        content: "This event represents something at the core of ConnectCo—community members supporting each other's growth, creativity, and courage. By attending, you're showing up for a fellow community member who's put themselves out there in the wider art world. That kind of support creates the foundation for authentic community, where people feel safe to be vulnerable, creative, and genuinely themselves. While you'll experience work from multiple talented artists, knowing one of them is from our community makes this exhibition personally meaningful for all of us."
      }
    ]
  },
  
  whatToExpect: [
    "Free entry from 6:00 PM to 7:00 PM (one drink purchase required)",
    "Multi-artist exhibit featuring a ConnectCo community member on unconventional Shibuya club B1 floor",
    "Follow-up email with final venue details and arrival instructions",
    "Underground gallery experience with club lighting and atmosphere",
    "Small group (max 10 people) for meaningful engagement with artists and artwork",
    "Full bar available for required drink minimum and additional purchases",
    "Three-hour evening from 6:00 PM to 9:00 PM",
    "Artists present throughout for genuine conversation and questions"
  ],
  
  venue: { 
    name: "TBD - Shibuya Club (B1 Floor), Tokyo",
    description: "The atmospheric B1 floor of a distinctive multi-level entertainment venue in the heart of Shibuya, featuring industrial-chic design, dramatic underground lighting capabilities, and intimate spaces perfect for art that defies traditional gallery settings.",
    fullAddress: "TBD - Shibuya, Tokyo (venue name and exact location revealed to confirmed attendees 24 hours prior)",
    priceRange: "Free entry (6-7 PM) + one drink purchase required",
    amenities: ["Underground B1 floor space", "Full bar service", "Industrial-chic aesthetic", "Dramatic lighting", "Easy Shibuya Station access", "Cash & credit accepted", "Unique club-turned-gallery atmosphere"]
  },
  
  memberBenefits: [
    {
      icon: "🎨",
      title: "Community Support",
      description: "Show up for a fellow ConnectCo member exhibiting alongside other artists"
    },
    {
      icon: "✨", 
      title: "Unconventional Space",
      description: "Experience art on a club's B1 floor—where expectations go underground"
    },
    {
      icon: "👥",
      title: "Intimate Engagement", 
      description: "Just 10 people means real conversation with multiple artists and their work"
    },
    {
      icon: "🌃",
      title: "Shibuya After Dark",
      description: "Art, atmosphere, and authentic connection in Tokyo's most electric neighborhood"
    }
  ],
  
  relatedEvents: ['community-brunch-january-2026', 'french-vietnamese-lunch-january-2026', 'fika', 'weekly-fika'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "one-time",
  dayOfWeek: "Friday", 
  focusArea: "Cultural experience and community support",
  experienceType: "intimate multi-artist exhibition",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "ConnectCo Community & Multi-Artist Collective",
    bio: "Celebrating creativity and vulnerability—supporting our community member exhibiting alongside talented artists",
    avatar: "connectco-cultural-hosts.jpg"
  },

  // Special features for art exhibit experience  
  culturalFocus: {
    style: "Contemporary multi-artist exhibit featuring ConnectCo community member in unconventional underground nightlife setting",
    groupSize: "Intimate gathering of 10 for meaningful engagement",
    duration: "3 hours of self-paced exploration and conversation",
    artistPresence: "Multiple artists including ConnectCo community member present throughout for genuine dialogue"
  },
  
  includedExperience: {
    atmosphere: "Underground club aesthetic transformed into intimate gallery space",
    connection: "Small group experience supporting a ConnectCo member alongside appreciating diverse artists", 
    quality: "Original artwork from multiple artists including our community member",
    pace: "Three unhurried hours to explore, contemplate, and connect",
    pricing: "Free entry (6-7 PM) with one drink purchase requirement"
  },
  
  culturalPhilosophy: "The bravest thing any artist does is share their work—and the most meaningful thing a community does is show up to witness that courage",
  
  idealFor: [
    "Community members who value supporting each other's creative pursuits",
    "Anyone curious about art in unexpected, unconventional underground spaces",
    "People seeking intimate cultural experiences over massive gallery openings",
    "Those who believe art and conversation naturally enhance each other",
    "Anyone tired of pretentious art world atmospheres",
    "Members looking to deepen community connections through shared cultural appreciation",
    "Creatives and creative-appreciators who value authenticity over formality",
    "Those who appreciate diverse artistic voices in one curated space"
  ],
  
  preparationTips: [
    "Arrive by 5:45 PM (15 minutes early) for 6:00 PM group welcome",
    "Bring cash or card for your required one-drink purchase",
    "Come with genuine curiosity about all the artwork and each artist's journey",
    "Prepare thoughtful questions—the artists will be present and available",
    "Dress however makes you feel comfortable (casual club-appropriate attire)",
    "Bring openness to both the art and your fellow attendees",
    "Remember we're here to support our community member alongside appreciating all artists"
  ],
  
  uniqueAspects: [
    "Free entry from 6:00 PM to 7:00 PM with just one drink purchase",
    "Features ConnectCo community member exhibiting alongside other talented artists",
    "Art exhibit on B1 floor of functioning Shibuya nightclub—truly unconventional underground setting",
    "Limited to 10 attendees for genuine intimacy and engagement",
    "Artists present throughout for authentic conversation and connection",
    "Underground venue creates intimate, atmospheric exhibition experience",
    "Club lighting and B1 aesthetic become part of the artistic presentation",
    "Demonstrates ConnectCo's commitment to supporting member creativity and vulnerability",
    "Full bar available transforms typical gallery reception into relaxed evening out",
    "Community shows up to support one of our own in the wider art world"
  ]
},


{
  id: 'french-vietnamese-lunch-january-2026',
  eventType: 'foodie',
  iconType: 'image',
  iconImage: '/images/events/FrenchVietnameseLunch.jpg',
  iconValue: '🍜',
  title: "French-Vietnamese Culinary Journey, Tokyo (Social Coffee - Optional)",
  description: "Where Vietnam's soulful flavors meet French culinary artistry. An intimate lunch experience featuring modern Vietnamese cuisine refined through French technique—fresh spring rolls, fragrant pho, and dishes that honor tradition while embracing innovation.",
  waitlist: "0",
  next: "January 24, 2026",
  time: "1:00 PM - 3:00 PM",
  eventDateTime: "2026-01-24T04:00:00Z",  // ✅ 1:00 PM JST = 4:00 AM UTC

  location: "TBD - Restaurant, Tokyo",
  color: "from-red-500 to-amber-600",
  capacity: 6,
  difficulty: "Casual Fine Dining",
  tags: ["French-Vietnamese", "Modern Vietnamese", "Lunch", "Culinary Experience",  "Tokyo"],
  featured: true,
  slug: 'french-vietnamese-lunch-january-2026',
  
  // City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan", 
  timezone: "Asia/Tokyo",
  currency: "JPY",
  cost: "¥1,500-3,900 per person (drinks separate)",

  // Payment configuration properties
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },

  philosophy: {
    title: "French-Vietnamese Culinary Journey: Where Two Traditions Become One",
    sections: [
      {
        type: 'header',
        content: "For a seamless registration experience, please review the information below before reserving your spot."
      },
      {
        type: 'paragraph',
        content: "Join us for an intimate lunch that tells the story of two culinary traditions woven together—where Vietnam's vibrant, aromatic soul meets the refined precision of French technique. This is modern Vietnamese cuisine at its finest: respectful of heritage, elevated through craft, and utterly delicious."
      },
      {
        type: 'paragraph',
        content: "Our selected venue brings together the best of both worlds. Imagine fresh summer rolls with herbs so fragrant you can taste the garden. Pho broth simmered for hours with French finesse. Banh mi reimagined as French dip sandwiches. Every dish honors Vietnamese home cooking while embracing the culinary techniques that make French cuisine legendary."
      },
      {
        type: 'header',
        content: "What Makes This Experience Special"
      },
      {
        type: 'paragraph',
        content: "This isn't fusion for the sake of being clever—it's the genuine expression of a chef who grew up with Vietnamese flavors and trained in French kitchens. The result is cuisine that feels both familiar and revelatory, where every bite reveals the care, history, and innovation behind it."
      },
      {
        type: 'paragraph',
        content: "You'll taste the difference in the details: spring rolls wrapped with precision, pho that balances depth and clarity, and seasonal ingredients that connect you to Japan's rhythms while honoring Vietnamese tradition. This is food that respects its roots while growing confidently into something new."
      },
     
      {
        type: 'paragraph',
        content: "⏰ IMPORTANT: Please arrive by 12:45 PM (15 minutes before 1:00 PM). This ensures everyone is seated and settled so we can begin our culinary journey together at 1:00 PM sharp. Late arrivals disrupt the carefully choreographed dining experience for the entire group."
      },
      {
        type: 'header',
        content: "The Culinary Experience"
      },
      {
        type: 'paragraph',
        content: "Expect the classics reimagined through a French lens—pho with broths that reveal layers of complexity, fresh spring rolls that showcase pristine ingredients, banh mi served with rich dipping broth in the French dip tradition. You'll encounter dishes like Burmese tea salad with fermented tea leaves and crispy legumes, alongside seasonal creations that marry Japanese ingredients with Vietnamese-French technique."
      },
      {
        type: 'paragraph',
        content: "The setting matches the cuisine—elegant marble tables, thoughtful presentation, and an atmosphere that balances sophistication with the warmth of Vietnamese hospitality. This is fine dining that never forgets food is meant to bring people together, served in a space that honors both culinary excellence and genuine connection."
      },
      {
        type: 'header',
        content: "Post-Lunch Social Coffee (Optional)"
      },
      {
        type: 'paragraph',
        content: "For those who'd like to extend the afternoon, we'll gather at a nearby café for coffee and continued conversation. This optional extension (approximately 60 minutes) offers a perfect opportunity to reflect on the meal, deepen connections made at lunch, and transition gently back to your day. Coffee and any additional refreshments are ordered and paid for individually."
      },
      {
        type: 'header',
        content: "Payment & Pricing"
      },
      {
        type: 'bullets',
        items: [
          "Lunch dishes typically range from ¥1,500 to ¥3,900 per person",
          "Each person pays for what they order",
          "Drinks ordered and paid separately",
          "Cash and credit cards accepted"
        ]
      },
      {
        type: 'header',
        content: "Perfect For Those Who"
      },
      {
        type: 'bullets',
        items: [
          "Appreciate the artistry behind cross-cultural cuisine",
          "Love Vietnamese food and are curious about French influences",
          "Seek intimate dining experiences with thoughtful people",
          "Believe food tells stories worth listening to",
          "Want to discover Tokyo's most innovative culinary voices",
          "Are looking for lunch that feels like an occasion, not just a meal"
        ]
      },
     
      {
        type: 'header',
        content: "Location & Getting There"
      },
      {
        type: 'paragraph',
        content: "Our selected venue is located in one of Tokyo's most sophisticated neighborhoods, just minutes from the Station. The restaurant features elegant marble tables and an atmosphere that balances refinement with warmth—the perfect setting for modern Vietnamese cuisine. The venue name and exact address will be shared with confirmed attendees 24 hours before the event."
      },
      {
        type: 'header',
        content: "What to Bring"
      },
      {
        type: 'paragraph',
        content: "Bring an open mind and an appetite for both excellent food and genuine conversation. This is a small group experience where the food sparks connection and the conversation enriches the meal. Come ready to savor, share, and discover something new about the intersection of Vietnamese soul and French precision."
      }
    ]
  },
  
  whatToExpect: [
    "Intimate French-Vietnamese lunch in sophisticated Tokyo setting",
    "Modern Vietnamese cuisine elevated through French culinary technique",
    "Signature dishes: fresh spring rolls, artisanal pho, French dip banh mi",
    "Small group dining (max 8 people) for quality conversation",
    "Optional post-lunch coffee gathering at nearby café",
    "120-minute culinary journey from 1:00 PM to 3:00 PM"
  ],
  
  venue: { 
    name: "TBD - Restaurant, Tokyo",
    description: "An award-winning modern Vietnamese restaurant where French culinary technique meets traditional Vietnamese flavors, featuring elegant marble tables and sophisticated yet welcoming ambiance.",
    fullAddress: "TBD - Tokyo (venue name and exact location revealed to confirmed attendees 24 hours prior)",
    priceRange: "¥1,500-3,900 per person (drinks separate)",
    amenities: ["Modern Vietnamese cuisine", "French culinary techniques", "Elegant marble tables", "Sophisticated atmosphere", "Easy station access", "Cash & credit accepted"]
  },
  
  memberBenefits: [
    {
      icon: "🍜",
      title: "Culinary Artistry",
      description: "French technique elevates Vietnamese tradition into something extraordinary"
    },
    {
      icon: "👥", 
      title: "Intimate Connection",
      description: "Small group ensures meaningful conversation alongside exceptional food"
    },
    {
      icon: "🌿",
      title: "Heritage Meets Innovation", 
      description: "Dishes that honor Vietnamese roots while embracing culinary evolution"
    },
    {
      icon: "☕",
      title: "Extended Connection",
      description: "Optional post-lunch coffee to continue the afternoon's connections"
    }
  ],
  
  relatedEvents: ['community-brunch-january-2026', 'curated-spanish-tapas-december-2025', 'fika', 'weekly-fika'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "monthly",
  dayOfWeek: "Friday", 
  focusArea: "French-Vietnamese culinary experience",
  experienceType: "intimate fine casual dining",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark & ConnectCo Team",
    bio: "Curating intimate culinary experiences where exceptional food meets genuine connection",
    avatar: "mark-foodie-hosts.jpg"
  },

  // Special features for French-Vietnamese experience  
  culinaryFocus: {
    style: "Modern Vietnamese with French culinary technique",
    groupSize: "Intimate gathering of 8 for quality conversation",
    duration: "120 minutes of unhurried culinary exploration",
    expertise: "Award-winning chef trained in French technique with Vietnamese heritage"
  },
  
  includedExperience: {
    atmosphere: "Elegant yet welcoming, sophisticated without pretense",
    connection: "Shared table experience with fellow food enthusiasts", 
    quality: "Dishes that honor tradition while embracing innovation",
    pace: "Leisurely lunch allowing you to savor both food and conversation"
  },
  
  foodiePhilosophy: "The best cuisine doesn't choose between tradition and innovation—it honors both by letting each inform the other",
  
  idealFor: [
    "Food enthusiasts who appreciate culinary craftsmanship",
    "Anyone curious about cross-cultural cuisine done with integrity",
    "People seeking intimate dining experiences with substance",
    "Those who believe meals are better when shared with interesting people",
    "Anyone looking for lunch that transcends the ordinary",
    "Community members ready for culinary exploration"
  ],
  
  preparationTips: [
    "Arrive by 12:45 PM (15 minutes early) for a 1:00 PM start",
    "Share any dietary restrictions when you RSVP—essential for this experience",
    "Come with curiosity about both the cuisine and your dining companions",
    "Plan for optional post-lunch coffee if you'd like to extend the afternoon",
    "Bring an appetite for conversation as much as for exceptional food"
  ],
  
  uniqueAspects: [
    "Genuine French-Vietnamese fusion rooted in chef's heritage and training",
    "Small intimate group ensures both excellent food and real connection",
    "Modern Vietnamese cuisine elevated through French technique",
    "Seasonal Japanese ingredients woven into Vietnamese-French traditions",
    "Optional post-lunch coffee gathering to extend the experience",
    "Award-winning culinary approach that respects both traditions",
    "Dining experience that honors food as both art and community"
  ]
},

{
  id: 'community-brunch-january-2026',
  eventType: 'foodie',
  iconType: 'image',
  iconImage: '/images/events/CommunityBrunch.jpg',
  iconValue: '🥞',
  title: "The Saturday Brunch Table (Tokyo)",
  description: "Leisurely weekend brunch at a beloved Tokyo restaurant. Good food, great conversation, and the kind of unhurried morning that makes weekends feel like weekends.",
  waitlist: "0",
  next: "January 17, 2025",
  time: "11:30 AM - 1:00 PM",
  eventDateTime: "2026-01-17T02:30:00Z",  // ✅ 11:30 AM JST = 2:30 AM UTC

  location: "TBD - Restaurant, Tokyo",
  color: "from-amber-500 to-green-600",
  capacity: 6,
  difficulty: "Casual Dining",
  tags: ["Brunch", "Weekend Dining", "Community", "Tokyo", "Good Conversation"],
  featured: true,
  slug: 'community-brunch-january-2026',
  
  // City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan", 
  timezone: "Asia/Tokyo",
  currency: "JPY",
  cost: "¥1,500-2,500 per person (drinks separate)",

  // Payment configuration properties
  paymentEnabled: true,
  requiresSubscription: true,
  perEventPriceCents: 2000,     // ¥2,000 cover charge
  currency: "JPY",
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: true,
    allowWaitlist: true
  },

  philosophy: {
    title: "Community Weekend Brunch: Where Good Food Meets Great Conversation",
    sections: [
      {
        type: 'header',
        content: "For a seamless registration experience, please review the information below and visit the Details tab before reserving your spot."
      },
      {
        type: 'paragraph',
        content: "Join us for a leisurely weekend brunch at one of Tokyo's welcoming neighborhood restaurants. This is brunch the way it should be—good food, genuine conversation, and the kind of unhurried morning that makes you remember why weekends exist."
      },
      {
        type: 'paragraph',
        content: "We've chosen a spot that strikes the perfect balance: comfortable enough to linger over coffee refills, interesting enough to spark conversation, and welcoming enough that everyone feels at home. Whether you're a weekend brunch regular or trying the ritual for the first time, you'll find good company here."
      },
      {
        type: 'header',
        content: "What Makes This Different"
      },
      {
        type: 'paragraph',
        content: "This isn't about finding the trendiest spot or the most Instagram-worthy plate. It's about sharing a genuinely good meal with interesting people, having conversations that meander pleasantly, and building the kind of weekend connections that carry into the week ahead."
      },
      {
        type: 'paragraph',
        content: "Think of it as the brunch you'd organize with friends—except we've handled the reservation, gathered the group, and made sure everyone arrives ready to connect over good food."
      },
      {
        type: 'header',
        content: "Registration Process & Important Timing"
      },
      {
        type: 'paragraph',
        content: "After you register for this brunch, you will receive a follow-up email within 24 hours requesting:"
      },
      {
        type: 'bullets',
        items: [
          "Full name and phone number for reservation confirmation",
          "Any dietary restrictions or food allergies"
        ]
      },
      {
        type: 'paragraph',
        content: "⏰ IMPORTANT: Please arrive by 11:45 AM (15 minutes before noon). This allows us to get everyone seated and settled so we can begin our brunch together at 12:00 PM sharp. Late arrivals disrupt the experience for everyone."
      },
      {
        type: 'header',
        content: "What to Expect at Brunch"
      },
      {
        type: 'paragraph',
        content: "Expect the classics done well—eggs prepared the way you like them, hearty breakfast favorites, fresh pastries, and satisfying plates that make you want to order one more coffee and stay a little longer. The kind of brunch menu where everything sounds appealing and nothing disappoints."
      },
      {
        type: 'paragraph',
        content: "The atmosphere is relaxed and conversational. Weekend light fills the space. There's no rush, no performance, just the simple pleasure of a good meal with good people. This is what weekend mornings are for."
      },
      {
        type: 'header',
        content: "Payment & Pricing"
      },
      {
        type: 'bullets',
        items: [
          "Brunch dishes typically range from ¥1,500 to ¥2,500 per person",
          "Drinks ordered and paid separately",
          "Cash only accepted",
          "Each person orders and pays individually"
        ]
      },
      {
        type: 'header',
        content: "Perfect For Those Who"
      },
      {
        type: 'bullets',
        items: [
          "Miss the ritual of a proper weekend brunch",
          "Appreciate good conversation over a leisurely meal",
          "Want to meet people outside of evening networking events",
          "Believe the best connections happen over food and coffee",
          "Are looking for the kind of weekend morning Tokyo rarely offers space for"
        ]
      },
      {
        type: 'header',
        content: "Important Cancellation Policy"
      },
      {
        type: 'paragraph',
        content: "Due to the intimate nature of this experience and advance coordination required with the restaurant, cancellations must be made at least 72 hours before the event. Cancellations made with less than 72 hours notice will be charged a ¥2,000 cancellation fee. This policy ensures fairness to others on our waitlist and allows the restaurant adequate time to adjust their preparation."
      },
      
      {
        type: 'header',
        content: "Location & Getting There"
      },
      {
        type: 'paragraph',
        content: "Our selected venue is conveniently located in central Tokyo with easy access via train. The restaurant offers a comfortable, welcoming atmosphere—the kind of neighborhood spot that feels familiar even on your first visit. The venue name and exact address will be shared with confirmed attendees 24 hours before the event."
      }
    ]
  },
  
  whatToExpect: [
    "Relaxed weekend brunch at a welcoming Tokyo restaurant",
    "Follow-up email requesting dietary preferences and contact information",
    "Intimate dining experience with fellow community members",
    "Classic brunch favorites done well—nothing trendy, just good",
    "Unhurried weekend atmosphere perfect for conversation",
    "Approximately 2-hour brunch with time to linger over coffee"
  ],
  
  venue: { 
    name: "TBD - Restaurant, Tokyo",
    description: "A comfortable neighborhood restaurant in Tokyo offering classic brunch favorites in a welcoming, conversational atmosphere.",
    fullAddress: "TBD - Central Tokyo (venue name and exact location revealed to confirmed attendees 24 hours prior)",
    priceRange: "¥1,500-2,500 per person (drinks separate)",
    amenities: ["Brunch menu", "Weekend service", "Comfortable atmosphere", "Easy train access", "Cash & credit accepted"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Unhurried Morning",
      description: "The weekend brunch experience Tokyo's pace rarely allows"
    },
    {
      icon: "👥", 
      title: "Genuine Connection",
      description: "Small group ensures real conversation over good food"
    },
    {
      icon: "🥞",
      title: "Brunch Done Right", 
      description: "Classic favorites that satisfy without the need to be trendy"
    },
    {
      icon: "☀️",
      title: "Weekend Ritual",
      description: "Start your weekend with community and conversation"
    }
  ],
  
  relatedEvents: ['curated-spanish-tapas-december-2025', 'fika', 'weekly-fika', 'happy-hour'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "monthly",
  dayOfWeek: "Saturday", 
  focusArea: "weekend brunch gathering",
  experienceType: "communal dining",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark & ConnectCo",
    bio: "Creating space for meaningful weekend connections over good food and unhurried conversation",
    avatar: "mark-foodie-hosts.jpg"
  },

  // Special features for brunch experience  
  culinaryFocus: {
    style: "Classic brunch favorites done well",
    groupSize: "Intimate gathering for quality conversation",
    duration: "2 hours of leisurely weekend brunch",
    expertise: "Comfortable neighborhood dining"
  },
  
  includedExperience: {
    atmosphere: "Relaxed, welcoming weekend brunch setting",
    connection: "Shared table experience with community members", 
    quality: "Good food without pretense or performance",
    pace: "Unhurried dining that honors the weekend ritual"
  },
  
  foodiePhilosophy: "Great brunch isn't about what's trendy—it's about slowing down, enjoying good food, and the people you share it with",
  
  idealFor: [
    "Anyone who misses a proper weekend brunch ritual",
    "People seeking weekend connections beyond evening events",
    "Those who believe the best conversations happen over food",
    "Community members looking for unhurried Saturday mornings",
    "Anyone tired of Tokyo's relentless pace and ready to slow down"
  ],
  
  preparationTips: [
    "Arrive by 11:15 AM (15 minutes early) for a 11:30 PM start",
    "Come ready to linger—there's no rush to leave",
    "Bring an appetite for conversation as much as food",
    "Let us know any dietary needs when you RSVP"
  ],
  
  uniqueAspects: [
    "The weekend brunch ritual Tokyo rarely makes space for",
    "Small group size ensures genuine connection",
    "No pressure to order the 'right' thing—just what sounds good",
    "Conversation flows as naturally as the coffee refills",
    "Weekend morning connection that carries into the week ahead"
  ]
},




{
  id: 'authentic-swedish-fika-december-saturday-2025',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo’s Beloved Saturday Tradition (Dec)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "December 20, 2025",
  time: "10:00 AM - 1:00 PM",
  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 25,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee", "Riverside", "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-december-saturday-2025',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Nestled along the serene banks of a gently flowing river, our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a waterside community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
      {
        type: 'paragraph',
        content: "Riverside Tranquility: Enjoy our fika in a setting that embodies the love for nature and peaceful moments. With sweeping views of flowing water and verdant parkland, this location offers the perfect backdrop for the contemplative, restorative experience that defines true fika."
      },
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. In this riverside sanctuary, surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
          "Riverside Reflection: Take advantage of the peaceful waterside setting to practice mindfulness and finding perfect balance and contentment in the moment"
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene riverside, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting", "Riverside views", "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
    {
      icon: "🌊",
      title: "Riverside Sanctuary",
      description: "Serene waterside setting with panoramic views creating perfect backdrop for contemplative fika"
    }
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},

{
  id: 'authentic-swedish-fika-december-sunday-2025',  
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo’s Beloved Sunday Tradition (Dec)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's bustling cityline. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "December 14, 2025",
  time: "10:00 AM - 1:00 PM",
  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 25,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee", "Riverside", "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-december-sunday-2025',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Nestled along the serene banks of a gently flowing river, our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a waterside community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
      {
        type: 'paragraph',
        content: "Riverside Tranquility: Enjoy our fika in a setting that embodies the love for nature and peaceful moments. With sweeping views of flowing water and verdant parkland, this location offers the perfect backdrop for the contemplative, restorative experience that defines true fika."
      },
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. In this riverside sanctuary, surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
          "Riverside Reflection: Take advantage of the peaceful waterside setting to practice mindfulness and finding perfect balance and contentment in the moment"
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene riverside, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting", "Riverside views", "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
    {
      icon: "🌊",
      title: "Riverside Sanctuary",
      description: "Serene waterside setting with panoramic views creating perfect backdrop for contemplative fika"
    }
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},


{
  id: 'authentic-swedish-fika-november-2025', // monthly-fika-october-2025
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Monthly Swedish Fika: Tokyo's Beloved Sunday Tradition (Nov)",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's serene riverside. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "November 29, 2025",
  time: "10:00 AM - 1:00 PM",
  location: "TBD - 3rd wave coffee venue (Central Tokyo - details sent one day before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 25,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee", "Riverside", "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'authentic-swedish-fika-november-2025',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "What's a belief or assumption you held at the beginning of this year that you've since reconsidered or let go of?",
        japanese: "今年の初めに持っていた信念や思い込みで、その後見直したり手放したりしたものは何ですか？",
        context: "As the year winds down and we enter its reflective season, we often find that our understanding of ourselves and the world has shifted in unexpected ways. This could be about work, relationships, your own capabilities, or how you spend your time. What made you reconsider it, and what has taken its place?"
      },
      {
        number: 2,
        english: "If you could have a conversation with yourself from five years ago, what's one thing you'd want them to know that they were worried about unnecessarily?",
        japanese: "5年前の自分と話せるとしたら、当時無駄に心配していたことについて、どんなことを伝えたいですか？",
        context: "Sometimes our biggest worries don't unfold the way we feared, or we discover we were more resilient than we thought. Reflecting on what used to keep you up at night - but ultimately worked out differently or taught you something valuable - can reveal how much you've grown and what truly matters to you now."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Tokyo's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
      },
      {
        type: 'paragraph',
        content: "Nestled along the serene banks of a gently flowing river, our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a waterside community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
      {
        type: 'paragraph',
        content: "Riverside Tranquility: Enjoy our fika in a setting that embodies the love for nature and peaceful moments. With sweeping views of flowing water and verdant parkland, this location offers the perfect backdrop for the contemplative, restorative experience that defines true fika."
      },
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. In this riverside sanctuary, surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
          "Riverside Reflection: Take advantage of the peaceful waterside setting to practice mindfulness and finding perfect balance and contentment in the moment"
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - (Nakameguro) Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene riverside, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one day prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting", "Riverside views", "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
    {
      icon: "🌊",
      title: "Riverside Sanctuary",
      description: "Serene waterside setting with panoramic views creating perfect backdrop for contemplative fika"
    }
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},



{
  id: 'lisbon-swedish-fika-november-2025',
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Inaugural Monthly (November) Fika: Lisbon's Beloved Tradition",
  description: "Experience fika culture at Baobá Café Bica, a charming Brazilian specialty coffee shop in Lisbon's historic Bica district. Single-origin Brazilian coffee, house-roasted beans, and meaningful connections.",
  waitlist: "0",
  next: "November 16, 2025",
  time: "10:00 AM - 1:00 PM",
  location: "Baobá Café Bica - Bica District (details sent one week before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 10,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Brazilian Coffee", "Single-Origin", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'lisbon-swedish-fika-november-2025',
  
  // ✅ City-related fields
  city: "lisbon",
  cityDisplay: "Lisbon",
  country: "Portugal",
  timezone: "Europe/Lisbon",
  currency: "EUR",

  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "November reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "As we approach the end of the year, what's one experience or lesson from this year that has stayed with you?",
      
        context: "November is a time of reflection as the year winds down. Think about moments that shaped you, challenges that taught you something valuable, or small discoveries that changed your perspective. Share something meaningful from your journey this year."
      },
      {
        number: 2,
        english: "What's one thing you're hoping to create, experience, or bring into your life as we move toward the new year?",
      
        context: "As November bridges us from reflection to intention, consider what you'd like to cultivate - a new habit, a creative project, a connection, an adventure, or simply a way of being. What calls to you as you look ahead?"
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika at Baobá Café in Lisbon's Historic Bica District",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at Baobá Café Bica, a charming Brazilian specialty coffee shop in the heart of Lisbon's historic Bica neighborhood. This wonderful farm-to-roaster café represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional single-origin Brazilian coffee and delicious treats. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Lisbon."
      },
      {
        type: 'paragraph',
        content: "Nestled on Rua de São Paulo in the picturesque Bica district, Baobá Café sits in one of Lisbon's most charming and historic neighborhoods - famous for its steep streets, iconic yellow funicular, and bohemian atmosphere. This specialty coffee shop embodies everything that makes fika magical: exceptional coffee grown on their own Brazilian farm, expert roasting done on-site, and a warm, welcoming space that brings together coffee lovers from around the world. The Bica district itself feels like stepping into old Lisbon, with its narrow cobblestone streets and vibrant community spirit creating the perfect backdrop for meaningful conversation and connection."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Farm-to-Roaster Excellence: Experience coffee from Baobá's own Brazilian farm, with beans scoring above 80 points on the Specialty Coffee Association scale. Every cup showcases their commitment to quality from cultivation to roasting, with varieties like Icatu, Obatã Amarelo, and other exceptional Brazilian coffees."
      },
      {
        type: 'paragraph',
        content: "House-Roasted on Probat: Enjoy expertly roasted coffee prepared fresh on their Probat roaster right in the café. Ancient roasting techniques meet modern expertise to unlock the full potential of each batch, creating exceptional flavor profiles that honor Brazil's coffee heritage."
      },
      {
        type: 'paragraph',
        content: "Brazilian Delights & Pastries: Indulge in authentic Brazilian treats like pão de queijo (cheese bread), brigadeiro (in chocolate and coffee flavors), açaí smoothies, alongside European pastries, banana bread, and gluten-free options - all perfectly complementing your coffee experience."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture in Historic Bica"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Lisbon's most charming neighborhood. In this cozy café, surrounded by the aroma of freshly roasted Brazilian coffee and the vibrant energy of the Bica district, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "Baobá Café's dedication to quality and their unique farm-to-roaster approach means every element has been thoughtfully curated. From growing the coffee with care and respect for the land in Brazil, to roasting it with precision in Lisbon, to serving it with passion - this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another in one of Europe's most beautiful cities."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Brazilian Coffee Heritage: Step into a space where Brazilian coffee culture meets Portuguese hospitality, creating an environment that celebrates both traditions while fostering genuine connection",
          "Meaningful Conversations: Experience the art of unhurried conversation in historic Bica, where ideas flow as smoothly as the single-origin coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about Brazilian coffee cultivation and roasting while enjoying exceptional specialty coffee in one of Lisbon's most picturesque and bohemian neighborhoods"   
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Brazilian treats and European pastries at café prices (including gluten-free options)",
          "Single-origin Brazilian specialty coffee with espresso from €1, expertly prepared by passionate baristas",
          "Baobá Espresso Experience: specialty microbatch coffee paired with artisan chocolate bonbons",
          "Credit cards and electronic payments accepted"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful inaugural month as we continue our beloved fika tradition in Lisbon. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional Brazilian specialty coffee and delicious treats in one of Europe's most charming and historic settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Taste authentic Brazilian treats like pão de queijo, brigadeiro, and European pastries (gluten-free options available)",
    "Enjoy single-origin Brazilian specialty coffee grown on Baobá's farm and roasted on-site with a Probat roaster",
    "Connect with fellow community members in historic Bica, one of Lisbon's most charming bohemian neighborhoods",
    "Learn about Brazilian coffee cultivation, expert roasting techniques, and specialty coffee culture",
    "Practice the art of unhurried conversation and meaningful connection in a warm, welcoming atmosphere"
  ],
  
  venue: { 
    name: "Baobá Café Bica",
    description: "A charming Brazilian specialty coffee shop in Lisbon's historic Bica district, featuring single-origin coffee from their own farm in Brazil, roasted on-site with a Probat roaster. Known for exceptional coffee scoring 80+ points on the SCA scale, authentic Brazilian treats, and warm hospitality.",
    fullAddress: "Rua de S. Paulo 256 a 258, 1200-430 Lisboa, Portugal",
    priceRange: "Free event - Brazilian treats and specialty coffee at café prices (espresso from €1)",
    amenities: ["Farm-to-roaster coffee", "On-site Probat roaster", "Brazilian treats", "Gluten-free options", "Specialty coffee 80+ SCA", "Indoor seating", "Terrace seating", "Historic Bica district", "Near Bica Funicular", "Credit cards accepted"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Farm-to-Roaster Excellence",
      description: "Experience Brazilian specialty coffee from Baobá's own farm, expertly roasted on-site with Probat equipment"
    },
    {
      icon: "🇧🇷", 
      title: "Authentic Brazilian Treats",
      description: "Taste pão de queijo, brigadeiro in chocolate and coffee flavors, açaí smoothies, and European pastries"
    },
    {
      icon: "🏛️",
      title: "Historic Bica District", 
      description: "Gather in one of Lisbon's most picturesque and bohemian neighborhoods, famous for its charm and character"
    },
    {
      icon: "🌍",
      title: "Brazilian-Portuguese Fusion",
      description: "Experience the unique blend of Brazilian coffee heritage and Portuguese hospitality in Europe's sunniest capital"
    }
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and Brazilian specialty coffee culture",
  specialFeature: "Farm-to-roaster Brazilian coffee in historic Lisbon Bica district",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Lisbon through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    brazilianTreats: {
      description: "Authentic Brazilian specialties including pão de queijo and brigadeiro, plus European pastries",
      variety: "Traditional Brazilian treats, gluten-free options, and artisan chocolate bonbon pairings"
    },
    farmToRoaster: {
      origin: "Single-origin coffee from Baobá's own farm in São Sebastião da Grama, Brazil",
      roasting: "On-site roasting with Probat equipment using ancient techniques and modern expertise",
      quality: "All coffees score above 80 points on the Specialty Coffee Association scale",
      taste_profile: "Exceptional Brazilian varieties including Icatu and Obatã Amarelo with complex flavor notes"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by Brazilian coffee aroma and Lisbon's historic Bica charm"
    },
    farmToRoaster: {
      definition: "Coffee journey from growing on their own farm to roasting and serving in the same café",
      practice: "Baobá's commitment to quality, sustainability, and honoring Brazilian coffee heritage"
    }
  },
  
  venueFeatures: {
    location: "Lisbon: Historic Bica district on Rua de São Paulo, near the iconic yellow Bica Funicular",
    atmosphere: "Brazilian specialty coffee shop with warm, welcoming vibe in one of Lisbon's most charming neighborhoods",
    uniqueness: "Only specialty coffee shop in Portugal with farm-to-roaster model, featuring their own Brazilian farm",
    cultural_bridge: "Connects Brazilian coffee heritage with Portuguese café culture in Europe's sunniest capital"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Brazilian coffee cultivation and the journey from farm to cup",
    "The art of specialty coffee roasting and tasting complex flavor profiles", 
    "Living in Lisbon: exploring historic neighborhoods and their unique characters",
    "Portuguese culture, fado music, and the art of slow living in Europe",
    "Building authentic community connections across cultures and countries"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in farm-to-roaster specialty coffee and Brazilian varieties",
    "Community members seeking meaningful connections in Lisbon's charming neighborhoods",
    "Anyone interested in experiencing fika tradition in historic European settings",
    "People who appreciate authentic Brazilian culture and exceptional coffee quality",
    "Those seeking warm, welcoming gathering spaces with cultural depth"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a unique farm-to-roaster Brazilian specialty coffee shop",
    "Single-origin coffee from Baobá's own farm, roasted on-site with Probat equipment",
    "Authentic Brazilian treats including pão de queijo and brigadeiro alongside European pastries",
    "Located in historic Bica, one of Lisbon's most picturesque and bohemian neighborhoods",
    "Exceptional coffee quality: all beans score 80+ points on SCA scale"
  ],
  
  preparationTips: [
    "Arrive early to explore the charming Bica neighborhood and take photos of the iconic yellow funicular",
    "Come with curiosity about Brazilian coffee culture and specialty coffee preparation",
    "Consider taking the historic Bica Funicular (Ascensor da Bica) to experience Lisbon's charm",
    "Bring willingness to slow down and embrace both fika and Portuguese café culture"
  ],
  
  culturalLearning: [
    "Brazilian coffee cultivation practices and the farm-to-roaster journey",
    "Specialty coffee roasting techniques and understanding SCA scoring (80+ points)",
    "The history and character of Lisbon's Bica district and its bohemian heritage",
    "How Baobá connects Brazilian coffee tradition with Portuguese café culture",
    "The importance of quality, sustainability, and community in specialty coffee"
  ]
},

{
  id: 'chicago-swedish-fika-december-2025',
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Inaugural Monthly Swedish Fika: Chicago's (US) Beloved Tradition (December)",
  description: "Experience fika culture at Dayglow, a charming artisan café in Chicago. Organic single-origin coffee, house-baked pastries, and meaningful connections.",
  waitlist: "0",
  next: "December 14, 2025",
  time: "10:00 AM - 1:00 PM",
  location: "Black Crow Coffee Co - Grand Central District (details sent one week before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 15,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Artisan Coffee", "House-Baked Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'chicago-swedish-fika-december-2025',

   //cost: "2,000 YEN per person",
 
   paymentEnabled: true,           // Toggle for this specific event - default to false
   requiresSubscription: true,     // Does this event require subscription? - default to false
   perEventPriceCents: 2000,     // $30 in cents (¥30 * 100)
   currency: "JPY",
   paymentOptions: {
     allowSubscription: true,       // Can subscribers access this?
     allowPerEventPayment: true,    // Can non-subscribers pay?
     allowWaitlist: true          // Can non-subscribers join waitlist?
   },
  
  
  // ✅ City-related fields
  city: "chicago",
  cityDisplay: "Chicago",
  country: "United States",
  timezone: "America/New_York",
  currency: "USD",

  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "November reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "As Chicago's holiday season begins and the year draws to a close, what's one way you've noticed yourself changing or growing this season?",
      
        context: "Just like the festive lights along Central Avenue slowly illuminate the Grand Central District, we often undergo our own quiet transformations in December. Share a shift in perspective, a new understanding about yourself, or a gradual change you've been experiencing as the year winds down."
      },
      {
        number: 2,
        english: "What's one small winter ritual or experience you're most looking forward to as the holiday season unfolds in St. Petersburg?",
     
        context: "As we embrace the holiday spirit, think about what draws you to this season - perhaps your first cup of hot coffee on a crisp Florida morning, exploring the festive murals in the Grand Central District, or the anticipation of cozy gatherings that come with the end of the year."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika at Dayglow in the Grand Central District",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at Dayglow, a charming artisan café in Chicago's vibrant Grand Central District. This wonderful locally-owned venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional organic coffee and delicious house-baked pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in St. Pete."
      },
      {
        type: 'paragraph',
        content: "Tucked away like a hidden gem in the heart of the Grand Central District, Dayglow sits in a shared courtyard space alongside a charming bookstore and juice bar. This eclectic café embodies everything that makes fika magical - from its inviting garden courtyard filled with greenery and quirky vintage furniture to its cozy interior adorned with local art and eco-conscious touches. The location itself feels like stepping into a peaceful urban sanctuary, where the creative energy of St. Pete's arts district creates the perfect backdrop for meaningful conversation and deep connection."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Organic Single-Origin Coffee Excellence: Experience meticulously crafted coffee featuring organic single-origin beans prepared with pour-over precision and cold brew artistry. Every cup is prepared with care and dedication, showcasing the natural flavors and complexity of ethically-sourced beans."
      },
      {
        type: 'paragraph',
        content: "House-Baked Pastries & Treats: Indulge in a selection of freshly baked pastries and treats made in-house daily. From flaky croissants (including vegan options) to decadent chocolate treats and hearty muffins, each bite is crafted with love and complements your coffee experience perfectly."
      },
      {
        type: 'paragraph',
        content: "Community-Centered Artisan Atmosphere: Savor your coffee in a space that celebrates local artists, supports eco-conscious values, and welcomes everyone. The café's commitment to community and sustainability creates an authentic, warm environment where connections flourish naturally."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture in the Grand Central District"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in St. Petersburg. In this vibrant courtyard sanctuary, surrounded by the aroma of freshly brewed organic coffee and the creative spirit of the Grand Central District, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "Black Crow Coffee Co's dedication to quality, community, and sustainability means every element has been thoughtfully curated to create a wonderful fika experience. From the careful single-origin bean selection to the house-baked pastries and welcoming garden atmosphere, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another in one of St. Pete's most eclectic neighborhoods."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Artisan Coffee Garden Atmosphere: Step into a space where organic coffee culture meets local art and vintage charm, creating an environment that feels both welcoming and inspiring in the heart of St. Pete's creative district",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the pour-over coffee and connections deepen with each shared moment in the garden courtyard",
          "Cultural & Community Discovery: Learn about coffee origins and brewing techniques while enjoying exceptional organic coffee and house-baked pastries, all while connecting with St. Pete's vibrant LGBTQ+-friendly creative community"   
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "House-baked pastries and treats at café prices (including vegan and gluten-free options)",
          "Organic single-origin coffee beverages prepared with pour-over and cold brew techniques",
          "Credit cards and electronic payments accepted"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful inaugural month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional organic coffee and delicious house-baked pastries in one of St. Petersburg's most vibrant and welcoming settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Taste house-baked pastries and treats made fresh daily with vegan and gluten-free options",
    "Enjoy organic single-origin coffee with pour-over precision and cold brew artistry",
    "Connect with fellow community members in a charming garden courtyard filled with greenery and local art",
    "Learn about coffee origins and brewing techniques in St. Pete's vibrant creative district",
    "Practice the art of unhurried conversation and meaningful connection in an eco-conscious, welcoming space"
  ],
  
  venue: { 
    name: "Dayglow",
    description: "A charming locally-owned artisan café featuring organic single-origin coffee, house-baked pastries, and an inviting garden courtyard. Located in St. Pete's vibrant Grand Central District alongside a bookstore, this eclectic space celebrates local art, eco-conscious values, and community connection.",
    fullAddress: "1757 N Kimball Ave, Chicago, IL 60647, United States",
    priceRange: "Free event - House-baked pastries and organic coffee at café prices",
    amenities: ["Organic single-origin coffee", "House-baked pastries", "Pour-over & cold brew", "Garden courtyard seating", "Indoor vintage seating", "Adjacent bookstore", "Vegan & gluten-free options", "Local art displays", "Eco-conscious", "LGBTQ+-friendly", "Credit cards accepted"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Organic Coffee Artistry",
      description: "Gathering at a locally-owned café that takes pride in organic single-origin beans and precise pour-over techniques"
    },
    {
      icon: "🥐", 
      title: "House-Baked Pastries & Treats",
      description: "Taste freshly baked pastries made in-house daily, including vegan and gluten-free options that perfectly complement your coffee"
    },
    {
      icon: "🌿",
      title: "Garden Courtyard Sanctuary", 
      description: "Charming shared outdoor space filled with plants and vintage furniture creating perfect backdrop for contemplative fika"
    },
    {
      icon: "🎨",
      title: "Creative District Energy",
      description: "Experience St. Pete's vibrant arts community in the heart of the Grand Central District's eclectic, welcoming atmosphere"
    }
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and artisan coffee culture",
  specialFeature: "House-baked pastries and organic single-origin coffee in Grand Central District garden setting",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to St. Petersburg through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "House-baked pastries and treats made fresh daily with attention to dietary preferences",
      variety: "Selection includes traditional favorites, vegan options, and gluten-free treats"
    },
    artisanCoffee: {
      sourcing: "Organic single-origin beans ethically sourced and carefully selected",
      preparation: "Pour-over and cold brew techniques emphasizing natural bean characteristics",
      taste_profile: "Clean, complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by organic coffee aroma, local art, and garden courtyard ambiance"
    },
    community: {
      definition: "Building authentic connections in welcoming, inclusive spaces",
      practice: "Embraced through St. Pete's vibrant Grand Central District creative culture"
    }
  },
  
  /*
  venueFeatures: {
    location: "St. Petersburg: Grand Central District's eclectic arts and entertainment hub",
    atmosphere: "Locally-owned artisan café with garden courtyard, vintage furniture, and local art displays",
    uniqueness: "Charming shared courtyard space with bookstore, celebrating eco-conscious values and LGBTQ+ community",
    cultural_bridge: "Connects St. Pete's creative arts scene with the peaceful tradition of fika and community gathering"
  },
  */

  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee origins and artisan brewing techniques from around the world",
    "The art of slow living and mindful connection in vibrant creative communities", 
    "Community building through shared experiences and local traditions",
    "St. Pete's arts scene and the role of creative spaces in fostering connection",
    "Sustainability, eco-conscious living, and supporting local businesses"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in organic single-origin beans and artisan brewing",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in St. Pete's creative district",
    "People who appreciate locally-owned, eco-conscious spaces and house-baked treats",
    "Those seeking welcoming, inclusive gathering spaces with artistic character"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a locally-owned artisan café in the Grand Central District",
    "House-baked pastries and organic single-origin coffee in charming garden courtyard",
    "Combination of coffee culture with local art, vintage ambiance, and community spirit",
    "LGBTQ+-friendly, eco-conscious space celebrating St. Pete's creative culture",
    "High-quality organic coffee preparation with vegan and gluten-free food options"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to explore the garden courtyard and adjacent bookstore",
    "Come with curiosity about organic coffee origins and local St. Pete culture",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of community connection in an eclectic, welcoming space"
  ],
  
  culturalLearning: [
    "Organic single-origin coffee sourcing and pour-over brewing techniques",
    "The role of locally-owned cafés in creating vibrant, inclusive communities",
    "Fika traditions and their adaptation to American urban creative culture",
    "How the Grand Central District became St. Pete's arts and LGBTQ+ hub",
    "The importance of eco-conscious spaces for fostering authentic connection and reflection"
  ]
},



{
  id: 'st-petersburg-swedish-fika-november-2025',
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Inaugural Monthly (November) Fika: St. Petersburg's Beloved Tradition",
  description: "Experience fika culture at Black Crow Coffee Co, a charming artisan café in St. Pete's vibrant Grand Central District. Organic single-origin coffee, house-baked pastries, and meaningful connections.",
  waitlist: "0",
  next: "November 15, 2025",
  time: "10:00 AM - 12:00 PM",
  location: "Black Crow Coffee Co - Grand Central District (details sent one week before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 12,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Artisan Coffee", "House-Baked Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'st-petersburg-swedish-fika-november-2025',

   //cost: "2,000 YEN per person",
 
   paymentEnabled: true,           // Toggle for this specific event - default to false
   requiresSubscription: true,     // Does this event require subscription? - default to false
   perEventPriceCents: 2000,     // $30 in cents (¥30 * 100)
   currency: "JPY",
   paymentOptions: {
     allowSubscription: true,       // Can subscribers access this?
     allowPerEventPayment: true,    // Can non-subscribers pay?
     allowWaitlist: true          // Can non-subscribers join waitlist?
   },
  
  
  // ✅ City-related fields
  city: "st-petersburg",
  cityDisplay: "St Petersburg",
  country: "United States",
  timezone: "America/New_York",
  currency: "USD",

  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "November reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "As Chicago's holiday season begins and the year draws to a close, what's one way you've noticed yourself changing or growing this season?",
      
        context: "Just like the festive lights along Central Avenue slowly illuminate the Grand Central District, we often undergo our own quiet transformations in December. Share a shift in perspective, a new understanding about yourself, or a gradual change you've been experiencing as the year winds down."
      },
      {
        number: 2,
        english: "What's one small winter ritual or experience you're most looking forward to as the holiday season unfolds in St. Petersburg?",
     
        context: "As we embrace the holiday spirit, think about what draws you to this season - perhaps your first cup of hot coffee on a crisp Florida morning, exploring the festive murals in the Grand Central District, or the anticipation of cozy gatherings that come with the end of the year."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika at Black Crow Coffee Co in the Grand Central District",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at Dayglow, a charming artisan café in Chicago's vibrant Grand Central District. This wonderful locally-owned venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional organic coffee and delicious house-baked pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in St. Pete."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Organic Single-Origin Coffee Excellence: Experience meticulously crafted coffee featuring organic single-origin beans prepared with pour-over precision and cold brew artistry. Every cup is prepared with care and dedication, showcasing the natural flavors and complexity of ethically-sourced beans."
      },
      {
        type: 'paragraph',
        content: "House-Baked Pastries & Treats: Indulge in a selection of freshly baked pastries and treats made in-house daily. From flaky croissants (including vegan options) to decadent chocolate treats and hearty muffins, each bite is crafted with love and complements your coffee experience perfectly."
      },
      {
        type: 'paragraph',
        content: "Community-Centered Artisan Atmosphere: Savor your coffee in a space that celebrates local artists, supports eco-conscious values, and welcomes everyone. The café's commitment to community and sustainability creates an authentic, warm environment where connections flourish naturally."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture in the Grand Central District"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in St. Petersburg. In this vibrant courtyard sanctuary, surrounded by the aroma of freshly brewed organic coffee and the creative spirit of the Grand Central District, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "Black Crow Coffee Co's dedication to quality, community, and sustainability means every element has been thoughtfully curated to create a wonderful fika experience. From the careful single-origin bean selection to the house-baked pastries and welcoming garden atmosphere, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another in one of St. Pete's most eclectic neighborhoods."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Artisan Coffee Garden Atmosphere: Step into a space where organic coffee culture meets local art and vintage charm, creating an environment that feels both welcoming and inspiring in the heart of St. Pete's creative district",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the pour-over coffee and connections deepen with each shared moment in the garden courtyard",
          "Cultural & Community Discovery: Learn about coffee origins and brewing techniques while enjoying exceptional organic coffee and house-baked pastries, all while connecting with St. Pete's vibrant LGBTQ+-friendly creative community"   
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "House-baked pastries and treats at café prices (including vegan and gluten-free options)",
          "Organic single-origin coffee beverages prepared with pour-over and cold brew techniques",
          "Credit cards and electronic payments accepted"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful inaugural month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional organic coffee and delicious house-baked pastries in one of St. Petersburg's most vibrant and welcoming settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
    "Taste house-baked pastries and treats made fresh daily with vegan and gluten-free options",
    "Enjoy organic single-origin coffee with pour-over precision and cold brew artistry",
    "Connect with fellow community members in a charming garden courtyard filled with greenery and local art",
    "Learn about coffee origins and brewing techniques in St. Pete's vibrant creative district",
    "Practice the art of unhurried conversation and meaningful connection in an eco-conscious, welcoming space"
  ],
  
  venue: { 
    name: "Black Crow Coffee Co - Grand Central District",
    description: "A charming locally-owned artisan café featuring organic single-origin coffee, house-baked pastries, and an inviting garden courtyard. Located in St. Pete's vibrant Grand Central District alongside a bookstore, this eclectic space celebrates local art, eco-conscious values, and community connection.",
    fullAddress: "2157 1st Ave S, St. Petersburg, FL 33712, United States",
    priceRange: "Free event - House-baked pastries and organic coffee at café prices",
    amenities: ["Organic single-origin coffee", "House-baked pastries", "Pour-over & cold brew", "Garden courtyard seating", "Indoor vintage seating", "Adjacent bookstore", "Vegan & gluten-free options", "Local art displays", "Eco-conscious", "LGBTQ+-friendly", "Credit cards accepted"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Organic Coffee Artistry",
      description: "Gathering at a locally-owned café that takes pride in organic single-origin beans and precise pour-over techniques"
    },
    {
      icon: "🥐", 
      title: "House-Baked Pastries & Treats",
      description: "Taste freshly baked pastries made in-house daily, including vegan and gluten-free options that perfectly complement your coffee"
    },
    {
      icon: "🌿",
      title: "Garden Courtyard Sanctuary", 
      description: "Charming shared outdoor space filled with plants and vintage furniture creating perfect backdrop for contemplative fika"
    },
    {
      icon: "🎨",
      title: "Creative District Energy",
      description: "Experience St. Pete's vibrant arts community in the heart of the Grand Central District's eclectic, welcoming atmosphere"
    }
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Saturday", 
  culturalFocus: "Fika tradition and artisan coffee culture",
  specialFeature: "House-baked pastries and organic single-origin coffee in Grand Central District garden setting",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to St. Petersburg through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "House-baked pastries and treats made fresh daily with attention to dietary preferences",
      variety: "Selection includes traditional favorites, vegan options, and gluten-free treats"
    },
    artisanCoffee: {
      sourcing: "Organic single-origin beans ethically sourced and carefully selected",
      preparation: "Pour-over and cold brew techniques emphasizing natural bean characteristics",
      taste_profile: "Clean, complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by organic coffee aroma, local art, and garden courtyard ambiance"
    },
    community: {
      definition: "Building authentic connections in welcoming, inclusive spaces",
      practice: "Embraced through St. Pete's vibrant Grand Central District creative culture"
    }
  },
  
  venueFeatures: {
    location: "St. Petersburg: Grand Central District's eclectic arts and entertainment hub",
    atmosphere: "Locally-owned artisan café with garden courtyard, vintage furniture, and local art displays",
    uniqueness: "Charming shared courtyard space with bookstore, celebrating eco-conscious values and LGBTQ+ community",
    cultural_bridge: "Connects St. Pete's creative arts scene with the peaceful tradition of fika and community gathering"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee origins and artisan brewing techniques from around the world",
    "The art of slow living and mindful connection in vibrant creative communities", 
    "Community building through shared experiences and local traditions",
    "St. Pete's arts scene and the role of creative spaces in fostering connection",
    "Sustainability, eco-conscious living, and supporting local businesses"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in organic single-origin beans and artisan brewing",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in St. Pete's creative district",
    "People who appreciate locally-owned, eco-conscious spaces and house-baked treats",
    "Those seeking welcoming, inclusive gathering spaces with artistic character"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a locally-owned artisan café in the Grand Central District",
    "House-baked pastries and organic single-origin coffee in charming garden courtyard",
    "Combination of coffee culture with local art, vintage ambiance, and community spirit",
    "LGBTQ+-friendly, eco-conscious space celebrating St. Pete's creative culture",
    "High-quality organic coffee preparation with vegan and gluten-free food options"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to explore the garden courtyard and adjacent bookstore",
    "Come with curiosity about organic coffee origins and local St. Pete culture",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of community connection in an eclectic, welcoming space"
  ],
  
  culturalLearning: [
    "Organic single-origin coffee sourcing and pour-over brewing techniques",
    "The role of locally-owned cafés in creating vibrant, inclusive communities",
    "Fika traditions and their adaptation to American urban creative culture",
    "How the Grand Central District became St. Pete's arts and LGBTQ+ hub",
    "The importance of eco-conscious spaces for fostering authentic connection and reflection"
  ]
},

{
  id: 'fukuoka-swedish-fika-december-2025', 
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
  iconValue: '🇸🇪',
  title: "Inaugural Monthly (December) Fika: Fukuoka's Beloved Tradition",
  description: "Experience fika culture at a beautiful 3rd wave coffee shop along Fukuoka's vibrant spot. Quality coffee, delicious pastries, and meaningful connections.",
  waitlist: "0",
  next: "December 6, 2024",
  time: "10:00 AM - 1:00 PM",
  location: "TBD - 3rd wave coffee venue (details sent one week before event)",
  color: "from-amber-500 to-orange-600",
  capacity: 15,
  difficulty: "Cultural Immersion",
  tags: ["Fika Culture", "Third Wave Coffee",  "Pastries", "Community", "Coffee Culture", "Connections"],
  featured: true,
  slug: 'fukuoka-swedish-fika-december-2025',
  
  // ✅ City-related fields
  city: "fukuoka",
  cityDisplay: "Fukuoka",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Autumn reflections to warm our conversations",
    questions: [
      {
        number: 1,
        english: "As Fukuoka's autumn leaves begin their transformation, what's one way you've noticed yourself changing or growing this season?",
        japanese: "東京の紅葉が色づき始めるこの季節、あなた自身が変化や成長していることに気づいたことはありますか？",
        context: "Just like the ginkgo trees along Fukuoka's streets slowly turn golden, we often undergo our own quiet transformations in autumn. Share a shift in perspective, a new understanding about yourself, or a gradual change you've been experiencing as the seasons turn."
      },
      {
        number: 2,
        english: "What's one small autumn ritual or experience you're most looking forward to as the season unfolds in Fukuoka?",
        japanese: "東京で秋が深まっていく中で、最も楽しみにしている小さな秋の習慣や体験は何ですか？",
        context: "As we're just entering autumn, think about what draws you to this season - perhaps your first cup of hot coffee on a crisp morning, visiting temples when the maple leaves turn red, or the anticipation of cozy evenings that come with shorter days."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika in Fukuoka's Finest Third Wave Café",
    sections: [
      {
        type: 'paragraph',
        content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Fukuoka."
      },
      {
        type: 'paragraph',
        content: "Nestled along the serene banks of a gently flowing river, our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a waterside community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Fukuoka's urban intensity."
      },
      {
        type: 'header',
        content: "What Makes This Month's Fika Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
      },
      {
        type: 'paragraph',
        content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
      },
      {
        type: 'paragraph',
        content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
      },
     
      {
        type: 'header',
        content: "The Magic of Fika Culture"
      },
      {
        type: 'paragraph',
        content: "This month, we're continuing our beautiful tradition of embracing fika culture in Fukuoka. In this riverside sanctuary, surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
      },
      {
        type: 'paragraph',
        content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
          "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
          "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings"   
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Fresh pastries and treats available at café prices",
          "Third wave coffee beverages prepared with expertly roasted beans",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this wonderful inaugural month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Fukuoka's most serene settings."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
      }
    ]
  },
  
  whatToExpect: [
  
    "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
    "Enjoy expertly roasted coffee with complex flavors and careful preparation",
    "Connect with fellow community members in a serene riverside setting with panoramic water views",
    "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
    "Practice the art of unhurried conversation and meaningful connection"
  ],
  
  venue: { 
    name: "TBD - (Fukuoka) Third Wave Coffee Shop",
    description: "A beautiful third wave coffee venue nestled along serene riverside, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
    fullAddress: "Location revealed to confirmed attendees one week prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Free event - Fresh pastries and quality coffee at café prices",
    amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting", "Riverside views", "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Third Wave Coffee Experience",
      description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
    },
    {
      icon: "🥐", 
      title: "Fresh Pastries & Treats",
      description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
    },
    {
      icon: "☕",
      title: "Quality Coffee Artistry", 
      description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
    },
    {
      icon: "🌊",
      title: "Riverside Sanctuary",
      description: "Serene waterside setting with panoramic views creating perfect backdrop for contemplative fika"
    }
  ],
  
  relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special-monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Fika tradition and coffee culture",
  specialFeature: "Beautiful riverside third wave coffee experience",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
    avatar: "mark-hosts.jpg"
  },

  // Special features for coffee experience
  coffeeExperience: {
    pastries: {
      description: "Fresh pastries and café treats that enhance the coffee experience",
      variety: "Selection of traditional favorites and modern creations"
    },
    thirdWaveCoffee: {
      roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
      preparation: "Carefully prepared beans with precision and attention to detail",
      taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
    }
  },
  
  culturalConcepts: {
    fika: {
      definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
      experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
    },
    mindfulness: {
      definition: "Finding perfect balance and contentment in the present moment",
      practice: "Encouraged during peaceful waterside reflection"
    }
  },
  
  venueFeatures: {
    location: "Fukuoka: Serene riverside within waterside community facility",
    atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
    uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
    cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Coffee culture and brewing techniques from around the world",
    "The art of slow living and mindful connection in fast-paced cities", 
    "Community building through shared experiences and traditions",
    "Cross-cultural experiences and finding connection in diverse communities",
    "The philosophy of mindfulness and finding balance in daily life"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in third wave coffee culture",
    "Community members seeking meaningful connections and conversations",
    "Anyone interested in experiencing fika tradition in beautiful settings",
    "People who appreciate quality coffee and the art of slow living",
    "Those seeking peaceful, contemplative gathering spaces"
  ],
  
  uniqueAspects: [
    "Monthly fika tradition at a beautiful third wave coffee venue",
    "Fresh pastries and expertly crafted coffee in serene riverside setting",
    "Combination of modern coffee culture with peaceful nature views",
    "Community gathering focused on meaningful connection and conversation",
    "High-quality coffee preparation with attention to flavor and technique"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to secure preferred riverside viewing spot",
    "Come with curiosity about coffee culture and community connections",
    "Bring willingness to slow down and embrace the art of unhurried conversation",
    "Prepare to experience the joy of quality coffee in beautiful surroundings"
  ],
  
  culturalLearning: [
    "Third wave coffee preparation and roasting techniques",
    "The role of quality ingredients in creating exceptional café experiences",
    "Fika traditions and their adaptation to modern urban life",
    "How coffee culture brings communities together across cultures",
    "The importance of creating mindful spaces for connection and reflection"
  ]
},

{
  id: 'excursion-fukuoka-december-2025',
  eventType: 'fika',
  // iconType: 'emoji',
  //iconValue: '🏮',
  iconType: 'image',
  iconImage: '/images/events/Fukuoka.jpg',
  title: "Inaugural Fukuoka Food & Connection Excursion",
  description: "Join our first-ever weekend Fika excursion to Fukuoka! Three days of authentic connections, legendary street food, and exploring Kyushu's vibrant gateway city—December 5-7, 2025.",
  waitlist: "0",
  next: "December 5, 2024",
  time: "Flexible arrival Friday Dec 5 - Fika Saturday 11:00 AM - Depart Sunday Dec 7",
  location: "Fukuoka, Kyushu",
  color: "from-red-500 to-orange-600",
  capacity: 10,
  difficulty: "Weekend Adventure",
  tags: ["Weekend Excursion", "Fukuoka", "Travel", "Street Food", "Fika", "Kyushu", "Inaugural Event"],
  featured: true,
  slug: 'excursion-fukuoka-december-2025',

  cost: "Self-funded travel (flights + hotels). Fika & activities coordinated.",
  paymentEnabled: false,
  requiresSubscription: false,
  perEventPriceCents: 0,
  currency: "JPY",
  price: 0,
  paymentOptions: {
    allowSubscription: true,
    allowPerEventPayment: false,
    allowWaitlist: true
  },

  city: "fukuoka",
  cityDisplay: "Fukuoka",
  country: "Japan",
  timezone: "Asia/Tokyo",

  philosophy: {
    title: "Fukuoka Food & Connection Excursion",
    sections: [
      {
        type: 'paragraph',
        content: "We're taking Connect & Co. on the road! Join us for our very first weekend excursion to Fukuoka—Kyushu's vibrant gateway city where ancient temples meet modern izakayas, and legendary street food culture thrives in centuries-old yatai stalls."
      },
      {
        type: 'paragraph',
        content: "This isn't just a trip; it's an adventure in connection. Imagine: Saturday afternoon Fika overlooking Hakata Bay, followed by exploring Fukuoka's famous yatai street food scene together as the city lights up. Sunday? Optional temple visits, ramen museum tours, or simply wandering the charming streets with your new travel companions."
      },
      {
        type: 'header',
        content: "🏮 Why Fukuoka for Our Inaugural Excursion?"
      },
      {
        type: 'bullets',
        items: [
          "Food Paradise: Fukuoka is Japan's street food capital—tonkotsu ramen birthplace, yatai stalls, Hakata specialties you can't find in Tokyo",
          "Compact & Walkable: Everything's accessible, perfect for a weekend of spontaneous exploration",
          "Relaxed Vibe: Less tourist crowds than Tokyo/Kyoto, more authentic local experiences",
          "90-Minute Flight: Short hop from Tokyo makes it perfect for a 3-day escape",
          "Beach Meets City: Coastal energy with urban sophistication—best of both worlds"
        ]
      },
      {
        type: 'header',
        content: "📅 The 3-Day Weekend Flow (Totally Flexible)"
      },
      {
        type: 'paragraph',
        content: "Friday, December 5: Arrive on your schedule. Evening flights work great. Maybe grab drinks with early arrivals? We'll coordinate via group chat."
      },
      {
        type: 'paragraph',
        content: "Saturday, December 6 - THE MAIN EVENT: 10:00 AM - 1 PM Inaugural Monthly Fika at  3rd wave café. 5:00 PM onwards: Group street food adventure through Fukuoka's legendary yatai district. Evening: Explore Nakasu nightlife or continue the food tour—your call!"
      },
      {
        type: 'paragraph',
        content: "Sunday, December 7: Your adventure. Some might visit Dazaifu Tenmangu Shrine, others the Ramen Museum, or simply brunch before flying back to Tokyo. Coordinate with your new crew!"
      },
      {
         type: 'header',
                content: "💰 What You'll Pay (Book & Pay Individually)"
              },
              {
              type: 'paragraph',
                content: "Everyone books and pays for their own travel and accommodation independently. Here's what to budget:"
              },
      {
        type: 'bullets',
        items: [
          "Flights: Tokyo-Fukuoka round-trip ~¥15,000-30,000 (90 mins each way)",
          "Accommodation: Hyatt Regency or Marriott Fukuoka ~¥12,000-20,000/night",
          "Accommodation: Airbnb shared apartments or private rooms ~¥5,000-15,000/night", 
          "Food & Activities: Budget ¥5,000-8,000 for weekend meals & experiences",
          "Total Estimate: ¥35,000-65,000 depending on your accommodation choice"
        ]
      },
      {
        type: 'header',
        content: "🏨 Accommodation Options"
      },
      {
        type: 'paragraph',
        content: "Hyatt Regency Fukuoka and Fukuoka Marriott are excellent upscale options in the Hakata district, walking distance to everything. Airbnb options are also available. Book near Hakata Station or Tenjin for best access."
      },
      {
        type: 'header',
        content: "🎯 Why This Will Be Amazing"
      },
      {
        type: 'bullets',
        items: [
          "Intimate Group: Maximum 10 people means real connections, not crowd management",
          "Inaugural Event: Be part of Connect & Co. history as we launch weekend excursions",
          "Flexible Schedule: Arrive/depart when it works for you—no rigid itinerary pressure",
          "Built-in Adventures: Saturday's organized, freedom to explore solo or with subgroups",
          "New City Energy: Experience Fukuoka's unique culture with people who get it",
          "Travel Buddies: Weekend adventures create bonds that regular events can't match"
        ]
      },
      {
        type: 'header',
        content: "📝 After Registration"
      },
      {
        type: 'paragraph',
        content: "Mark, your organizer, will create a WhatsApp group chat to coordinate all event details. "
      },
      {
        type: 'header',
        content: "✈️ Travel Tips"
      },
      {
        type: 'paragraph',
        content: "Suggested flights: Friday evening 6:00-8:00 PM departures from Haneda. Sunday return: Morning flights 10:00 AM-12:00 PM. Budget option: Peach Aviation. Weather in December: Cool 8-15°C, bring layers and comfortable walking shoes."
      },
      {
        type: 'header',
        content: "Important Notes"
      },
      {
        type: 'bullets',
        items: [
          "Capacity limited to 10 people for optimal group dynamics",
          "Book flights & accommodation AFTER registration confirmation",
          "Additional coordination details sent to registered members",
          "Travel insurance recommended",
          "Dietary restrictions accommodated—let us know in advance"
        ]
      }
    ]
  },

  whatToExpected: [
    "3-day weekend adventure in Fukuoka with max 10 people",
    "Saturday Monthly Fika at waterfront venue",
    "Guided street food exploration of legendary yatai district",
    "Flexible free time for temples, shopping, or relaxing",
    "Private group chat for coordination and spontaneous meetups",
    "Authentic Kyushu experience far from tourist traps"
  ],

  venue: {
    name: "Fukuoka - Multiple Locations",
    description: "Weekend excursion centered on Saturday's Monthly Fika at waterfront café (exact venue TBA), followed by group exploration of Fukuoka's famous yatai street food scene. Accommodation: Hyatt Regency, Marriott Fukuoka, or Airbnb options available.",
    fullAddress: "Fukuoka City, Fukuoka Prefecture (Detailed locations shared with registered attendees)",
    priceRange: "Self-funded: Flights ¥15k-30k, Hotels ¥12k-20k/night, Airbnb ¥5k-15k/night",
    amenities: ["Waterfront venue", "Yatai district access", "Central accommodation options", "Group coordination"]
  },

  memberBenefits: [
    {
      icon: "🏮",
      title: "Inaugural Excursion",
      description: "Be part of Connect & Co. history as we launch our first weekend travel experience"
    },
    {
      icon: "👥",
      title: "Intimate Group",
      description: "Maximum 10 people ensures genuine connections and manageable dynamics"
    },
    {
      icon: "✈️",
      title: "Flexible Schedule",
      description: "Arrive and depart on your timeline—no rigid group obligations"
    }
  ],

  hostInfo: {
    name: "Mark",
    bio: "Creating relaxed and authentic connection experiences across Japan's best cities",
    avatar: "mark-jason-host.jpg"
  },

  relatedEvents: ['monthly-fika-tokyo-november-2024', 'shoot-the-breeze-ginza-november-2025'],
  status: "open"
},

{
  id: 'natural-wine-journey-december-2025',
  eventType: 'happy-hour',
  iconType: 'image',
  iconImage: '/images/events/NaturalWine.jpg',
  iconValue: '🍷',
  title: "California Dreaming: Natural Wine & Art Evening (Tokyo)",
  description: "Intimate natural wine gathering at artisan Meguro wine bar with California vibes. A journey through rare French and German natural wines with select Japanese offerings in a Georgia O'Keeffe-inspired space featuring handcrafted ceramics.",
  waitlist: "0",
  next: "December 13, 2025",
  time: "5:30 PM - 8:00 PM",
  location: "TBD - Artisan Natural Wine Bar, Meguro",
  color: "from-pink-500 to-green-400",
  capacity: 18,
  difficulty: "Wine Experience",
  tags: ["Natural Wine", "Wine Tasting", "French Wine", "German Wine", "Art Space", "Intimate Setting"],
  featured: true,
  slug: 'natural-wine-journey-december-2025',
  
  // City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan", 
  timezone: "Asia/Tokyo",
  currency: "JPY",

   // NEW: Payment configuration properties
   //cost: "2,000 YEN per person",
 
   paymentEnabled: false,           // Toggle for this specific event - default to false
   requiresSubscription: false,     // Does this event require subscription? - default to false
   perEventPriceCents: 3000,     // $30 in cents (¥30 * 100)
   currency: "JPY",
   paymentOptions: {
     allowSubscription: true,       // Can subscribers access this?
     allowPerEventPayment: true,    // Can non-subscribers pay?
     allowWaitlist: true          // Can non-subscribers join waitlist?
   },
  

  philosophy: {
    title: "Natural Wine Journey: Where California Warmth Meets Japanese Craftsmanship",
    sections: [
      {
        type: 'header',
        content: "For a seamless registration experience, please review the information below and visit the Details tab before reserving your spot."
      },
      {
        type: 'paragraph',
        content: "Step into an evening where natural wine culture meets artistic expression in one of Meguro's most enchanting hidden gems. This isn't just a wine tasting—it's an immersive experience in a space where dusty pink and mint gradients evoke Georgia O'Keeffe paintings, handcrafted ceramics tell their own stories, and every bottle has been selected with the same care a curator brings to a gallery opening."
      },
      {
        type: 'paragraph',
        content: "Created by a California chef who brought farm-to-table philosophy to Tokyo, this intimate wine bar exists in the peaceful residential streets of Meguro as a love letter to natural wine culture. The space features around 60 carefully selected bottles—primarily from France and Germany's most respected natural wine producers, complemented by select bottles from Japan's emerging winemakers. Here, the sommelier doesn't just pour wine—they guide you through flavor profiles, regional stories, and the passionate producers behind each bottle."
      },
      {
        type: 'header',
        content: "What Makes This Evening Special"
      },
      {
        type: 'paragraph',
        content: "Relaxed Atmosphere: Wine served in a gallery-like space featuring handcrafted ceramics, wine coolers carved from Hokkaido birch."
      },
      {
        type: 'paragraph',
        content: "European Natural Wine Focus: Access to rare French and German natural wines that would cost multiples more in other cities—from classic Pinot Noir to citrus-scented naturals and hard-to-find producers, plus select Japanese wines showcasing local terroir."
      },
      {
        type: 'paragraph',
        content: "Intimate Setting: Limited to just 20 wine enthusiasts, ensuring personal attention from the sommelier and the unhurried pace that natural wine culture deserves."
      },
      {
        type: 'paragraph',
        content: "Pay As You Go: Order wines by the glass at your own pace (¥1,000-¥2,500 per glass) with optional artisan cheeses and small plates available to complement your selections."
      },
      {
        type: 'header',
        content: "Your Natural Wine Journey"
      },
      {
        type: 'bullets',
        items: [
          "French Exploration: Choose from exceptional natural wines from France's renowned regions—perhaps a Loire Valley white or a Jura oxidative beauty",
          "German Discovery: Experience Germany's celebrated natural wine movement with selections showcasing minerality and precision",
          "Japanese Touch: Sample select bottles from Japan's emerging wine scene as an intriguing contrast to European styles",
          "Artisan Accompaniments: Order handcrafted cheese selections and small plates that complement each wine's character",
          "Sommelier Guidance: Receive expert recommendations based on your preferences and learn about the winemakers, terroir, and techniques behind each bottle in an intimate, conversational setting",
          "Flexible Experience: Taste at your own pace, ordering as many or as few glasses as you'd like throughout the evening"
        ]
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Wines by the glass ranging from ¥1,000 to ¥2,500, paid individually as you order",
          "Access to purchase any featured bottles at bottle shop prices to take home",
          "Intimate setting limited to 20 wine lovers in an art-filled, California-inspired space",
          "Personal recommendations for your future wine explorations",
          "Casual, unhurried atmosphere where you control your own experience"
        ]
      },
      {
        type: 'header',
        content: "Perfect For Wine Lovers Who"
      },
      {
        type: 'bullets',
        items: [
          "Are curious about natural wine but don't know where to start",
          "Appreciate the stories and craftsmanship behind what they drink", 
          "Want to explore European natural wine traditions with expert guidance",
          "Love intimate experiences where the setting is as thoughtful as what's in the glass",
          "Prefer genuine conversation with knowledgeable guides over formal, stuffy tastings",
          "Are drawn to spaces where art, craft, and hospitality intersect",
          "Seek access to rare French and German natural wines at Tokyo's unbeatable prices",
          "Enjoy the flexibility of ordering at their own pace without fixed tasting menus"
        ]
      },
      {
        type: 'paragraph',
        content: "Join us for this exclusive evening where California warmth, Japanese craftsmanship, and European natural wine culture come together in one of Meguro's most beautiful hidden spaces. Limited to 20 guests who understand that the best wines are meant to be savored slowly and shared with interesting company."
      },
      {
        type: 'header',
        content: "Important Cancellation Policy"
      },
      {
        type: 'paragraph',
        content: "Due to the intimate nature of this experience and limited capacity, please cancel at least 72 hours before the event if you cannot attend. This ensures fairness to other wine enthusiasts on our waitlist."
      },
      {
        type: 'header',
        content: "Important Details"
      },
      {
        type: 'paragraph',
        content: "This is a pay-as-you-go experience. Wines by the glass range from ¥1,000 to ¥2,500, and artisan cheeses and small plates are available for purchase. You can also purchase bottles to take home at shop prices."
      },
      {
        type: 'header',
        content: "Location & Atmosphere"
      },
      {
        type: 'paragraph',
        content: "Our selected venue is tucked into the quiet residential streets of Meguro, near the peaceful Meguro River. The space features warm, dusty tones of pink and mint with soft gradients reminiscent of Georgia O'Keeffe paintings. Every element - from the handcrafted ceramics to the Hokkaido birch wine coolers - reflects the intersection of California warmth and Japanese attention to detail. Exact location will be shared with confirmed attendees 48 hours before the event."
      }
    ]
  },
  
  whatToExpect: [
    "Natural wine experience featuring French and German selections with Japanese highlights",
    "Pay-as-you-go format: wines by the glass (¥1,000-¥2,500) ordered at your own pace",
    "Intimate evening with maximum 12 fellow wine enthusiasts",
    "Optional artisan cheese boards and small plates available for purchase",
    "Art-filled space featuring handcrafted ceramics and California-inspired design",
    "Relaxed 2 -hour experience focused on discovery and conversation",
    "Option to purchase featured wines at bottle shop prices"
  ],
  
  venue: { 
    name: "TBD - Artisan Natural Wine Bar",
    description: "Natural wine bar and bottle shop featuring California vibes meets Japanese craftsmanship. Art-filled space with around 60 carefully curated bottles—primarily French and German natural wines with select Japanese offerings. Created by California chef bringing farm-to-table philosophy to Tokyo's wine scene.",
    fullAddress: "TBD - Meguro area, Meguro City, Tokyo (exact location revealed to confirmed attendees 48 hours prior)",
    priceRange: "Wines by the glass ¥1,000-¥2,500 (pay as you go)",
    amenities: ["European natural wine focus", "Bottle shop", "Handcrafted ceramics", "Art-filled atmosphere", "Expert sommelier", "Intimate 12-person capacity", "California-Japanese fusion aesthetic"]
  },
  
  memberBenefits: [
    {
      icon: "🎨",
      title: "Art Meets Wine",
      description: "Experience wines in a gallery-like space where every element is thoughtfully curated"
    },
    {
      icon: "👥", 
      title: "Intimate Discovery",
      description: "Limited to 20 people for personal attention and meaningful wine conversations"
    },
    {
      icon: "🌸",
      title: "Hidden Gem Access", 
      description: "Visit artisan spaces that locals cherish and tourists rarely discover"
    },
    {
      icon: "🍇",
      title: "Expert Guidance",
      description: "Learn from passionate sommeliers who know the European winemakers and stories behind each bottle"
    }
  ],
  
  relatedEvents: ['curated-spanish-tapas-october-2025', 'weekly-fika', 'happy-hour'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special",
  dayOfWeek: "Saturday", 
  focusArea: "European natural wine culture and artisan craft",
  experienceType: "guided wine journey",
  
  socialProof: {
    totalAttended: 0, // New event
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark & Tokyo Wine Experts",
    bio: "Passionate about connecting people through Tokyo's vibrant natural wine scene and artisan spaces",
    avatar: "mark-wine-hosts.jpg"
  },

  // Special features for curated wine experience  
  culinaryFocus: {
    style: "European natural wine culture with California warmth and Japanese craftsmanship",
    groupSize: "Maximum 12 for intimate tastings",
    duration: "2 hours of guided wine exploration",
    expertise: "Expert sommeliers with deep European producer relationships"
  },
  
  includedExperience: {
    guide: "Expert sommelier wine guide",
    tastings: "Pay-as-you-go wines by the glass—primarily French and German with Japanese highlights", 
    cultural_context: "Stories about European winemakers, regions, and natural wine philosophy",
    futureGuide: "Personalized recommendations for continued wine exploration"
  },
  
  foodiePhilosophy: "Discover European natural wine traditions through intimate experiences in art-filled spaces where the setting, the stories, and the selections are equally thoughtful",
  
  idealFor: [
    "Wine enthusiasts seeking authentic, unpretentious tasting experiences",
    "Curious drinkers wanting to explore French and German natural wines with expert guidance",
    "Art and design lovers who appreciate thoughtfully curated spaces", 
    "Anyone wanting access to rare European wines at Tokyo's unbeatable prices",
    "People who believe the best tastings happen in conversation, not lecture",
    "Explorers of Tokyo's hidden cultural gems beyond the tourist trail",
    "Francophiles and Germanophiles seeking authentic European wine culture in Tokyo",
    "Those who prefer flexible, pay-as-you-go experiences over fixed tasting menus"
  ],
  
  preparationTips: [
    "Come with curiosity about natural wine—no prior knowledge needed",
    "Arrive with an open palate ready to explore French and German flavor profiles",
    "Bring questions about wine, winemaking, or the natural wine movement",
    "Bring cash for easiest payment (¥3,000-¥7,000 recommended depending on your preferences)"
  ],
  
  uniqueAspects: [
    "Access to rare French and German natural wines at Tokyo prices (unrivaled globally)",
    "Art-filled space featuring handcrafted ceramics and thoughtful design",
    "Focus on European natural wine traditions with select Japanese discoveries",
    "California warmth meets Japanese attention to detail in every aspect",
    "Small group ensuring personal attention and genuine conversation",
    "Pay-as-you-go flexibility—order as many or as few glasses as you like",
    "Option to purchase featured wines at bottle shop prices",
    "Focus on discovery and enjoyment over formality and pretension",
    "Around 60 bottles curated primarily from France and Germany's best producers"
  ]
},



  {
    id: 'shoot-the-breeze-ginza-november-2025', 
    eventType: 'happy-hour',
    iconType: 'image',
    iconImage: '/images/events/GinzaHappyHour.jpg',
    iconValue: '🥂',
    title: "Shoot the Breeze Happy Hour Saturday: Tokyo Connections at Ginza Terrace Rooftop",
    description: "Casual Saturday happy hour where authentic connections happen over drinks on a beautiful Ginza terrace. No agenda, no awkward networking—just good people and great conversations.",
    waitlist: "0",
    next: "November 1, 2025",
    time: "6:00 PM - 8:30 PM",
    location: "TBD - Ginza Terrace Rooftop",
    color: "from-amber-500 to-orange-500",
    capacity: 35,
    difficulty: "Casual Social",
    tags: ["Happy Hour", "Ginza", "Rooftop", "Casual", "Saturday", "Networking"],
    featured: true,
    slug: 'shoot-the-breeze-ginza-november-2025',
  
    // Payment configuration
    cost: "FREE (pay for your own drinks)",
    paymentEnabled: false,
    requiresSubscription: false,
    perEventPriceCents: 0,
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: false,
      allowWaitlist: false
    },
  
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
  
    philosophy: {
      title: "Shoot the Breeze Happy Hour Saturday: Tokyo Connections at Ginza Terrace Rooftop",
      sections: [
        {
          type: 'paragraph',
          content: "Join us for Connect & Co.'s \"Shoot the Breeze\" Happy Hour Saturday! We're gathering at a beautiful terrace location in Ginza for 2.5 hours of relaxed conversation, new connections, and Tokyo stories."
        },
        {
          type: 'paragraph',
          content: "No agenda, no awkward networking games—just good people, great drinks, and the kind of conversations that make Tokyo feel like home. Whether you're new to the city, a seasoned expat, digital nomad, or a local looking to expand your international circle, you'll find friendly faces ready to chat about everything or nothing at all."
        },
        {
          type: 'header',
          content: "🥂 What is Shoot the Breeze?"
        },
        {
          type: 'paragraph',
          content: "Shooting the breeze means having relaxed, casual conversations without any particular agenda—just enjoying the moment and the company. It's about those organic chats where friendships naturally form over shared stories and laughter. Our Shoot the Breeze Happy Hours embrace this spirit, creating a space where Tokyo residents can unwind, connect authentically, and build genuine friendships in a sophisticated yet unpretentious setting."
        },
       {
          type: 'header',
          content: "Cost & Drinks"
        },
        {
          type: 'paragraph',
          content: "Pay for your own drinks (one drink minimum, ¥1,000-3,000 range, sodas, mocktails & cocktails available)"
        },
        {
          type: 'header',
          content: "Why Join Us?"
        },
        {
          type: 'paragraph',
          content: "This isn't your typical forced networking event. It's for people who value authentic connections, enjoy a good laugh, and appreciate a perfectly mixed drink in a beautiful setting. The intimate group size means you'll actually remember the people you meet and the conversations you have."
        },
        {
          type: 'paragraph',
          content: "Bring your stories, your questions about Tokyo life, or just your willingness to go with the flow. Sometimes the most meaningful connections happen when we're simply shooting the breeze."
        },
        {
          type: 'header',
          content: "What to Expect"
        },
        {
          type: 'bullets',
          items: [
            "6:00-6:15 PM: Arrive, grab a drink, meet your first new friend",
            "6:15-8:00 PM: Organic mingling and conversation on the terrace",
            "8:00-8:30 PM: Wind down with your favorite new connections"
          ]
        },
        {
          type: 'paragraph',
          content: "You'll leave with genuine connections, interesting stories, and probably plans for future meetups."
        },
        {
          type: 'header',
          content: "The Vibe"
        },
        {
          type: 'bullets',
          items: [
            "Sophisticated yet utterly unpretentious—just like the best friendships",
            "Beautiful Ginza terrace setting (weather permitting)",
            "Intimate group of 25 max—you'll actually remember everyone",
            "Mix of newcomers and regulars, expats and locals",
            "English-friendly environment with international crowd"
          ]
        },
        {
          type: 'header',
          content: "Important Attendance Policy"
        },
        {
          type: 'paragraph',
          content: "Members who RSVP 'yes' but fail to attend the event without canceling their RSVP beforehand will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
        }
      ]
    },
    
    whatToExpected: [
      "Relaxed terrace atmosphere in the heart of Ginza",
      "Organic conversations with 20-25 like-minded people",
      "Beautiful rooftop views (weather permitting)",
      "Mix of expats, locals, and international Tokyo residents",
      "Authentic connections over drinks—no forced networking"
    ],
    
    venue: { 
      name: "TBD - Ginza Terrace Rooftop",
      description: "Beautiful terrace location in Ginza perfect for casual happy hour mingling. Exact venue details will be shared with confirmed attendees closer to the event date.",
      fullAddress: "Ginza, Tokyo (Exact address TBD - will be shared with attendees)",
      priceRange: "FREE entry - drinks ¥1,000-3,000 range (pay for your own)",
      amenities: ["Rooftop terrace", "Central Ginza location", "Full bar service", "Indoor/outdoor seating"]
    },
    
    memberBenefits: [
      {
        icon: "🥂",
        title: "Pay-As-You-Go",
        description: "FREE event entry - just order your own drinks (¥1,000-3,000 range)"
      },
      {
        icon: "🏙️", 
        title: "Premium Ginza Location",
        description: "Beautiful terrace setting in Tokyo's most sophisticated neighborhood"
      },
      {
        icon: "🌅",
        title: "Rooftop Atmosphere", 
        description: "Enjoy drinks with views in an open-air terrace environment"
      },
      {
        icon: "🤝",
        title: "Intimate Group Size",
        description: "Just 25 people max—small enough for genuine connections"
      }
    ],
    
    relatedEvents: ['fika', 'weekly-fika', 'discussions', 'happy-hour'],
    status: "open",
    
    // Additional metadata
    eventFrequency: "occasional",
    dayOfWeek: "Saturday", 
    neighborhood: "Ginza",
    pricePoint: "free-entry",
    
    socialProof: {
      totalAttended: 0,
      averageRating: 0,
      repeatAttendees: "0%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Creating relaxed and authentic connection experiences across Tokyo's best neighborhoods",
      avatar: "mark-jason-host.jpg"
    },
  
    drinkSpecials: {
      packagePrice: "FREE entry",
      paymentMethod: "Pay for your own drinks at the venue",
      drinkRange: "¥1,000-3,000",
      options: ["Cocktails", "Mocktails", "Sodas", "Wine", "Beer"],
      minimum: "One drink minimum per person"
    },
    
    venueFeatures: {
      rooftopAccess: true,
      terraceSeating: "Primary seating area",
      weatherDependent: "Terrace access weather permitting",
      neighborhood: "Ginza"
    },
    
    attendancePolicy: "Members who RSVP 'yes' but fail to attend the event without canceling their RSVP beforehand will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system.",
    
    networkingStyle: "Organic, casual mingling—no structured activities or forced networking. Just good conversation flowing naturally over drinks on a beautiful terrace.",
    
    idealFor: [
      "New Tokyo residents wanting to build their social circle", 
      "Seasoned expats looking for genuine connections", 
      "Digital nomads seeking local community", 
      "Anyone tired of awkward networking events", 
      "People who believe the best friendships start with casual conversation"
    ],
  
    atmosphereKeywords: ["casual", "relaxed", "sophisticated", "unpretentious", "organic", "authentic", "intimate", "rooftop", "terrace", "Ginza"]
  },

  {
    id: 'osaka-monthly-fika-october-2025',
    eventType: 'fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
    iconValue: '☕',
    title: "October Fika: Osaka's Beloved Monthly Tradition",
    description: "Experience fika culture at a charming specialty coffee venue in Osaka. Quality coffee, delicious pastries, and meaningful connections in an intimate setting.",
    waitlist: "0",
    next: "October 25, 2025",
    time: "11:00 AM - 1:00 PM",
    location: "TBD - Specialty coffee venue (details sent one week before event)",
    color: "from-amber-500 to-orange-500",
    capacity: 20,
    difficulty: "Beginner Friendly",
    tags: ["Coffee", "Culture", "Make New Friends", "Expat", "Digital Nomad", "Osaka", "Kansai", "Specialty Coffee", "Community"],
    featured: true,
    slug: 'osaka-monthly-fika-october-2025',
    
    // ✅ City-related fields for Osaka
    city: "osaka",
    cityDisplay: "Osaka",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Autumn reflections to deepen our Osaka connections",
      questions: [
        {
          number: 1,
          english: "As we move deeper into autumn, what's one small pleasure or tradition you're looking forward to experiencing in Osaka this season?",
          japanese: "秋が深まるにつれて、この季節に大阪で経験したい小さな楽しみや伝統は何ですか？",
          context: "October in Osaka brings cooler evenings, the anticipation of koyo (autumn leaves), and seasonal comfort foods. Whether it's your first oden of the season, visiting Minoo for the maple leaves, or simply enjoying a warm cup of coffee on a crisp morning, share what you're most looking forward to as autumn unfolds in Kansai."
        },
        {
          number: 2,
          english: "This is our third monthly fika in Osaka - what's one thing you've learned about yourself or discovered about this city since our last gathering?",
          japanese: "大阪での2回目のマンスリーフィーカです。前回の集まりから、自分自身について学んだこと、またはこの街について発見したことは何ですか？",
          context: "As our monthly fika tradition takes root in Osaka, reflect on how the past month has shaped your experience here. Perhaps you've discovered a new neighborhood, deepened a connection, found a favorite local spot, or learned something unexpected about Kansai culture. Share how Osaka is revealing itself to you, one month at a time."
        }
      ]
    },
    
    philosophy: {
      title: "Monthly Fika Osaka: Discovering Hidden Gems in Kansai's Coffee Scene",
      sections: [
        {
          type: 'paragraph',
          content: "Join us for October's Monthly Fika as we continue our journey through Osaka's remarkable coffee landscape! Building on the success of our previous gathering, this month we're exploring another exceptional specialty coffee venue that embodies the warmth and craftsmanship that makes Kansai's coffee culture so special. This carefully selected café represents the perfect fusion of Swedish fika philosophy and Osaka's welcoming spirit."
        },
        {
          type: 'paragraph',
          content: "This month's venue is a charming specialty coffee shop where attention to detail meets genuine hospitality. With its inviting atmosphere and dedication to quality coffee, this hidden gem provides the ideal setting for our monthly tradition of slowing down, connecting deeply, and savoring meaningful moments together."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, and get to know new people."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "Our Monthly Fika Osaka embraces this beautiful Swedish tradition in venues that reflect Osaka's unique character – warm, welcoming, and authentic. Each month, we discover new spaces where quality coffee and genuine connection create something truly special."
        },
        {
          type: 'header',
          content: "What Makes This Month's Venue Special"
        },
        {
          type: 'paragraph',
          content: "This month's specialty coffee shop captures the essence of what makes Osaka's coffee scene so distinctive. The venue combines careful coffee preparation with a warm, intimate atmosphere that encourages conversation and connection."
        },
        {
          type: 'bullets',
          items: [
            "Specialty Coffee Craft: Experience thoughtfully prepared coffee where each cup reflects dedication to quality and flavor",
            "Intimate Setting: A cozy space perfect for meaningful conversations and authentic connections",
            "Welcoming Atmosphere: The kind of genuine Osaka hospitality that makes everyone feel at home",
            "Community Focus: A café that understands coffee is best enjoyed together, making it ideal for our fika gatherings"
          ]
        },
        {
          type: 'header',
          content: "The Swedish-Japanese Coffee Connection"
        },
        {
          type: 'paragraph',
          content: "There's a beautiful parallel between Swedish fika culture and Osaka's approach to hospitality. Both emphasize warmth, authenticity, and the belief that the best moments happen when people slow down and connect. Just as Swedes understand that coffee is about community, Osaka's coffee culture reflects the city's famous friendliness and love for bringing people together."
        },
        {
          type: 'paragraph',
          content: "In Sweden, fika is about finding balance and taking time for what matters. In Osaka, this same spirit lives in the way locals gather over coffee, share stories, and build lasting connections. Our monthly fika brings these two beautiful traditions together."
        },
        {
          type: 'header',
          content: "What to Expect at Our October Gathering"
        },
        {
          type: 'bullets',
          items: [
            "Quality Coffee: Enjoy specialty coffee prepared with care and attention to detail",
            "Cozy Atmosphere: Gather in an intimate setting perfect for autumn conversations",
            "Cultural Exchange: Connect with fellow coffee enthusiasts and community members in Kansai",
            "Meaningful Connections: Experience the art of unhurried conversation and genuine relationship building"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Specialty coffee drinks at café prices",
            "Pastries and light snacks available for individual purchase",
            "Cashless payments accepted (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We're limiting this gathering to 20 people to ensure intimate conversations and the perfect fika atmosphere. This allows for deeper connections while fully appreciating the cozy, welcoming environment that makes this venue special."
        },
        {
          type: 'paragraph',
          content: "Join us for this beautiful fusion of Swedish tradition and Osaka warmth, where every sip connects you to our growing Kansai community and every conversation enriches the relationships we're building together!"
        },
        {
          type: 'header',
          content: "Meeting Details"
        },
        {
          type: 'paragraph',
          content: "The exact venue location will be revealed to confirmed attendees one week prior to the event, ensuring an exclusive experience for our committed community members. Look for the Connect & Co group—we'll have a small sign to make us easy to find!"
        },
        {
          type: 'header',
          content: "Important RSVP Policy"
        },
        {
          type: 'paragraph',
          content: "Your response confirms your attendance. If your plans change and you can no longer join us, please update & cancel your RSVP promptly to allow others the opportunity to participate."
        }
      ]
    },
    
    whatToExpect: [
      "Experience specialty coffee at a charming café in Osaka",
      "Enjoy quality coffee and pastries in an intimate, welcoming setting",
      "Connect with up to 20 community members in a cozy atmosphere",
      "Learn about coffee culture through natural conversation",
      "Discover the intersection of Swedish fika culture and Osaka hospitality",
      "Build meaningful relationships in Kansai's growing international community"
    ],
    
    venue: { 
      name: "TBD - Specialty Coffee Venue",
      description: "A charming specialty coffee shop in Osaka that embodies the warmth and quality of Kansai's coffee culture. Features carefully prepared specialty coffee, a cozy intimate atmosphere, and genuine Osaka hospitality.",
      fullAddress: "Location revealed to confirmed attendees one week prior to the event, ensuring an exclusive experience for our committed community members",
      nearestStation: "Details provided with venue announcement",
      priceRange: "Free event - Specialty coffee and pastries at café prices",
      amenities: ["Specialty coffee", "Cozy atmosphere", "Intimate setting", "Quality pastries", "Welcoming environment", "Cashless payments"]
    },
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Specialty Coffee Quality",
        description: "Experience thoughtfully prepared specialty coffee in a venue dedicated to quality and craft"
      },
      {
        icon: "🏠", 
        title: "Cozy Intimate Setting",
        description: "Gather in a warm, welcoming space perfect for meaningful conversations and authentic connections"
      },
      {
        icon: "🤝",
        title: "Genuine Hospitality", 
        description: "Experience the kind of warm Osaka welcome that makes everyone feel at home"
      },
      {
        icon: "👥",
        title: "Community Building",
        description: "Limited to 20 people for deeper connections and personalized fika experience"
      }
    ],
    
    relatedEvents: ['osaka-monthly-fika-august-2025', 'weekly-fika', 'fika', 'forest-walks'],
    status: "open",
    
    // Additional metadata for October Osaka event
    eventFrequency: "monthly",
    dayOfWeek: "Sunday", 
    culturalFocus: "Swedish fika tradition meets Osaka hospitality",
    specialFeature: "Specialty coffee in intimate, welcoming setting",
    
    socialProof: {
      totalAttended: 0,
      averageRating: 0,
      repeatAttendees: "0%"
    },
    
    hostInfo: {
      name: "Mark & Sayoko",
      bio: "Passionate about bringing Swedish coffee culture to Kansai region while showcasing Osaka's finest specialty coffee venues",
      avatar: "mark-takuro-bettu-osaka-hosts.jpg"
    },
  
    // Venue-specific features
    venueSpecialties: {
      coffee_expertise: {
        preparation: "Carefully crafted specialty coffee with attention to detail",
        quality: "Dedication to quality beans and proper brewing techniques"
      },
      atmosphere: {
        style: "Cozy, intimate café with warm and welcoming ambiance",
        seating: "Comfortable setting perfect for conversations",
        character: "Embodies Osaka's friendly, authentic spirit"
      },
      philosophy: {
        approach: "Coffee as a way to bring people together and build community",
        hospitality: "Genuine Osaka warmth and welcoming service",
        focus: "Creating space for meaningful connection and conversation"
      }
    },
    
    menuHighlights: {
      specialty_coffees: {
        description: "Thoughtfully prepared specialty coffee drinks",
        quality: "Quality beans prepared with care and precision"
      },
      food_options: {
        pastries: "Pastries and light snacks available for individual purchase",
        atmosphere: "Perfect complement to quality coffee in cozy setting"
      }
    },
    
    attendancePolicy: "Your response confirms your attendance. If your plans change and you can no longer join us, please update & cancel your RSVP promptly to allow others the opportunity to participate.",
    
    conversationTopics: [
      "Swedish fika tradition and its adaptation to Japanese coffee culture",
      "Discovering Osaka's specialty coffee scene and hidden gems", 
      "Autumn in Kansai - seasonal traditions and experiences",
      "Living in Osaka and building international community",
      "The art of slowing down in Japan's most welcoming city",
      "Cross-cultural connections and finding belonging in new places"
    ],
    
    idealFor: [
      "Coffee enthusiasts interested in specialty coffee culture",
      "International professionals in Kansai region seeking community",
      "Swedish culture enthusiasts and fika tradition lovers",
      "Anyone curious about Osaka's coffee scene and local culture",
      "People seeking meaningful connections in intimate settings",
      "Community builders wanting regular gatherings in Osaka"
    ],
    
    uniqueAspects: [
      "Monthly fika at carefully selected specialty coffee venues",
      "Free format with access to quality coffee at café prices",
      "Intimate 20-person maximum for deeper connections",
      "Cozy, welcoming atmosphere reflecting Osaka's warm spirit",
      "Focus on authentic relationships and meaningful conversation",
      "Discovery of Osaka's hidden coffee gems each month"
    ],
    
    preparationTips: [
      "Arrive a few minutes early to settle into the cozy space",
      "Come ready to connect and share stories over quality coffee",
      "Bring curiosity about Swedish culture and Osaka hospitality",
      "Prepare to slow down and embrace the art of fika"
    ],
    
    accessInfo: {
      venue_reveal: "Location shared one week before event with confirmed attendees",
      neighborhood: "Osaka specialty coffee venue",
      atmosphere: "Cozy, intimate café perfect for community gatherings"
    },
  
    // Enhanced experience details
    experienceFormat: {
      cost: "Free event - pay for individual drinks and food",
      coffee_selection: "Quality specialty coffee prepared with care",
      atmosphere: "Intimate, cozy setting for authentic connections",
      style: "Relaxed social gathering in welcoming environment"
    },
  
    // Cultural component details
    culturalComponent: {
      fika_tradition: "Swedish art of slowing down and connecting over coffee",
      osaka_spirit: "Warm, genuine hospitality that makes everyone feel welcome",
      community_focus: "Building lasting relationships through regular monthly gatherings",
      seasonal_awareness: "Celebrating autumn in Osaka through shared experiences"
    },
  
    // Premium experience with accessible format
    venueExperience: {
      venue_tier: "Specialty coffee venue with intimate, cozy atmosphere",
      group_size: "Intimate 20-person maximum for meaningful connections",
      format: "Free social gathering - individual coffee and food purchases",
      uniqueness: "Carefully selected venue reflecting both Swedish fika values and Osaka warmth",
      community: "Monthly tradition building lasting relationships in Kansai"
    }
  },

 
 
 
 {
    id: 'curated-spanish-tapas-october-2025',
    eventType: 'foodie',
    iconType: 'image',
    iconImage: '/images/events/SpanishTapas.jpg',
    iconValue: '🍷',
    title: "Hidden Basque: Spanish Tapas Experience (Tokyo)",
    description: "Intimate Omakase Spanish tapas dinner at authentic Waseda wine bar (¥5,500 cash, drinks separate). Chef-curated menu featuring Basque specialties, grilled dishes, and premium Spanish wines.",
    waitlist: "8",
    next: "October 18, 2025",
    time: "5:00 PM - 7:30 PM",
    location: "TBD - Authentic Spanish Wine Bar, Waseda",
    color: "from-red-600 to-amber-600",
    capacity: 11,
    difficulty: "Culinary Experience",
    tags: ["Spanish Tapas", "Wine Pairing", "Intimate Dining", "Basque Cuisine", "Grilled", "Premium Experience"],
    featured: true,
    slug: 'curated-spanish-tapas-october-2025',
    
    // City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan", 
    timezone: "Asia/Tokyo",
    currency: "JPY",
    cost: "¥5,500 (Cash only) per person (Omakase included, drinks separate)",
  
    philosophy: {
      title: "Curated Spanish Tapas Evening: Authentic Basque Dining Experience",
      sections: [
        {
          type: 'header',
          content: "For a seamless registration experience, please review the information below and visit the Details tab before reserving your spot."
        },
        {
          type: 'paragraph',
          content: "Experience an intimate evening of authentic Spanish cuisine at a hidden gem in Waseda where the warmth of Spanish hospitality meets Tokyo's attention to detail. This isn't just dinner—it's a cultural journey through Spain's rich culinary traditions, guided by a chef who brings authentic Basque flavors to life through grilling and time-honored techniques."
        },
        {
          type: 'paragraph',
          content: "Tucked away in the quiet residential neighborhood of Waseda, our carefully selected Spanish wine bar offers an atmosphere that transports you straight to a cozy Spanish taverna. The wood-warm interior and intimate setting create the perfect backdrop for an evening of slow food, good wine, and meaningful conversation with fellow food enthusiasts."
        },
        {
          type: 'header',
          content: "Registration Process & Additional Information Required"
        },
        {
          type: 'paragraph',
          content: "After you register for this exclusive dining experience, you will receive a follow-up email within 24 hours requesting additional details essential for planning your perfect evening:"
        },
        {
          type: 'bullets',
          items: [
            "Full name and phone number for reservation confirmation and day-of coordination",
            "Any dietary restrictions or food allergies (critical for chef planning)"
         
          ]
        },
        {
          type: 'paragraph',
          content: "Please respond to this follow-up email promptly to secure your spot and help our chef create the most personalized experience possible."
        },
        {
          type: 'header',
          content: "What Makes This Evening Special"
        },
        {
          type: 'paragraph',
          content: "Authentic Basque Experience: Our chef specializes in traditional Spanish regional cuisine, featuring signature grilled dishes and house-made pintxos that showcase the true flavors of northern Spain."
        },
        {
          type: 'paragraph',
          content: "Intimate Setting: Limited to just 8 guests for this exclusive evening, ensuring personalized attention and the kind of unhurried dining experience that defines Spanish food culture."
        },
        {
          type: 'paragraph',
          content: "Curated Wine Selection: Including the rare Basque wine 'Txakoli' and carefully selected Spanish varietals that perfectly complement each course."
        },
        {
          type: 'header',
          content: "Your Omakase Spanish Basque Omakase Journey"
        },
        {
          type: 'bullets',
          items: [
            "Chef's Selection Pintxos: Seasonal opening bites showcasing the finest Basque ingredients and traditional preparations",
            "Premium Seafood Course: Daily catch from Basque waters prepared using time-honored coastal cooking techniques", 
            "Signature Grilled Specialties: Chef's masterpiece featuring premium meats and vegetables over authentic Spanish charcoal",
            "Artisanal Basque Cheese & Charcuterie: Carefully selected regional products paired with traditional accompaniments",
            "Traditional Spanish Dessert: Seasonal sweet finish highlighting authentic Basque pastry and confectionery techniques"
          ]
        },
        {
          type: 'header',
          content: "What's Included in Your ¥5,500 Experience"
        },
        {
          type: 'bullets',
          items: [
            "Complete Omakase chef's menu featuring authentic Spanish Basque specialties",
            "Intimate dining experience limited to 12 food enthusiasts maximum",
            "Cultural context and stories behind traditional Spanish cooking techniques",
            "Access to exclusive Basque wine selection and premium Spanish varietals (drinks paid separately)"
          ]
        },
        {
          type: 'header',
          content: "Perfect For Food Lovers Who"
        },
        {
          type: 'bullets',
          items: [
            "Appreciate authentic regional cuisine over fusion interpretations",
            "Enjoy intimate dining experiences with fellow food enthusiasts", 
            "Want to discover traditional Spanish flavors and cooking techniques",
            "Love the unhurried pace of European dining culture",
            "Are curious about Spanish wine culture, especially rare Basque varietals"
          ]
        },
        {
          type: 'paragraph',
          content: "Join us for this exclusive culinary journey where Spanish tradition meets Japanese precision. Limited to 8 guests who understand that the best meals are meant to be savored slowly and shared with interesting company."
        },
        {
          type: 'header',
          content: "Important Cancellation Policy"
        },
        {
          type: 'paragraph',
          content: "Due to the intimate nature of this experience and advance planning required with our chef, cancellations must be made at least 48 hours before the event. Cancellations made with less than 48 hours notice will be responsible for the full ¥5,500 payment. This policy ensures fairness to other food enthusiasts on our waitlist and allows our chef adequate time to adjust preparations."
        },
        {
          type: 'header',
          content: "Important Details"
        },
        {
          type: 'paragraph',
          content: "Please inform us of any dietary restrictions when you respond to our follow-up email. The chef can accommodate most preferences while maintaining the authenticity of the Spanish menu. Wine and drink selections are ordered separately and paid individually."
        },
        {
          type: 'header',
          content: "Location & Atmosphere"
        },
        {
          type: 'paragraph',
          content: "Our selected venue is located in the peaceful Waseda neighborhood. The restaurant's wood-warm interior and intimate setting create an authentic Spanish taverna atmosphere perfect for unhurried dining and conversation. Exact location will be shared with confirmed attendees 48 hours before the event."
        }
      ]
    },
    
    whatToExpect: [
      "5-course chef-curated Spanish tapas menu featuring authentic Basque specialties",
      "Follow-up email requesting additional dining preferences and contact information",
      "Intimate dining experience with maximum 8 fellow food enthusiasts",
      "Signature grilled dishes showcasing traditional Spanish techniques",
      "Access to rare Basque wine 'Txakoli' and premium Spanish wine selection",
      "Unhurried 2.5-hour dining experience in authentic Spanish taverna atmosphere"
    ],
    
    venue: { 
      name: "TBD - Authentic Spanish Wine Bar",
      description: "Authentic Spanish wine bar specializing in Basque regional cuisine and grilled specialties. Wood-warm interior creates intimate Spanish taverna atmosphere in quiet Waseda neighborhood.",
      fullAddress: "TBD - Waseda area, Shinjuku City, Tokyo (exact location revealed to confirmed attendees 48 hours prior)",
      priceRange: "¥5,500 (Cash) Omakase (drinks paid separately)",
      amenities: ["Authentic Spanish cuisine", "Grilling", "Basque wine selection", "Intimate 8-person capacity", "Traditional Spanish atmosphere"]
    },
    
    memberBenefits: [
      {
        icon: "🎯",
        title: "Expert Curation",
        description: "Local insider guide and authentic Spanish culinary experience"
      },
      {
        icon: "👥", 
        title: "Intimate Experience",
        description: "Limited to 8 people for personal attention and access to tiny venues"
      },
      {
        icon: "🏮",
        title: "Hidden Gems Access", 
        description: "Visit family-run shops and locals-only spots tourists never discover"
      },
      {
        icon: "📚",
        title: "Cultural Deep Dive",
        description: "Learn the stories, techniques, and traditions behind each authentic flavor"
      }
    ],
    
    relatedEvents: ['pizza-night-daikanyama', 'fika', 'weekly-fika', 'happy-hour'],
    status: "open",
    
    // Additional metadata
    eventFrequency: "monthly",
    dayOfWeek: "Saturday", 
    focusArea: "authentic food culture",
    experienceType: "guided culinary adventure",
    
    socialProof: {
      totalAttended: 0, // New event
      averageRating: 0,
      repeatAttendees: "0%"
    },
    
    hostInfo: {
      name: "Mark & Tokyo Food Experts",
      bio: "Passionate about connecting people through authentic Tokyo food culture and hidden culinary gems",
      avatar: "mark-foodie-hosts.jpg"
    },
  
    // Special features for curated foodie experience  
    culinaryFocus: {
      style: "Authentic Tokyo establishments off the tourist trail",
      groupSize: "Maximum 12 for intimate access",
      duration: "4 hours of guided tastings",
      expertise: "Local insiders with deep establishment relationships"
    },
    
    includedExperience: {
      guide: "Expert local food guide",
      tastings: "All food samples at 5-6 locations", 
      cultural_context: "Stories and traditions behind each dish",
      futureGuide: "Detailed recommendations for solo adventures"
    },
    
    foodiePhilosophy: "Discover Tokyo's authentic food soul through small group experiences at family-run establishments where recipes and techniques haven't changed in generations",
    
    idealFor: [
      "Food enthusiasts seeking authentic over touristy experiences",
      "Curious eaters who appreciate cultural context with their meals",
      "Small group experience lovers who value quality over quantity", 
      "Anyone wanting insider access to Tokyo's hidden culinary gems",
      "People who believe the best food comes with the best stories"
    ],
    
    preparationTips: [
      "Come with an adventurous palate and open mind",
      "Wear comfortable walking shoes for neighborhood exploration",
      "Bring curiosity about food traditions and techniques",
      "Inform us of dietary restrictions when you RSVP"
    ],
    
    uniqueAspects: [
      "Access to establishments that don't accommodate large tour groups",
      "Relationships built over years with authentic food artisans",
      "Stories and cultural context unavailable on typical food tours",
      "Small group ensuring personal attention from expert guide",
      "Focus on authenticity over Instagram-worthy spots"
    ]
  },

   {
    id: 'weekly-dec-17-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "December 17, 2025", 
    time: "11:00 AM - 1:00 PM",
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-dec-17-2025',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

    {
    id: 'weekly-dec-23-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "December 24, 2025", 
    time: "11:00 AM - 1:00 PM",
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-dec-23-2025',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

        {
    id: 'weekly-jan-1-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "January 1, 2025", 
    time: "2:00 PM - 5:00 PM",
    eventDateTime: "2026-01-01T05:00:00Z",  // ✅ 11 AM JST = 2 AM UTC

    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-jan-1-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
   // exclusive: true,  
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


      {
    id: 'weekly-jan-7-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "January 7, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-01-07T02:00:00Z",  // ✅ 11 AM JST = 2 AM UTC

    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-jan-7-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    // exclusive: true,  
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

      {
    id: 'weekly-jan-14-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Tuesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "January 14, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-01-14T02:00:00Z",  // ✅ 11 AM JST = 2 AM UTC

    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-jan-14-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Tuesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


     {
    id: 'weekly-jan-21-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "January 21, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-01-21T02:00:00Z",  // ✅ 11 AM JST = 2 AM UTC
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-jan-21-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    cost: "¥2,500",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

    {
    id: 'weekly-jan-28-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "January 28, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-01-28T02:00:00Z",

    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-jan-28-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    cost: "¥2,500",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


    {
    id: 'weekly-feb-4-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "February 4, 2025", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-02-04T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-feb-4-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
     cost: "¥2,500",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


    {
    id: 'weekly-feb-11-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "February 11, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-02-04T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-feb-11-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


    {
    id: 'weekly-feb-18-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "February 18, 2026", 
   // time: "11:00 AM - 1:00 PM",
   // eventDateTime: "2026-02-04T02:00:00Z", 
    time: "3:00 PM - 5:00 PM",
    eventDateTime: "2026-02-18T06:00:00Z",
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-feb-18-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


    {
    id: 'weekly-feb-25-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "February 25, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-02-25T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-feb-25-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },



    {
    id: 'weekly-march-4-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "March 4, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-03-04T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-march-4-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

   {
    id: 'weekly-march-11-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "March 11, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-03-11T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-march-11-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

   {
    id: 'weekly-march-18-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "March 18, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-03-18T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-march-18-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

   {
    id: 'weekly-march-25-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "March 25, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-03-25T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-march-25-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

   {
    id: 'weekly-april-1-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "April 1, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-04-01T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-april-1-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

    {
    id: 'weekly-april-8-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "April 8, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-04-08T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-april-8-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

    {
    id: 'weekly-april-21-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Tues, April 21, 2025", 
    time: "12:00 PM - 1:00 PM",
    eventDateTime: "2026-04-21T03:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-april-21-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

    {
    id: 'weekly-april-22-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, April 22, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-04-22T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-april-22-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

     {
    id: 'weekly-april-29-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, April 29, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-04-29T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-april-29-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

    {
    id: 'weekly-may-6-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, May 6, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-05-06T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-may-6-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

     {
    id: 'weekly-may-12-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, May 13, 2026", 
    //time: "11:00 AM - 1:00 PM",
    //eventDateTime: "2026-05-13T02:00:00Z", 
    time: "3:00 PM - 5:00 PM",
    eventDateTime: "2026-05-13T06:00:00Z", // 3:00 PM JST
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-may-13-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

     {
    id: 'weekly-may-20-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, May 20, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-05-20T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-may-20-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

     {
    id: 'weekly-june-5-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Fri, June 5, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-06-05T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-june-5-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


     {
    id: 'weekly-june-10-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, June 10, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-06-10T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-june-10-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

     {
    id: 'weekly-june-17-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, June 17, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-06-17T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-june-17-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

     {
    id: 'weekly-june-24-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, June 24, 2025", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-06-24T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-june-24-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

      {
    id: 'weekly-july-01-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, July 1, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-07-01T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-july-01-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

        {
    id: 'weekly-july-08-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, July 8, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-07-08T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-july-08-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


  {
    id: 'weekly-july-15-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, July 15, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-07-15T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-july-15-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

   {
    id: 'weekly-july-22-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, July 22, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-07-22T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-july-22-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

     {
    id: 'weekly-july-29-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, July 29, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-07-29T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-july-29-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

      {
    id: 'weekly-august-5-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, August 5, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-08-05T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-august-5-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

      {
    id: 'weekly-august-12-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, August 12, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-08-12T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-august-12-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

      {
    id: 'weekly-august-19-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, August 19, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-08-19T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-august-19-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

        {
    id: 'weekly-august-26-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, August 26, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-08-26T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-august-26-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

       {
    id: 'weekly-september-02-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, September 2, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-09-02T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-september-02-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

      {
    id: 'weekly-september-09-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, September 9, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-09-09T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-september-09-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

     {
    id: 'weekly-september-16-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, September 16, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-09-16T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-september-16-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

      {
    id: 'weekly-september-23-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, September 23, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-09-23T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-september-23-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

     {
    id: 'weekly-september-30-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, September 30, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-09-30T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-september-30-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


     {
    id: 'weekly-october-7-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, October 7, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-10-07T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-october-7-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


     {
    id: 'weekly-october-14-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, October 14, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-10-14T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-october-14-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

      {
    id: 'weekly-october-21-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, October 21, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-10-21T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-october-21-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

        {
    id: 'weekly-october-28-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, October 28, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-10-28T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-october-28-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

          {
    id: 'weekly-november-4-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, November 5, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-11-04T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-november-4-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


          {
    id: 'weekly-november-11-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, November 11, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-11-11T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-november-11-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

          {
    id: 'weekly-november-18-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, November 18, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-11-18T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-november-18-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


          {
    id: 'weekly-november-25-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, November 25, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-11-25T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-november-25-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


     {
    id: 'weekly-may-27-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "Wed, May 27, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-05-27T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-may-27-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
 
    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

    {
    id: 'weekly-april-15-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "April 15, 2026", 
    time: "11:00 AM - 1:00 PM",
    eventDateTime: "2026-04-15T02:00:00Z", 
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-april-15-2026',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    paymentEnabled: true,
    requiresSubscription: true,
    perEventPriceCents: 2000,     // ¥2,000 cover charge
    currency: "JPY",
    paymentOptions: {
      allowSubscription: true,
      allowPerEventPayment: true,
      allowWaitlist: true
    },
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 10 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD ",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

   {
    id: 'weekly-dec-10-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "December 10, 2025", 
    time: "11:00 AM - 1:00 PM",
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-dec-10-2025',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Kitazawa)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

    {
    id: 'weekly-dec-2-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "December 2, 2025", 
    time: "11:00 AM - 1:00 PM",
    location: "TBD - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-dec-2-2025',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

    {
    id: 'weekly-nov-26-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "November 26, 2025", 
    time: "11:00 AM - 1:00 PM",
    location: "TBD (Nakameguro) - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-nov-26-2025',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "95%"
    },
    
    hostInfo: {
      name: "/Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },



  {
    id: 'weekly-nov-19-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "November 19, 2025", 
    time: "11:00 AM - 1:00 PM",
    location: "TBD (Kitazawa) - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-nov-19-2025',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Kitazawa)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "85%"
    },
    
    hostInfo: {
      name: "Mark & Miriam",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


  {
    id: 'weekly-nov-12-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "November 12, 2025", 
    time: "11:00 AM - 1:00 PM",
    location: "TBD (Nakameguro) - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-nov-12-2025',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "85%"
    },
    
    hostInfo: {
      name: "Mark & Terry",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


  {
    id: 'weekly-nov-5-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "November 5, 2025", 
    time: "11:00 AM - 1:00 PM",
    location: "TBD (Kitazawa) - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-nov-5-2025',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Kitazawa)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "85%"
    },
    
    hostInfo: {
      name: "Mark & Myriam",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


  {
    id: 'weekly-oct-29-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "October 29, 2025", //October 22, 2025
    time: "11:00 AM - 1:00 PM",
    location: "TBD (Shibuya) - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 10,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-oct-29-2025',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Shibuya (Yoyogi-Uehara or Hatagaya Stations))",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },

    // Community Perks Section
    communityPerks: [
      {
        id: 'brewed-awakening-cold-brew',
        type: 'beverage',
        enabled: true,
        name: 'Brewed Awakening Cold Brew',
        description: 'Small-batch cold brew crafted by our founder, Mark',
        price: 800,
        currency: 'JPY',
        emoji: '🥤',
        availability: 'at-event',
        preOrderEnabled: true,
    //    preOrderUrl: 'https://connectco.app/pre-order-cold-brew',
        emailMention: {
          enabled: true,
          heading: '🥤 Refresh at the Event',
          message: 'Small-batch **Brewed Awakening** cold brew (crafted by our founder) will be available at select events. A refreshing way to fuel great conversations!',
          callToAction: '💬 **Want one?** Reply "YES" to this email and I\'ll bring enough! (¥500/bottle, optional)'
        }
      }
    ],
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "85%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

  {
    id: 'weekly-oct-22-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "October 22, 2025", //October 22, 2025
    time: "11:00 AM - 1:00 PM",
    location: "TBD (Nakameguro) - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 20,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-oct-22-2025',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "85%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },
 

  {
    id: 'weekly-oct-15-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "October 15, 2025", //October 15, 2025
    time: "CANCELLED",
    location: "TBD (Omotesando) - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 20,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: false,
    slug: 'weekly-swedish-fika-oct-15-2025',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
          japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
          context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
        },
        {
          number: 2,
          english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
          japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
          context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika meetup community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Omotesando)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "85%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },
  


{
  id: 'weekly-oct-8-fika',
  eventType: 'weekly-fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
  iconValue: '🍵',
  title: "Tokyo Weekly Signature Fika | Drop-In Coffee Community ☕",
  description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
  waitlist: "0",
  next: "October 8, 2025", 
  time: "11:00 AM - 1:00 PM",
  location: "TBD (Kitazawa) - 3rd wave coffee venue",
  color: "from-emerald-500 to-teal-500",
  capacity: 20,
  difficulty: "Beginner Friendly", 
  tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
  featured: false,
  slug: 'weekly-swedish-fika-oct-8-2025',
  
 
  // ✅ NEW: City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Conversation starters to deepen our connections",
    questions: [
      {
        number: 1,
        english: "What's one place you've traveled to that completely surprised you - either because it was so different from what you expected, or because it changed something about how you see the world?",
        japanese: "今まで旅行した場所の中で、期待していたものと全く違っていたり、世界の見方が変わったりして、完全に驚かされた場所はありますか？",
        context: "Think about a destination that challenged your assumptions, showed you a different way of living, or simply turned out to be nothing like the guidebooks described. Share what made it so unexpected and what you learned from the experience."
      },
      {
        number: 2,
        english: "If you could take someone special on a dream trip anywhere in the world, where would you go and what would you most want to share or experience together?",
        japanese: "大切な人と一緒に世界中のどこでも夢の旅行に行けるとしたら、どこに行って、何を一番共有したり一緒に体験したりしたいですか？",
        context: "This could be a romantic partner, family member, close friend, or anyone meaningful to you. Think about what kind of experience would be most meaningful to share - adventure, relaxation, culture, food, nature - and why that particular place and person combination feels perfect."
      }
    ]
  },
  
  philosophy: {
    title: "Find Time To Slow Down Through Weekly Swedish Fika",
    sections: [
      {
        type: 'paragraph',
        content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika meetup community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
      },
      {
        type: 'paragraph',
        content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
      },
      {
        type: 'header',
        content: "Understanding Fika: More Than Just Coffee"
      },
      {
        type: 'paragraph',
        content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
      },
      {
        type: 'paragraph',
        content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
      },
      {
        type: 'paragraph',
        content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
      },
      {
        type: 'header',
        content: "What makes our weekly Fika special"
      },
      {
        type: 'bullets',
        items: [
          "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
          "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
          "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
          "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
        ]
      },
      {
        type: 'header',
        content: "What to expect each week"
      },
      {
        type: 'bullets',
        items: [
          "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
          "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
          "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
          "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
          "Pastry and light meal options available (selection varies by café)",
          "Most venues accept cashless payments (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
      },
      {
        type: 'paragraph',
        content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
      }
    ]
  },
  
  whatToExpect: [
    "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
    "Connect with community members weekly for deeper relationship building",
    "Explore different neighborhoods and their unique café cultures",
    "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
    "Engage in conversations about travel, culture, coffee, and life in Tokyo"
  ],
  
  venue: { 
    name: "TBD (Kitazawa)",
    description: "",
    fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
    priceRange: "Coffee ¥450-800, Light meals vary by café",
    amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Weekly Coffee Discovery",
      description: "Explore a new third wave coffee shop each week with expert curation"
    },
    {
      icon: "🤝", 
      title: "Consistent Community",
      description: "Build deeper relationships through regular weekly interactions"
    },
    {
      icon: "🗾",
      title: "Tokyo Exploration", 
      description: "Discover hidden coffee gems across different Tokyo neighborhoods"
    },
    {
      icon: "🎯",
      title: "Intimate Gatherings",
      description: "Small group size (10 people) ensures meaningful connections"
    }
  ],
  
  relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
  status: "open",
  
  // Additional metadata for weekly event
  eventFrequency: "weekly",
  dayOfWeek: "Wednesday",
  socialProof: {
    totalAttended: 45,
    averageRating: 4.9,
    repeatAttendees: "85%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
    avatar: "mark-jason-hosts.jpg"
  }
},


  {
    id: 'weekly-sept-24-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Weekly Signature Fika | Drop-In Coffee Community ☕",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "September 24, 2025", 
    time: "11:00 AM - 1:00 PM",
    location: "TBD (Nakameguro/Daikan-Yama) - 3rd wave coffee venue",
    color: "from-emerald-500 to-teal-500",
    capacity: 20,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: false,
    slug: 'weekly-swedish-fika-sept-24-2025',
    
   
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
  
    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What activity or experience consistently gives you energy and makes you feel most like yourself?",
          japanese: "いつもあなたにエネルギーを与えてくれて、「自分らしい」と感じられる活動や体験は何ですか？",
          context: "Whether it's something creative, physical, social, or solitary - share what reliably lifts your spirits and reconnects you with who you are. This could be anything from morning walks to deep conversations, cooking, or working on a hobby."
        },
        {
          number: 2,
          english: "What's your ideal way to spend a cozy evening as the weather gets cooler - and is there a comfort food or drink that's essential to the experience?",
          japanese: "涼しくなってきたこの時期、居心地の良い夜の過ごし方はどんな感じですか？その時間に欠かせない食べ物や飲み物はありますか？",
          context: "As autumn settles in and evenings become more inviting to stay indoors, share what comfort looks like to you - whether it's reading with tea, cooking something warm, watching movies, or any ritual that makes you feel content and cozy."
        }
      ]
    },
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika meetup community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD (Nakameguro)",
      description: "",
      fullAddress: "Location revealed to confirmed attendees days prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "85%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

{
  id: 'weekly-sept-10-fika',
  eventType: 'weekly-fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
  iconValue: '🍵',
  title: "Weekly Signature Fika | Drop-In Coffee Community ☕",
  description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
  waitlist: "0",
  next: "September 10, 2025", 
  time: "11:00 AM - 1:00 PM",
  location: "STREAMER COFFEE COMPANY AZABU-JUBAN",
  color: "from-emerald-500 to-teal-500",
  capacity: 20,
  difficulty: "Beginner Friendly", 
  tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
  featured: true,
  slug: 'weekly-swedish-fika-sept-10-2025',
  
 
  // ✅ NEW: City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Conversation starters to deepen our connections",
    questions: [
      {
        number: 1,
        english: "What's one small ritual or habit that helps you transition from summer into fall, or what are you looking forward to most about the changing season?",
        japanese: "夏から秋への移り変わりの中で、心地よく過ごすための小さな習慣やルーティンはありますか？または、季節の変化の中で楽しみにしていることは何ですか？",
        context: "As we move into September and early fall, share something that helps you embrace the seasonal change - perhaps a new coffee ritual, a favorite autumn activity, or simply what you're excited about as the weather shifts."
      },
      {
        number: 2,
        english: "If you could introduce someone new to Tokyo to just one neighborhood or hidden spot, where would you take them and why?",
        japanese: "東京に初めて来た人に、一つだけ地域や隠れた場所を紹介するとしたら、どこに連れて行きますか？その理由も教えてください。",
        context: "Whether you're a longtime resident or relatively new yourself, share a place that captures something special about Tokyo for you - it could be a neighborhood, a specific café, a park, or any spot that feels meaningful."
      }
    ]
  },
  
  philosophy: {
    title: "Find Time To Slow Down Through Weekly Swedish Fika",
    sections: [
      {
        type: 'paragraph',
        content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika meetup community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
      },
      {
        type: 'paragraph',
        content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
      },
      {
        type: 'header',
        content: "Understanding Fika: More Than Just Coffee"
      },
      {
        type: 'paragraph',
        content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
      },
      {
        type: 'paragraph',
        content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
      },
      {
        type: 'paragraph',
        content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
      },
      {
        type: 'header',
        content: "What makes our weekly Fika special"
      },
      {
        type: 'bullets',
        items: [
          "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
          "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
          "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
          "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
        ]
      },
      {
        type: 'header',
        content: "What to expect each week"
      },
      {
        type: 'bullets',
        items: [
          "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
          "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
          "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
          "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
          "Pastry and light meal options available (selection varies by café)",
          "Most venues accept cashless payments (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
      },
      {
        type: 'paragraph',
        content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
      }
    ]
  },
  
  whatToExpect: [
    "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
    "Connect with community members weekly for deeper relationship building",
    "Explore different neighborhoods and their unique café cultures",
    "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
    "Engage in conversations about travel, culture, coffee, and life in Tokyo"
  ],
  
  venue: { 
    name: "STREAMER COFFEE COMPANY AZABU-JUBAN",
    description: "",
    fullAddress: "〒106-0032 Tokyo, Minato City, Roppongi, 6 Chome−11−16 中銀六本木マンシオン",
    priceRange: "Coffee ¥450-800, Light meals vary by café",
    amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Weekly Coffee Discovery",
      description: "Explore a new third wave coffee shop each week with expert curation"
    },
    {
      icon: "🤝", 
      title: "Consistent Community",
      description: "Build deeper relationships through regular weekly interactions"
    },
    {
      icon: "🗾",
      title: "Tokyo Exploration", 
      description: "Discover hidden coffee gems across different Tokyo neighborhoods"
    },
    {
      icon: "🎯",
      title: "Intimate Gatherings",
      description: "Small group size (10 people) ensures meaningful connections"
    }
  ],
  
  relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
  status: "open",
  
  // Additional metadata for weekly event
  eventFrequency: "weekly",
  dayOfWeek: "Wednesday",
  socialProof: {
    totalAttended: 45,
    averageRating: 4.9,
    repeatAttendees: "85%"
  },
  
  hostInfo: {
    name: "Mark",
    bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
    avatar: "mark-jason-hosts.jpg"
  }
},

{
  id: 'weekly-aug-27-fika',
  eventType: 'weekly-fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
  iconValue: '🍵',
  title: "Weekly Signature Fika | Drop-In Coffee Community ☕",
  description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
  waitlist: "0",
  next: "August 27, 2025", 
  time: "11:00 AM - 1:00 PM",
  location: "Sarutahiko Coffee The Bridge",
  color: "from-emerald-500 to-teal-500",
  capacity: 20,
  difficulty: "Beginner Friendly", 
  tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
  featured: true,
  slug: 'weekly-swedish-fika-aug-27-2025',
  
 
  // ✅ NEW: City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",
  
  philosophy: {
    title: "Find Time To Slow Down Through Weekly Swedish Fika",
    sections: [
      {
        type: 'paragraph',
        content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika meetup community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
      },
      {
        type: 'paragraph',
        content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
      },
      {
        type: 'header',
        content: "Understanding Fika: More Than Just Coffee"
      },
      {
        type: 'paragraph',
        content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
      },
      {
        type: 'paragraph',
        content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
      },
      {
        type: 'paragraph',
        content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
      },
      {
        type: 'header',
        content: "What makes our weekly Fika special"
      },
      {
        type: 'bullets',
        items: [
          "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
          "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
          "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
          "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
        ]
      },
      {
        type: 'header',
        content: "What to expect each week"
      },
      {
        type: 'bullets',
        items: [
          "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
          "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
          "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
          "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
          "Pastry and light meal options available (selection varies by café)",
          "Most venues accept cashless payments (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
      },
      {
        type: 'paragraph',
        content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
      }
    ]
  },
  
  whatToExpect: [
    "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
    "Connect with community members weekly for deeper relationship building",
    "Explore different neighborhoods and their unique café cultures",
    "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
    "Engage in conversations about travel, culture, coffee, and life in Tokyo"
  ],
  
  venue: { 
    name: "Sarutahiko Coffee The Bridge",
    description: "",
    fullAddress: "〒150-0001 Tokyo, Shibuya, Jingumae, 1 Chome−18−20 Harajuku Station, 3階",
    priceRange: "Coffee ¥450-800, Light meals vary by café",
    amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
  },
  
  memberBenefits: [
    {
      icon: "☕",
      title: "Weekly Coffee Discovery",
      description: "Explore a new third wave coffee shop each week with expert curation"
    },
    {
      icon: "🤝", 
      title: "Consistent Community",
      description: "Build deeper relationships through regular weekly interactions"
    },
    {
      icon: "🗾",
      title: "Tokyo Exploration", 
      description: "Discover hidden coffee gems across different Tokyo neighborhoods"
    },
    {
      icon: "🎯",
      title: "Intimate Gatherings",
      description: "Small group size (10 people) ensures meaningful connections"
    }
  ],
  
  relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
  status: "open",
  
  // Additional metadata for weekly event
  eventFrequency: "weekly",
  dayOfWeek: "Wednesday",
  socialProof: {
    totalAttended: 45,
    averageRating: 4.9,
    repeatAttendees: "85%"
  },
  
  hostInfo: {
    name: "MarkJason",
    bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
    avatar: "mark-jason-hosts.jpg"
  }
},

// /images/events/goal-accountability-hero.jpg
{
  id: 'goal-accountability-partners-info-session-sept-2025',
  eventType: 'goal-accountability',
 // iconType: 'emoji',
 // iconValue: '🎯',
  iconType: 'image',
  iconImage: '/images/events/goal-accountability-hero.jpg',
  title: "Goal Accountability Partners: Turn Solo Struggles Into Shared Success",
  description: "Discover how 6-8 like-minded people pursuing your exact same goal can transform your achievement rate from 8% to 92%. Learn about our new Goal Accountability Partners program combining Swedish Fika tradition with proven accountability science.",
  waitlist: "0",
  next: "March 14, 2025",
  time: "10:00 AM - 12:00 PM",
  location: "TBD",
  color: "from-purple-600 to-blue-600",
  capacity: 30,
  difficulty: "Goal Achievement Workshop",
  tags: ["Goal Achievement", "Accountability", "Community Support", "Personal Development", "Swedish Fika", "Goal Setting", "Achievement Science", "Habit Formation"],
  featured: true,
  slug: 'goal-accountability-partners-info-session-sept-2025',
  
  // City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo", 
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",
  cost: "Free (pay for your own drinks)",

  philosophy: {
    title: "Why We Quit Goals Alone But Celebrate Victories Together",
    sections: [
      {
        type: 'paragraph',
        content: "Quick question: What happened to your 2025 goals? If you're like most of us, they started with excitement in January... and quietly disappeared by March. You're not alone. Studies show 92% of people abandon their goals, and there's one glaring reason why: We pursue goals in isolation, but humans are wired for community."
      },
      {
        type: 'header',
        content: "The Problem: Solo Struggle Cycle"
      },
      {
        type: 'paragraph',
        content: "January 1st: 'This is my year!' By March: quietly abandoning another goal, wondering what's wrong with you. Sound familiar? Through years of building community and watching friends (including myself) repeat this cycle, we noticed something: The goals we achieve aren't the ones we pursue alone."
      },
      {
        type: 'paragraph',
        content: "When friends launch businesses, it's because they have co-founders checking in daily. When community members stick to fitness goals, it's because they have workout buddies. The pattern is clear: **accountability + community = unstoppable momentum.**"
      },
      {
        type: 'header', 
        content: "The Solution: Goal Accountability Partners"
      },
      {
        type: 'paragraph',
        content: "Goal Accountability Partners matches you with 6-8 people pursuing your *exact same goal* and timeline. Not similar goals. Not related goals. The **exact same outcome** you're chasing."
      },
      {
        type: 'header',
        content: "How It Works"
      },
      {
        type: 'bullets',
        items: [
          "🎯 You tell us your specific goal (like 'Pass JLPT N2 by December 2026' or 'Read 53 books in 2026' or something else)",
          "👥 We find your tribe (6-8 people with identical goals and timelines)",
          "🤝 You check in together (weekly, bi-weekly, or monthly for 6 weeks—completely free)",
          "🚀 You actually achieve it (because accountability + community = unstoppable momentum)"
        ]
      },
      {
        type: 'header',
        content: "🚨 IMPORTANT: Complete Your Application BEFORE Attending"
      },
      {
        type: 'paragraph',
        content: "To make the most of this event and ensure we can provide personalized guidance, please complete your Goal Accountability Partners application before attending. This allows us to review your goals in advance and provide targeted advice during the workshop."
      },
      {
        type: 'link',
        text: "👇 Click here to fill out the matching form:",
        url: "https://connectco.app/goal-accountability-partners",
        linkText: "Complete Your Application Now",
        style: "primary"
      },
      {
        type: 'link',
        text: "❓ Have questions first? Get all your answers here:",
        url: "https://connectco.app/goal-accountability-faq", 
        linkText: "Read the FAQ",
        style: "secondary"
      },
      {
        type: 'header',
        content: "The Swedish Fika Connection"
      },
      {
        type: 'paragraph',
        content: "This isn't just another productivity hack. It's built on the same philosophy as our Fika gatherings—authentic human connection over shared experiences. Instead of coffee and conversation, it's goals and growth. Same intimate group size (6-8 people). Same focus on genuine relationships. Same 'show up as you are' energy that makes our community special."
      },
      {
        type: 'header',
        content: "Tokyo Edition: Premium In-Person Groups"
      },
      {
        type: 'paragraph',
        content: "We're launching with intimate accountability meetups at Tokyo's best cafés and co-working spaces—bringing our signature Fika philosophy to goal achievement. Think cozy corners, exceptional coffee, and 6-8 driven people who share your exact goal and timeline."
      },
      {
        type: 'paragraph',
        content: "*Global Network (video/hybrid format) launching next year for our international community—Tokyo members get first access to test this new experience.*"
      },
      {
        type: 'header',
        content: "Why This Actually Works"
      },
      {
        type: 'paragraph',
        content: "Remember the last time you achieved something big? I bet you weren't doing it completely alone. Maybe you had a workout buddy, study group, or business partner. There's magic in shared struggle."
      },
      {
        type: 'paragraph',
        content: "Your Goal Accountability Partners become your reality check when you're making excuses, celebration crew for small wins that matter, wisdom source when you hit obstacles they've already solved, and gentle push when motivation disappears (and it will)."
      },
      {
        type: 'header',
        content: "Who We're Looking For"
      },
      {
        type: 'bullets',
        items: [
          "Serious about a specific goal (with a clear deadline)",
          "Ready to commit to 6 weeks of consistent check-ins", 
          "Willing to both give and receive honest support",
          "Available for in-person meetups in Tokyo",
          "Done with the solo struggle cycle"
        ]
      },
      {
        type: 'header',
        content: "Cost: Completely Free 6-Week Program"
      },
      {
        type: 'paragraph',
        content: "This is a completely free 6-week program for Connect & Co community members. Because the best things in life—friendship, growth, and achievement—shouldn't cost anything. The application takes 5 minutes. We'll match you within 5-7 business days. Your first group check-in could happen as early as next week."
      },
      {
        type: 'header',
        content: "Event Format: Interactive Q&A + Application Workshop"
      },
      {
        type: 'paragraph',
        content: "Join us for this interactive session where we'll explain the program, answer all your questions, and help you craft a goal that's specific enough for perfect matching. Since you'll have already submitted your application, we can provide personalized feedback and guidance specific to your goals."
      }
    ]
  },
  
  
  
  whatToExpect: [
    "Learn the science behind why 92% of people abandon goals and how to join the 8% who succeed",
    "Understand the Goal Accountability Partners matching process and program structure",
    "Interactive workshop to define your specific goal with clear timeline and metrics",
    "Live Q&A about the program, commitment level, and Tokyo meetup locations",
    "Submit your application with expert guidance to ensure perfect matching",
    "Network with other goal-oriented community members considering the program"
  ],
  
  venue: { 
    name: "TBD",
    description: "Collaborative workspace and community hub designed for meaningful conversations and personal development gatherings in Meguro district.",
    fullAddress: "TBD",
    priceRange: "Free event, individual drink purchase required + optional food",
    amenities: ["Collaborative workspace", "Community focus", "Drink ordering required", "Group discussion areas", "Central Meguro location", "Interactive presentation setup"]
  },
  
  memberBenefits: [
    {
      icon: "🎯",
      title: "Free 6-Week Program",
      description: "Complete Goal Accountability Partners program at no cost for Connect & Co members"
    },
    {
      icon: "👥", 
      title: "Perfect Matching",
      description: "Get matched with 6-8 people pursuing your exact same goal and timeline"
    },
    {
      icon: "🇸🇪",
      title: "Swedish Fika Philosophy", 
      description: "Authentic connection approach combining goal achievement with community building"
    },
    {
      icon: "📍",
      title: "Tokyo Premium Locations",
      description: "In-person meetups at Tokyo's best cafés and co-working spaces"
    }
  ],
  
  relatedEvents: ['growth-circle', 'fika', 'weekly-fika', 'discussions'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "special",
  dayOfWeek: "Saturday", 
  focusArea: "goal achievement and accountability",
  programType: "info session and application workshop",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%" // New program launch
  },
  
  hostInfo: {
    name: "Mark & ConnectCo Team",
    bio: "Community builders passionate about transforming solo struggles into shared success through authentic accountability",
    avatar: "mark-goal-accountability-hosts.jpg"
  },

  // Program-specific features
  programDetails: {
    duration: "6 weeks",
    commitment: "Weekly, bi-weekly, or monthly check-ins",
    groupSize: "6-8 people",
    matchingCriteria: "Exact same goal and timeline",
    cost: "Completely free for Connect & Co members"
  },
  
  applicationProcess: {
    timeToComplete: "5 minutes",
    matchingTime: "5-7 business days",
    firstMeeting: "As early as next week after matching"
  },
  
  uniqueAspects: [
    "First Goal Accountability Partners program launch in Tokyo",
    "Swedish Fika tradition applied to goal achievement",
    "Exact goal matching (not similar or related goals)",
    "Premium Tokyo café and co-working space meetups",
    "Completely free 6-week program for members"
  ],
  
  idealFor: [
    "People serious about achieving a specific goal with clear deadline",
    "Those tired of the solo struggle cycle with personal goals",
    "Community members wanting to combine goal achievement with relationship building",
    "Anyone ready to commit to consistent check-ins and mutual support",
    "Tokyo residents available for in-person accountability meetups"
  ],
  
  preparationTips: [
    "Come with a specific goal in mind (we'll help refine it during the workshop)",
    "Think about your timeline and what success looks like to you",
    "Be ready to commit to 6 weeks of consistent participation",
    "Bring questions about the program structure and commitment level"
  ],
  
  sessionStructure: {
    duration: "2 hours",
    format: "Presentation + Interactive Workshop + Q&A + Application Submission",
    maxCapacity: "35 people for intimate discussion and personalized guidance"
  },
  
  takeaways: [
    "Clear understanding of Goal Accountability Partners program",
    "Refined, specific goal with measurable timeline", 
    "Submitted application for expert matching",
    "Knowledge of proven accountability science and community principles",
    "Connection with other goal-oriented community members"
  ],
  
  nextSteps: [
    "Application review and matching process (5-7 business days)",
    "Introduction to your Goal Accountability Partners group",
    "First group meetup at premium Tokyo venue",
    "Begin 6-week accountability journey with your matched tribe"
  ]
},


{
  id: 'osaka-monthly-fika-september-2025',
  eventType: 'fika',
  iconType: 'image',
  iconImage: '/images/events/ConnectCoMonthlyFika.jpg', // Same image as other Monthly Fika events
  iconValue: '☕',
  title: "Monthly Fika Osaka: Swedish Coffee Culture Meets Wine Expertise",
  description: "Experience the fusion of Swedish fika tradition and world-class coffee expertise at TAKAMURA WINE & COFFEE ROASTERS. Award-winning roasters, rare beans, and intimate conversations in Osaka's most sophisticated coffee warehouse.",
  waitlist: "0",
  next: "September 14, 2025",
  time: "11:00 AM - 1:00 PM",
  location: "TAKAMURA WINE & COFFEE ROASTERS (2nd Floor - Sofas & Table in Front of Restroom)",
  color: "from-amber-500 to-orange-500",
  capacity: 20,
  difficulty: "Beginner Friendly",
  tags: ["Coffee", "Culture", "Make New Friends", "Expat", "Digital Nomad", "Osaka", "Kansai", "Specialty Coffee", "Wine Culture", "Award-Winning"],
  featured: true,
  slug: 'osaka-monthly-fika-september-2025',
  
  // ✅ City-related fields for Osaka
  city: "osaka",
  cityDisplay: "Osaka",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",

  fikaQuestion: {
    title: "Today's Fika Questions",
    subtitle: "Conversation starters to deepen our connections",
    questions: [
      {
        number: 1,
        english: "What drew you to Osaka, or if you're from here, what's something about Kansai culture that you think outsiders don't fully understand?",
        japanese: "大阪に来た理由は何ですか？関西出身の方は、関西の文化について、外の人にはなかなか理解されないと思うことはありますか？",
        context: "Whether you're new to Osaka or a lifelong resident, share what makes this region special to you - perhaps the warmth of Kansai hospitality, the food culture, the unique dialect, or the entrepreneurial spirit that defines this area."
      },
      {
        number: 2,
        english: "Since this is our second monthly fika gathering in Osaka, what kind of community or connections are you hoping we'll build together here?",
        japanese: "大阪での初めてのマンスリーフィーカということで、ここでどのようなコミュニティやつながりを築いていきたいと思いますか？",
        context: "As we launch this new tradition in Japan's culinary capital, share your hopes for what this monthly gathering could become - whether it's deep friendships, cultural exchange, business connections, or simply a regular moment of Swedish-inspired calm in your Osaka life."
      }
    ]
  },
  
  philosophy: {
    title: "Monthly Fika Osaka: Swedish Coffee Culture Meets Wine Expertise at TAKAMURA",
    sections: [
      {
        type: 'paragraph',
        content: "Join us for September's Monthly Fika at one of Osaka's most extraordinary coffee destinations! Building on the success of our inaugural August gathering, we're now exploring TAKAMURA WINE & COFFEE ROASTERS—a unique warehouse where award-winning coffee artistry meets world-class wine expertise. This isn't just another café; it's a temple of craftsmanship where every cup tells a story of origin, terroir, and meticulous attention to detail."
      },
      {
        type: 'paragraph',
        content: "TAKAMURA represents the perfect fusion of Swedish fika philosophy and Japanese precision. Here, award-winning head roaster Yuya Iwasaki and his elite team treat coffee with the same reverence that sommeliers reserve for fine wine. With around 30 varieties of rare specialty beans on the menu—including Cup of Excellence winners and rarities like Geisha from Panama's Creativa Coffee District—this is where coffee transcends beverage and becomes art."
      },
      {
        type: 'header',
        content: "Understanding Fika: More Than Just Coffee"
      },
      {
        type: 'paragraph',
        content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
      },
      {
        type: 'paragraph',
        content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
      },
      {
        type: 'paragraph',
        content: "Our Monthly Fika Osaka embraces this beautiful Swedish tradition in an environment where coffee is elevated to the highest artistic standards. At TAKAMURA, every cup is a journey through origin, process, and flavor—the perfect setting for mindful conversation and authentic connection."
      },
      {
        type: 'header',
        content: "What Makes TAKAMURA WINE & COFFEE ROASTERS Extraordinary"
      },
      {
        type: 'paragraph',
        content: "TAKAMURA occupies a stunning two-story warehouse with corrugated metal ceilings, exposed steel beams, and warm incandescent lighting that creates an atmosphere both industrial and intimate. This former commercial warehouse has been transformed into one of Japan's most sophisticated coffee and wine destinations, housing over 2,200 wines alongside some of the world's finest coffee beans."
      },
      {
        type: 'bullets',
        items: [
          "Award-Winning Expertise: Head roaster Yuya Iwasaki and his elite team of baristas serve up rare varieties using one of only 10 Smart Roasters in Japan—the most advanced roasting technology available",
          "Exceptional Value: Premium specialty coffees, including rare Geisha varieties, priced incredibly reasonably (around ¥450) to make artisanal coffee accessible to everyone",
          "Wine Culture Philosophy: TAKAMURA applies wine principles to coffee—emphasizing origin, terroir, tasting notes, and food pairing, bringing a sophisticated approach to coffee appreciation",
          "Intimate 2nd Floor Setting: We'll gather in the comfortable upstairs area with sofas and chairs, away from the main retail bustle, perfect for meaningful conversations"
        ]
      },
      {
        type: 'header',
        content: "The Swedish-Japanese Coffee Connection"
      },
      {
        type: 'paragraph',
        content: "There's a beautiful parallel between Swedish fika culture and TAKAMURA's philosophy. Both treat coffee as more than caffeine—it's about slowing down, appreciating craftsmanship, and creating space for human connection. Just as Swedes understand that the best conversations happen over coffee, TAKAMURA has created a space where coffee appreciation becomes a communal experience."
      },
      {
        type: 'paragraph',
        content: "In Sweden, fika is about finding balance and taking time for what matters. At TAKAMURA, every cup represents hours of careful cultivation, precise roasting, and expert brewing—a meditation on the craft that perfectly complements the Swedish art of mindful pause."
      },
      {
        type: 'header',
        content: "What to Expect at Our September Gathering"
      },
      {
        type: 'bullets',
        items: [
          "Coffee Excellence: Experience some of the world's finest coffee, prepared by award-winning baristas using cutting-edge roasting technology",
          "Educational Component: Learn about coffee origins, processing methods, and tasting notes in an accessible, conversational format",
          "Comfortable Setting: Relax in the spacious 2nd floor lounge area with sofas and comfortable seating, away from retail activity",
          "Cultural Exchange: Connect with fellow coffee enthusiasts and experience the intersection of Swedish tradition and Japanese craftsmanship"
        ]
      },
      {
        type: 'header',
        content: "Cost: Just pay for your own drinks and food"
      },
      {
        type: 'bullets',
        items: [
          "Specialty coffee drinks range from ¥450-¥600+ (exceptional value for premium quality)",
          "Rare varieties like Geisha from Panama available at incredibly reasonable prices",
          "Light pastries and snacks available for individual purchase",
          "Cashless payments accepted (credit cards and electronic payments)"
        ]
      },
      {
        type: 'paragraph',
        content: "We're limiting this gathering to 20 people to ensure intimate conversations and the perfect fika atmosphere. This allows for deeper connections while fully appreciating TAKAMURA's exceptional coffee artistry and the wide selection of premium beans available at incredibly reasonable prices."
      },
      {
        type: 'paragraph',
        content: "Join us for this unique fusion of Swedish tradition and Japanese precision, where every sip connects you to the global community of coffee artisans and every conversation enriches our growing Kansai community!"
      },
      {
        type: 'header',
        content: "Meeting Details"
      },
      {
        type: 'paragraph',
        content: "We'll gather on the 2nd floor at the sofas and table positioned in front of the restroom. This comfortable lounge area provides the perfect intimate setting for our fika gathering. Look for the Connect & Co group—we'll have a small sign to make us easy to find!"
      },
      {
        type: 'header',
        content: "Important RSVP Policy"
      },
      {
        type: 'paragraph',
        content: "Your response confirms your attendance. If your plans change and you can no longer join us, please update & cancel your RSVP promptly to allow others the opportunity to participate."
      }
    ]
  },
  
  whatToExpect: [
    "Experience award-winning specialty coffee from one of Japan's most sophisticated roasters",
    "Choose from around 30 premium varieties including rare Geisha from Panama at exceptional prices",
    "Learn about coffee origins, processing methods, and tasting notes through natural conversation",
    "Connect with up to 20 community members at sofas & table in front of restroom (2nd floor)",
    "Enjoy individually-selected coffee and pastries in stunning industrial warehouse setting",
    "Discover the intersection of Swedish fika culture and Japanese coffee artistry"
  ],
  
  venue: { 
    name: "TAKAMURA WINE & COFFEE ROASTERS (2nd Floor - Sofas & Table in Front of Restroom)",
    description: "Award-winning specialty coffee roaster and wine destination housed in a stunning industrial warehouse. Features one of only 10 Smart Roasters in Japan, over 2,200 wines, and 80+ seats across two floors with comfortable upstairs lounge area. We'll gather at the sofas and table positioned in front of the restroom on the 2nd floor.",
    fullAddress: "2 Chome-2-18 Edobori, Nishi Ward, Osaka, 550-0002",
    nearestStation: "Higobashi Station (10-minute walk west)",
    priceRange: "Free event - Specialty coffee ¥450-¥600+, Light pastries and snacks available",
    amenities: ["Award-winning roasters", "Smart Roaster technology", "2nd floor lounge", "Specialty coffee varieties", "Wine collection", "Industrial warehouse aesthetic", "Comfortable seating", "Educational experience"]
  },
  
  memberBenefits: [
    {
      icon: "🏆",
      title: "Award-Winning Coffee",
      description: "Experience rare specialty beans roasted by award-winning head roaster Yuya Iwasaki using advanced Smart Roaster technology"
    },
    {
      icon: "🍷", 
      title: "Wine Culture Approach",
      description: "Discover coffee through wine principles - origin, terroir, and tasting notes in sophisticated warehouse setting"
    },
    {
      icon: "☕",
      title: "Exceptional Coffee Access", 
      description: "Choose from ~30 premium varieties including rare beans at incredibly reasonable prices (¥450+ for Geisha quality)"
    },
    {
      icon: "👥",
      title: "Intimate Gathering",
      description: "Limited to 20 people in comfortable 2nd floor lounge for meaningful connections and personalized experience"
    }
  ],
  
  relatedEvents: ['osaka-monthly-fika-august-2025', 'weekly-fika', 'fika', 'forest-walks'], // Links to other Osaka and fika events
  status: "open",
  
  // Additional metadata for September Osaka event
  eventFrequency: "monthly",
  dayOfWeek: "Sunday", 
  culturalFocus: "Swedish fika tradition meets Japanese coffee artistry",
  specialFeature: "Award-winning specialty coffee tasting at premium roastery",
  
  socialProof: {
    totalAttended: 0, // New September event
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark & Takuro",
    bio: "Passionate about bringing Swedish coffee culture to Kansai region while showcasing Japan's finest specialty coffee craftsmanship",
    avatar: "mark-takuro-bettu-osaka-hosts.jpg"
  },

  // TAKAMURA-specific features
  venueSpecialties: {
    coffee_expertise: {
      head_roaster: "Award-winning Yuya Iwasaki and elite barista team",
      equipment: "Smart Roaster technology (only ~10 in Japan)",
      varieties: "Around 30 specialty beans including Cup of Excellence winners"
    },
    atmosphere: {
      style: "Industrial warehouse with corrugated metal ceilings and exposed steel beams",
      lighting: "Warm incandescent lighting creating intimate yet spacious atmosphere",
      seating: "80+ seats total, comfortable 2nd floor lounge with sofas and chairs"
    },
    philosophy: {
      approach: "Wine principles applied to coffee - origin, terroir, tasting notes",
      accessibility: "Premium specialty coffee at reasonable prices (¥450+ for rare varieties)",
      education: "Focus on coffee appreciation and understanding of craft"
    }
  },
  
  menuHighlights: {
    specialty_coffees: {
      description: "Choose from around 30 premium specialty beans",
      varieties: "Cup of Excellence winners, rare Geisha, unique processing methods",
      pricing: "¥450-¥600+ (exceptional value for premium quality)"
    },
    food_options: {
      pastries: "Light pastries and snacks available for individual purchase",
      atmosphere: "Perfect complement to world-class coffee in stunning warehouse setting"
    }
  },
  
  attendancePolicy: "Your response confirms your attendance. If your plans change and you can no longer join us, please update & cancel your RSVP promptly to allow others the opportunity to participate.",
  
  conversationTopics: [
    "Swedish fika tradition and its adaptation to Japanese coffee culture",
    "Coffee appreciation - origins, processing methods, and tasting notes", 
    "The intersection of wine and coffee expertise",
    "Living in Kansai region and building international community",
    "Specialty coffee scene in Japan vs international markets",
    "The art of slowing down in Japan's culinary capital"
  ],
  
  idealFor: [
    "Coffee enthusiasts interested in specialty beans and roasting techniques",
    "International professionals in Kansai region seeking community",
    "Swedish culture enthusiasts and fika tradition lovers",
    "Anyone curious about the intersection of wine and coffee expertise",
    "People seeking educational yet social coffee experiences",
    "Community builders wanting regular connection in Osaka"
  ],
  
  uniqueAspects: [
    "Monthly Fika at award-winning specialty roastery and wine destination",
    "Free format with access to premium coffee at exceptional prices",
    "Access to one of only 10 Smart Roasters in Japan",
    "Industrial warehouse aesthetic with world-class coffee expertise",
    "Wine culture principles applied to coffee appreciation",
    "Intimate 2nd floor setting away from retail activity"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to settle into the 2nd floor lounge area",
    "Come ready to learn about coffee origins and appreciation through conversation",
    "Bring curiosity about Swedish culture and Japanese craftsmanship",
    "Prepare to explore TAKAMURA's incredible selection of specialty coffees individually"
  ],
  
  accessInfo: {
    station: "Higobashi Station (10-minute walk west)",
    building: "Two-story warehouse with gleaming glass front", 
    floor_location: "2nd floor - sofas and table in front of the restroom",
    nearby_landmarks: "Edobori district, Utsubo Park area"
  },

  // Enhanced experience details
  experienceFormat: {
    cost: "Free event - pay for individual drinks and food",
    coffee_selection: "Choose from ~30 specialty varieties individually",
    learning_style: "Natural conversation about coffee appreciation",
    atmosphere: "Relaxed social gathering in premium coffee setting"
  },

  // Educational component details
  educationalComponent: {
    format: "Natural conversation and coffee appreciation through social interaction",
    topics: ["Coffee origins and terroir", "Roasting techniques", "Tasting different varieties", "Swedish fika culture meets Japanese craftsmanship"],
    level: "Casual learning through authentic coffee experience"
  },

  // Premium venue with accessible format
  venueExperience: {
    venue_tier: "Award-winning specialty roastery with premium setting",
    group_size: "Intimate 20-person maximum for meaningful connections",
    format: "Free social gathering - individual coffee and food purchases",
    expertise_access: "Natural interaction with award-winning coffee environment",
    uniqueness: "Access to rare coffee varieties and advanced roasting technology at exceptional prices"
  }
},


  {
    id: 'osaka-monthly-fika-august-2025',
    eventType: 'fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoMonthlyFika.jpg', // Same image as Tokyo Monthly Fika
    iconValue: '☕',
    title: "Inaugural Monthly Fika Osaka: Swedish Coffee Culture Meets Kansai Hospitality",
    description: "Inaugural launch of Connect & Co's Monthly Fika tradition in Japan's culinary capital! Experience Swedish art of slowing down at Brooklyn Roasting Company Kitahama along the beautiful Dojima River.",
    waitlist: "0",
    next: "August 16, 2025",
    time: "8:00 AM - 12:00 PM",
    location: "Brooklyn Roasting Company Kitahama",
    color: "from-amber-500 to-orange-500",
    capacity: 15,
    difficulty: "Beginner Friendly",
    tags: ["Coffee", "Culture", "Make New Friends", "Expat", "Digital Nomad", "Osaka", "Kansai", "Riverside", "Brooklyn Coffee"],
    featured: true,
    slug: 'osaka-monthly-fika-august-2025',
    
    // ✅ City-related fields for Osaka
    city: "osaka",
    cityDisplay: "Osaka",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    
    philosophy: {
      title: "Monthly Fika Osaka: Swedish Coffee Culture Meets Kansai Hospitality",
      sections: [
        {
          type: 'paragraph',
          content: "Join us for the inaugural launch of Connect & Co's Monthly Fika tradition in Japan's culinary capital! Experience the Swedish art of slowing down and meaningful connection at one of Osaka's most spectacular coffee destinations, nestled along the beautiful Dojima River."
        },
        {
          type: 'paragraph',
          content: "Building on the overwhelming success of our Tokyo Fika community—Kansai's first regular Swedish coffee gathering—we're bringing this cherished tradition of mindful pausing to Osaka's vibrant coffee scene. Each month, we'll explore Osaka's finest specialty coffee venues where exceptional beans, expert craftsmanship, and thoughtful brewing methods create the perfect backdrop for genuine human connection."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too)."
        },
        {
          type: 'paragraph',
          content: "Our Monthly Fika Osaka embraces this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Osaka's dynamic specialty coffee culture together."
        },
        {
          type: 'header',
          content: "What Makes Brooklyn Roasting Company Kitahama Special"
        },
        {
          type: 'paragraph',
          content: "Brooklyn Roasting Company Kitahama represents the perfect fusion of New York coffee culture and Japanese precision. This stunning riverside location offers beautiful views of the Dojima River and is often considered the most picturesque Brooklyn Roasting Company location in all of Japan, featuring a popular outdoor terrace."
        },
        {
          type: 'bullets',
          items: [
            "Authentic Brooklyn Coffee Culture: Founded in 2009 in Williamsburg Brooklyn, they brought that same energy to Japan with their first overseas café in Kitahama, serving Brooklyn-style coffee roasted in-house using eco-friendly smart roasters made by Loring",
            "Specialty Coffee Excellence: Known for their superb and sustainable coffee with small batch roasting, they work directly with farms and focus on traceability, offering darker roasts that are more common in New York than typically found in Japan",
            "Riverside Ambiance: Enjoy exceptional coffee while overlooking the river and the beautiful Osaka City Central Public Hall, creating a waterside experience reminiscent of Brooklyn's Dumbo neighborhood"
          ]
        },
        {
          type: 'header',
          content: "What Makes Our Monthly Fika Special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Osaka's Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across Kansai's coffee capital",
            "Quality & Craft: Enjoy specialty drinks like their signature blends, cortados, and carefully prepared pour-overs by skilled baristas who treat coffee as both science and art", 
            "Community Building: Connect with familiar faces and welcome newcomers to our growing Kansai community every month, creating deeper bonds through regular interaction in Japan's most hospitable region",
            "Cultural Exchange: Embrace the Swedish tradition of slowing down and connecting through coffee while exploring Osaka's unique neighborhoods and their distinctive café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each month"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Kansai",
            "Monthly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different specialty coffee shops across Osaka, each with its own character, story, and exceptional beans",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: ¥3,000 bottomless drinks package (pay in person at check-in)"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks range from ¥550-¥825 (Drip Coffee ¥550, Latte ¥770, Cappuccino ¥682, Americano ¥693)",
            "Pastries and light bites available including donuts, croissants, and their famous scones",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate monthly gatherings, creating truly personal connections while exploring Osaka's incredible coffee landscape. Our Tokyo events consistently attract engaged participants, so we encourage you to sign up early to secure your spot in our inaugural Osaka gathering!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting launch of Monthly Fika Osaka as we bring Swedish coffee culture to Japan's culinary capital. Let's discover Osaka's exceptional specialty coffee scene together while building lasting connections within our growing Kansai community!"
        },
        {
          type: 'paragraph',
          content: "Looking forward to sharing our first \"Fika\" moments with you along the beautiful Dojima River! 🌸☕"
        },
        {
          type: 'header',
          content: "Important RSVP Policy"
        },
        {
          type: 'paragraph',
          content: "Your response confirms your attendance. If your plans change and you can no longer join us, please update & cancel your RSVP promptly to allow others the opportunity to participate."
        }
      ]
    },
    
    whatToExpect: [
      "Experience expertly crafted single-origin coffees and signature Brooklyn-style blends at riverside location",
      "Enjoy beautiful views of the Dojima River and Osaka City Central Public Hall from outdoor terrace",
      "Connect with up to 10 community members in intimate, personal setting",
      "Taste authentic Brooklyn coffee culture fused with Japanese precision and hospitality",
      "Engaging conversations about travel, culture, coffee, and life in Kansai region",
      "Discover specialty drinks like cortados and pour-overs alongside donuts, croissants, and famous scones"
    ],
    
    venue: { 
      name: "Brooklyn Roasting Company Kitahama",
      description: "Stunning riverside location representing the perfect fusion of New York coffee culture and Japanese precision. Often considered the most picturesque Brooklyn Roasting Company location in all of Japan, featuring popular outdoor terrace with beautiful Dojima River views.",
      fullAddress: "〒541-0041 Osaka, Chuo Ward, Kitahama, 1-1-9 Hauser Kitahama Building 1F",
      nearestStation: "Kitahama Station",
      priceRange: "Coffee ¥550-825, Pastries and light bites available",
      amenities: ["Riverside views", "Outdoor terrace", "Brooklyn-style coffee", "In-house roasting", "Eco-friendly smart roasters", "Cashless payments", "WiFi", "English-friendly"]
    },
    
    memberBenefits: [
      {
        icon: "🏢",
        title: "Brooklyn Coffee Culture",
        description: "Experience authentic Williamsburg Brooklyn coffee culture brought to Japan's first overseas location"
      },
      {
        icon: "🌊", 
        title: "Riverside Terrace Setting",
        description: "Beautiful Dojima River views and outdoor terrace - most picturesque Brooklyn Roasting location in Japan"
      },
      {
        icon: "☕",
        title: "Specialty Coffee Excellence", 
        description: "Small batch roasting, direct farm relationships, and darker roasts uncommon in Japan"
      },
      {
        icon: "👥",
        title: "Intimate Community",
        description: "Limited to 10 people for truly personal connections in Kansai's inaugural Fika gathering"
      }
    ],
    
    relatedEvents: ['weekly-fika', 'fika', 'forest-walks', 'happy-hour'], // Links to existing events
    status: "open",
    
    // Additional metadata for Osaka event
    eventFrequency: "monthly",
    dayOfWeek: "Saturday", 
    culturalFocus: "Swedish fika tradition meets Brooklyn coffee culture in Osaka",
    specialFeature: "Inaugural Osaka Monthly Fika at riverside Brooklyn Roasting Company",
    
    socialProof: {
      totalAttended: 0, // New inaugural event
      averageRating: 0,
      repeatAttendees: "0%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Bringing Fika Swedish coffee culture to Japan's culinary capital with inaugural Monthly Fika tradition",
      avatar: "mark-osaka-hosts.jpg"
    },
  
    // Brooklyn Roasting Company specific features
    venueSpecialties: {
      coffee_culture: {
        origin: "Founded in 2009 in Williamsburg Brooklyn",
        uniqueness: "First overseas café location bringing Brooklyn energy to Japan",
        roasting: "In-house roasting using eco-friendly smart roasters made by Loring"
      },
      sustainability: {
        approach: "Superb and sustainable coffee with small batch roasting",
        sourcing: "Work directly with farms and focus on traceability",
        style: "Darker roasts more common in New York than typically found in Japan"
      },
      location: {
        views: "Beautiful views of Dojima River and Osaka City Central Public Hall",
        atmosphere: "Waterside experience reminiscent of Brooklyn's Dumbo neighborhood",
        terrace: "Popular outdoor terrace for riverside coffee experience"
      }
    },
    
    menuHighlights: {
      coffee: {
        drip_coffee: "¥550",
        americano: "¥693", 
        cappuccino: "¥682",
        latte: "¥770",
        specialty: "Signature blends, cortados, and carefully prepared pour-overs"
      },
      food: {
        pastries: "Donuts, croissants, and their famous scones",
        pricing: "Light bites available at café prices"
      }
    },
    
    attendancePolicy: "Your response confirms your attendance. If your plans change and you can no longer join us, please update & cancel your RSVP promptly to allow others the opportunity to participate.",
    
    conversationTopics: [
      "Inaugural Monthly Fika experience and building Kansai community",
      "Brooklyn vs Japanese coffee culture and brewing techniques", 
      "Life in Kansai region vs Tokyo experiences",
      "Specialty coffee appreciation and sustainable sourcing",
      "Swedish fika tradition and its adaptation to Osaka culture",
      "Riverside dining and Osaka's waterfront culture"
    ],
    
    idealFor: [
      "Coffee enthusiasts interested in Brooklyn-style specialty coffee",
      "International professionals new to Osaka/Kansai region",
      "Community builders wanting to establish regular social connections",
      "Swedish culture enthusiasts and fika tradition lovers",
      "Anyone seeking intimate, meaningful conversations over exceptional coffee",
      "Tokyo members visiting Osaka who want to connect locally"
    ],
    
    uniqueAspects: [
      "Inaugural Monthly Fika launch in Japan's culinary capital",
      "Brooklyn Roasting Company's most picturesque location with river views",
      "Intimate 10-person capacity for truly personal connections",
      "Fusion of Swedish fika, Brooklyn coffee culture, and Osaka hospitality",
      "First regular Swedish coffee tradition in Kansai region"
    ],
    
    preparationTips: [
      "Arrive early to secure outdoor terrace seating with river views",
      "Come ready to experience authentic Brooklyn coffee culture in Japan",
      "Bring curiosity about Swedish fika tradition and specialty coffee",
      "Prepare for intimate conversations in small group setting"
    ],
    
    accessInfo: {
      station: "Kitahama Station",
      building: "Hauser Kitahama Building 1F", 
      river_access: "Beautiful Dojima River frontage",
      nearby_landmarks: "Osaka City Central Public Hall"
    }
  },
  
  {
    id: 'authentic-swedish-fika-september-2025',
    eventType: 'fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
    iconValue: '🇸🇪',
    title: "Monthly Swedish Fika: Tokyo's Beloved Monthly Tradition",
    description: "Join us this month as our beloved monthly Fika tradition continues in the comfort of a carefully curated third wave café housed within modern air-conditioned architecture in central Tokyo. Recognizing the intense summer heat and humidity that defines Tokyo's September weather, we've selected a beautifully climate-controlled venue with exceptional architectural design that creates an inspiring backdrop for meaningful connections over exceptional coffee.",
    waitlist: "1",
    next: "September 21, 2025",
    time: "11:00 AM - 1:00 PM",
    location: "TBD (Nakameguro/Daikan-Yama) - 3rd wave coffee venue",
    color: "from-amber-500 to-orange-600",
    capacity: 50,
    difficulty: "Cultural Immersion",
    tags: ["Swedish Culture", "Third Wave Coffee", "Modern Architecture", "Climate Comfort", "Traditional Pastries", "Swedish Heritage"],
    featured: true,
    slug: 'authentic-swedish-fika-september-2025',
    
    // City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Conversation starters to deepen our connections",
      questions: [
        {
          number: 1,
          english: "What's something you've discovered about yourself or life in Tokyo that you never expected - whether you're new to the city or have lived here your whole life?",
          japanese: "東京での生活や自分自身について、予想していなかった発見はありますか？東京に来たばかりの方でも、ずっと住んでいる方でも構いません。",
          context: "This could be a hidden neighborhood you love, a way you've grown personally, a surprising challenge you've embraced, or simply how living here has shifted your perspective in ways you never anticipated."
        },
        {
          number: 2,
          english: "As we move into fall, what's one thing you want to be more intentional about in how you connect with others or spend your time?",
          japanese: "秋に向かう今、人とのつながり方や時間の過ごし方で、もっと意識的に取り組みたいことは何ですか？",
          context: "Whether it's deepening existing friendships, trying a new way of meeting people, setting boundaries, or simply being more present - what feels important to focus on as the season changes?"
        }
      ]
    },
    
    philosophy: {
      title: "Monthly Swedish Fika in Tokyo's Finest Third Wave Café",
      sections: [
        {
          type: 'paragraph',
          content: "Join us this month as our beloved monthly Fika tradition continues in the comfort of a carefully curated third wave café housed within modern air-conditioned architecture in central Tokyo. Recognizing the intense summer heat and humidity that defines Tokyo's weather, we've selected a beautifully climate-controlled venue with exceptional architectural design that creates an inspiring backdrop for meaningful connections over exceptional coffee."
        },
        {
          type: 'paragraph',
          content: "Our chosen location embodies the artisanal coffee movement with expertly crafted single-origin beans, precise brewing methods, and passionate baristas who elevate coffee to an art form. This intimate setting provides the perfect respite from Tokyo's summer intensity while maintaining the serene, contemplative atmosphere that makes our monthly Fika gatherings so special."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our monthly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats."
        },
        {
          type: 'header',
          content: "What Makes This Month's Venue Special"
        },
        {
          type: 'paragraph',
          content: "Modern Architecture & Climate Comfort: Escape Tokyo's intense summer heat in a beautifully designed, fully air-conditioned space that combines contemporary architectural elements with the warm, welcoming atmosphere essential for authentic fika experiences."
        },
        {
          type: 'paragraph',
          content: "Third Wave Coffee Excellence: Experience expertly crafted single-origin coffees and innovative brewing methods by passionate baristas who treat coffee as both science and art, creating the perfect foundation for meaningful conversation and connection."
        },
        {
          type: 'paragraph',
          content: "Thoughtful Curation: Our chosen venue represents the artisanal coffee movement at its finest, offering a sophisticated yet comfortable environment where the Swedish tradition of slowing down feels natural and inspired."
        },
        {
          type: 'header',
          content: "What to Expect"
        },
        {
          type: 'bullets',
          items: [
            "Climate-Controlled Comfort: Enjoy our gathering in a beautifully designed, fully air-conditioned space that provides perfect respite from Tokyo's summer intensity",
            "Exceptional Coffee Experience: Taste expertly crafted single-origin coffees and innovative brewing methods from passionate third wave baristas",
            "Meaningful Conversations: Experience the Swedish art of unhurried conversation in an inspiring architectural setting designed for connection",
            "Artisanal Atmosphere: Connect with fellow community members in a thoughtfully curated venue that represents Tokyo's finest coffee culture"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Third wave specialty coffee drinks and artisanal pastries available at café prices",
            "Full menu of coffee beverages prepared by expert baristas",
            "Cashless payments accepted (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "Join us for this month's thoughtfully curated gathering where modern architecture meets Swedish tradition. Let's honor the beautiful ritual of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional third wave coffee in Tokyo's most inspiring café setting."
        },
        {
          type: 'header',
          content: "Important Attendance Policy"
        },
        {
          type: 'paragraph',
          content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
        }
      ]
    },
    
    whatToExpect: [
      "Experience exceptional third wave coffee in beautifully designed, climate-controlled modern architecture",
      "Connect with fellow community members while staying cool and comfortable during Tokyo's intense summer weather",
      "Enjoy expertly crafted single-origin coffees and artisanal pastries from passionate baristas",
      "Engage in meaningful conversations in an inspiring architectural setting designed for connection",
      "Practice the Swedish art of unhurried conversation in Tokyo's finest coffee culture environment",
      "Discover the perfect balance of modern comfort and timeless fika tradition"
    ],
    
    venue: { 
      name: "TBD (Nakameguro/Daikan-Yama) - Curated Third Wave Café",
      description: "Beautifully designed third wave café housed within modern air-conditioned architecture in central Tokyo. Features expertly crafted single-origin coffees, innovative brewing methods, and contemporary design that creates the perfect respite from summer heat.",
      fullAddress: "Location revealed to confirmed attendees one week prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Free event - Third wave specialty coffee and artisanal pastries at café prices",
      amenities: ["Modern air-conditioned architecture", "Third wave coffee excellence", "Expert baristas", "Contemporary design", "Climate controlled comfort", "Cashless payments", "Inspiring atmosphere", "Central Tokyo location"]
    },
    
    memberBenefits: [
      {
        icon: "🏢",
        title: "Modern Architecture Experience",
        description: "Enjoy our gathering in beautifully designed, climate-controlled space perfect for Tokyo's summer weather"
      },
      {
        icon: "☕", 
        title: "Third Wave Coffee Excellence",
        description: "Experience expertly crafted single-origin coffees and innovative brewing methods by passionate baristas"
      },
      {
        icon: "🌡️",
        title: "Climate Comfort", 
        description: "Stay cool and comfortable in fully air-conditioned modern architectural setting"
      },
      {
        icon: "🤝",
        title: "Inspiring Atmosphere",
        description: "Connect meaningfully in thoughtfully curated venue representing Tokyo's finest coffee culture"
      }
    ],
    
    relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
    status: "open",
    
    // Additional metadata
    eventFrequency: "monthly",
    dayOfWeek: "Sunday", 
    culturalFocus: "Swedish fika tradition in modern third wave café",
    specialFeature: "Climate-controlled modern architecture with third wave coffee excellence",
    
    socialProof: {
      totalAttended: 0,
      averageRating: 0,
      repeatAttendees: "0%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Passionate about bringing Swedish traditions to Tokyo's international community in comfortable, inspiring settings",
      avatar: "mark-swedish-hosts.jpg"
    }
  },



  {
    id: 'authentic-swedish-fika-october-2025', // monthly-fika-october-2025
    eventType: 'fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
    iconValue: '🇸🇪',
    title: "Monthly (October) Fika: Tokyo's Beloved Monthly Tradition",
    description: "Experience fika culture at a beautiful 3rd wave coffee shop along Tokyo's serene riverside. Quality coffee, delicious pastries, and meaningful connections.",
    waitlist: "0",
    next: "October 19, 2025",
    time: "9:00 AM - 12:00 PM",
    location: "TBD - 3rd wave coffee venue (details sent one week before event)",
    color: "from-amber-500 to-orange-600",
    capacity: 60,
    difficulty: "Cultural Immersion",
    tags: ["Fika Culture", "Third Wave Coffee", "Riverside", "Pastries", "Community", "Coffee Culture", "Connections"],
    featured: true,
    slug: 'authentic-swedish-fika-october-2025',
    
    // ✅ City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",

    fikaQuestion: {
      title: "Today's Fika Questions",
      subtitle: "Autumn reflections to warm our conversations",
      questions: [
        {
          number: 1,
          english: "As Tokyo's autumn leaves begin their transformation, what's one way you've noticed yourself changing or growing this season?",
          japanese: "東京の紅葉が色づき始めるこの季節、あなた自身が変化や成長していることに気づいたことはありますか？",
          context: "Just like the ginkgo trees along Tokyo's streets slowly turn golden, we often undergo our own quiet transformations in autumn. Share a shift in perspective, a new understanding about yourself, or a gradual change you've been experiencing as the seasons turn."
        },
        {
          number: 2,
          english: "What's one small autumn ritual or experience you're most looking forward to as the season unfolds in Tokyo?",
          japanese: "東京で秋が深まっていく中で、最も楽しみにしている小さな秋の習慣や体験は何ですか？",
          context: "As we're just entering autumn, think about what draws you to this season - perhaps your first cup of hot coffee on a crisp morning, visiting temples when the maple leaves turn red, or the anticipation of cozy evenings that come with shorter days."
        }
      ]
    },
    
    philosophy: {
      title: "Monthly Fika in Tokyo's Finest Third Wave Café",
      sections: [
        {
          type: 'paragraph',
          content: "Join us this month for our beloved monthly Fika tradition as we gather at a beautiful third wave coffee shop. This wonderful venue represents the perfect setting for our community to come together and celebrate the art of meaningful connection over exceptional coffee and delicious pastries. This month's gathering continues our tradition of creating spaces for genuine conversation and deep connection, right here in Tokyo."
        },
        {
          type: 'paragraph',
          content: "Nestled along the serene banks of a gently flowing river, our chosen venue embodies everything that makes fika magical. This charming third wave coffee shop sits like a hidden gem within a waterside community facility, where the gentle murmur of flowing water creates the perfect soundtrack for meaningful conversation and deep connection. The location itself feels like stepping into a peaceful sanctuary, with panoramic views of the river and surrounding parkland that transport you far from Tokyo's urban intensity."
        },
        {
          type: 'header',
          content: "What Makes This Month's Fika Special"
        },
        {
          type: 'paragraph',
          content: "Third Wave Coffee Excellence: Experience expertly crafted coffee at a venue that takes pride in their roasting and brewing techniques. Every cup is prepared with precision and care, showcasing the natural flavors and complexity of high-quality beans."
        },
        {
          type: 'paragraph',
          content: "Delicious Pastries & Treats: Indulge in a selection of freshly baked pastries and café treats that perfectly complement your coffee experience. From traditional favorites to modern creations, each bite enhances the overall fika experience."
        },
        {
          type: 'paragraph',
          content: "Quality Coffee Artistry: Savor expertly roasted coffee beans prepared with third wave precision—carefully selected beans that emphasize the natural flavors and unique characteristics of each origin, creating exceptional taste profiles that elevate your coffee experience."
        },
        {
          type: 'paragraph',
          content: "Riverside Tranquility: Enjoy our fika in a setting that embodies the love for nature and peaceful moments. With sweeping views of flowing water and verdant parkland, this location offers the perfect backdrop for the contemplative, restorative experience that defines true fika."
        },
        {
          type: 'header',
          content: "The Magic of Fika Culture"
        },
        {
          type: 'paragraph',
          content: "This month, we're continuing our beautiful tradition of embracing fika culture in Tokyo. In this riverside sanctuary, surrounded by the aroma of freshly brewed coffee and the gentle sounds of flowing water, you'll experience that special feeling of coziness, warmth, and contentment that makes community gatherings truly meaningful."
        },
        {
          type: 'paragraph',
          content: "The café's dedication to quality coffee and excellent service means every element has been thoughtfully curated to create a wonderful fika experience. From the careful preparation methods to the selection of high-quality beans that highlight natural complexity, this venue provides the perfect setting for our monthly tradition of slowing down and connecting with one another."
        },
        {
          type: 'header',
          content: "What to Expect"
        },
        {
          type: 'bullets',
          items: [
            "Third Wave Coffee Atmosphere: Step into a space where modern coffee culture meets Japanese attention to detail, creating an environment that feels both welcoming and inspiring",
            "Meaningful Conversations: Experience the art of unhurried conversation, where ideas flow as smoothly as the coffee and connections deepen with each shared moment",
            "Cultural Discovery: Learn about coffee traditions and brewing techniques while enjoying exceptional coffee and pastries in beautiful surroundings",
            "Riverside Reflection: Take advantage of the peaceful waterside setting to practice mindfulness and finding perfect balance and contentment in the moment"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Fresh pastries and treats available at café prices",
            "Third wave coffee beverages prepared with expertly roasted beans",
            "Cashless payments accepted (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "Join us for this wonderful month as we continue our beloved fika tradition. Let's honor the beautiful practice of slowing down, connecting deeply, and savoring the moment—all while enjoying exceptional coffee and delicious pastries in one of Tokyo's most serene settings."
        },
        {
          type: 'header',
          content: "Important Attendance Policy"
        },
        {
          type: 'paragraph',
          content: "Members who RSVP 'yes' but fail to attend the event, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy helps ensure fair opportunities for all interested participants and maintains the integrity of our RSVP system."
        }
      ]
    },
    
    whatToExpect: [
      "Experience fika culture at a beautiful third wave coffee shop along Tokyo's riverside",
      "Taste fresh pastries and café treats that perfectly complement exceptional coffee",
      "Enjoy expertly roasted coffee with complex flavors and careful preparation",
      "Connect with fellow community members in a serene riverside setting with panoramic water views",
      "Learn about coffee culture and brewing techniques in a welcoming atmosphere",
      "Practice the art of unhurried conversation and meaningful connection"
    ],
    
    venue: { 
      name: "TBD - (Fukuoka) Third Wave Coffee Shop",
      description: "A beautiful third wave coffee venue nestled along serene riverside, where modern coffee culture meets Japanese attention to detail. Features expertly roasted coffee, fresh pastries, and panoramic water views.",
      fullAddress: "Location revealed to confirmed attendees one week prior to the event, ensuring an exclusive experience for our committed community members",
      priceRange: "Free event - Fresh pastries and quality coffee at café prices",
      amenities: ["Third wave coffee shop", "Fresh pastries", "Expert coffee roasting", "Riverside views", "Modern coffee atmosphere", "Cashless payments", "Climate controlled", "Panoramic parkland views"]
    },
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Third Wave Coffee Experience",
        description: "Gathering at a beautiful coffee venue that takes pride in exceptional roasting and brewing techniques"
      },
      {
        icon: "🥐", 
        title: "Fresh Pastries & Treats",
        description: "Taste freshly baked pastries and café treats that perfectly complement your coffee experience"
      },
      {
        icon: "☕",
        title: "Quality Coffee Artistry", 
        description: "Expertly roasted beans prepared with precision emphasizing natural flavors and complexity"
      },
      {
        icon: "🌊",
        title: "Riverside Sanctuary",
        description: "Serene waterside setting with panoramic views creating perfect backdrop for contemplative fika"
      }
    ],
    
    relatedEvents: ['weekly-fika', 'mys-evening-july-2025', 'fika', 'forest-walks'],
    status: "open",
    
    // Additional metadata
    eventFrequency: "special-monthly",
    dayOfWeek: "Sunday", 
    culturalFocus: "Fika tradition and coffee culture",
    specialFeature: "Beautiful riverside third wave coffee experience",
    
    socialProof: {
      totalAttended: 0,
      averageRating: 0,
      repeatAttendees: "0%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Passionate about bringing meaningful community connections to Tokyo through fika traditions",
      avatar: "mark-hosts.jpg"
    },
  
    // Special features for coffee experience
    coffeeExperience: {
      pastries: {
        description: "Fresh pastries and café treats that enhance the coffee experience",
        variety: "Selection of traditional favorites and modern creations"
      },
      thirdWaveCoffee: {
        roasting_style: "Expert roasting emphasizing natural flavors and bean characteristics",
        preparation: "Carefully prepared beans with precision and attention to detail",
        taste_profile: "Complex flavors that showcase the unique qualities of each coffee origin"
      }
    },
    
    culturalConcepts: {
      fika: {
        definition: "The beautiful tradition of taking time to slow down, connect, and savor meaningful moments",
        experience: "What you'll feel surrounded by great coffee aroma and flowing water sounds"
      },
      mindfulness: {
        definition: "Finding perfect balance and contentment in the present moment",
        practice: "Encouraged during peaceful waterside reflection"
      }
    },
    
    venueFeatures: {
      location: "Serene riverside within waterside community facility",
      atmosphere: "Modern third wave coffee shop with panoramic river and parkland views",
      uniqueness: "Beautiful riverside setting perfect for meaningful community gatherings",
      cultural_bridge: "Connects Tokyo's coffee culture with the peaceful tradition of fika"
    },
    
    attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
    
    conversationTopics: [
      "Coffee culture and brewing techniques from around the world",
      "The art of slow living and mindful connection in fast-paced cities", 
      "Community building through shared experiences and traditions",
      "Cross-cultural experiences and finding connection in diverse communities",
      "The philosophy of mindfulness and finding balance in daily life"
    ],
    
    idealFor: [
      "Coffee enthusiasts interested in third wave coffee culture",
      "Community members seeking meaningful connections and conversations",
      "Anyone interested in experiencing fika tradition in beautiful settings",
      "People who appreciate quality coffee and the art of slow living",
      "Those seeking peaceful, contemplative gathering spaces"
    ],
    
    uniqueAspects: [
      "Monthly fika tradition at a beautiful third wave coffee venue",
      "Fresh pastries and expertly crafted coffee in serene riverside setting",
      "Combination of modern coffee culture with peaceful nature views",
      "Community gathering focused on meaningful connection and conversation",
      "High-quality coffee preparation with attention to flavor and technique"
    ],
    
    preparationTips: [
      "Arrive a few minutes early to secure preferred riverside viewing spot",
      "Come with curiosity about coffee culture and community connections",
      "Bring willingness to slow down and embrace the art of unhurried conversation",
      "Prepare to experience the joy of quality coffee in beautiful surroundings"
    ],
    
    culturalLearning: [
      "Third wave coffee preparation and roasting techniques",
      "The role of quality ingredients in creating exceptional café experiences",
      "Fika traditions and their adaptation to modern urban life",
      "How coffee culture brings communities together across cultures",
      "The importance of creating mindful spaces for connection and reflection"
    ]
  },


 


{
  id: 'mys-evening-july-2025',
  eventType: 'happy-hour',
  iconType: 'image',
  iconImage: '/images/events/MysEveningSwedenFika.jpg',
  iconValue: '🌅',
  title: "Mys Evening: Swedish-Inspired Social (Happy Hour)",
  description: "Experience the Swedish art of cozy connection as day transitions to evening. Coffee culture meets evening social tradition in Tokyo's most genuine Swedish-inspired space.",
  waitlist: "12",
  next: "August 1, 2025",
  time: "6:30 PM - 10:00 PM",
  location: "TBD",
  color: "from-amber-500 to-orange-600",
  capacity: 65,
  difficulty: "Evening Social",
  tags: ["Swedish Culture", "Mys", "Coffee", "Wine", "Evening", "Cozy", "Cultural Exchange", "Community"],
  featured: true,
  slug: 'mys-evening-swedish-social-july-2025',
  
  // ✅ City-related fields
  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",
  currency: "JPY",
  
  philosophy: {
    title: "Mys Evening: The Swedish Art of Cozy Connection",
    sections: [
      {
        type: 'paragraph',
        content: "Join us for our inaugural Mys Evening – a uniquely Swedish-inspired social gathering that captures the essence of deep connection as the day transitions to evening. Building on the beloved tradition of our monthly Sunday morning Fika, this new evening experience invites you to discover the Swedish concept of mys (pronounced 'mees') – that special feeling of cozy togetherness that makes ordinary moments extraordinary."
      },
      {
        type: 'paragraph',
        content: "Set in a cozy venue overlooking a beautiful park and river, this location is perfect for evening relaxation. Mys Evening offers something beautifully different: the rare opportunity to experience both exceptional third-wave coffee culture and thoughtfully curated coffee-based cocktails, Swedish drinks, and beer in one of Tokyo's most genuine Swedish-inspired spaces."
      },
      {
        type: 'header',
        content: "Understanding Mys: The Swedish Art of Cozy Connection"
      },
      {
        type: 'paragraph',
        content: "Mys represents something deeper than mere coziness – it's the Swedish embrace of depp, unhurried togetherness. Where our morning Fika celebrates the ritual of coffee and connection, Mys Evening honors the Swedish tradition of creating warmth and belonging as daylight fades."
      },
      {
        type: 'paragraph',
        content: "This isn't about networking or rushed socializing. Like its morning counterpart, Mys Evening is about hitting the pause button on Tokyo's intensity, gathering with intentional presence, and allowing authentic conversations to unfold naturally. It's the Swedish understanding that the most meaningful connections happen when we slow down and truly see each other."
      },
      {
        type: 'header',
        content: "What Makes Mys Evening Special"
      },
      {
        type: 'paragraph',
        content: "Third Wave Coffee House Setting: Experience Tokyo's most genuine Fika environment, where Swedish hospitality meets exceptional coffee craftsmanship. Our venue's unique character allows for both the artisanal coffee culture you love and the evening social tradition that complements it perfectly."
      },
      {
        type: 'paragraph',
        content: "Choice of Connection: Coffee Enthusiasts can continue their love affair with expertly coffee-based cocktails, Swedish beers, Wine/Cocktails. Evening Socializers can explore thoughtfully selected wines, Swedish beers, and Swedish-inspired beverages. Best of Both: Why choose? Many Swedish coffee houses naturally transition from day to evening offerings."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "Unhurried Conversations: No rush, no agenda – just the natural flow of getting to know each other",
          "Intentional Presence: Phones down, attention up – creating space for real connection",
          "Cozy Atmosphere: Comfortable seating overlooking serene river views and park scenery, creating the perfect backdrop for meaningful conversation",
          "Community Building: Familiar faces welcoming newcomers, creating bonds that extend beyond the event",
          "Flexible Engagement: Coffee-infused & Swedish Cocktails, Wine, Swedish beers & conversation for purists, wine & stories for evening socializers, or mixed & matched options"
        ]
      },
      {
        type: 'header',
        content: "Cost Structure"
      },
      {
        type: 'paragraph',
        content: "You simply pay for your own selections:"
      },
      {
        type: 'bullets',
        items: [
          "Coffee based cocktails: ¥1050 -  ¥1150",
          "Swedish Cocktails:  ¥950 -  ¥1250",
          "Wine & Swedish beer: ¥700 - ¥1,250", 
          "Pastries & small plates: ¥450 - ¥900",
          "Cashless payments accepted (credit cards, suica and electronic payments)"
        ]
      },
      {
        type: 'header',
        content: "A New Chapter in Our Community Story"
      },
      {
        type: 'paragraph',
        content: "This Mys Evening represents something special – the natural evolution of our Sunday morning tradition into new territory. It's for those who've found meaning in our coffee gatherings and want to explore connection in a different light. It's for newcomers curious about Swedish social culture. It's for anyone who believes that authentic relationships are built through intentional presence and unhurried conversation."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Following our established community standards: Members who RSVP 'yes' but fail to attend, or who cancel on the event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked. This policy ensures fair opportunities for all interested participants."
      }
    ]
  },
  
  whatToExpect: [
    "Experience the Swedish concept of 'mys' - cozy togetherness as day transitions to evening",
    "Choice between exceptional third-wave coffee or thoughtfully curated wines and beers",
    "Cozy atmosphere with and comfortable seating for 25 people",
    "Unhurried conversations and intentional presence - phones down, attention up",
    "Cultural exchange about Swedish social traditions and work-life balance",
    "Flexible engagement options from coffee purists to evening socializers"
  ],
  
  venue: { 
    name: "TBD - Third wave cafe",
    description: "Set in a cozy venue overlooking a beautiful park and river, this location is perfect for evening relaxation. Tokyo's most genuine Fika environment where Swedish hospitality meets exceptional coffee craftsmanship, seamlessly transitioning from day to evening social traditions.",
    fullAddress: "Location revealed to confirmed attendees one week prior to the event",
    priceRange: "Free event - Coffee ¥500-800, Wine/Beer ¥800-1,200, Food ¥450-900",
    amenities: ["Fun atmosphere", "Coffee-based cocktails", "Swedish wines & beers", "Cozy ambiance", "Comfortable seating",  "Cashless payments", "Evening social setup"]
  },
  
  memberBenefits: [
    {
      icon: "🌅",
      title: "Authentic Mys Experience",
      description: "Discover the Swedish art of cozy connection in Tokyo's most genuine Fika setting"
    },
    {
      icon: "🥂", 
      title: "Flexible Evening Options",
      description: "Choose from exceptional coffee-based cocktails, wines, Swedish cocktails & beers, or mix and match"
    },
    {
      icon: "🤗",
      title: "Cozy Atmosphere", 
      description: "Cozy evening setting with comfortable seating and warm, inviting ambiance"
    },
    {
      icon: "🤝",
      title: "Quality Community Building",
      description: "Limited to 25 people ensuring meaningful connections and authentic conversations"
    }
  ],
  
  relatedEvents: ['fika', 'weekly-fika', 'happy-hour-august-2025', 'discussions'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "inaugural",
  dayOfWeek: "Friday", 
  socialConcept: "mys",
  culturalFocus: "Swedish evening social tradition",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%"
  },
  
  hostInfo: {
    name: "Mark ",
    bio: "Passionate about bringing authentic Swedish social traditions to Tokyo's international community",
    avatar: "mark-swedish-hosts.jpg"
  },

  // Special features for Mys Evening
  beverageOptions: {
    coffee: {
      range: "¥500 - ¥800",
      description: "Expertly roasted single-origin beans and precise brewing methods"
    },
    alcoholic: {
      range: "¥800 - ¥1,200", 
      description: "Thoughtfully selected wines, craft beers, and Swedish-inspired beverages"
    },
    food: {
      range: "¥450 - ¥900",
      description: "Pastries and small plates perfect for evening socializing"
    }
  },
  
  culturalConcept: {
    term: "Mys",
    pronunciation: "mees",
    definition: "The Swedish feeling of cozy togetherness that makes ordinary moments extraordinary",
    tradition: "Swedish evening social tradition that creates warmth and belonging as daylight fades"
  },
  
  atmosphereFeatures: {
    lighting: "Ambient lighting",
    seating: "Comfortable seating arrangements for intimate conversations",
    timing: "Golden hour gathering bridging day to night transition",
    phonePolicy: "Phones down, attention up - creating space for real connection"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without sufficient advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
  
  conversationTopics: [
    "Stories from your week and weekend adventures",
    "Cultural exchanges and travel experiences", 
    "The art of slowing down in fast-paced Tokyo",
    "Building meaningful community in a megacity",
    "Swedish secrets to work-life balance"
  ],
  
  idealFor: [
    "Regulars from Sunday morning Fika wanting to explore evening connection",
    "Newcomers curious about Swedish social culture",
    "Coffee enthusiasts ready to try evening socializing",
    "Anyone seeking authentic relationships through intentional presence",
    "Cultural exchange enthusiasts interested in Swedish traditions"
  ],
  
  uniqueAspects: [
    "Inaugural event - first of its kind in Tokyo",
    "Seamless blend of coffee culture and evening social tradition",
    "Authentic Swedish cultural experience",
    "Flexible engagement options (coffee or alcohol)",
    "Evolution of beloved Sunday morning tradition"
  ],
  
  preparationTips: [
    "Arrive a few minutes early to settle in and choose your preferred spot",
    "Come ready to relax into the rhythm of Swedish-inspired connection",
    "Bring your curiosity about Swedish culture and social traditions",
    "Prepare to disconnect from devices and connect with people"
  ]
},



  // NEW: GROWTH CIRCLE EVENT - Enhanced with city fields
  {
    id: 'growth-circle',
    eventType: 'growth-circle',
    iconType: 'image',
    iconImage: '/images/events/growth-circle.jpg',
    iconName: 'ChatBubbleLeftRightIcon',
    iconValue: '🌱',
    title: "Growth Circle (Beyond the Book Club)",
    description: "Multi-modal community learning experience exploring life-changing ideas through books, videos, podcasts,  discussion-based exploration,  and meaningful conversation. This month: 'The Courage to Be Disliked'.  (English & Japanese Discussion)",
    waitlist: "2",
    next: "September 18, 2025", //growth-circle-beyond-book-club-sep-2025 or Cancelled
    time: "2:00 PM - 4:30 PM",
    location: "Impact Hub Tokyo, Meguro",
    color: "from-emerald-500 to-green-600",
    capacity: 30,
    difficulty: "Transformative Learning",
    tags: ["Personal Growth", "Book Club", "Psychology", "Multi-Modal Learning", "Deep Conversations", "Authenticity"],
    featured: true,
    slug: 'growth-circle-beyond-book-club-sep-2025',
    
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    
    philosophy: {
      title: "The Growth Circle: Multi-Modal Community Learning Evolution (English & Japanese Discussion)",
      sections: [
        {
          type: 'paragraph',
          content: "Ready to explore life-changing ideas your way? Our Growth Circle isn't just about reading—it's about transformation through meaningful conversation. Whether you love diving into books, prefer watching videos, learn best through listening, or thrive in discussion-based exploration, we've created multiple pathways for you to engage with powerful concepts and connect with fellow growth seekers"
        },
        {
          type: 'header',
          content: "This Month's Focus: 'The Courage to Be Disliked'"
        },
        {
          type: 'paragraph',
          content: "We're exploring authentic living, healthy relationships, and personal freedom through Adlerian psychology. The magic isn't in how you absorbed the ideas—it's in how we explore them together and support each other in applying them to real life."
        },
        {
          type: 'header',
          content: "Core Questions We'll Explore Together"
        },
        {
          type: 'bullets',
          items: [
            "What does it really mean to live authentically, even when others disapprove?",
            "How do we break free from constantly seeking approval?",
            "Can we truly separate what's 'our business' from 'other people's business'?",
            "What would change if we stopped trying to meet everyone's expectations?"
          ]
        },
        {
          type: 'header',
          content: "Choose Your Learning Journey (All Lead to the Same Discussion!)"
        },
        {
          type: 'paragraph',
          content: "📖 FOR THE BOOK LOVERS: Full Experience reading 'The Courage to Be Disliked' by Ichiro Kishimi. Deep dive into Adlerian psychology through engaging dialogue format with rich philosophical concepts and practical applications."
        },
        {
          type: 'paragraph',
          content: "🎧 FOR THE LISTENERS: Audio Path through audiobook + key chapter highlights. Full audiobook available on Audible, Spotify, Apple Books (~7 hours total, or focus on Nights 3-4 for core themes). Ideal for commuters, multitaskers, and audio learners."
        },
        {
          type: 'paragraph',
          content: "📺 FOR THE VISUAL LEARNERS: Curated video playlist (25-35 minutes total) including Brené Brown's 'The Power of Vulnerability' and Irene Rose's 'Authenticity - The Courage to be Yourself' with perfect alignment to the book's core themes."
        },
        {
          type: 'header',
          content: "🗾 FOR JAPANESE SPEAKERS: Comfortable Discussion in Your Native Language"
        },
        {
          type: 'paragraph',
          content: "We understand that exploring deep psychological concepts feels more natural in your mother tongue. We'll have a dedicated Japanese discussion table facilitated by our bilingual moderator, allowing you to dive into Adlerian psychology and authentic living with the nuance and depth that comes from discussing in Japanese. During our cross-pollination sessions, key insights will be shared between the Japanese and English tables, creating a rich multicultural exchange of perspectives while ensuring everyone feels comfortable expressing their thoughts fully."
        },
        {
          type: 'header',
          content: "Our Growth Circle Flow"
        },

        {
          type: 'bullets',
          items: [
            "✨ Opening Connection - Quick pulse check on the themes",
            "🔄 Small Group Deep Dives - Intimate discussions on core concepts",
            "🌟 Cross-Pollination - Share insights across groups",
            "🎯 Personal Application - Turn insights into action steps",
            "🤝 Integration Circle - Leave with clear growth commitments"
          ]
        },
        {
          type: 'header',
          content: "Why Multiple Paths Work Better"
        },
        {
          type: 'paragraph',
          content: "Different perspectives enrich our conversations. Someone who watched a TED talk on vulnerability might spark an insight that someone who read the full book hadn't considered. A podcast listener might share a personal story that brings a concept to life in a way the written word couldn't. We believe growth happens in community, not just in consumption."
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Everyone must order at least one drink (venue requirement)",
            "Food and additional beverages at your own expense",
            "No entrance or event fees"
          ]
        },
        {
          type: 'paragraph',
          content: "Join us for 2.5 transformative hours where meaningful connections accelerate growth. Come as you are - whether you're new to personal development or a seasoned growth enthusiast, introverted deep thinker or extroverted processor, busy professional or dedicated learner."
        }
      ]
    },
    
    whatToExpect: [
      "Choose your learning path: book, audiobook, or curated video content",
      "Engage in intimate small group discussions on core psychological concepts",
      "Cross-pollinate insights across different learning modalities",
      "Transform concepts into practical, actionable personal growth steps",
      "Build meaningful connections with fellow growth-oriented community members",
      "Leave with clear commitments and accountability for implementing insights"
    ],

    //   description: "Collaborative workspace and community hub designed for meaningful conversations and personal development gatherings in Meguro district.",
    
    venue: { 
      name: "Impact Hub Tokyo",
      description: "Impact HUB Tokyo is part of the Impact HUB Global network, a global community of entrepreneurs seeking to create social impact. Since opening in 2013, they have provided a community co-working space for a diverse range of entrepreneurship, working styles, industries and cultures, bringing together many entrepreneurs and start-ups, freelancers, artists, intrapreneurs and NPO leaders from Japan and abroad, and continues to foster collaboration between its members. From 2022, it became an incubation center accredited by the Tokyo Metropolitan Government and offers entrepreneurial support. In 2023, it launched a café, \"Communita Cafe\", which is not only open to the community but allows locals, neighbors, entrepreneurs and freelancers to come together.社会にインパクトを生み出そうとする起業家たちの世界的なコミュニティ「Impact HUB」ネットワークの一員。2013年開設以来、多様な起業、働き方、業種、文化が集まるコミュニティ・コワーキング「Impact HUB Tokyo」を提供し、国内外から多くの起業家やスタートアップ、フリーランサー、アーティスト、企業内起業家、NPOリーダーなどが集まり、メンバー間のコラボレーションを生み出し続けています。2022年より東京都認定インキュベーション施設となり、2023年には地域に開かれたデリカフェ「Communita Cafe」をオープンし、より多くの地域の起業家やフリーランサーが集まる場所となっています。",
      fullAddress: "〒153-0063 Tokyo, Meguro City, Meguro, 2 Chome−11−3 印刷工場 １階",
      priceRange: "Free event, individual drink purchase required + optional food",
      amenities: ["Collaborative workspace", "Community focus", "Drink ordering required", "Group discussion areas", "Central Meguro location"]
    },
    
    memberBenefits: [
      {
        icon: "🌱",
        title: "Multi-Modal Learning",
        description: "Choose your path: full book, audiobook highlights, or curated video content"
      },
      {
        icon: "🔄", 
        title: "Cross-Pollination Insights",
        description: "Rich discussions combining perspectives from different learning approaches"
      },
      {
        icon: "🎯",
        title: "Practical Application", 
        description: "Transform philosophical concepts into actionable personal growth steps"
      },
      {
        icon: "🤝",
        title: "Growth Accountability",
        description: "Leave with clear commitments and community support for implementation"
      }
    ],
    
    relatedEvents: ['discussions', 'weekly-fika', 'fika', 'forest-walks'],
    status: "open",
    
    // Additional metadata for growth circle event
    eventFrequency: "monthly",
    dayOfWeek: "Saturday", 
    focusArea: "personal development",
    learningFormat: "multi-modal",
    
    socialProof: {
      totalAttended: 0,
      averageRating: 0,
      repeatAttendees: "0"
    },
    
    hostInfo: {
      name: "Mark & Growth Facilitators",
      bio: "Passionate about creating transformative learning experiences through community connection",
      avatar: "mark-growth-hosts.jpg"
    },

    // Special features for growth circle
    learningPaths: [
      {
        type: "book",
        title: "Full Book Experience",
        content: "Read 'The Courage to Be Disliked' by Ichiro Kishimi",
        duration: "Complete book",
        icon: "📖"
      },
      {
        type: "audio", 
        title: "Audio Path",
        content: "Audiobook + key chapter highlights (focus on Nights 3-4)",
        duration: "~7 hours total or key sections",
        icon: "🎧"
      },
      {
        type: "video",
        title: "Visual Learning Path", 
        content: "Curated playlist: Brené Brown + Irene Rose + bonus content",
        duration: "25-35 minutes total",
        icon: "📺"
      }
    ],
    
    monthlyFocus: {
      book: "The Courage to Be Disliked",
      author: "Ichiro Kishimi",
      themes: ["Authenticity", "Personal Freedom", "Adlerian Psychology", "Courage", "Healthy Relationships"]
    },
    
    sessionStructure: {
      duration: "2 hours",
      format: "Small groups + cross-pollination + integration",
      maxGroupSize: "8-10 people per discussion group",
      facilitationStyle: "Participant-led with guided prompts"
    },
    
    idealFor: [
      "Growth seekers who prefer non-traditional book clubs", 
      "People who learn better through videos/podcasts than reading",
      "Book lovers wanting diverse discussion perspectives",
      "Anyone curious about psychology and personal development",
      "Those who thrive in small group conversations",
      "People seeking accountability for implementing insights"
    ],
    
    preparationOptions: [
      "Choose your preferred learning path (book/audio/video)",
      "Engage with content at your own pace before the event", 
      "Come ready to share insights and experiences",
      "Bring an open mind for meaningful conversation"
    ],
    
    whatYoullLeaveWith: [
      "New perspectives on authentic living and relationships",
      "Practical tools for developing courage in daily life", 
      "Meaningful connections with fellow growth seekers",
      "Clear action steps for implementing insights",
      "Accountability partners for continued growth"
    ]
  },

    // HAPPY HOUR - Enhanced with city fields
   
    
  
    // August Monthly Fika
    {
      id: 'fika',
      eventType: 'fika',

      exclusive: true, // 🌟 NEW: Explicit exclusive flag
      exclusiveType: 'founding-members', // 🌟 NEW: Type of exclusivity
      priorityLevel: 1, // 🌟 NEW: Display priority (1 = highest)

      iconType: 'image',
      iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
      iconValue: '☕',
      title: "🏆 Founding Members Exclusive - Monthly Swedish Fika",
      description: "Monthly coffee meetups at the world's finest cafés. Experience authentic Swedish coffee culture with international members.",
      waitlist: "0",
      next: "August 24, 2025",
      time: "10:00 AM - 1:00PM",
      location: "Little Darling Coffee Roasters ｜ リトル ダーリン コーヒー ロースターズ",
      color: "from-amber-500 to-orange-500",
      capacity: 40,
      difficulty: "Beginner Friendly",
      tags: ["Coffee", "Culture", "Make New Friends", "Expat", "Digital Nomad"],
      featured: true,
      slug: 'swedish-fika-august-2025',
      
      // ✅ NEW: City-related fields
      city: "tokyo",
      cityDisplay: "Tokyo",
      country: "Japan",
      timezone: "Asia/Tokyo",
      currency: "JPY",

     
      fikaQuestion: {
        title: "Today's Fika Questions",
        subtitle: "Conversation starters to deepen our connections",
        questions: [
          {
            number: 1,
            english: "What's one thing you started this year that surprised you with how much it's changed you?",
            japanese: "今年始めたことの中で、自分がこんなに変わるとは思わなかったことは何ですか？",
            context: "Share something new you began in 2025 - maybe a hobby, habit, a ritual monthly coffee gathering, mindset shift, or challenge - that ended up transforming you in ways you never expected."
          },
          {
            number: 2,
            english: "Based on what you just shared or heard, what are you most curious to explore in the remaining months of this year?",
            japanese: "今共有したことや聞いたことを踏まえて、今年の残りの月で最も探求してみたいことは何ですか？",
            context: "Let the stories and insights from our first question inspire what you'd like to discover, learn, or experience before 2025 ends."
          }
        ]
      },
      
      
      philosophy: {
        foundingMemberIntro: {
          type: 'paragraph',
          title: "Exclusive for Founding Members",
          content: "For the first time ever, our beloved monthly Fika tradition is becoming exclusive to you – our ConnectCo.app Founding Members. After hosting our first six Monthly Fika events openly on Meetup.com where anyone could join, we're taking a meaningful step forward by making this August event available only through our platform. This marks a special milestone: the transition from open community gatherings to an exclusive experience reserved for our most committed members. You've been part of our journey from the very beginning, and now you're receiving access that even our Meetup community members won't have unless they've joined our platform. While hundreds discovered us through Meetup, you believed in our vision early enough to become a Founding Member – and this exclusive invitation is our way of saying thank you and giving you priority access to our most intimate gatherings."

        },
        title: "Understanding Fika: More Than Just Coffee",
        sections: [
          {
            type: 'paragraph',
            content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
          },
          {
            type: 'paragraph',
            content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
          },
          {
            type: 'paragraph',
            content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our monthly Fika events embrace this beautiful Swedish tradition, creating a space where we can all take a moment to connect meaningfully over excellent coffee and treats."
          },
          {
            type: 'header',
            content: "What makes our monthly Fika special"
          },
          {
            type: 'bullets',
            items: [
              "Experience expertly roasted and crafted coffee from one of Tokyo's finest roasters",
              "Enjoy their selection of delicious pastries that perfectly complement your brew", 
              "Connect with familiar faces and welcome newcomers to our growing community",
              "Continue our monthly tradition of slowing down and connecting through coffee"
            ]
          },
          {
            type: 'header',
            content: "What to expect"
          },
          {
            type: 'bullets',
            items: [
              "Engaging conversations about travel, culture, coffee, and life",
              "Strengthening our monthly Fika community with new and existing friendships"
            ]
          },
          {
            type: 'header',
            content: "Cost: Just pay for your own drinks and food"
          },
          {
            type: 'bullets',
            items: [
              "Coffee drinks range from ¥500 - ¥800",
              "Pastries available from ¥450",
              "Cashless payments accepted (credit cards and electronic payments)"
            ]
          },
          {
            type: 'paragraph',
            content: "Join us for this month's cozy Sunday morning gathering. Let's continue our monthly Fika tradition with exceptional coffee and even better company! Looking forward to sharing another memorable \"Fika\" moment with you all!"
          }
        ]
      },
      
      whatToExpect: [
        "Experience expertly roasted and crafted coffee from one of Tokyo's finest roasters",
        "Enjoy their selection of delicious pastries that perfectly complement your brew",
        "Connect with familiar faces and welcome newcomers to our growing community",
        "Engaging conversations about travel, culture, coffee, and life"
      ],
      venue: { 
        name: "Little Darling Coffee Roasters ｜ リトル ダーリン コーヒー ロースターズ",
        fullAddress: "1 Chome-12-32 Minamiaoyama, Minato City, Tokyo 107-0062",
        priceRange: "Coffee ¥500-800, Pastries ¥450+",
        amenities: ["WiFi", "Cashless payments", "English-friendly staff"]
      },
      relatedEvents: ['weekly-fika', 'forest-walks', 'pizza-night-daikanyama', 'happy-hour'],
      status: "open"
    },


  // EXISTING MONTHLY FIKA - Enhanced with city fields
  {
    id: 'fika-old',
    eventType: 'fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoMonthlyFika.jpg',
    iconValue: '☕',
    title: "Monthly Swedish-Inspired Fika",
    description: "Monthly coffee meetups at the world's finest cafés. Experience authentic Swedish coffee culture with international members.",
    waitlist: "50",
    next: "July 13, 2025",
    time: "9:00 AM - 12:00PM",
    location: "TBD",
    color: "from-amber-500 to-orange-500",
    capacity: 50,
    difficulty: "Beginner Friendly",
    tags: ["Coffee", "Culture", "Make New Friends", "Expat", "Digital Nomad"],
    featured: true,
    slug: 'swedish-fika-january-2025',
    
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    
    philosophy: {
      title: "Understanding Fika: More Than Just Coffee",
      sections: [
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our monthly Fika events embrace this beautiful Swedish tradition, creating a space where we can all take a moment to connect meaningfully over excellent coffee and treats."
        },
        {
          type: 'header',
          content: "What makes our monthly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Experience expertly roasted and crafted coffee from one of Tokyo's finest roasters",
            "Enjoy their selection of delicious pastries that perfectly complement your brew", 
            "Connect with familiar faces and welcome newcomers to our growing community",
            "Continue our monthly tradition of slowing down and connecting through coffee"
          ]
        },
        {
          type: 'header',
          content: "What to expect"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, and life",
            "Strengthening our monthly Fika community with new and existing friendships"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks range from ¥500 - ¥800",
            "Pastries available from ¥450",
            "Cashless payments accepted (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "Join us for this month's cozy Sunday morning gathering. Let's continue our monthly Fika tradition with exceptional coffee and even better company! Looking forward to sharing another memorable \"Fika\" moment with you all!"
        }
      ]
    },
    
    whatToExpect: [
      "Experience expertly roasted and crafted coffee from one of Tokyo's finest roasters",
      "Enjoy their selection of delicious pastries that perfectly complement your brew",
      "Connect with familiar faces and welcome newcomers to our growing community",
      "Engaging conversations about travel, culture, coffee, and life"
    ],
    venue: { 
      name: "TBD - Exclusive Third Wave Café",
      fullAddress: "Location revealed to confirmed attendees one week prior",
      priceRange: "Coffee ¥500-800, Pastries ¥450+",
      amenities: ["WiFi", "Cashless payments", "English-friendly staff"]
    },
    relatedEvents: ['weekly-fika', 'forest-walks', 'pizza-night-daikanyama', 'happy-hour'],
    status: "open"
  },

  // NEW: WEEKLY FIKA EVENT 
  {
    id: 'weekly-july30-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Weekly Swedish Fika",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "July 30, 2025", 
    time: "11:00 AM - 1:00 PM",
    location: "STREAMER COFFEE COMPANY AZABU-JUBAN",
    color: "from-emerald-500 to-teal-500",
    capacity: 20,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-july-2025',
    
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika meetup community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "STREAMER COFFEE COMPANY AZABU-JUBAN",
      description: "The specific venue location will be shared with confirmed attendees one week prior to the event, ensuring an exclusive experience for our committed community members while allowing everyone time to plan their journey to this hidden gem in Tokyo's coffee landscape.",
      fullAddress: "〒106-0032 Tokyo, Minato City, Roppongi, 6 Chome−11−16 中銀六本木マンシオン",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "85%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },

  {
    id: 'weekly-aug6-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Weekly Swedish Fika",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "August 6, 2025", 
    time: "11:00 AM - 1:00 PM",
    location: "TBD",
    color: "from-emerald-500 to-teal-500",
    capacity: 2,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-august-2025',
    
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika meetup community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "TBD - Curated Third Wave Coffee Shop",
      description: "The specific venue location will be shared with confirmed attendees one week prior to the event, ensuring an exclusive experience for our committed community members while allowing everyone time to plan their journey to this hidden gem in Tokyo's coffee landscape.",
      fullAddress: "Location revealed to confirmed attendees one week prior",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "85%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Passionate about Swedish Fika culture and Tokyo's coffee scene",
      avatar: "mark-jason-hosts.jpg"
    }
  },


  {
    id: 'weekly-aug13-fika',
    eventType: 'weekly-fika',
    iconType: 'image',
    iconImage: '/images/events/ConnectCoWeeklyFika.jpg',
    iconValue: '🍵',
    title: "Weekly Fika Hangout | Drop-In Coffee Community Spaces Worldwide ☕",
    description: "Regular Wednesday gatherings exploring Tokyo's third wave coffee scene. Find time to slow down through our weekly Swedish Fika tradition.",
    waitlist: "0",
    next: "August 13, 2025", 
    time: "11:00 AM - 1:00 PM",
    location: "Sarutahiko Coffee The Bridge",
    color: "from-emerald-500 to-teal-500",
    capacity: 15,
    difficulty: "Beginner Friendly", 
    tags: ["Weekly", "Coffee", "Third Wave", "Community", "Slow Living", "Cultural Exchange"],
    featured: true,
    slug: 'weekly-swedish-fika-august-13-2025',
    
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    cost: "Free  (pay for your own drinks and food)",
    
    philosophy: {
      title: "Find Time To Slow Down Through Weekly Swedish Fika",
      sections: [
        {
          type: 'paragraph',
          content: "Join us once a week as we expand our beloved Fika tradition with regular gatherings at Tokyo's finest third wave coffee shops. If you're feeling stressed out by the demands of city life, join us to de-stress and unwind in the true Swedish spirit of slowing down. Building on the overwhelming success of our pioneering monthly Fika events—Tokyo's first regular Fika meetup community—we're now bringing this cherished tradition of mindful pausing to your weekly routine."
        },
        {
          type: 'paragraph',
          content: "Each week, we'll discover a new carefully curated café that represents the artisanal coffee movement—tranquil spaces where coffee is treated as craft, featuring single-origin beans, precise brewing methods, and passionate baristas who elevate both coffee and conversation to an art form."
        },
        {
          type: 'header',
          content: "Understanding Fika: More Than Just Coffee"
        },
        {
          type: 'paragraph',
          content: "Fika is a Swedish ritual of slowing down. It's always social – fika is not something that you do by yourself – and typically involves a cup of coffee and a sweet bite to eat. It has been described as a social institution or even phenomena in Sweden, giving people the opportunity to meet and hang out with friends, get to know new people, or network with business folks."
        },
        {
          type: 'paragraph',
          content: "What distinguishes the Swedish coffee break from our own coffee-consuming traditions is that fika is about slowing down. Coffee represents a true break, a moment to sit and contemplate on your own, or to gather with friends. It's meant to be a way to hit the pause button on work and life stress and just enjoy the moment."
        },
        {
          type: 'paragraph',
          content: "While people commonly meet for fika in a café, the break can take place anywhere, including parks, meeting rooms, or even at home. In Sweden, fika is also a state of mind, an indispensable part of the day that's more about the act of socializing and its effects than the comestibles involved (although they're important too). Our weekly Fika events embrace this beautiful Swedish tradition, creating regular opportunities where we can all take a moment to connect meaningfully over exceptional coffee and treats, while exploring Tokyo's vibrant third wave coffee scene together."
        },
        {
          type: 'header',
          content: "What makes our weekly Fika special"
        },
        {
          type: 'bullets',
          items: [
            "Discover Tokyo's Third Wave Coffee Scene: Experience expertly crafted single-origin coffees, innovative brewing methods, and artisanal roasting techniques at carefully selected venues across the city",
            "Quality & Craft: Enjoy specialty coffee drinks prepared by skilled baristas who treat coffee as both science and art, often featuring beautiful latte art and unique flavor profiles",
            "Community Building: Connect with familiar faces and welcome newcomers to our growing community every week, creating deeper bonds through regular interaction",
            "Cultural Exchange: Continue our tradition of slowing down and connecting through coffee while exploring different neighborhoods and their unique café cultures"
          ]
        },
        {
          type: 'header',
          content: "What to expect each week"
        },
        {
          type: 'bullets',
          items: [
            "Engaging conversations about travel, culture, coffee, current events, and life in Tokyo",
            "Weekly community growth with opportunities to build lasting friendships through consistent gathering", 
            "Café exploration as we visit different third wave coffee shops, each with its own character, specialty, and story",
            "Relaxed networking in warm, hospitable environments with carefully curated atmospheres"
          ]
        },
        {
          type: 'header',
          content: "Cost: Just pay for your own drinks and food"
        },
        {
          type: 'bullets',
          items: [
            "Coffee drinks typically range from ¥450 - ¥800 depending on the venue",
            "Pastry and light meal options available (selection varies by café)",
            "Most venues accept cashless payments (credit cards and electronic payments)"
          ]
        },
        {
          type: 'paragraph',
          content: "We can welcome up to 15 people for these intimate weekly gatherings, creating truly personal connections while exploring Tokyo's diverse coffee landscape. Our events consistently attract engaged participants, so we encourage you to sign up early to secure your spot!"
        },
        {
          type: 'paragraph',
          content: "Join us for this exciting expansion of our Fika tradition as we make Wednesday mornings a regular celebration of coffee, community, and conversation. Let's discover Tokyo's incredible third wave coffee scene together while building lasting connections within our community!"
        }
      ]
    },
    
    whatToExpect: [
      "Discover Tokyo's Third Wave Coffee Scene with expertly crafted single-origin coffees",
      "Connect with community members weekly for deeper relationship building",
      "Explore different neighborhoods and their unique café cultures",
      "Enjoy quality coffee prepared by skilled baristas who treat coffee as art",
      "Engage in conversations about travel, culture, coffee, and life in Tokyo"
    ],
    
    venue: { 
      name: "Sarutahiko Coffee The Bridge",
      description: "",
      fullAddress: "〒150-0001 Tokyo, Shibuya, Jingumae, 1 Chome−18−20 Harajuku Station, 3階",
      priceRange: "Coffee ¥450-800, Light meals vary by café",
      amenities: ["Third Wave Coffee", "Artisanal Brewing", "Cashless payments", "WiFi", "English-friendly"]
    },
    
    memberBenefits: [
      {
        icon: "☕",
        title: "Weekly Coffee Discovery",
        description: "Explore a new third wave coffee shop each week with expert curation"
      },
      {
        icon: "🤝", 
        title: "Consistent Community",
        description: "Build deeper relationships through regular weekly interactions"
      },
      {
        icon: "🗾",
        title: "Tokyo Exploration", 
        description: "Discover hidden coffee gems across different Tokyo neighborhoods"
      },
      {
        icon: "🎯",
        title: "Intimate Gatherings",
        description: "Small group size (10 people) ensures meaningful connections"
      }
    ],
    
    relatedEvents: ['fika', 'discussions', 'forest-walks', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata for weekly event
    eventFrequency: "weekly",
    dayOfWeek: "Wednesday",
    socialProof: {
      totalAttended: 45,
      averageRating: 4.9,
      repeatAttendees: "85%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Passionate about Swedish Fika Culture",
      avatar: "mark-jason-hosts.jpg"
    }
  },

  // FOREST WALKS - Enhanced with city fields
  {
    id: 'forest-walks',
    eventType: 'walks',
    iconType: 'image',
    iconImage: '/images/events/MeijiForestWalk.jpg',
    iconValue: '🌳',
    title: "Dawn & Zen: Tokyo's Secret Forest Walk & Coffee Social",
    description: "Monthly weekend morning walk through Meiji Shrine's enchanting forest paths, followed by optional coffee social with fellow morning enthusiasts.",
    waitlist: "10",
    next: "August 9, 2025",
    time: "10:00 PM",
    location: "Meiji Shrine (Harajuku Station South)",
    color: "from-green-600 to-emerald-600",
    capacity: 50,
    difficulty: "Easy Walk",
    tags: ["Morning", "Nature", "English Conversation", "Mindfulness", "Coffee", "Weekend"],
    featured: true,
    slug: 'meiji-secret-forest-walk-july-2025',
    
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    
    philosophy: {
      title: "Dawn & Zen: Tokyo's Secret Forest Walk & Coffee Social",
      sections: [
        {
          type: 'paragraph',
          content: "Discover your urban oasis on our monthly weekend morning walk through one of Tokyo's most enchanting green spaces. Weave through the towering century-old trees that encircle the Meiji Shrine, where ancient forest meets modern city. This isn't your typical tourist trek – we'll stay along the peaceful outer paths, sharing stories and conversations in English while immersing ourselves in the mystical morning atmosphere."
        },
        {
          type: 'header',
          content: "In just 30-45 minutes, you'll experience:"
        },
        {
          type: 'bullets',
          items: [
            "Experience the magical morning transition of Tokyo's hidden forest",
            "Connect with fellow morning enthusiasts in casual English conversation", 
            "Reset your mind before the busy day ahead",
            "Discover scenic photo spots most visitors miss"
          ]
        },
        {
          type: 'paragraph',
          content: "Cap off your morning adventure with an optional coffee stop at a local café, where our conversations can continue to brew."
        },
        {
          type: 'header',
          content: "Perfect for:"
        },
        {
          type: 'bullets',
          items: [
            "Early birds seeking a mindful start to their day",
            "Nature lovers who want to escape the city buzz",
            "English speakers of all levels (conversation practice welcome)",
            "Anyone seeking connection with like-minded morning wanderers",
            "Photography enthusiasts looking for unique angles"
          ]
        },
        {
          type: 'paragraph',
          content: "Join our growing community of morning wanderers – where every day begins with steps through serenity."
        },
        {
          type: 'header',
          content: "Important Details"
        },
        {
          type: 'bullets',
          items: [
            "Duration: 30-45 minutes walk + optional coffee social",
            "Language: English conversation throughout",
            "Cost: Completely free (optional coffee at your own expense)",
            "What to bring: Comfortable walking shoes and a smile",
            "Weather: Event continues rain or shine (covered forest paths)"
          ]
        },
        {
          type: 'header',
          content: "Special Note About Our Route"
        },
        {
          type: 'paragraph',
          content: "This walk focuses on the stunning forest paths surrounding the shrine, offering a unique perspective of this historic area without entering the shrine itself. We explore the peaceful outer pathways where ancient trees create a natural cathedral, providing the perfect setting for mindful conversation and connection."
        },
        {
          type: 'header',
          content: "Meeting Point Details"
        },
        {
          type: 'paragraph',
          content: "Look for us at the bicycle parking area next to the single security booth – we'll be the friendly group ready to start our morning adventure! The meeting point is right across from Cafe Mori no Terrace, making it easy to spot and accessible from Harajuku Station's south entrance."
        }
      ]
    },
    
    whatToExpected: [
      "30-45 minute peaceful walk through century-old forest paths",
      "English conversation with international morning enthusiasts", 
      "Discover hidden photo spots and scenic viewpoints",
      "Optional coffee social to continue conversations",
      "Mindful start to your weekend in Tokyo's urban oasis"
    ],
    
    venue: { 
      name: "Meiji Shrine Outer Forest Paths",
      description: "Ancient forest paths surrounding the historic Meiji Shrine, offering a mystical morning atmosphere away from tourist crowds.",
      fullAddress: "Meiji Shrine grounds, 1-1 Yoyogikamizonocho, Shibuya City, Tokyo (Meeting Point: Bicycle parking area (across Cafe Mori no Terrace) by the security booth at Meiji Shrine's outer ground - Harajuku Station South entrance)",
      meetingPoint: "Bicycle parking area across from Cafe Mori no Terrace, next to the security booth",
      priceRange: "Free event, optional coffee ¥400-600",
      amenities: ["English conversation", "Covered forest paths", "Photo opportunities", "Optional café nearby", "Accessible from Harajuku Station"]
    },
    
    memberBenefits: [
      {
        icon: "🌅",
        title: "Morning Mindfulness",
        description: "Start your weekend with intentional connection to nature and community"
      },
      {
        icon: "🗣️", 
        title: "English Conversation",
        description: "Practice casual English in a relaxed, supportive environment"
      },
      {
        icon: "📸",
        title: "Hidden Photo Spots", 
        description: "Discover scenic viewpoints that most tourists never see"
      },
      {
        icon: "☕",
        title: "Optional Coffee Social",
        description: "Continue connections over coffee at a local neighborhood café"
      }
    ],
    
    relatedEvents: ['weekly-fika', 'fika', 'pizza-night-daikanyama'],
    status: "open",
    
    // Additional metadata
    eventFrequency: "monthly",
    dayOfWeek: "Saturday", 
    walkDuration: "30-45 minutes",
    language: "English",
    
    socialProof: {
      totalAttended: 89,
      averageRating: 4.7,
      repeatAttendees: "78%"
    },
    
    hostInfo: {
      name: "Mark ",
      bio: "Morning enthusiasts passionate about Tokyo's hidden natural spaces",
      avatar: "mark-morning-hosts.jpg"
    },

    // Special logistics 
    meetingInstructions: "Look for the friendly group at bicycle parking area next to security booth, across from Cafe Mori no Terrace",
    weatherPolicy: "Event continues rain or shine - forest paths provide natural cover",
    whatToBring: ["Comfortable walking shoes", "Positive energy", "Camera (optional)"],
    accessibilityNotes: "Gentle forest paths suitable for most fitness levels"
  },


  {
    id: 'meguro-river-walk',
    eventType: 'walks',
    iconType: 'image', 
    iconImage: '/images/events/MeguroRiver.jpg',
    iconValue: '🍂',
    title: "Fall Along the Meguro: Tokyo's Most Scenic River Walk & Coffee Social",
    description: "Monthly autumn morning walk along Tokyo's most picturesque river, following the famous cherry blossom trees in their golden fall glory from Meguro to trendy Nakameguro.",
    waitlist: "20",
    next: "October 12, 2025",
    time: "1:00 PM - 5:30 PM",
    location: "Meguro Station (Meeting point TBA to registered attendees)",
    color: "from-amber-600 to-orange-600",
    capacity: 30,
    difficulty: "Easy River Walk",
    tags: ["Morning", "River", "Autumn", "English Conversation", "Photography", "Coffee", "Weekend"],
    featured: true,
    slug: 'meguro-river-autumn-walk-october-2025',
    
    // City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo", 
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    
    philosophy: {
      title: "Fall Along the Meguro: Tokyo's Most Scenic River Walk & Coffee Social (Optional)",
      sections: [
        {
          type: 'paragraph',
          content: "Experience Tokyo's most beloved river walk as autumn transforms the famous Meguro River cherry trees into a golden corridor of fall beauty. This intimate morning journey takes you along the gentle waters from historic Meguro to the vibrant coffee culture of Nakameguro, where the same trees that create spring's pink canopy now offer a tapestry of amber, gold, and crimson reflections on the water."
        },
        {
          type: 'header', 
          content: "In just 30 minutes, you'll discover:"
        },
        {
          type: 'bullets',
          items: [
            "Tokyo's most photogenic autumn river scenery as morning light dances on water",
            "The seasonal transformation of iconic cherry blossom trees into fall masterpieces",
            "Hidden riverside spots where locals start their weekend mornings",
            "Stories and casual English conversation with fellow autumn enthusiasts",
            "The perfect transition from peaceful Meguro to bustling Nakameguro's cafe scene"
          ]
        },
        {
          type: 'paragraph',
          content: "End your riverside journey in Nakameguro's renowned coffee district, where our optional cafe social continues with specialty brews surrounded by the area's creative energy."
        },
        {
          type: 'header',
          content: "Perfect for:"
        },
        {
          type: 'bullets', 
          items: [
            "Early birds seeking Tokyo's most beautiful autumn morning views",
            "Photography lovers capturing fall foliage reflections on water",
            "English speakers wanting mindful conversation while walking",
            "Anyone discovering the seasonal magic of Tokyo's urban waterways",
            "Coffee enthusiasts exploring Nakameguro's famous cafe culture"
          ]
        },
        {
          type: 'paragraph',
          content: "Join our intimate group of 20 morning wanderers for this exclusive autumn experience – where Tokyo's urban river becomes your gateway to seasonal serenity."
        },
        {
          type: 'header',
          content: "Important Details"
        },
        {
          type: 'bullets',
          items: [
            "Arrival time: 8:30 AM at designated meeting point",
            "Walk starts promptly: 8:45 AM (no late arrivals can be accommodated)",
            "Duration: 30 minutes riverside walk + optional Nakameguro cafe social",
            "Language: English conversation throughout the journey", 
            "Cost: Completely free (optional coffee at your own expense)",
            "Group size: Limited to 20 people for intimate experience",
            "Meeting point: Exact location sent to registered attendees one week before event",
            "Weather: Event rescheduled if rain is forecasted (weather permitting)",
            "Coffee Social: Optional",
          ]
        },
        {
          type: 'header',
          content: "About the Meguro River Route"
        },
        {
          type: 'paragraph',
          content: "Our carefully chosen path follows the river's most scenic stretches, where hundreds of cherry trees create an autumn tunnel of gold and crimson. The gentle riverside walkways offer perfect spots for photography and conversation, while the morning light creates magical reflections on the calm waters. This route showcases why Meguro River is considered one of Tokyo's most beautiful urban waterways."
        },
        {
          type: 'header',
          content: "Ending in Nakameguro"
        },
        {
          type: 'paragraph',
          content: "Our walk concludes in the heart of Nakameguro, Tokyo's beloved coffee and culture district. Those joining the optional cafe social will gather at a charming riverside cafe in Nakameguro, where we'll continue our morning conversations over specialty coffee while the autumn river views provide the perfect backdrop. Please note that seating cannot be guaranteed due to our group size and busy weekend crowds."
        }
      ]
    },
    
    whatToExpected: [
      "30-minute scenic walk along autumn-transformed Meguro River",
      "English conversation with international morning enthusiasts",
      "Capture stunning fall foliage reflections on calm river waters", 
      "Optional coffee social in trendy Nakameguro district",
      "Intimate group experience limited to 20 participants"
    ],
    
    venue: {
      name: "Meguro River Riverside Path",
      description: "Tokyo's most picturesque urban river walk, featuring hundreds of cherry trees in spectacular autumn colors creating golden reflections on calm waters.",
      fullAddress: "Meguro Station, starting in Meguro and ending in Nakameguro, Tokyo (Exact meeting point sent to registered attendees one week prior)",
      meetingPoint: "Specific location along Meguro River (details provided to attendees)", 
      priceRange: "Free event, optional coffee ¥400-800",
      amenities: ["Riverside walking paths", "English conversation", "Photography opportunities", "Multiple cafe options in Nakameguro", "Autumn foliage viewing"]
    },
    
    memberBenefits: [
      {
        icon: "🍂",
        title: "Autumn River Magic",
        description: "Experience Tokyo's most beautiful fall foliage reflections on tranquil waters"
      },
      {
        icon: "🗣️",
        title: "English Riverside Chat", 
        description: "Practice conversational English while strolling along scenic waterways"
      },
      {
        icon: "📸",
        title: "Golden Hour Photography",
        description: "Capture morning light transforming autumn leaves into riverside art"
      },
      {
        icon: "☕",
        title: "Shibuya Cafe Culture",
        description: "End with optional coffee social in Tokyo's most beloved cafe district"
      }
    ],
    
    relatedEvents: ['forest-walks', 'weekly-fika', 'fika'],
    status: "open",
    
    // Additional metadata
    eventFrequency: "monthly",
    dayOfWeek: "Sunday",
    walkDuration: "30 minutes", 
    language: "English",
    
    socialProof: {
      totalAttended: 0, // New event
      averageRating: null,
      repeatAttendees: null
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Morning enthusiast passionate about Tokyo's seasonal urban beauty and riverside walks",
      avatar: "mark-river-walks.jpg"
    },
  
    // Special logistics
    meetingInstructions: "Arrive between 8:30-8:45 AM at the designated Meguro River meeting point (exact location sent to registered attendees one week prior). Walk departs promptly at 8:45 AM",
    weatherPolicy: "Event continues rain or shine - covered walkways and cafe options available",
    whatToBring: ["Comfortable walking shoes", "Camera for autumn photography", "Positive morning energy"],
    accessibilityNotes: "Flat riverside paths suitable for all fitness levels, wheelchair accessible routes available"
  },
  

  // PIZZA NIGHT - Enhanced with city fields
  {
    id: 'pizza-night-daikanyama',
    eventType: 'foodie',
    iconType: 'image',
    iconImage: '/images/events/PizzaNightDaikanyama.jpg',
    iconValue: '🍕',
    title: "Slice of Tokyo Life: Connect & Co NY-Style Pizza Night",
    description: "Casual Saturday evening featuring authentic New York-style pizza in Daikanyama. Good company, hard-to-find pizza, and relaxed conversations.",
    waitlist: "57",
    next: "July 26, 2025",
    time: "4:30 PM - 6:30 PM",
    location: "Pizza Slice Daikanyama",
    color: "from-red-500 to-orange-500",
    capacity: 16,
    difficulty: "Casual Social",
    tags: ["Food", "Social", "Daikanyama", "NY Pizza", "Weekend", "Make Friends"],
    featured: true,
    slug: 'ny-pizza-night-daikanyama-july-2025',
    
    // ✅ NEW: City-related fields
    city: "tokyo",
    cityDisplay: "Tokyo",
    country: "Japan",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    
    philosophy: {
      title: "Slice of Tokyo Life: Connect & Co NY-Style Pizza Night in Daikanyama",
      sections: [
        {
          type: 'paragraph',
          content: "Join us for a casual evening featuring that rarest of Tokyo treasures—authentic New York-style pizza! Pizza Slice's Daikanyama location is one of the few places in Japan where you can experience genuine NY thin-crust slices in all their foldable, perfectly charred glory."
        },
        {
          type: 'paragraph',
          content: "No elaborate agendas or forced interactions—just good company, delicious hard-to-find pizza, and the kind of relaxed conversations that turn strangers into friends. Whether you're a homesick New Yorker, a Japanese local curious about authentic American pizza, or simply looking to connect with interesting people, this is your perfect Saturday evening."
        },
        {
          type: 'header',
          content: "The Tasty Details & How It Works"
        },
        {
          type: 'paragraph',
          content: "After chatting with the restaurant manager, we've secured a special group arrangement that guarantees everyone gets their slice of New York heaven! Here's how our pizza party works:"
        },
        {
          type: 'bullets',
          items: [
            "🍕 Your Guaranteed Slice: We're ordering 2 whole pizzas (that's 16 perfect slices for our group of 16). Everyone pays ¥750 at the start to cover your guaranteed slice—we'll collect this when you arrive and handle the pizza payment as a group.",
            "🥤 Drinks Are Essential: Everyone must order at least one drink directly from the waitress (soda, beer, juice, wine—your choice, your cost). This is a restaurant requirement, and honestly, what's pizza without a cold drink?",
            "🍕 Want More? Craving a second (or third) slice? Just head to the counter like a regular customer and order additional slices and drinks à la carte.",
            "📝 Pizza Varieties: When you RSVP, you'll see a question about your pizza preference—this helps us choose our two pizzas based on what the group wants most!"
          ]
        },
        {
          type: 'header',
          content: "Important Notes About Group Arrangement"
        },
        {
          type: 'paragraph',
          content: "The restaurant has reserved space and prepared for exactly 16 people. If fewer people show up (say only 14), the event hosts will need to cover the cost for the missing attendees. This is why keeping your RSVP updated is so crucial—it helps us and ensures fairness for everyone!"
        },
        {
          type: 'header',
          content: "Cost Breakdown"
        },
        {
          type: 'bullets',
          items: [
            "¥750 per person (paid at arrival) for your guaranteed slice",
            "Your own drinks (ordered individually at the table)",
            "Additional slices purchased separately if desired",
            "Optional drinks at nearby bar afterward (individual cost)"
          ]
        },
        {
          type: 'header',
          content: "Why Join Us?"
        },
        {
          type: 'paragraph',
          content: "This isn't about polished networking or business card exchanges—it's about finding your people in Tokyo over something truly special: authentic New York pizza that's nearly impossible to find in Japan. The limited group size ensures meaningful conversations and connections that last beyond the last bite."
        },
        {
          type: 'paragraph',
          content: "Whether you're debating the merits of different NYC boroughs' pizza scenes, sharing Tokyo adventures, or just savoring that perfect cheese-to-sauce ratio, you'll be among fellow food lovers who appreciate the hunt for authentic flavors in this incredible city."
        },
        {
          type: 'header',
          content: "Pro Tips for Pizza Night"
        },
        {
          type: 'bullets',
          items: [
            "Arrive promptly at 4:30—we'll collect the ¥750 and get our pizza party started!",
            "Bring your pizza opinions and Tokyo tales",
            "Come hungry for both food and friendship",
            "RSVP required—all 16 spots fill up quickly!",
            "Stay flexible for optional drinks afterward at nearby bar"
          ]
        },
        {
          type: 'paragraph',
          content: "Cheers to good food, better company, and finally finding that perfect New York slice in Tokyo! 🍕"
        }
      ]
    },
    
    whatToExpect: [
      "Authentic New York-style thin-crust pizza in foldable, charred glory",
      "Intimate group of 16 food lovers and conversation enthusiasts", 
      "Relaxed atmosphere where strangers become friends over shared slices",
      "Group payment system ensuring everyone gets their guaranteed slice",
      "Optional post-pizza drinks at nearby Daikanyama bar"
    ],
    
    venue: { 
      name: "Pizza Slice Daikanyama",
      description: "One of the few places in Japan serving genuine New York-style pizza with authentic thin-crust slices.",
      fullAddress: "〒150-0033 Tokyo, Shibuya, Sarugakucho, 1−3 Daikanyama Kamijin Building 1F",
      priceRange: "¥750 per slice (group rate) + individual drinks",
      amenities: ["Authentic NY pizza", "Group seating for 16", "Full drink menu", "Additional slices available", "Near Daikanyama Station"]
    },
    
    memberBenefits: [
      {
        icon: "🍕",
        title: "Authentic NY Experience",
        description: "Taste genuine New York-style pizza, rare to find in Tokyo"
      },
      {
        icon: "👥", 
        title: "Intimate Group Setting",
        description: "Limited to 16 people for meaningful conversations and connections"
      },
      {
        icon: "💰",
        title: "Group Rate Advantage", 
        description: "Special arrangement ensures everyone gets their slice at ¥750"
      },
      {
        icon: "🍻",
        title: "Optional Bar Extension",
        description: "Continue the evening with drinks at nearby Daikanyama establishments"
      }
    ],
    
    relatedEvents: ['fika', 'weekly-fika', 'happy-hour', 'discussions'],
    status: "open",
    
    // Additional metadata
    eventFrequency: "special",
    dayOfWeek: "Saturday", 
    foodFocus: "NY-style pizza",
    neighborhood: "Daikanyama",
    
    socialProof: {
      totalAttended: 48,
      averageRating: 4.9,
      repeatAttendees: "92%"
    },
    
    hostInfo: {
      name: "Mark",
      bio: "Food enthusiasts on a mission to find authentic international flavors in Tokyo",
      avatar: "mark-jason-food-hosts.jpg"
    },

    // Special logistics for pizza event
    paymentStructure: {
      entryFee: "¥750 per person (collected at arrival)",
      drinks: "Individual purchase required",
      additionalSlices: "Available for separate purchase"
    },
    
    attendancePolicy: "Members who RSVP 'yes' but fail to attend, or cancel on event day without advance notice, will be considered a 'no-show.' After three no-show instances, membership will be automatically revoked.",
    
    rsvpImportance: "Restaurant expects exactly 16 people - missing attendees cost will be covered by hosts",
    
    pizzaPreferences: ["Margherita", "Pepperoni", "Mushroom", "White Pizza", "Meat Lovers"],
    
    whatToBring: ["¥750 cash for pizza slice", "Appetite for authentic NY pizza", "Pizza opinions to share"],
    
    postEventOptions: ["Optional drinks at nearby Daikanyama bars", "Continue conversations over cocktails"]
  },



  // ✅ NEW:  Inaugrual Speed Fika Connection
{
  id: 'speed-fika-connection-september-2025', 
  eventType: 'speed-fika-connection',
  iconType: 'image',
  iconImage: '/images/events/SaturdayHappyHourGinza.jpg',
  iconValue: '🥂',
  title: "Inaugural Speed Fika Connection Happy Hour Saturday: Tokyo Connections at NAMIKI667",
  description: "First-ever Speed Fika Connection Happy Hour where 12 thoughtfully crafted questions create authentic relationships. Structured conversations over bottomless drinks at Hyatt Centric Ginza.",
  waitlist: "0",
  next: "September 13, 2025",
  time: "5:00 PM - 7:00 PM",
  location: "NAMIKI667 Bar & Lounge, Hyatt Centric Ginza",
  color: "from-indigo-500 to-purple-500",
  capacity: 57,
  difficulty: "Deep Connection Networking",
  tags: ["Speed Fika", "Ginza", "Deep Connection", "Structured Conversations", "Saturday", "Psychology-Based"],
  featured: true,
  slug: 'inaugural-speed-fika-connection-namiki667-sep-2025',

   // NEW: Payment configuration properties
   cost: "3,000 YEN per person",
 
   paymentEnabled: true,           // Toggle for this specific event - default to false
   requiresSubscription: true,     // Does this event require subscription? - default to false
   perEventPriceCents: 3000,     // $30 in cents (¥30 * 100)
   currency: "JPY",
   paymentOptions: {
     allowSubscription: true,       // Can subscribers access this?
     allowPerEventPayment: true,    // Can non-subscribers pay?
     allowWaitlist: false          // Can non-subscribers join waitlist?
   },
  

  city: "tokyo",
  cityDisplay: "Tokyo",
  country: "Japan",
  timezone: "Asia/Tokyo",

 

  speedFikaGuide: {
    title: "Speed Fika Connection: 12 Questions for Meaningful Connection",
    subtitle: "Designed to move from comfortable curiosity to authentic vulnerability over 4 rounds",
    
    rounds: [
      {
        number: 1,
        title: "Playful Curiosity",
        subtitle: "Breaking the ice with imagination and lighthearted sharing",
        questions: [
          {
            english: "Given the choice of anyone in the world, whom would you want as a dinner guest?",
            japanese: "世界中の誰でも選べるとしたら、誰と一緒にディナーを楽しみたいですか？"
          },
          {
            english: "If you could wake up tomorrow having gained any one quality or ability, what would it be?",
            japanese: "明日目覚めたときに何か一つの資質や能力を身につけているとしたら、それは何でしょうか？"
           },
          {
            english: "What's something that happened this week that made you smile or laugh?",
            japanese: "今週あったことで、笑顔になったり笑ったりしたことは何ですか？"
          }
        ]
      },
      {
        number: 2,
        title: "Personal Aspirations",
        subtitle: "Moving into desires, dreams, and self-reflection",
        questions: [
          {
            english: "If you knew that in one year you would die suddenly, would you change anything about the way you are now living? Why?",
            japanese: "もし1年後に突然死ぬことが分かったら、今の生き方で何か変えることはありますか？なぜですか？"
           },
          {
            english: "What's something you've always wanted to try but haven't yet? What's holding you back?",
            japanese: "ずっとやってみたかったけれど、まだやっていないことは何ですか？何が足かせになっていますか？"
          },
          {
            english: "If you could send a message to yourself 5 years ago, what would you say?",
            japanese: "5年前の自分にメッセージを送れるとしたら、何と言いますか？"
          }
        ]
      },
      {
        number: 3,
        title: "Values & Connection",
        subtitle: "Exploring what matters most and how we relate to others",
        questions: [
          {
            english: "What do you value most in a friendship?",
            japanese: "友情において最も大切にしていることは何ですか？"
          },
          {
            english: "What roles do love and affection play in your life?",
            japanese: "あなたの人生において、愛情や愛はどのような役割を果たしていますか？"
           },
          {
            english: "If you could change one thing about how people treat each other, what would it be?",
            japanese: "人々がお互いをどう扱うかについて、一つだけ変えられるとしたら何でしょうか？"
          }
        ]
      },
      {
        number: 4,
        title: "Vulnerability & Authentic Sharing",
        subtitle: "Creating space for deeper connection and authentic expression",
        questions: [
          {
            english: "Complete this sentence: 'I wish I had someone with whom I could share...'",
            japanese: "この文を完成させてください：「一緒に分かち合える人がいたらいいのにと思うことは...」"
          },
          {
            english: "What feeds your soul?",
            japanese: "あなたの魂を満たしてくれるものは何ですか？"
          },    
          {
            english: "When did you last cry in front of another person? By yourself? Why?",
            japanese: "最後に誰かの前で泣いたのはいつですか？一人で泣いたのはいつですか？"
           }
        ]
      }
    ],
    
    facilitatorGuidelines: {
      english: {
        title: "Question Guidelines for Facilitators",
        timing: {
          title: "Timing per Question:",
          items: [
            "6-7 minutes per question",
            "Allow natural flow between questions", 
            "Give 1-minute warning before transitioning"
          ]
        },
        backupPrompts: {
          title: "Backup for Early Finishers:",
          subtitle: "Optional prompts you can use if needed:",
          items: [
            "Tell us more about that...",
            "Has anyone had a similar experience?",
            "What made you think of that answer?"
          ]
        },
        facilitationTips: {
          title: "Facilitation Tips:",
          items: [
            "Questions are starting points, not rigid scripts",
            "Encourage follow-up questions and natural conversation",
            "Remind participants they can adapt questions to their comfort level",
            "Model vulnerability as the host by sharing first when needed"
          ]
        },
        creatingSafety: {
          title: "Creating Safety:",
          items: [
            'Remind groups: "What\'s shared here, stays here"',
            'Encourage listening without trying to "fix" or give advice',
            'It\'s okay to say "I\'d prefer not to share about that" and choose a different angle'
          ]
        }
      },
      japanese: {
        title: "司会者のための質問ガイドライン",
        timing: {
          title: "質問あたりの時間配分:",
          items: [
            "1つの質問につき6-7分",
            "自然な流れを大切にする",
            "次の質問に移る1分前に時間の目安をお知らせする"
          ]
        },
        backupPrompts: {
          title: "早く終わった場合のバックアップ:",
          subtitle: "必要に応じて使用できるオプションのプロンプト:",
          items: [
            "それについてもう少し教えてください...",
            "似たような経験をした人はいますか？",
            "その答えを思いついたきっかけは何ですか？"
          ]
        },
        facilitationTips: {
          title: "進行のコツ:",
          items: [
            "質問は出発点であり、厳格な台本ではありません",
            "フォローアップの質問や自然な会話を促してください",
            "参加者が自分のコンフォートレベルで質問を調整できることを思い出させてください",
            "必要に応じて、ホストが最初にシェアすることで脆さのモデルを示してください"
          ]
        },
        creatingSafety: {
          title: "安全な場を作る:",
          items: [
            "グループに思い出させる：「ここで分かち合われたことは、ここにとどまります」",
            "「修正」やアドバイスを与えようとせずに聞くことを促してください",
            "「それについてはシェアしたくない」と言って、別の角度を選ぶのは問題ないことを伝えてください"
          ]
        }
      }
    },

    logisticsManagement: {
      english: {
        title: "Managing 8-Person Tables:",
        sections: [
          {
            title: "Managing 8-Person Tables:",
            items: [
              "Go around in order - helps ensure everyone gets airtime",
              "\"Popcorn style\" sharing - after everyone shares once, open up naturally", 
              "Watch for quiet voices - \"Let's hear from someone who hasn't shared yet\"",
              "Time awareness - \"We have about 2 minutes left for this question\""
            ]
          },
          {
            title: "Manual Rotation Instructions for Participants:",
            items: [
              "When host announces time - finish your thought and stand up",
              "Remember your rotation pattern (you'll need to tell people their path at check-in)",
              "Move quickly but safely - you have 3-4 minutes to transition",
              "Introduce yourselves briefly - first names only at new table",
              "Wait for host's \"start\" signal before beginning next round"
            ]
          },
          {
            title: "Host Rotation Management:",
            items: [
              "Clear verbal announcements: \"Time to rotate! Everyone please move to your next assigned table\"",
              "Call out specific people if needed: \"Sarah, you're going to Table 3\"",
              "Visual check - ensure everyone found their new table before starting",
              
              "Use your voice - clear directions"
            ]
          },
          {
            title: "Energy & Flow:",
            items: [
              "Keep transitions upbeat but not rushed",
              "Acknowledge the complexity - \"40 people moving around - you're doing great!\"",
              "Encourage mixing during breaks - bathroom breaks are connection opportunities",
              "End on a high note - celebrate the connections made"
            ]
          }
        ]
      },
      japanese: {
        title: "8人テーブルの管理:",
        sections: [
          {
            title: "8人テーブルの管理:",
            items: [
              "順番に回る - 全員が話す時間を確保するために",
              "「ポップコーンスタイル」シェアリング - 全員が一度話した後、自然に開放する",
              "静かな声に注意 - 「まだシェアしていない人から聞いてみましょう」",
              "時間を意識する - 「この質問はあと約2分です」"
            ]
          },
          {
            title: "手動ローテーション参加者への指示:",
            items: [
              "ベルが鳴ったら - 話をまとめて立ち上がる",
              "カードを確認して次のテーブル番号を見る",
              "素早く安全に移動 - 移動時間は3-4分あります",
              "簡単に自己紹介 - 新しいテーブルでファーストネームのみ",
              "ホストの合図を待つ次のラウンドを始める前に"
            ]
          },
          {
            title: "ホストのローテーション管理:",
            items: [
              "明確なアナウンス：「青いカードの方はラウンド2のテーブルへ、赤いカードの方はそのままお待ちください」",
              "視覚的確認 - 次のラウンドを始める前に全員が新しいテーブルに着いたことを確認",
              
              "忍耐強く - 最初のローテーションは常に最も時間がかかります"
            ]
          },
          {
            title: "エネルギーと流れ:",
            items: [
              "移行を活発に保つが急かさない",
              "複雑さを認める - 「40人が移動するのは大変ですが、皆さん素晴らしくやっています！」",
              "休憩中の交流を促す - トイレ休憩もつながりの機会",
              "高いトーンで終了 - 作られたつながりを祝う"
            ]
          }
        ]
      }
    }


  },

  philosophy: {
    title: "Inaugural Speed Fika Connection Happy Hour Saturday: Tokyo Connections at NAMIKI667",
    sections: [
      {
        type: 'paragraph',
        content: "Join us for Connect & Co.'s first-ever \"Speed Fika Connection\" Happy Hour—where we're turning 12 Questions That Build Deep Connection into an unforgettable group experience at the stylish NAMIKI667 Bar & Lounge."
      },
      {
        type: 'paragraph',
        content: "Forget awkward networking and superficial chat. We're creating something different: four rounds of rotating small-group conversations using scientifically-designed questions that help strangers become friends. You'll move through intimate groups of 3-4 people, sharing stories and discovering surprising connections with fellow Tokyo residents."
      },
      {
        type: 'header',
        content: "How Speed Fika Connection Works"
      },
      {
        type: 'paragraph',
        content: "The Magic Formula: 35 participants + 12 thought-provoking questions + 4 rotating rounds = meaningful connections with 12-16 different people in one evening."
      },
      {
        type: 'header',
        content: "Your Journey"
      },
      {
        type: 'bullets',
        items: [
          "Round 1: Ice-breaker questions with your first group",
          "Round 2: Rotate to new faces, dive a little deeper", 
          "Round 3: Another rotation, more personal territory",
          "Round 4: Final rotation, the most vulnerable and connecting questions"
        ]
      },
      {
        type: 'paragraph',
        content: "Each round lasts 20 minutes—just enough time to move beyond \"What do you do?\" and into \"What makes you come alive?\" The questions are thoughtfully crafted to create authentic connection through graduated sharing, so you'll leave feeling like you've made real friends, not just exchanged business cards."
      },
      {
        type: 'header',
        content: "The Sweet Details"
      },
      {
        type: 'bullets',
        items: [
          "Bottomless Drinks Package: ¥3,000 per person (pay in person as you check in)",
          "Unlimited Red Wine, White Wine, Sparkling Wine, Non Alcoholic Drinks( Soda, Ginger Ale, Oolong Tea, Orange Juice",
          "Full 2-hour access",
          "All cover charges & service fees included",
          "Private room access near the balcony",
          "Sophisticated yet unpretentious atmosphere—perfect for meaningful conversation"
        ]
      },
      {
        type: 'header',
        content: "Why This Experience is Different"
      },
      {
        type: 'paragraph',
        content: "This isn't speed dating—it's speed fika connection. Questions are designed to move from playful to profound, creating authentic connection through graduated sharing."
      },
      {
        type: 'header',
        content: "Sample Questions You'll Explore"
      },
      {
        type: 'bullets',
        items: [
          "Round 1: \"Given the choice of anyone in the world, whom would you want as a dinner guest?\"",
          "Round 2: \"If you could wake up tomorrow having gained any one quality or ability, what would it be?\"",
          "Round 3: \"What do you value most in a friendship?\"",
          "Round 4: \"Complete this sentence: 'I wish I had someone with whom I could share...'\"" 
        ]
      },
      {
        type: 'paragraph',
        content: "The magic happens in that sweet spot between comfortable and vulnerable."
      },
      {
        type: 'header',
        content: "What to Expect"
      },
      {
        type: 'bullets',
        items: [
          "5:00-5:15 PM: Arrive, grab a drink, get your table assignment",
          "5:15-6:45 PM: Four rounds of Speed Fika Connection magic", 
          "6:45-7:00 PM: Free mingling with your new connections"
        ]
      },
      {
        type: 'paragraph',
        content: "You'll leave with genuine connections, interesting stories, and probably plans for future meetups."
      },
      {
        type: 'header',
        content: "A Few Friendly Guidelines"
      },
      {
        type: 'bullets',
        items: [
          "Come with an open heart—authenticity creates the best connections",
          "Embrace vulnerability—that's where the magic happens",
          "Share at your comfort level—adapt questions as needed",
          "Respect confidentiality—what's shared here, stays here",
          "Stay present—no phones during rounds",
          "Trust the process—initial intensity is normal"
        ]
      },
      {
        type: 'paragraph',
        content: "The intimate group size means you'll remember the people you meet and their stories."
      },
      {
        type: 'header',
        content: "Important Attendance Policy"
      },
      {
        type: 'paragraph',
        content: "Members who RSVP 'yes' but fail to attend the event, or who cancel their RSVP on the same day as the event, will be considered a 'no-show.' Only cancellations made in advance (before the event date) are acceptable. After three no-show instances, membership will be automatically revoked."
      }
    ]
  },
  
 
  
  whatToExpected: [
    "Four structured rounds of deep connection conversations",
    "Scientifically-designed questions moving from playful to profound", 
    "Intimate groups of 3-4 people rotating every 20 minutes",
    "Bottomless drinks package with premium wine, beer, and sparkling wine",
    "Private room access near balcony at sophisticated NAMIKI667"
  ],
  
  venue: { 
    name: "NAMIKI667 Bar & Lounge (3rd Floor)",
    description: "Stylish bar and lounge at Hyatt Centric Ginza featuring sophisticated atmosphere, private room access near balcony, perfect for structured connection experiences.",
    fullAddress: "6-6-7 Ginza, Chuo-ku, Tokyo, 104-0061, Japan (Hyatt Centric Ginza, 3rd Floor)",
    priceRange: "¥3,000 bottomless drinks package (pay in person at check-in)",
    amenities: ["Private room access", "Balcony area", "Bottomless drinks", "Central Ginza location", "Hotel-quality service"]
  },
  
  memberBenefits: [
    {
      icon: "🥂",
      title: "Bottomless Drinks Package",
      description: "Unlimited wine, beer & sparkling wine for full 2 hours at ¥3,000"
    },
    {
      icon: "🏙️", 
      title: "Premium Ginza Location",
      description: "Sophisticated atmosphere at Hyatt Centric's stylish NAMIKI667"
    },
    {
      icon: "🌅",
      title: "Private Room Access", 
      description: "Exclusive access to private room area near balcony"
    },
    {
      icon: "🧠",
      title: "Swedish art of deep connection",
      description: "Thoughtfully crafted questions for authentic relationship building"
    }
  ],
  
  relatedEvents: ['fika', 'weekly-fika', 'discussions', 'growth-circle'],
  status: "open",
  
  // Additional metadata
  eventFrequency: "inaugural",
  dayOfWeek: "Saturday", 
  neighborhood: "Ginza",
  pricePoint: "premium",
  
  socialProof: {
    totalAttended: 0,
    averageRating: 0,
    repeatAttendees: "0%" // inaugural event
  },
  
  hostInfo: {
    name: "/Mark & ConnectCo Facilitators",
    bio: "Creating meaningful and intentional connection experiences across Tokyo's premium venues",
    avatar: "mark-ginza-host.jpg"
  },

  // Special features for this structured connection event
  drinkSpecials: {
    packagePrice: "¥3,000",
    paymentMethod: "Pay in person as you check in",
    featuredDrinks: ["Red Wine", "White Wine", "Sparkling Wine", "Beer"],
    includedServices: ["All cover charges", "Service fees", "Full 2-hour access"]
  },
  
  venueFeatures: {
    privateRoomAccess: true,
    balconyAccess: "Near balcony area",
    floorLevel: "3rd Floor",
    hotelBrand: "Hyatt Centric"
  },
  
  attendancePolicy: "Members who RSVP 'yes' but fail to attend the event, or who cancel their RSVP on the same day as the event, will be considered a 'no-show.' Only cancellations made in advance (before the event date) are acceptable. After three no-show instances, membership will be automatically revoked.",
  
  networkingStyle: "Structured deep connection conversations using psychology-based questions - four rotating rounds of meaningful dialogue",
  
  idealFor: ["New Tokyo residents wanting to build their social circle", "Seasoned locals and expats ready to deepen existing connections", "Digital nomads seeking authentic local relationships", "Anyone tired of surface-level networking events", "People who believe the best friendships start with real conversation"],

  // Unique to Speed Fika Connection format
  connectionFormat: {
    totalRounds: 4,
    roundDuration: "20 minutes each",
    groupSize: "3-4 people",
    questionTypes: ["Ice-breakers", "Personal preferences", "Personal values", "Deeper connection"],
    totalConnections: "12-16 different people"
  },

  sampleQuestions: [
    "Given the choice of anyone in the world, whom would you want as a dinner guest?",
    "If you could wake up tomorrow having gained any one quality or ability, what would it be?",
    "What do you value most in a friendship?",
    "Complete this sentence: 'I wish I had someone with whom I could share...'"
  ]
},

 
  // NEW: SAMPLE HONOLULU EVENTS (for demonstration)
  {
    id: 'honolulu-beach-fika',
    eventType: 'fika',
    iconType: 'emoji',
    iconValue: '🏖️',
    title: "Beach Sunrise Fika",
    description: "Swedish coffee tradition meets Hawaiian sunrise. Join us for morning coffee and pastries on Waikiki Beach as the sun rises over the Pacific.",
    waitlist: "15",
    next: "August 5, 2025",
    time: "7:30 AM - 10:30 AM",
    location: "Waikiki Beach, Honolulu",
    color: "from-orange-400 to-pink-500",
    capacity: 25,
    difficulty: "Early Bird Special",
    tags: ["Beach", "Sunrise", "Coffee", "Swedish Tradition", "Ocean Views"],
    featured: true,
    slug: 'beach-sunrise-fika-august-2025',
    
    // ✅ NEW: City-related fields
    city: "honolulu",
    cityDisplay: "Honolulu",
    country: "USA",
    timezone: "Pacific/Honolulu",
    currency: "USD",
    
    philosophy: {
      title: "Fika Meets Aloha: Swedish Mindfulness on Hawaiian Shores",
      sections: [
        {
          type: 'paragraph',
          content: "Experience the perfect fusion of Swedish hygge and Hawaiian aloha spirit. Our Beach Sunrise Fika brings the mindful Swedish coffee tradition to the stunning shores of Waikiki, where the rhythm of the waves complements the ritual of slowing down."
        },
        {
          type: 'header',
          content: "What makes this special"
        },
        {
          type: 'bullets',
          items: [
            "Watch the sunrise over the Pacific while enjoying artisanal coffee",
            "Swedish pastries meet local Hawaiian flavors",
            "Connect with fellow early risers and island lovers",
            "Practice mindfulness with ocean sounds as your soundtrack"
          ]
        }
      ]
    },
    
    whatToExpected: [
      "Spectacular sunrise views over Waikiki Beach",
      "Artisanal coffee and Swedish-Hawaiian fusion pastries",
      "Intimate group conversations with ocean backdrop",
      "Perfect start to your Hawaiian day"
    ],
    
    venue: { 
      name: "Waikiki Beach Sunrise Spot",
      description: "Prime beachfront location for optimal sunrise viewing and peaceful morning gatherings.",
      fullAddress: "Waikiki Beach, Honolulu, HI 96815",
      priceRange: "Coffee $4-6, Pastries $3-5",
      amenities: ["Ocean views", "Sunrise location", "Beach access", "Photo opportunities"]
    },
    
    relatedEvents: ['honolulu-hiking-group'],
    status: "open"
  },

 
];

// Helper function to get events by city
export const getEventsByCity = (city) => {
  if (city === 'all') return enhancedEventsData;
  return enhancedEventsData.filter(event => event.city === city);
};

// Helper function to get event count by city
export const getEventCountByCity = (city) => {
  return getEventsByCity(city).length;
};

// ENHANCED DEBUG GUIDE:
// 1. All existing Tokyo events now have: city: "tokyo", cityDisplay: "Tokyo", country: "Japan", timezone: "Asia/Tokyo", currency: "JPY"
// 2. Added sample Honolulu events to demonstrate multi-city functionality
// 3. Added citiesConfig for easy city management
// 4. Added helper functions for city-based filtering
// 5. Maintains backward compatibility - all existing functionality preserved
// 6. Ready for EventsPage.jsx city tab navigation implementation

// CITY DISTRIBUTION:
// Tokyo: 7 events (growth-circle, fika, weekly-fika, forest-walks, pizza-night-daikanyama, happy-hour, discussions)
// Honolulu: 2 events (honolulu-beach-fika, honolulu-hiking-group)
// Total: 9 events across 2 cities

// NEXT STEPS:
// - Update EventsPage.jsx to use city tabs
// - Implement two-tier filtering (city → event type)
// - Add city-specific routing (/events/tokyo, /events/honolulu)

// ENHANCED DEBUG GUIDE:
// 1. Monthly Fika: Original event - unchanged functionality
// 2. Weekly Fika: NEW - Uses image icon, different color, comprehensive content
// 3. Meiji Forest Walk: NEW - Secret forest paths with coffee social, Saturday mornings
// 4. Pizza Night Daikanyama: NEW - NY-style pizza social with group payment system
// 5. Forest Walks: Existing - now references all new events
// 6. Happy Hour: Existing - now references all new events  
// 7. Discussions: Existing - now references all new events

// CROSS-REFERENCES UPDATED:
// - All existing events now include new events in their relatedEvents
// - New events reference each other and existing events appropriately
// - Creates comprehensive event ecosystem across different types

// ROBUST DIFFERENTIATION:
// - Monthly Fika (☕): Monthly Sunday coffee tradition, 50 capacity
// - Weekly Fika (🍵): Weekly Wednesday third-wave exploration, 10 capacity 
// - Meiji Forest Walk (🌳): Saturday morning secret paths + coffee, 15 capacity
// - Pizza Night (🍕): Saturday evening authentic NY pizza social, 16 capacity
// - Original Forest Walks: General mindfulness, Inokashira Park
// - Happy Hour: Friday networking drinks, 40 capacity
// - Discussions: Thoughtful conversations, 20 capacity

// EVENT DIVERSITY:
// - Time diversity: Morning (forest), afternoon (pizza), evening (happy hour)
// - Day diversity: Wednesday (weekly fika), Saturday (monthly fika, forest, pizza), Friday (happy hour)
// - Activity diversity: Coffee culture, nature walks, food social, drinks networking, discussions
// - Capacity diversity: 10-50 people across different event types
// - Location diversity: Various Tokyo neighborhoods and venues

// COMPATIBILITY ASSURANCE:
// - Uses same data structure as existing events
// - Compatible with existing icon rendering system
// - Works with existing routing (unique slugs)
// - Philosophy sections follow established format
// - All existing components will render new events without changes